import wretch, { WretchError } from "wretch";
import QueryStringAddon from "wretch/addons/queryString";
import * as z from "zod";
import qs from "qs";
import {
	useQuery,
	UseQueryOptions,
	UseQueryResult as UseBaseQueryResult,
	useMutation,
	UseMutationOptions,
	useInfiniteQuery,
	UseInfiniteQueryOptions,
	UseInfiniteQueryResult as UseBaseInfiniteQueryResult,
	QueryClient,
	QueryFunction,
	QueryKey,
} from "@tanstack/react-query";

const isProduction: boolean = process.env.NODE_ENV === "production";
const invariantPrefix: string = "Invariant failed";

// Throw an error if the condition fails
// Strip out error messages for production
// > Not providing an inline default argument for message as the result is smaller
function invariant(
	condition: any,
	// Can provide a string, or a function that returns a string for cases where
	// the message takes a fair amount of effort to compute
	message?: string | (() => string),
): asserts condition {
	if (condition) {
		return;
	}
	// Condition not passed

	// In production we strip the message but still throw
	if (isProduction) {
		throw new Error(invariantPrefix);
	}

	// When not in production we allow the message to pass through
	// *This block will be removed in production builds*

	const provided: string | undefined =
		typeof message === "function" ? message() : message;

	// Options:
	// 1. message provided: `${invariantPrefix}: ${provided}`
	// 2. message not provided: invariantPrefix
	const value: string = provided
		? `${invariantPrefix}: ${provided}`
		: invariantPrefix;
	throw new Error(value);
}

function error(err: unknown): WretchError {
	return err as WretchError;
}

type ErrorRecord = Record<string, string>;

interface GenericWretchError<T = ErrorRecord> extends WretchError {
	json: T;
}

export function toApiError<T extends ErrorRecord>(
	error: unknown,
): GenericWretchError<T> {
	return error as GenericWretchError<T>;
}

export interface ApiResponse<T> extends Pick<Response, "headers" | "url"> {
	data: T;
	status: number;
}

type UseQueryResult<TData = unknown, TError = unknown> = UseBaseQueryResult<
	TData,
	TError
> & {
	key: QueryKey;
	invalidate: () => Promise<void>;
};

type UseInfiniteQueryResult<
	TData = unknown,
	TError = unknown,
> = UseBaseInfiniteQueryResult<TData, TError> & {
	key: QueryKey;
	invalidate: () => Promise<void>;
};

export const filters = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum([
						"id",
						"amount",
						"clubId",
						"clubName",
						"teamId",
						"teamName",
						"relatedClubId",
						"relatedClubName",
						"relatedTeamId",
						"relatedTeamName",
						"userId",
						"userName",
						"movementType",
						"authorType",
						"source",
						"purchasedAt",
						"createdAt",
						"note",
						"paymentLink",
					])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters = z.infer<typeof filters>;

export const order = z
	.object({
		field: z
			.enum([
				"id",
				"amount",
				"clubName",
				"teamName",
				"relatedClubName",
				"relatedTeamName",
				"userName",
				"movementType",
				"purchasedAt",
				"createdAt",
				"note",
				"authorType",
				"paymentLink",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order = z.infer<typeof order>;

export const TokenMovementType = z.enum([
	"add_purchase",
	"add_gift",
	"add_analysis_refund",
	"remove_refund",
	"remove_remove",
	"remove_analysis",
	"remove_subscription",
	"remove_shared_analysis",
	"transfer_from_club",
	"transfer_to_team",
]);
export type TokenMovementType = z.infer<typeof TokenMovementType>;

export const TokenMovementAuthorType = z.enum(["user", "admin", "system"]);
export type TokenMovementAuthorType = z.infer<typeof TokenMovementAuthorType>;

export const ProcessingLevelEnum = z.enum(["full", "limited"]);
export type ProcessingLevelEnum = z.infer<typeof ProcessingLevelEnum>;

export const TokenMovementSource = z.enum([
	"eyeball_admin",
	"clubhouse_admin",
	"team_admin",
	"user",
	"system",
]);
export type TokenMovementSource = z.infer<typeof TokenMovementSource>;

export const DiscoverTokenMovementRowResponse = z.object({
	id: z.number().int(),
	clubId: z.number().int().nullish(),
	clubName: z.string().nullish(),
	teamId: z.number().int().nullish(),
	teamName: z.string().nullish(),
	relatedClubId: z.number().int().nullish(),
	relatedClubName: z.string().nullish(),
	relatedTeamId: z.number().int().nullish(),
	relatedTeamName: z.string().nullish(),
	userId: z.number().int().nullish(),
	userName: z.string().nullish(),
	amount: z.number().int().optional().default(0),
	movementType: TokenMovementType,
	authorType: TokenMovementAuthorType,
	note: z.string().nullish(),
	purchasedAt: z.string().datetime({ offset: true }).nullish(),
	createdAt: z.string().datetime({ offset: true }).nullish(),
	processingLevel: ProcessingLevelEnum.nullish(),
	homeClubName: z.string().nullish(),
	homeTeamName: z.string().nullish(),
	awayClubName: z.string().nullish(),
	awayTeamName: z.string().nullish(),
	receiptUrl: z.string().nullish(),
	source: TokenMovementSource,
});
export type DiscoverTokenMovementRowResponse = z.infer<
	typeof DiscoverTokenMovementRowResponse
>;

export const DiscoverTokenMovementsResponse = z.object({
	page: z.number().int().default(0),
	limit: z.number().int().default(0),
	pages: z.number().int().default(0),
	total: z.number().int().default(0),
	items: z.array(DiscoverTokenMovementRowResponse).default([]),
});
export type DiscoverTokenMovementsResponse = z.infer<
	typeof DiscoverTokenMovementsResponse
>;

export const DiscoverTokenMovementPostRequest = z.object({
	type: TokenMovementType,
	amount: z.number().int().gt(0),
	purchasedAt: z.string().datetime({ offset: true }).nullish().default(null),
	note: z.string().nullish().default(null),
	paymentLink: z.string().nullish().default(null),
	clubId: z.number().int().nullish().default(null),
	teamId: z.number().int().nullish().default(null),
	relatedClubId: z.number().int().nullish().default(null),
	relatedTeamIds: z.array(z.number().int()).optional().default([]),
	authorType: TokenMovementAuthorType.nullish().default("user"),
});
export type DiscoverTokenMovementPostRequest = z.infer<
	typeof DiscoverTokenMovementPostRequest
>;

export const GPDownloadTypeResponse = z.object({
	title: z.string(),
	type: z.string(),
});
export type GPDownloadTypeResponse = z.infer<typeof GPDownloadTypeResponse>;

export const GeneratedContentReadinessStatus = z.enum([
	"in_progress",
	"ready",
	"failed",
]);
export type GeneratedContentReadinessStatus = z.infer<
	typeof GeneratedContentReadinessStatus
>;

export const TeamTaggedEnum = z.enum(["home", "away", "both"]);
export type TeamTaggedEnum = z.infer<typeof TeamTaggedEnum>;

export const GPGameResponse = z.object({
	id: z.number().int(),
	category: z.string().nullable(),
	type: z.string().nullable(),
	date: z.string().datetime({ offset: true }).nullable(),
	isAnalysisFinished: z.boolean(),
	isAnalysisApproved: z.boolean(),
	isLockedForActualUser: z.boolean(),
	analysisSyncRunning: z.boolean(),
	leftSideStart: z.string().nullable(),
	userClipsCount: z.number().int(),
	isFavorite: z.boolean().nullable(),
	teamSheetDownloadTypes: z.array(GPDownloadTypeResponse).default([]),
	description: z.string().nullable().default(null),
	location: z.string().nullish().default(null),
	providerTag: z.string().nullish().default(null),
	processingLevel: ProcessingLevelEnum.nullish().default(null),
	aiSummaryStatus: GeneratedContentReadinessStatus.nullable().default(null),
	videoUploadId: z.number().int().nullish().default(null),
	teamTagged: TeamTaggedEnum.nullish().default(null),
});
export type GPGameResponse = z.infer<typeof GPGameResponse>;

export const ColorEnum = z.enum([
	"Black",
	"White",
	"Red",
	"Orange",
	"Yellow",
	"Green",
	"Blue",
	"Gray",
	"Brown",
	"Purple",
]);
export type ColorEnum = z.infer<typeof ColorEnum>;

export const ShirtPatternEnum = z.enum([
	"solid",
	"half-and-half",
	"stripes-vertical",
	"stripes-horizontal",
	"stripes-diagonal",
	"other",
]);
export type ShirtPatternEnum = z.infer<typeof ShirtPatternEnum>;

export const GPGameDressKitResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	shirtPattern: ShirtPatternEnum,
	shortsColor: z.string(),
	shirtColorPrimary: z.string(),
	shirtColorSecondary: z.string().nullable().default(null),
});
export type GPGameDressKitResponse = z.infer<typeof GPGameDressKitResponse>;

export const GPClubResponse = z.object({
	id: z.number().int(),
	name: z.string().nullable(),
	slug: z.string().nullable(),
	logo: z.string().nullable(),
	country: z.string().nullable(),
	isPartner: z.boolean(),
});
export type GPClubResponse = z.infer<typeof GPClubResponse>;

export const GPTeamResponse = z.object({
	id: z.number().int(),
	name: z.string().nullable(),
	slug: z.string().nullable(),
	category: z.string().nullable(),
});
export type GPTeamResponse = z.infer<typeof GPTeamResponse>;

export const RatingIcon = z.enum(["good", "neutral", "bad"]);
export type RatingIcon = z.infer<typeof RatingIcon>;

export const RatingVisibility = z.enum([
	"coach",
	"player",
	"player_and_parent",
]);
export type RatingVisibility = z.infer<typeof RatingVisibility>;

export const GPCoachRatingResponse = z.object({
	icon: RatingIcon.nullable(),
	score: z.number().nullable(),
	badgeIds: z.array(z.number().int()),
	visibility: RatingVisibility.nullable(),
	ratedBy: z.string().nullable(),
	ratedAt: z.string().nullable(),
});
export type GPCoachRatingResponse = z.infer<typeof GPCoachRatingResponse>;

export const GenderEnum = z.enum(["Male", "Female", "Mixed"]);
export type GenderEnum = z.infer<typeof GenderEnum>;

export const GPPlayerGradingResponse = z.object({
	id: z.number().int(),
	performanceGrade: z.number().int(),
	lastTimeGraded: z.string().datetime({ offset: true }),
	potentialGrade: z.number().int().nullable(),
	note: z.string().nullable(),
});
export type GPPlayerGradingResponse = z.infer<typeof GPPlayerGradingResponse>;

export const GPGamePlayerResponse = z.object({
	id: z.number().int().nullable(),
	playerId: z.number().int().nullable(),
	teamId: z.number().int().nullable(),
	name: z.string().nullable(),
	birthYear: z.string().nullable(),
	position: z.string().nullable(),
	positionKey: z.string().nullable(),
	numberSign: z.number().int().nullable(),
	isSubstitute: z.boolean(),
	rating: z.number().nullable(),
	goals: z.number().int().nullable(),
	minutesPlayed: z.number().int().nullable(),
	playerUrl: z.string().nullable(),
	coachRating: GPCoachRatingResponse.nullable(),
	photo: z.string().nullable(),
	isAnonymized: z.boolean(),
	gender: GenderEnum.nullable(),
	playerGrading: GPPlayerGradingResponse.nullish(),
	gradedByScouts: z.number().int().nullish().default(null),
});
export type GPGamePlayerResponse = z.infer<typeof GPGamePlayerResponse>;

export const GPTeamPermissionsResponse = z.object({
	editTeamSheet: z.boolean(),
	editColors: z.boolean(),
	ratingBadgeEdit: z.boolean(),
	removePlayers: z.boolean(),
	changeNumbers: z.boolean(),
	anonymizePlayers: z.boolean(),
	supportsPlainTeamSheet: z.boolean(),
	editGender: z.boolean(),
});
export type GPTeamPermissionsResponse = z.infer<
	typeof GPTeamPermissionsResponse
>;

export const GPTeamSheetFormationResponse = z.object({
	value: z.string(),
	name: z.string(),
});
export type GPTeamSheetFormationResponse = z.infer<
	typeof GPTeamSheetFormationResponse
>;

export const GPGameTeamResponse = z.object({
	id: z.number().int(),
	clubName: z.string().nullable(),
	teamName: z.string().nullable(),
	score: z.number().int().nullable(),
	hasStats: z.boolean(),
	colorShirt: ColorEnum.nullable(),
	colorShort: ColorEnum.nullable(),
	gameDressKit: GPGameDressKitResponse.nullable(),
	club: GPClubResponse.nullable(),
	team: GPTeamResponse.nullable(),
	players: z.array(GPGamePlayerResponse),
	permissions: GPTeamPermissionsResponse,
	teamSheetFormations: z.array(GPTeamSheetFormationResponse),
	clubPlayersCount: z.number().int(),
	url: z.string().nullable(),
	logo: z.string().nullable(),
	averageRating: z.number().nullable(),
	teamsheetHidden: z.boolean(),
});
export type GPGameTeamResponse = z.infer<typeof GPGameTeamResponse>;

export const GPVideoResponse = z.object({
	id: z.number().int(),
	url: z.string(),
	isStream: z.boolean(),
	poster: z.string().nullable(),
	thumbs: z.string().nullable(),
});
export type GPVideoResponse = z.infer<typeof GPVideoResponse>;

export const GPPermissionsResponse = z.object({
	showPlayerStats: z.boolean(),
	showTeamStats: z.boolean(),
	saveFavorites: z.boolean(),
	editScore: z.boolean(),
	useClips: z.boolean(),
	adminOptions: z.boolean(),
	showEvents: z.boolean(),
	reportEventError: z.boolean(),
	downloadEvents: z.boolean(),
	showEventComments: z.boolean(),
	downloadVideo: z.boolean(),
	downloadPlayerStats: z.boolean(),
	downloadTeamStats: z.boolean(),
	showRatings: z.boolean(),
	showCoachTooltip: z.boolean(),
	showDataInsights: z.boolean(),
	accessAiSummaryFeature: z.boolean(),
	accessAiSummaryContent: z.boolean(),
	allowedPlayers: z.array(z.number().int()).nullable(),
	showPhysicalAnalysis: z.boolean().default(false),
	playerGrading: z.boolean().default(false),
	showAiCoachReview: z.boolean().default(false),
	showCoachToolbar: z.boolean().default(false),
});
export type GPPermissionsResponse = z.infer<typeof GPPermissionsResponse>;

export const GPHalfResponse = z.object({
	id: z.number().int(),
	formalLength: z.number().int(),
	padding: z.number().int(),
	matchTimeStart: z.number().int(),
	matchTimeEnd: z.number().int(),
	videoTimeStart: z.number().int(),
	videoTimeEnd: z.number().int(),
	halfOffset: z.number().int(),
	isBase: z.boolean(),
});
export type GPHalfResponse = z.infer<typeof GPHalfResponse>;

export const GPPlayerPositionResponse = z.object({
	value: z.string(),
	title: z.string(),
});
export type GPPlayerPositionResponse = z.infer<typeof GPPlayerPositionResponse>;

export const RatingBadgeResponse = z.object({
	id: z.number().int(),
	key: z.string(),
	name: z.string(),
	description: z.string(),
	asset: z.string(),
});
export type RatingBadgeResponse = z.infer<typeof RatingBadgeResponse>;

export const GamePageResponse = z.object({
	game: GPGameResponse,
	homeGameTeam: GPGameTeamResponse,
	awayGameTeam: GPGameTeamResponse,
	video: GPVideoResponse.nullable(),
	permissions: GPPermissionsResponse,
	halves: z.array(GPHalfResponse),
	playerPositions: z.array(GPPlayerPositionResponse),
	ratingBadges: z.array(RatingBadgeResponse),
});
export type GamePageResponse = z.infer<typeof GamePageResponse>;

export const GameEventCommentResponse = z.object({
	id: z.number().int(),
	providerEventId: z.string(),
	text: z.string(),
	lockerRoomVisibility: z.string().nullish(),
	visibleToAll: z.boolean(),
	createdBy: z.string(),
	createdAt: z.string(),
	canEdit: z.boolean(),
	canDelete: z.boolean(),
});
export type GameEventCommentResponse = z.infer<typeof GameEventCommentResponse>;

export const GPEventResponse = z.object({
	id: z.number().int(),
	category: z.string(),
	type: z.string(),
	success: z.boolean().nullable(),
	underPressure: z.boolean().nullable(),
	gamePlayerId: z.number().int().nullable(),
	gameTeamId: z.number().int().nullable(),
	playerName: z.string().nullable(),
	backNumber: z.number().int().nullable(),
	x: z.number().nullable(),
	y: z.number().nullable(),
	half: z.number().int().nullable(),
	halfTime: z.number().int().nullable(),
	halfOvertime: z.number().int().nullable(),
	footballTime: z.number().int().nullable(),
	videoTime: z.number().int(),
	clipTime: z.number().int().nullable(),
	prepend: z.number().int().nullable(),
	append: z.number().int().nullable(),
	providerEventId: z.string().nullable(),
	relativeProviderEventId: z.string().nullable(),
	details: z.record(z.union([z.string(), z.boolean(), z.number()])).nullable(),
	groups: z.array(z.string()).nullable(),
	tags: z.array(z.string()).nullish(),
	isFavorite: z.boolean(),
	comments: z.array(GameEventCommentResponse).default([]),
	commentCount: z
		.number()
		.int()
		.describe("Number of Game Page comments on this event")
		.optional()
		.default(0),
	inCollection: z
		.boolean()
		.describe(
			"Whether this event is saved in any collection (null for desktop)",
		)
		.nullish()
		.default(null),
	teamClubLogo: z
		.string()
		.describe(
			"Club logo of the team the event belongs to. Resolved from the event own game, so it stays correct in cross game clips.",
		)
		.nullish()
		.default(null),
	teamShirtColor: z
		.string()
		.describe(
			"Shirt color of the team the event belongs to. Resolved from the event own game, so it stays correct in cross game clips.",
		)
		.nullish()
		.default(null),
});
export type GPEventResponse = z.infer<typeof GPEventResponse>;

export const GPCommentFormDefaultsResponse = z.object({
	lockerRoomVisibility: z.string().nullish(),
	visibleToAll: z.boolean(),
});
export type GPCommentFormDefaultsResponse = z.infer<
	typeof GPCommentFormDefaultsResponse
>;

export const GamePageLazyResponse = z.object({
	events: z.array(GPEventResponse),
	videoDownloadUrl: z.string().nullable(),
	commentFormDefaults: GPCommentFormDefaultsResponse.nullish().default(null),
});
export type GamePageLazyResponse = z.infer<typeof GamePageLazyResponse>;

export const GamePutScoreRequest = z.object({
	homeTeamScore: z.number().int().gte(0).lte(255),
	awayTeamScore: z.number().int().gte(0).lte(255),
});
export type GamePutScoreRequest = z.infer<typeof GamePutScoreRequest>;

export const GameTeamStatCategoryEnum = z.enum([
	"summary",
	"offensive",
	"defensive",
	"distribution",
]);
export type GameTeamStatCategoryEnum = z.infer<typeof GameTeamStatCategoryEnum>;

export const GameTeamStatTypeEnum = z.enum([
	"goals",
	"possession",
	"shots",
	"shotsOnTarget",
	"fouls",
	"offsides",
	"freeKicks",
	"corners",
	"averageRating",
	"blockedShots",
	"shotsOffTarget",
	"shotsInsidePa",
	"shotsOutsidePa",
	"takeOnsSuccessful",
	"takeOnsTotal",
	"tacklesSuccess",
	"tacklesTotal",
	"aerialDuelsSuccess",
	"aerialDuelsTotal",
	"groundDuelsSuccess",
	"groundDuelsTotal",
	"interceptions",
	"clearances",
	"interventions",
	"recoveries",
	"blocks",
	"mistakes",
	"foulsWon",
	"passesSuccess",
	"passesTotal",
	"passAccuracy",
	"keyPasses",
	"passesInFinalThirdSuccess",
	"passesInFinalThirdTotal",
	"passesInMiddleThirdSuccess",
	"passesInMiddleThirdTotal",
	"passesInDefensiveThirdSuccess",
	"passesInDefensiveThirdTotal",
	"longPassesSuccess",
	"longPassesTotal",
	"mediumRangePassesSuccess",
	"mediumRangePassesTotal",
	"shortPassesSuccess",
	"shortPassesTotal",
	"passesForwardSuccess",
	"passesForwardTotal",
	"passesSidewaysSuccess",
	"passesSidewaysTotal",
	"passesBackwardSuccess",
	"passesBackwardTotal",
	"crossesSuccess",
	"crossesTotal",
	"controlUnderPressure",
	"nonPenaltyXg",
	"nonPenaltyXgPerShot",
	"goalsXgRatio",
	"totalXa",
	"totalXg",
	"nonPenaltyShots",
	"shotCreatingActions",
	"longBallShare",
	"passesIntoFinalThird",
	"passesIntoPenaltyArea",
	"progressivePasses",
	"deepCompletions",
]);
export type GameTeamStatTypeEnum = z.infer<typeof GameTeamStatTypeEnum>;

export const GameTeamStatValueTypeEnum = z.enum([
	"count",
	"percentage",
	"rating",
	"decimal",
]);
export type GameTeamStatValueTypeEnum = z.infer<
	typeof GameTeamStatValueTypeEnum
>;

export const GPTSItemResponse = z.object({
	category: GameTeamStatCategoryEnum,
	statType: GameTeamStatTypeEnum,
	valueType: GameTeamStatValueTypeEnum,
	homeTeamValue: z.union([z.number(), z.number()]).nullable(),
	awayTeamValue: z.union([z.number(), z.number()]).nullable(),
});
export type GPTSItemResponse = z.infer<typeof GPTSItemResponse>;

export const GPTSClubResponse = z.object({
	teamId: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
});
export type GPTSClubResponse = z.infer<typeof GPTSClubResponse>;

export const GameTeamStatResponse = z.object({
	stats: z.array(GPTSItemResponse),
	homeTeam: GPTSClubResponse,
	awayTeam: GPTSClubResponse,
	downloadUrl: z.string().nullable(),
});
export type GameTeamStatResponse = z.infer<typeof GameTeamStatResponse>;

export const GamePlayerStatColumnEnum = z.enum([
	"position",
	"playerName",
	"backNumber",
	"uri",
	"rating",
	"minutesPlayed",
	"goalsScrored",
	"goalsOwn",
	"assists",
	"offsides",
	"shots",
	"freekicks",
	"corners",
	"throwIns",
	"fouls",
	"yellowCards",
	"redCards",
	"shotsOnTarget",
	"blockedShots",
	"shotsOffTarget",
	"shotsInsidePa",
	"shotsOutsidePa",
	"takeOnsSuccessful",
	"takeOnsTotal",
	"tacklesSuccess",
	"tacklesTotal",
	"aerialDuelsSuccess",
	"aerialDuelsTotal",
	"groundDuelsSuccess",
	"groundDuelsTotal",
	"interceptions",
	"clearances",
	"interventions",
	"recoveries",
	"blocks",
	"mistakes",
	"foulsWon",
	"passesSuccess",
	"passesTotal",
	"passAccuracy",
	"keyPasses",
	"passesInFinalThirdSuccess",
	"passesInFinalThirdTotal",
	"passesInMiddleThirdSuccess",
	"passesInMiddleThirdTotal",
	"passesInDefensiveThirdSuccess",
	"passesInDefensiveThirdTotal",
	"longPassesSuccess",
	"longPassesTotal",
	"mediumRangePassesSuccess",
	"mediumRangePassesTotal",
	"shortPassesSuccess",
	"shortPassesTotal",
	"passesForwardSuccess",
	"passesForwardTotal",
	"passesSidewaysSuccess",
	"passesSidewaysTotal",
	"passesBackwardSuccess",
	"passesBackwardTotal",
	"crossesSuccess",
	"crossesTotal",
	"controlUnderPressure",
	"goalsConceded",
	"catches",
	"punches",
	"goalsKicksSuccess",
	"goalsKicksTotal",
	"aerialClearancesSuccess",
	"aerialClearancesTotal",
	"nonPenaltyXg",
	"nonPenaltyXgPerShot",
	"goalsXgRatio",
	"totalXa",
	"totalXg",
	"nonPenaltyShots",
	"throwsSuccess",
	"throwsTotal",
	"throwsRatio",
	"averagePassLength",
	"launchPercent",
	"defensiveActionsOpaTotal",
	"averageDefensiveActionDistance",
	"crossesFacedTotal",
	"crossesStoppedTotal",
	"crossesStoppedRatio",
	"savePercent",
	"nonPenaltyGoals",
	"shotCreatingActions",
	"passesIntoPenaltyArea",
	"switchesOfPlay",
	"progressivePasses",
	"deepCompletions",
]);
export type GamePlayerStatColumnEnum = z.infer<typeof GamePlayerStatColumnEnum>;

export const GPPSCellResponse = z.object({
	type: GamePlayerStatColumnEnum,
	value: z.string().nullable(),
});
export type GPPSCellResponse = z.infer<typeof GPPSCellResponse>;

export const GPPSPlayerResponse = z.object({
	id: z.number().int(),
	teamId: z.number().int().nullable(),
	isGoalkeeper: z.boolean(),
	columns: z.array(GPPSCellResponse),
});
export type GPPSPlayerResponse = z.infer<typeof GPPSPlayerResponse>;

export const GPPSClubResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
});
export type GPPSClubResponse = z.infer<typeof GPPSClubResponse>;

export const GamePlayerStatResponse = z.object({
	players: z.array(GPPSPlayerResponse),
	homeClub: GPPSClubResponse,
	awayClub: GPPSClubResponse,
	downloadUrl: z.string().nullable(),
});
export type GamePlayerStatResponse = z.infer<typeof GamePlayerStatResponse>;

export const GameTeamPutColorsRequest = z
	.object({
		colorShort: ColorEnum.nullable().default(null),
		colorShirt: ColorEnum.nullable().default(null),
	})
	.partial();
export type GameTeamPutColorsRequest = z.infer<typeof GameTeamPutColorsRequest>;

export const RatingPutRequest = z.object({
	score: z.number(),
	visibility: RatingVisibility,
});
export type RatingPutRequest = z.infer<typeof RatingPutRequest>;

export const BadgesPutRequest = z
	.object({ badgeIds: z.array(z.number().int()).default([]) })
	.partial();
export type BadgesPutRequest = z.infer<typeof BadgesPutRequest>;

export const HighlightPostRequest = z.object({
	title: z.string().max(255),
	note: z.string(),
	timestamp: z.number().int(),
});
export type HighlightPostRequest = z.infer<typeof HighlightPostRequest>;

export const HighlightPatchRequest = z
	.object({
		title: z.string().max(255).nullable(),
		note: z.string().nullable(),
		timestamp: z.number().int().nullable(),
	})
	.partial();
export type HighlightPatchRequest = z.infer<typeof HighlightPatchRequest>;

export const filters__2 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum(["gameId", "author", "isFavorite", "tag"])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__2 = z.infer<typeof filters__2>;

export const order__2 = z
	.object({
		field: z.literal("createdAt").describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__2 = z.infer<typeof order__2>;

export const GameClipsRowResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	description: z.string().nullable(),
	url: z.string().nullable(),
	createdAt: z.string().datetime({ offset: true }),
	eventsCount: z.number().int().default(0),
	isFinished: z.boolean(),
	expireLinkAt: z.string().datetime({ offset: true }).nullable(),
	shareSlug: z.string().nullable(),
	password: z.string().nullable(),
});
export type GameClipsRowResponse = z.infer<typeof GameClipsRowResponse>;

export const GameClipsResponse = z
	.object({
		items: z.array(GameClipsRowResponse).default([]),
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
	})
	.partial();
export type GameClipsResponse = z.infer<typeof GameClipsResponse>;

export const GameClipGameTeamResponse = z.object({
	id: z.number().int(),
	clubName: z.string().nullable(),
	teamName: z.string().nullable(),
	colorShirt: ColorEnum.nullable(),
	colorShort: ColorEnum.nullable(),
	logo: z.string().nullable(),
});
export type GameClipGameTeamResponse = z.infer<typeof GameClipGameTeamResponse>;

export const GPCreatedByResponse = z.object({
	userPhoto: z.string().nullable(),
	name: z.string(),
});
export type GPCreatedByResponse = z.infer<typeof GPCreatedByResponse>;

export const GameClipResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	description: z.string().nullable(),
	isFinished: z.boolean(),
	url: z.string().nullable(),
	createdAt: z.string().datetime({ offset: true }),
	events: z.array(GPEventResponse),
	expireLinkAt: z.string().datetime({ offset: true }).nullable(),
	shareSlug: z.string().nullable(),
	password: z.string().nullable(),
	homeGameTeam: GameClipGameTeamResponse,
	awayGameTeam: GameClipGameTeamResponse,
	createdBy: GPCreatedByResponse,
});
export type GameClipResponse = z.infer<typeof GameClipResponse>;

export const GameClipPutRequest = z.object({
	name: z.string().max(255),
	description: z.string().max(255).nullish().default(null),
});
export type GameClipPutRequest = z.infer<typeof GameClipPutRequest>;

export const GameClipEventRequest = z.object({
	id: z.number().int(),
	prepend: z.number().int().gte(0).lte(120000),
	append: z.number().int().gte(0).lte(120000),
});
export type GameClipEventRequest = z.infer<typeof GameClipEventRequest>;

export const GameClipIntervalRequest = z.object({
	start: z.number().int(),
	end: z.number().int(),
	events: z.array(GameClipEventRequest),
});
export type GameClipIntervalRequest = z.infer<typeof GameClipIntervalRequest>;

export const GameClipPostRequest = z.object({
	name: z.string().max(255),
	description: z.string().max(255),
	intervals: z.array(GameClipIntervalRequest),
	tags: z.array(z.string()).nullish().default(null),
});
export type GameClipPostRequest = z.infer<typeof GameClipPostRequest>;

export const GamePlayerItemRequest = z.object({
	position: z.string().nullable().default(null),
	name: z.string().nullable().default(null),
	number: z.number().int().nullable().default(null),
	goals: z.number().int().nullable().default(null),
	idPlayer: z.string().nullish().default(null),
	id: z.string().nullish().default(null),
	substitute: z.boolean().optional().default(false),
	discover: z.boolean().optional().default(false),
	isAnonymized: z.boolean(),
	discovery: z.boolean(),
	valid: z.boolean(),
});
export type GamePlayerItemRequest = z.infer<typeof GamePlayerItemRequest>;

export const GameTeamSheetRequest = z.object({
	colors: GameTeamPutColorsRequest.nullable().default(null),
	squad: z.array(GamePlayerItemRequest).default([]),
	substitutes: z.array(GamePlayerItemRequest).default([]),
	saveAnyway: z.boolean().default(false),
	gender: z.string().nullable().default(null),
});
export type GameTeamSheetRequest = z.infer<typeof GameTeamSheetRequest>;

export const GTSCompositionPlayer = z.object({
	id: z.number().int(),
	position: z.string(),
	displayOrder: z.number().int(),
});
export type GTSCompositionPlayer = z.infer<typeof GTSCompositionPlayer>;

export const GTSCompositionPosition = z.object({
	position: z.string(),
	players: z.array(GTSCompositionPlayer),
});
export type GTSCompositionPosition = z.infer<typeof GTSCompositionPosition>;

export const GameTeamSheetCompositionPlayersResponse = z.object({
	compositionPlayers: z.array(GTSCompositionPosition),
});
export type GameTeamSheetCompositionPlayersResponse = z.infer<
	typeof GameTeamSheetCompositionPlayersResponse
>;

export const GSTGameTeamPlayerResponse = z.object({
	id: z.number().int(),
	name: z.string(),
});
export type GSTGameTeamPlayerResponse = z.infer<
	typeof GSTGameTeamPlayerResponse
>;

export const GameTeamSheetGameTeamPlayersSearchResponse = z.object({
	players: z.array(GSTGameTeamPlayerResponse),
});
export type GameTeamSheetGameTeamPlayersSearchResponse = z.infer<
	typeof GameTeamSheetGameTeamPlayersSearchResponse
>;

export const GTSPlayerResponse = z.object({
	id: z.number().int(),
	name: z.string().nullable(),
	birthDay: z.string().nullable(),
	position: z.string().nullable(),
});
export type GTSPlayerResponse = z.infer<typeof GTSPlayerResponse>;

export const GameTeamSheetPlayersResponse = z.object({
	players: z.array(GTSPlayerResponse),
});
export type GameTeamSheetPlayersResponse = z.infer<
	typeof GameTeamSheetPlayersResponse
>;

export const GamePutDescriptionRequest = z
	.object({ description: z.string().nullable().default(null) })
	.partial();
export type GamePutDescriptionRequest = z.infer<
	typeof GamePutDescriptionRequest
>;

export const GameStatsExportTypeEnum = z.enum(["playerStats", "teamStats"]);
export type GameStatsExportTypeEnum = z.infer<typeof GameStatsExportTypeEnum>;

export const FileExtensionEnum = z.enum(["csv", "json", "xlsx", "xml"]);
export type FileExtensionEnum = z.infer<typeof FileExtensionEnum>;

export const GameStatsExportCellRequest = z.object({
	key: z.string(),
	value: z.union([z.boolean(), z.number(), z.number(), z.string()]).nullable(),
});
export type GameStatsExportCellRequest = z.infer<
	typeof GameStatsExportCellRequest
>;

export const GameStatsExportRequest = z.object({
	type: GameStatsExportTypeEnum,
	format: FileExtensionEnum,
	name: z.string(),
	header: z.array(GameStatsExportCellRequest).default([]),
	rows: z.array(GameStatsExportCellRequest).default([]),
});
export type GameStatsExportRequest = z.infer<typeof GameStatsExportRequest>;

export const FileResponse = z.object({
	fileName: z.string(),
	file: z.string(),
	fileType: z.string(),
});
export type FileResponse = z.infer<typeof FileResponse>;

export const GameEventDataExportRequest = z.object({
	extension: FileExtensionEnum,
	events: z.array(z.number().int()).default([]),
});
export type GameEventDataExportRequest = z.infer<
	typeof GameEventDataExportRequest
>;

export const GameClipProtectedResponse = z.object({
	loggedIn: z.boolean(),
	protected: z.boolean(),
});
export type GameClipProtectedResponse = z.infer<
	typeof GameClipProtectedResponse
>;

export const GameClipPostPasswordRequest = z.object({ password: z.string() });
export type GameClipPostPasswordRequest = z.infer<
	typeof GameClipPostPasswordRequest
>;

export const GameEventFilterItemResponse = z.object({
	key: z.string(),
	value: z.array(z.string()),
});
export type GameEventFilterItemResponse = z.infer<
	typeof GameEventFilterItemResponse
>;

export const GameEventFilterResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	filters: z.array(GameEventFilterItemResponse),
});
export type GameEventFilterResponse = z.infer<typeof GameEventFilterResponse>;

export const GameEventFilterItemPostRequest = z.object({
	key: z.string(),
	value: z.array(z.string()).min(1),
});
export type GameEventFilterItemPostRequest = z.infer<
	typeof GameEventFilterItemPostRequest
>;

export const GameEventFilterPostRequest = z.object({
	name: z.string().max(80),
	filters: z.array(GameEventFilterItemPostRequest).min(1),
});
export type GameEventFilterPostRequest = z.infer<
	typeof GameEventFilterPostRequest
>;

export const GameEventCommentPostRequest = z.object({
	text: z.string(),
	lockerRoomVisibility: z.enum([
		"players",
		"players_and_parents",
		"tagged_players",
		"tagged_players_and_their_parents",
	]),
	visibleToAll: z.boolean(),
	setChoicesAsDefault: z.boolean(),
});
export type GameEventCommentPostRequest = z.infer<
	typeof GameEventCommentPostRequest
>;

export const StatusMessageDetailResponse = z.object({
	key: z.string(),
	message: z.string(),
});
export type StatusMessageDetailResponse = z.infer<
	typeof StatusMessageDetailResponse
>;

export const StatusMessageResponse = z.object({
	message: z.string().describe("Status message"),
	httpCode: z.number().int().describe("HTTP status code"),
	details: z.array(StatusMessageDetailResponse).describe("Additional details"),
});
export type StatusMessageResponse = z.infer<typeof StatusMessageResponse>;

export const SingleCountryData = z.object({
	code: z.string(),
	name: z.string(),
	tooltip: z.string().nullable(),
	enabled: z.boolean(),
});
export type SingleCountryData = z.infer<typeof SingleCountryData>;

export const CountryData = z.object({
	africa: z.array(SingleCountryData),
	asia: z.array(SingleCountryData),
	europe: z.array(SingleCountryData),
	northAmerica: z.array(SingleCountryData),
	southAmerica: z.array(SingleCountryData),
	oceania: z.array(SingleCountryData),
	nationalContinents: z.array(SingleCountryData),
});
export type CountryData = z.infer<typeof CountryData>;

export const GenderViewResponse = z.object({
	available: z.boolean(),
	countries: CountryData.nullish(),
});
export type GenderViewResponse = z.infer<typeof GenderViewResponse>;

export const GendersViewResponse = z.object({
	male: GenderViewResponse,
	female: GenderViewResponse,
});
export type GendersViewResponse = z.infer<typeof GendersViewResponse>;

export const DataSourceOptionResponse = z.object({
	key: z.string(),
	label: z.string(),
	isSelectable: z.boolean(),
	genders: GendersViewResponse.nullish().default(null),
	logo: z.string().nullish().default(null),
	country: z.string().nullish().default(null),
	teamCount: z.number().int().optional().default(0),
});
export type DataSourceOptionResponse = z.infer<typeof DataSourceOptionResponse>;

export const DataSourceViewResponse = z.object({
	key: z.string(),
	label: z.string(),
	isSelectable: z.boolean(),
	genders: GendersViewResponse.nullish(),
	teamGroups: z.array(DataSourceOptionResponse).nullish(),
});
export type DataSourceViewResponse = z.infer<typeof DataSourceViewResponse>;

export const UserViewResponse = z.object({
	dataSources: z.array(DataSourceViewResponse),
});
export type UserViewResponse = z.infer<typeof UserViewResponse>;

export const UserViewRequest = z.object({
	gender: GenderEnum,
	scope: z.string(),
	teamGroups: z
		.union([z.array(z.string()), z.record(z.string())])
		.optional()
		.default([]),
});
export type UserViewRequest = z.infer<typeof UserViewRequest>;

export const MfaDomainScopeEnum = z.enum(["Included", "Excluded"]);
export type MfaDomainScopeEnum = z.infer<typeof MfaDomainScopeEnum>;

export const MfaDomainProductEnum = z.literal("Scout");
export type MfaDomainProductEnum = z.infer<typeof MfaDomainProductEnum>;

export const MfaDomainResponse = z.object({
	id: z.number().int().nullable(),
	domain: z.string(),
	enabled: z.boolean(),
	scope: MfaDomainScopeEnum,
	product: MfaDomainProductEnum.nullable(),
});
export type MfaDomainResponse = z.infer<typeof MfaDomainResponse>;

export const MfaDomainListResponse = z.object({
	domains: z.array(MfaDomainResponse),
});
export type MfaDomainListResponse = z.infer<typeof MfaDomainListResponse>;

export const MfaDomainCheckResponse = z.object({ isMfaRequired: z.boolean() });
export type MfaDomainCheckResponse = z.infer<typeof MfaDomainCheckResponse>;

export const ReportedTaggingIssuePostRequest = z.object({
	errorType: z.enum([
		"wrong player",
		"wrong event type",
		"event missing",
		"duplicated event",
		"other",
	]),
	videoTime: z.number().int().gte(0),
	gameEventDataV3Id: z.number().int().gt(0).nullish().default(null),
	description: z.string().optional().default(""),
	jerseyNumber: z.number().int().nullish().default(null),
	details: z.object({}).partial().nullish().default(null),
	selectedGameTeamId: z.number().int().nullish().default(null),
	selectedClubName: z.string().max(255).nullish().default(null),
	correctGamePlayerId: z.number().int().nullish().default(null),
	correctPlayerName: z.string().max(255).nullish().default(null),
	wrongEventType: z.string().max(63).nullish().default(null),
	wrongEventLabel: z.string().max(255).nullish().default(null),
	correctEventType: z.string().max(63).nullish().default(null),
	correctEventLabel: z.string().max(255).nullish().default(null),
});
export type ReportedTaggingIssuePostRequest = z.infer<
	typeof ReportedTaggingIssuePostRequest
>;

export const SelectedCountriesItemsResponse = z.object({
	africa: z.array(z.string()),
	asia: z.array(z.string()),
	europe: z.array(z.string()),
	northAmerica: z.array(z.string()),
	southAmerica: z.array(z.string()),
	oceania: z.array(z.string()),
	nationalContinents: z.array(z.string()),
});
export type SelectedCountriesItemsResponse = z.infer<
	typeof SelectedCountriesItemsResponse
>;

export const SelectedCountriesResponse = z.object({
	countries: SelectedCountriesItemsResponse,
	count: z.number().int(),
});
export type SelectedCountriesResponse = z.infer<
	typeof SelectedCountriesResponse
>;

export const UserCountriesPatchRequest = z.object({
	africa: z.array(z.string()).default([]),
	asia: z.array(z.string()).default([]),
	europe: z.array(z.string()).default([]),
	northAmerica: z.array(z.string()).default([]),
	southAmerica: z.array(z.string()).default([]),
	oceania: z.array(z.string()).default([]),
	nationalContinents: z.array(z.string()).default([]),
});
export type UserCountriesPatchRequest = z.infer<
	typeof UserCountriesPatchRequest
>;

export const TeamGroupTeamResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullish(),
	country: z.string().nullish(),
	gender: GenderEnum.nullish(),
});
export type TeamGroupTeamResponse = z.infer<typeof TeamGroupTeamResponse>;

export const TeamGroupClubResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullish(),
	country: z.string().nullish(),
});
export type TeamGroupClubResponse = z.infer<typeof TeamGroupClubResponse>;

export const TeamGroupVisibleEntitiesResponse = z.object({
	teams: z.array(TeamGroupTeamResponse),
	clubs: z.array(TeamGroupClubResponse),
});
export type TeamGroupVisibleEntitiesResponse = z.infer<
	typeof TeamGroupVisibleEntitiesResponse
>;

export const GameSummaryStatResponse = z.object({
	name: z.string(),
	value: z.union([z.number(), z.number(), z.number(), z.number()]),
});
export type GameSummaryStatResponse = z.infer<typeof GameSummaryStatResponse>;

export const GameSummaryKeyPerformerResponse = z.object({
	playerName: z.string(),
	rating: z.number().nullable(),
	detail: z.string(),
	gamePlayerId: z.number().int(),
	playerDataId: z.number().int().nullable(),
	isProfileAccessAllowed: z.boolean(),
	playerId: z.number().int().nullable(),
	gameTeamId: z.number().int(),
	clubId: z.number().int().nullable(),
	teamId: z.number().int().nullable(),
	teamName: z.string().nullable(),
	numberSign: z.number().int().nullable(),
	stats: z.array(GameSummaryStatResponse),
});
export type GameSummaryKeyPerformerResponse = z.infer<
	typeof GameSummaryKeyPerformerResponse
>;

export const GameSummaryFeedbackResponse = z.object({
	id: z.string().uuid(),
	rating: z.union([z.literal(1), z.literal(-1)]),
	text: z
		.string()
		.describe("Optional text comment about the summary.")
		.nullable(),
});
export type GameSummaryFeedbackResponse = z.infer<
	typeof GameSummaryFeedbackResponse
>;

export const GameSummaryResponse = z.object({
	id: z.string().uuid(),
	gameId: z.number().int(),
	narrative: z.array(z.string()),
	keyPerformers: z.array(GameSummaryKeyPerformerResponse),
	generatedAt: z.string().datetime({ offset: true }),
	feedback: GameSummaryFeedbackResponse.nullable(),
});
export type GameSummaryResponse = z.infer<typeof GameSummaryResponse>;

export const PutGameSummaryFeedbackRequest = z.object({
	rating: z.union([z.literal(1), z.literal(-1)]),
	feedback: z.string().nullable(),
});
export type PutGameSummaryFeedbackRequest = z.infer<
	typeof PutGameSummaryFeedbackRequest
>;

export const UserInfoResponse = z.object({
	id: z.number().int(),
	access: z.enum(["demo", "full"]),
	selectedGender: z.enum(["Male", "Female", "Mixed"]),
	countries: z.array(z.string()).nullable(),
});
export type UserInfoResponse = z.infer<typeof UserInfoResponse>;

export const MessageResponse = z.object({
	id: z.string(),
	content: z.string(),
	createdAt: z.string().datetime({ offset: true }),
	turn: z.number().int(),
});
export type MessageResponse = z.infer<typeof MessageResponse>;

export const ConversationResponse = z.object({
	id: z.string(),
	title: z.string().nullable(),
	pinned: z.boolean(),
	createdAt: z.string().datetime({ offset: true }),
	updatedAt: z.string().datetime({ offset: true }),
	messages: z.array(MessageResponse).nullable(),
});
export type ConversationResponse = z.infer<typeof ConversationResponse>;

export const PaginationResponse = z.object({
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type PaginationResponse = z.infer<typeof PaginationResponse>;

export const ConversationsResponse = z.object({
	conversations: z.array(ConversationResponse),
	pagination: PaginationResponse,
});
export type ConversationsResponse = z.infer<typeof ConversationsResponse>;

export const PromptCardResponse = z.object({
	id: z.string(),
	text: z.string(),
	category: z.enum(["quick", "deep", "advanced"]),
});
export type PromptCardResponse = z.infer<typeof PromptCardResponse>;

export const portalAIPutExecutionFeedback_Body = z.object({
	feedback: z.union([z.literal(1), z.literal(-1)]),
});
export type portalAIPutExecutionFeedback_Body = z.infer<
	typeof portalAIPutExecutionFeedback_Body
>;

export const FeedbackResponse = z.object({
	id: z.string(),
	feedback: z.union([z.literal(1), z.literal(-1)]),
});
export type FeedbackResponse = z.infer<typeof FeedbackResponse>;

export const NationalLeagueResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
	category: z.string().nullable(),
});
export type NationalLeagueResponse = z.infer<typeof NationalLeagueResponse>;

export const NationalLeaguesResponse = z.object({
	leagues: z.array(NationalLeagueResponse),
	name: z.string(),
	country: z.string(),
});
export type NationalLeaguesResponse = z.infer<typeof NationalLeaguesResponse>;

export const NationalLeaguesClubsResponse = z.object({
	name: z.string(),
	id: z.number().int(),
});
export type NationalLeaguesClubsResponse = z.infer<
	typeof NationalLeaguesClubsResponse
>;

export const CountriesLeagueRequest = z.object({
	country: z.string(),
	leagueIds: z.array(z.number()).default([]),
});
export type CountriesLeagueRequest = z.infer<typeof CountriesLeagueRequest>;

export const GameData = z.object({
	id: z.number().int(),
	isAnalysisFinished: z.boolean().nullable(),
	type: z.string().nullable(),
	date: z.string().nullable(),
	description: z.string().nullable(),
	homeTeamName: z.string().nullable(),
	awayTeamName: z.string().nullable(),
	homeClubName: z.string().nullable(),
	awayClubName: z.string().nullable(),
	homeTeamsScore: z.number().int().nullable(),
	awayTeamsScore: z.number().int().nullable(),
	homeClubLogo: z.string().nullable(),
	awayClubLogo: z.string().nullable(),
	isFavorite: z.boolean(),
	category: z.string().nullable().default(null),
	playerRating: z.number().nullish().default(null),
	minutesPlayed: z.number().int().nullish().default(null),
	goalsScored: z.number().int().nullish().default(null),
	videoUploadId: z.number().int().nullish().default(null),
	isClickable: z.boolean().nullish(),
	imageUrl: z.string().nullable().default(null),
	gender: z.string().nullable().default(null),
});
export type GameData = z.infer<typeof GameData>;

export const GamesResponse = z.object({
	games: z.array(GameData),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
	nonOwnerShipGamesCount: z.number().int(),
});
export type GamesResponse = z.infer<typeof GamesResponse>;

export const NationalLeaguesCountriesResponse = z.object({
	name: z.string(),
	country: z.string(),
	isSubscription: z.boolean(),
});
export type NationalLeaguesCountriesResponse = z.infer<
	typeof NationalLeaguesCountriesResponse
>;

export const ErrorResponse = z.object({ code: z.string() });
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const PlayerGradingPostRequest = z.object({
	performanceGrade: z.number().int().gte(1).lte(10),
	potentialGrade: z.number().int().gte(1).lte(10).nullish().default(null),
	note: z.string().max(255).nullish().default(null),
});
export type PlayerGradingPostRequest = z.infer<typeof PlayerGradingPostRequest>;

export const PlayerGradingDetailResponseDTO = z.object({
	performanceGrade: z.number().int(),
	lastTimeGraded: z.string().datetime({ offset: true }),
	potentialGrade: z.number().int().nullable(),
	note: z.string().nullable(),
});
export type PlayerGradingDetailResponseDTO = z.infer<
	typeof PlayerGradingDetailResponseDTO
>;

export const PlayerGradingPutRequest = z.object({
	performanceGrade: z.number().int().gte(1).lte(10),
	potentialGrade: z.number().int().gte(1).lte(10).nullish().default(null),
	note: z.string().max(255).nullish().default(null),
});
export type PlayerGradingPutRequest = z.infer<typeof PlayerGradingPutRequest>;

export const filters__3 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum(["performanceGrade", "potentialGrade", "contributor"])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z.array(z.string()),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__3 = z.infer<typeof filters__3>;

export const order__3 = z
	.object({
		field: z.literal("gradedAt").describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__3 = z.infer<typeof order__3>;

export const PlayerGradingNoteHeaderResponseDTO = z.object({
	playerName: z.string(),
	performanceGrading: z.number(),
	eyeballRating: z.number().nullish().default(null),
	potentialRating: z.number().nullish().default(null),
});
export type PlayerGradingNoteHeaderResponseDTO = z.infer<
	typeof PlayerGradingNoteHeaderResponseDTO
>;

export const ListFilterItem = z.object({
	label: z.string(),
	value: z.string(),
});
export type ListFilterItem = z.infer<typeof ListFilterItem>;

export const NoteListFilters = z.object({
	performanceGrade: z.array(ListFilterItem),
	potentialGrade: z.array(ListFilterItem),
	sort: z.array(ListFilterItem),
	contributors: z.array(ListFilterItem).nullish().default(null),
});
export type NoteListFilters = z.infer<typeof NoteListFilters>;

export const PlayerGradingNoteResponseDTO = z.object({
	gameId: z.number().int(),
	gameName: z.string(),
	note: z.string().nullish(),
	gradedAt: z.string(),
	performanceGrade: z.number().int(),
	gradedBy: z.string().nullish().default(null),
	gradedByPhoto: z.string().nullish().default(null),
	potentialGrade: z.number().int().nullish().default(null),
});
export type PlayerGradingNoteResponseDTO = z.infer<
	typeof PlayerGradingNoteResponseDTO
>;

export const PlayerGradingNotesResponseDTO = z.object({
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
	header: PlayerGradingNoteHeaderResponseDTO,
	filters: NoteListFilters,
	items: z.array(PlayerGradingNoteResponseDTO).default([]),
});
export type PlayerGradingNotesResponseDTO = z.infer<
	typeof PlayerGradingNotesResponseDTO
>;

export const PlayerGradingContributorsResponseDTO = z.object({
	contributors: z.array(ListFilterItem),
});
export type PlayerGradingContributorsResponseDTO = z.infer<
	typeof PlayerGradingContributorsResponseDTO
>;

export const PlayerInfo = z.object({
	id: z.number().int(),
	isPartner: z.boolean(),
	isNonPartner: z.boolean(),
	isDiscover: z.boolean(),
	firstName: z.string(),
	lastName: z.string().nullable(),
	birthday: z.string().nullish(),
	strongestFoot: z.string().nullable(),
	height: z.number().int().nullable(),
	weight: z.number().int().nullable(),
	position: z.string().nullable(),
	position2: z.string().nullable(),
	photo: z.string().nullable(),
	nationality: z.string().nullable(),
	nationalityCode: z.string().nullable(),
	nationality2: z.string().nullable(),
	nationality2Code: z.string().nullable(),
	isFavorite: z.boolean(),
	hasTeam: z.boolean(),
	playedInNationalTeam: z.boolean(),
	notes: z.string().nullable(),
	gender: z.string().nullable(),
});
export type PlayerInfo = z.infer<typeof PlayerInfo>;

export const ClubInfo = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
	region: z.string().nullable(),
	country: z.string().nullable(),
	countryCode: z.string().nullable(),
	contactName: z.string().nullable(),
	contactPhone: z.string().nullable(),
	contactEmail: z.string().nullable(),
	contactAddress: z.string().nullable(),
	clubUrl: z.string().nullish(),
	biography: z.string().nullish(),
	zipcode: z.union([z.string(), z.number()]).nullish(),
	contactPosition: z.string().nullish(),
	city: z.string().nullish(),
	geographicalArea: z.string().nullish(),
	isFavorite: z.boolean(),
	showCase: z.boolean().nullish(),
	gamesCount: z.number().int().nullish(),
	playersCount: z.number().int().nullish(),
	teamsCount: z.number().int().nullish(),
	type: z.string(),
});
export type ClubInfo = z.infer<typeof ClubInfo>;

export const PlayerProfileHeaderTeamInfo = z.object({
	logo: z.string().nullable(),
	teamName: z.string().nullable(),
	clubName: z.string().nullable(),
	category: z.string().nullable(),
	teamId: z.number().int().nullish(),
});
export type PlayerProfileHeaderTeamInfo = z.infer<
	typeof PlayerProfileHeaderTeamInfo
>;

export const PlayerProfileHeaderResponse = z.object({
	playerInfo: PlayerInfo,
	clubInfo: ClubInfo.nullable(),
	teamsInfo: z.array(PlayerProfileHeaderTeamInfo),
});
export type PlayerProfileHeaderResponse = z.infer<
	typeof PlayerProfileHeaderResponse
>;

export const PlayerProfileLastGamesCountFilterOption = z.object({
	label: z.string(),
	value: z.number().int().nullable(),
});
export type PlayerProfileLastGamesCountFilterOption = z.infer<
	typeof PlayerProfileLastGamesCountFilterOption
>;

export const PlayerProfileLastGamesCountFilter = z.object({
	options: z.array(PlayerProfileLastGamesCountFilterOption),
});
export type PlayerProfileLastGamesCountFilter = z.infer<
	typeof PlayerProfileLastGamesCountFilter
>;

export const PlayerProfileAverageRatingResponse = z.object({
	rating: z.number().nullable(),
	ratingChange: z.number().nullable(),
	percentTop: z.number().int().nullable(),
	lastRatings: z.array(z.number()),
	filterGamesCount: PlayerProfileLastGamesCountFilter,
});
export type PlayerProfileAverageRatingResponse = z.infer<
	typeof PlayerProfileAverageRatingResponse
>;

export const PlayerProfileStandoutMetricsStat = z.object({
	name: z.string(),
	value: z.number(),
	percentTop: z.number(),
	percentChange: z.number().int().nullable(),
});
export type PlayerProfileStandoutMetricsStat = z.infer<
	typeof PlayerProfileStandoutMetricsStat
>;

export const PlayerProfileStandoutMetricsResponse = z.object({
	stats: z.array(PlayerProfileStandoutMetricsStat),
	filterGamesCount: PlayerProfileLastGamesCountFilter,
});
export type PlayerProfileStandoutMetricsResponse = z.infer<
	typeof PlayerProfileStandoutMetricsResponse
>;

export const PlayerProfileSeasonalStatsStat = z.object({
	name: z.string(),
	value: z.number().int(),
});
export type PlayerProfileSeasonalStatsStat = z.infer<
	typeof PlayerProfileSeasonalStatsStat
>;

export const PlayerProfileSeasonFilterOption = z.object({
	label: z.string(),
	value: z.number().int().nullable(),
});
export type PlayerProfileSeasonFilterOption = z.infer<
	typeof PlayerProfileSeasonFilterOption
>;

export const PlayerProfileSeasonFilter = z.object({
	options: z.array(PlayerProfileSeasonFilterOption),
});
export type PlayerProfileSeasonFilter = z.infer<
	typeof PlayerProfileSeasonFilter
>;

export const PlayerProfileSeasonalStatsResponse = z.object({
	stats: z.array(PlayerProfileSeasonalStatsStat),
	minutesPlayed: z.array(z.number().int()),
	filterSeason: PlayerProfileSeasonFilter,
});
export type PlayerProfileSeasonalStatsResponse = z.infer<
	typeof PlayerProfileSeasonalStatsResponse
>;

export const PlayerProfileGameTypeFilterOption = z.object({
	label: z.string(),
	value: z.string().nullable(),
});
export type PlayerProfileGameTypeFilterOption = z.infer<
	typeof PlayerProfileGameTypeFilterOption
>;

export const PlayerProfileGameTypeFilter = z.object({
	options: z.array(PlayerProfileGameTypeFilterOption),
});
export type PlayerProfileGameTypeFilter = z.infer<
	typeof PlayerProfileGameTypeFilter
>;

export const PlayerProfileLatestGamesResponse = z.object({
	games: z.array(GameData),
	filterGameType: PlayerProfileGameTypeFilter,
	filterSeason: PlayerProfileSeasonFilter,
});
export type PlayerProfileLatestGamesResponse = z.infer<
	typeof PlayerProfileLatestGamesResponse
>;

export const PlayerProfileGameNationalFilterOption = z.object({
	label: z.string(),
	value: z.string().nullable(),
});
export type PlayerProfileGameNationalFilterOption = z.infer<
	typeof PlayerProfileGameNationalFilterOption
>;

export const PlayerProfileGameNationalFilter = z.object({
	options: z.array(PlayerProfileGameNationalFilterOption),
});
export type PlayerProfileGameNationalFilter = z.infer<
	typeof PlayerProfileGameNationalFilter
>;

export const PlayerProfileTeamFilterOption = z.object({
	label: z.string(),
	value: z.number().int(),
});
export type PlayerProfileTeamFilterOption = z.infer<
	typeof PlayerProfileTeamFilterOption
>;

export const PlayerProfileTeamFilter = z.object({
	options: z.array(PlayerProfileTeamFilterOption),
});
export type PlayerProfileTeamFilter = z.infer<typeof PlayerProfileTeamFilter>;

export const PlayerProfileOnlyAnalyzedFilterOption = z.object({
	label: z.string(),
	value: z.boolean().nullable(),
});
export type PlayerProfileOnlyAnalyzedFilterOption = z.infer<
	typeof PlayerProfileOnlyAnalyzedFilterOption
>;

export const PlayerProfileOnlyAnalyzedFilter = z.object({
	options: z.array(PlayerProfileOnlyAnalyzedFilterOption),
});
export type PlayerProfileOnlyAnalyzedFilter = z.infer<
	typeof PlayerProfileOnlyAnalyzedFilter
>;

export const PlayerProfileGamesResponse = z.object({
	games: z.array(GameData),
	filterGameType: PlayerProfileGameTypeFilter,
	filterGameNational: PlayerProfileGameNationalFilter,
	filterSeason: PlayerProfileSeasonFilter,
	filterTeam: PlayerProfileTeamFilter,
	filterOnlyAnalyzed: PlayerProfileOnlyAnalyzedFilter,
});
export type PlayerProfileGamesResponse = z.infer<
	typeof PlayerProfileGamesResponse
>;

export const PlayerProfilePerformanceRadarStat = z.object({
	name: z.string(),
	rating: z.number().nullable(),
	ratingIsPercent: z.boolean(),
	ratingPercentTop: z.number().int().nullable(),
});
export type PlayerProfilePerformanceRadarStat = z.infer<
	typeof PlayerProfilePerformanceRadarStat
>;

export const PlayerProfilePositionFilterOption = z.object({
	label: z.string(),
	value: z.string(),
});
export type PlayerProfilePositionFilterOption = z.infer<
	typeof PlayerProfilePositionFilterOption
>;

export const PlayerProfilePositionFilter = z.object({
	options: z.array(PlayerProfilePositionFilterOption),
});
export type PlayerProfilePositionFilter = z.infer<
	typeof PlayerProfilePositionFilter
>;

export const PlayerProfilePerformanceRadarResponse = z.object({
	stats: z.array(PlayerProfilePerformanceRadarStat),
	filterPosition: PlayerProfilePositionFilter,
});
export type PlayerProfilePerformanceRadarResponse = z.infer<
	typeof PlayerProfilePerformanceRadarResponse
>;

export const PlayerProfileClubHistorySeasonClub = z.object({
	clubId: z.number().int(),
	clubType: z.enum(["partner", "nonpartner", "discover"]).nullish(),
	name: z.string(),
	category: z.string(),
	rating: z.number(),
	logo: z.string().nullish(),
});
export type PlayerProfileClubHistorySeasonClub = z.infer<
	typeof PlayerProfileClubHistorySeasonClub
>;

export const PlayerProfileClubHistorySeason = z.object({
	season: z.string(),
	clubs: z.array(PlayerProfileClubHistorySeasonClub),
});
export type PlayerProfileClubHistorySeason = z.infer<
	typeof PlayerProfileClubHistorySeason
>;

export const PlayerProfileClubHistoryResponse = z.object({
	seasons: z.array(PlayerProfileClubHistorySeason),
});
export type PlayerProfileClubHistoryResponse = z.infer<
	typeof PlayerProfileClubHistoryResponse
>;

export const PlayerOffensiveStatData = z.object({
	shots: z.number().nullable().default(null),
	shotsOnTarget: z.number().nullable().default(null),
	shotsInsidePa: z.number().nullable().default(null),
	shotsOutsidePa: z.number().nullable().default(null),
	totalXg: z.number().nullable().default(null),
	nonPenaltyXg: z.number().nullable().default(null),
	totalXa: z.number().nullable().default(null),
	nonPenaltyXgPerShot: z.number().nullable().default(null),
	goalsXgRatio: z.number().nullable().default(null),
	nonPenaltyXgPlusXaPer90: z.number().nullable().default(null),
	takeons: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	nonPenaltyGoals: z.number().nullish().default(null),
	shotCreatingActions: z.number().nullish().default(null),
});
export type PlayerOffensiveStatData = z.infer<typeof PlayerOffensiveStatData>;

export const PlayerDefensiveStatData = z.object({
	tackles: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	passAccuracy: z.number().nullish().default(null),
	aerialDuels: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	groundDuels: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	recoveries: z.number().nullable().default(null),
	interceptions: z.number().nullable().default(null),
	clearances: z.number().nullable().default(null),
	blocks: z.number().nullable().default(null),
});
export type PlayerDefensiveStatData = z.infer<typeof PlayerDefensiveStatData>;

export const PlayerDistributionStatData = z.object({
	passes: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	passesAccuracy: z.number().nullable().default(null),
	keyPasses: z.number().nullable().default(null),
	passesForward: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	crosses: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	controlUnderPressure: z.number().nullable().default(null),
	passesIntoPenaltyArea: z.number().nullish().default(null),
	switchesOfPlay: z.number().nullish().default(null),
	progressivePasses: z.number().nullish().default(null),
	deepCompletions: z.number().nullish().default(null),
});
export type PlayerDistributionStatData = z.infer<
	typeof PlayerDistributionStatData
>;

export const PlayerGoalKeepingStatData = z.object({
	goalsConceded: z.number().nullable().default(null),
	catches: z.number().nullable().default(null),
	punches: z.number().nullable().default(null),
	goalsKicks: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
	aerialClearances: z
		.object({ success: z.number().nullable(), total: z.number().nullable() })

		.nullable()
		.default(null),
});
export type PlayerGoalKeepingStatData = z.infer<
	typeof PlayerGoalKeepingStatData
>;

export const PlayerProfilePerformance = z.object({
	offensive: PlayerOffensiveStatData.nullable(),
	defensive: PlayerDefensiveStatData.nullable(),
	distribution: PlayerDistributionStatData.nullable(),
	goalkeeper: PlayerGoalKeepingStatData.nullable(),
	filterPosition: PlayerProfilePositionFilter,
	filterSeason: PlayerProfileSeasonFilter,
	filterLastGamesCount: PlayerProfileLastGamesCountFilter,
});
export type PlayerProfilePerformance = z.infer<typeof PlayerProfilePerformance>;

export const EventTypeItem = z.object({
	name: z.string(),
	eventValue: z.string(),
	count: z.number().int(),
});
export type EventTypeItem = z.infer<typeof EventTypeItem>;

export const EventTypeCategoryItem = z.object({
	categoryName: z.string(),
	items: z.array(EventTypeItem).nullable(),
});
export type EventTypeCategoryItem = z.infer<typeof EventTypeCategoryItem>;

export const EventTypes = z.object({
	events: z.array(EventTypeCategoryItem).nullable().default(null),
});
export type EventTypes = z.infer<typeof EventTypes>;

export const PlayerProfileAgeGroupFilterOption = z.object({
	label: z.string(),
	value: z.number().int(),
});
export type PlayerProfileAgeGroupFilterOption = z.infer<
	typeof PlayerProfileAgeGroupFilterOption
>;

export const PlayerProfileAgeGroupFilter = z.object({
	options: z.array(PlayerProfileAgeGroupFilterOption),
});
export type PlayerProfileAgeGroupFilter = z.infer<
	typeof PlayerProfileAgeGroupFilter
>;

export const PlayerProfileClubFilterOption = z.object({
	label: z.string(),
	value: z.number().int(),
});
export type PlayerProfileClubFilterOption = z.infer<
	typeof PlayerProfileClubFilterOption
>;

export const PlayerProfileClubFilter = z.object({
	options: z.array(PlayerProfileClubFilterOption),
});
export type PlayerProfileClubFilter = z.infer<typeof PlayerProfileClubFilter>;

export const EventFilters = z.object({
	filterSeason: PlayerProfileSeasonFilter,
	filterAgeGroup: PlayerProfileAgeGroupFilter,
	filterClub: PlayerProfileClubFilter,
	filterPosition: PlayerProfilePositionFilter,
});
export type EventFilters = z.infer<typeof EventFilters>;

export const EventItemDetail = z.object({
	gameName: z.string(),
	date: z.string(),
	ageGroup: z.string().nullable(),
	league: z.string().nullable(),
	jerseyNumber: z.number().int().nullable(),
	eventType: z.string().nullable(),
	success: z.boolean(),
	position: z.string().nullable(),
	minute: z.string().nullable(),
	bodyPart: z.string().nullable(),
});
export type EventItemDetail = z.infer<typeof EventItemDetail>;

export const EventItem = z.object({
	gameId: z.number().int(),
	success: z.boolean(),
	name: z.string(),
	date: z.string(),
	gameName: z.string(),
	eventLength: z.string(),
	event: GPEventResponse,
	video: GPVideoResponse.nullish(),
	detail: EventItemDetail,
});
export type EventItem = z.infer<typeof EventItem>;

export const Events = z.object({
	events: z.array(EventItem).nullable(),
	total: z.number().int().default(0),
	page: z.number().int().default(1),
	pages: z.number().int().optional().default(1),
	limit: z.number().int().default(15),
});
export type Events = z.infer<typeof Events>;

export const KeyEventItem = z.object({
	categoryName: z.string(),
	events: z.array(EventItem).nullish(),
});
export type KeyEventItem = z.infer<typeof KeyEventItem>;

export const KeyEvents = z.object({
	events: z.array(KeyEventItem).nullable().default(null),
});
export type KeyEvents = z.infer<typeof KeyEvents>;

export const PlayerProfileCrossGameClipIntervalRequest = z.object({
	gameId: z.number().int().gt(0),
	start: z.number().int(),
	end: z.number().int(),
	events: z.array(GameClipEventRequest),
});
export type PlayerProfileCrossGameClipIntervalRequest = z.infer<
	typeof PlayerProfileCrossGameClipIntervalRequest
>;

export const PlayerProfileCrossGameClipPostRequest = z.object({
	name: z.string().max(255),
	description: z.string().max(255),
	intervals: z.array(PlayerProfileCrossGameClipIntervalRequest).min(1),
	tags: z.array(z.string()).nullish().default(null),
});
export type PlayerProfileCrossGameClipPostRequest = z.infer<
	typeof PlayerProfileCrossGameClipPostRequest
>;

export const ShadowTeamItem = z.object({
	id: z.number().int(),
	name: z.string(),
	playersCount: z.number().int(),
	formation: z.string(),
	inShadowTeam: z.boolean(),
});
export type ShadowTeamItem = z.infer<typeof ShadowTeamItem>;

export const ShadowTeamList = z.object({
	teams: z.array(ShadowTeamItem).nullable(),
});
export type ShadowTeamList = z.infer<typeof ShadowTeamList>;

export const ShadowTeamPlayerPostRequest = z.object({
	shadowTeamIds: z.array(z.number().int()),
});
export type ShadowTeamPlayerPostRequest = z.infer<
	typeof ShadowTeamPlayerPostRequest
>;

export const ShadowTeamPostRequest = z.object({
	name: z.string(),
	formation: z.string(),
});
export type ShadowTeamPostRequest = z.infer<typeof ShadowTeamPostRequest>;

export const PlayerProfileRecruitmentInformationItem = z.object({
	key: z
		.enum([
			"toeflScore",
			"ieltsScore",
			"gpa",
			"satScore",
			"graduationYear",
			"commitmentStatus",
		])
		.describe("Stable field identifier"),
	value: z.string().describe("Display-ready value"),
});
export type PlayerProfileRecruitmentInformationItem = z.infer<
	typeof PlayerProfileRecruitmentInformationItem
>;

export const PlayerProfileRecruitmentInformationResponse = z.object({
	hasRecruitmentInformation: z
		.boolean()
		.describe(
			"False when there is nothing to show, so the section can be hidden",
		),
	openToOpportunities: z
		.boolean()
		.describe(
			"Whether the player is open to US college recruitment opportunities",
		),
	items: z
		.array(PlayerProfileRecruitmentInformationItem)
		.describe("Populated answers only, in display order")
		.default([]),
	updatedAt: z
		.string()
		.datetime({ offset: true })
		.describe("When the academic answers were last saved")
		.nullish()
		.default(null),
});
export type PlayerProfileRecruitmentInformationResponse = z.infer<
	typeof PlayerProfileRecruitmentInformationResponse
>;

export const PlayerProfileBiometricsAvailabilityResponse = z.object({
	hasScans: z
		.boolean()
		.describe("The player has completed at least one Fitmatch scan."),
	isDataShared: z
		.boolean()
		.describe(
			"The player shares biometric data with the current viewer. Always false when hasScans is false, which is the cheap path and skips the visibility lookup.",
		),
	isAvailable: z
		.boolean()
		.describe(
			"Both conditions are met, so the Biometrics sub-tab may be rendered.",
		),
});
export type PlayerProfileBiometricsAvailabilityResponse = z.infer<
	typeof PlayerProfileBiometricsAvailabilityResponse
>;

export const PlayerProfileBodyMeasurementStat = z.object({
	key: z.string(),
	name: z.string(),
	value: z.number().nullable(),
	unit: z.string(),
});
export type PlayerProfileBodyMeasurementStat = z.infer<
	typeof PlayerProfileBodyMeasurementStat
>;

export const PlayerProfileBodyMeasurementsSummary = z.object({
	stats: z.array(PlayerProfileBodyMeasurementStat),
	scanFileUrl: z.string().nullish(),
});
export type PlayerProfileBodyMeasurementsSummary = z.infer<
	typeof PlayerProfileBodyMeasurementsSummary
>;

export const PlayerProfileBodyCircumferences = z.object({
	upperBody: z.array(PlayerProfileBodyMeasurementStat),
	lowerBody: z.array(PlayerProfileBodyMeasurementStat),
});
export type PlayerProfileBodyCircumferences = z.infer<
	typeof PlayerProfileBodyCircumferences
>;

export const PlayerProfileBodyMeasurementsResponse = z.object({
	summary: PlayerProfileBodyMeasurementsSummary,
	circumferences: PlayerProfileBodyCircumferences,
	fullBody: z.array(PlayerProfileBodyMeasurementStat),
	lastScanDate: z.string().nullish(),
});
export type PlayerProfileBodyMeasurementsResponse = z.infer<
	typeof PlayerProfileBodyMeasurementsResponse
>;

export const PlayerProfileBodyMetricTrendPagination = z.object({
	page: z
		.number()
		.int()
		.describe("Page 1 holds the newest scans, the last page the oldest ones."),
	pageCount: z.number().int(),
	limit: z.number().int().describe("Scans per page."),
});
export type PlayerProfileBodyMetricTrendPagination = z.infer<
	typeof PlayerProfileBodyMetricTrendPagination
>;

export const PlayerProfileBodyMetricTrendPoint = z.object({
	scanNumber: z.number().int(),
	scanDate: z.string(),
	value: z.number().nullable(),
});
export type PlayerProfileBodyMetricTrendPoint = z.infer<
	typeof PlayerProfileBodyMetricTrendPoint
>;

export const PlayerProfileBodyMetricFilterOption = z.object({
	label: z.string(),
	value: z.string(),
});
export type PlayerProfileBodyMetricFilterOption = z.infer<
	typeof PlayerProfileBodyMetricFilterOption
>;

export const PlayerProfileBodyMetricFilter = z.object({
	options: z.array(PlayerProfileBodyMetricFilterOption),
});
export type PlayerProfileBodyMetricFilter = z.infer<
	typeof PlayerProfileBodyMetricFilter
>;

export const PlayerProfileBodyMetricTrendResponse = z.object({
	selectedMetric: z.string(),
	unit: z.string(),
	hasEnoughScans: z.boolean(),
	pagination: PlayerProfileBodyMetricTrendPagination,
	points: z.array(PlayerProfileBodyMetricTrendPoint),
	filterMetric: PlayerProfileBodyMetricFilter,
});
export type PlayerProfileBodyMetricTrendResponse = z.infer<
	typeof PlayerProfileBodyMetricTrendResponse
>;

export const PlayerProfileScanReference = z.object({
	scanId: z.string(),
	scanNumber: z.number().int(),
	scanDate: z.string(),
	label: z.string(),
});
export type PlayerProfileScanReference = z.infer<
	typeof PlayerProfileScanReference
>;

export const PlayerProfileBodyCompositionChangeAxis = z.object({
	name: z.string(),
	unit: z.string(),
	firstValue: z.number().nullable(),
	secondValue: z.number().nullable(),
	axisMin: z.number().nullable(),
	axisMax: z.number().nullable(),
});
export type PlayerProfileBodyCompositionChangeAxis = z.infer<
	typeof PlayerProfileBodyCompositionChangeAxis
>;

export const PlayerProfileScanFilterOption = z.object({
	label: z.string(),
	value: z.string(),
	selected: z.boolean().default(false),
});
export type PlayerProfileScanFilterOption = z.infer<
	typeof PlayerProfileScanFilterOption
>;

export const PlayerProfileScanFilter = z.object({
	options: z.array(PlayerProfileScanFilterOption),
});
export type PlayerProfileScanFilter = z.infer<typeof PlayerProfileScanFilter>;

export const PlayerProfileBodyCompositionChangeResponse = z.object({
	hasEnoughScans: z.boolean(),
	firstScan: PlayerProfileScanReference.nullable(),
	secondScan: PlayerProfileScanReference.nullable(),
	axes: z.array(PlayerProfileBodyCompositionChangeAxis),
	fullBodyMeasurements: z.array(PlayerProfileBodyMeasurementStat),
	filterScan: PlayerProfileScanFilter,
});
export type PlayerProfileBodyCompositionChangeResponse = z.infer<
	typeof PlayerProfileBodyCompositionChangeResponse
>;

export const PlayerProfilePhysicalMetricsFiltersResponse = z.object({
	filterSeason: PlayerProfileSeasonFilter,
});
export type PlayerProfilePhysicalMetricsFiltersResponse = z.infer<
	typeof PlayerProfilePhysicalMetricsFiltersResponse
>;

export const PlayerProfilePhysicalMetricsGameReference = z.object({
	gameId: z.number().int(),
	opponentName: z.string(),
	playedAt: z.string(),
});
export type PlayerProfilePhysicalMetricsGameReference = z.infer<
	typeof PlayerProfilePhysicalMetricsGameReference
>;

export const PlayerProfilePhysicalMetricsTopSpeedPeak = z.object({
	value: z.number(),
	unit: z.string(),
	game: PlayerProfilePhysicalMetricsGameReference.describe(
		"Game where the peak was achieved.",
	).nullable(),
	isPeakRecordedThisSeason: z
		.boolean()
		.describe(
			"True when the all-time peak was achieved in the selected season.",
		),
});
export type PlayerProfilePhysicalMetricsTopSpeedPeak = z.infer<
	typeof PlayerProfilePhysicalMetricsTopSpeedPeak
>;

export const PlayerProfilePhysicalMetricsMetric = z.object({
	value: z.number(),
	unit: z.string().nullable(),
	changeVsPreviousSeason: z
		.number()
		.describe("Difference against the previous season, in the metric unit.")
		.nullable(),
});
export type PlayerProfilePhysicalMetricsMetric = z.infer<
	typeof PlayerProfilePhysicalMetricsMetric
>;

export const PlayerProfilePhysicalMetricsPeakPerformance = z.object({
	highestTopSpeed: PlayerProfilePhysicalMetricsTopSpeedPeak.nullable(),
	averageTopSpeed: PlayerProfilePhysicalMetricsMetric.describe(
		"Sum of per game top speeds divided by the number of games in the selected season.",
	).nullable(),
});
export type PlayerProfilePhysicalMetricsPeakPerformance = z.infer<
	typeof PlayerProfilePhysicalMetricsPeakPerformance
>;

export const PlayerProfilePhysicalMetricsVolumeAndLoad = z.object({
	averageDistanceCovered: PlayerProfilePhysicalMetricsMetric.describe(
		"Total distance per game normalised to 90 minutes, averaged across games in the selected season.",
	).nullable(),
	accelerations: PlayerProfilePhysicalMetricsMetric.describe(
		"Acceleration counts per game normalised to 90 minutes (threshold >= 10.8 km/hr).",
	).nullable(),
	decelerations: PlayerProfilePhysicalMetricsMetric.describe(
		"Deceleration counts per game normalised to 90 minutes (threshold <= -10.8 km/hr).",
	).nullable(),
});
export type PlayerProfilePhysicalMetricsVolumeAndLoad = z.infer<
	typeof PlayerProfilePhysicalMetricsVolumeAndLoad
>;

export const PlayerProfilePhysicalMetricsMovementZone = z.object({
	zone: z.enum([
		"standing",
		"walking",
		"jogging",
		"running",
		"highSpeedRunning",
		"sprinting",
	]),
	percentage: z
		.number()
		.describe("Share of the total time spent in the zone, 0-100."),
	minutesPer90: z
		.number()
		.describe("Minutes spent in the zone, normalised to 90 minutes."),
});
export type PlayerProfilePhysicalMetricsMovementZone = z.infer<
	typeof PlayerProfilePhysicalMetricsMovementZone
>;

export const PlayerProfilePhysicalMetricsDocument = z.object({
	id: z.number().int(),
	name: z.string(),
	gameId: z
		.number()
		.int()
		.describe("Null for documents not tied to a single game.")
		.nullable(),
	homeTeamName: z.string().nullable(),
	awayTeamName: z.string().nullable(),
	playedAt: z.string().nullable(),
	seasonLabel: z.string().nullable(),
	viewUrl: z
		.string()
		.describe("PDF opened in the viewer, same asset as used on the game page.")
		.nullable(),
	downloadUrl: z.string().nullable(),
});
export type PlayerProfilePhysicalMetricsDocument = z.infer<
	typeof PlayerProfilePhysicalMetricsDocument
>;

export const PlayerProfilePhysicalMetricsResponse = z.object({
	season: z
		.number()
		.int()
		.describe(
			"Selected season, null when metrics are aggregated across all seasons.",
		)
		.nullable(),
	seasonLabel: z.string().nullable(),
	games: z
		.number()
		.int()
		.describe("Games contributing to the aggregated metrics."),
	minutes: z
		.number()
		.int()
		.describe("Minutes contributing to the aggregated metrics."),
	positions: z.array(z.string()),
	peakPerformance: PlayerProfilePhysicalMetricsPeakPerformance,
	volumeAndLoad: PlayerProfilePhysicalMetricsVolumeAndLoad,
	movementDistribution: z
		.array(PlayerProfilePhysicalMetricsMovementZone)
		.describe(
			"Time zones averaged per 90 minutes, ordered from the slowest to the fastest zone.",
		),
	documents: z.array(PlayerProfilePhysicalMetricsDocument),
});
export type PlayerProfilePhysicalMetricsResponse = z.infer<
	typeof PlayerProfilePhysicalMetricsResponse
>;

export const PlayerProfileBehavioralPeriodFilterOption = z.object({
	label: z.string(),
	value: z.string(),
	isDefault: z.boolean(),
});
export type PlayerProfileBehavioralPeriodFilterOption = z.infer<
	typeof PlayerProfileBehavioralPeriodFilterOption
>;

export const PlayerProfileBehavioralPeriodFilter = z.object({
	options: z.array(PlayerProfileBehavioralPeriodFilterOption),
});
export type PlayerProfileBehavioralPeriodFilter = z.infer<
	typeof PlayerProfileBehavioralPeriodFilter
>;

export const PlayerProfileBehavioralSeasonFilterOption = z.object({
	label: z.string(),
	value: z.number().int().nullable(),
	isDefault: z
		.boolean()
		.describe(
			"Preselect this option. Set on the only season when a player has exactly one.",
		),
});
export type PlayerProfileBehavioralSeasonFilterOption = z.infer<
	typeof PlayerProfileBehavioralSeasonFilterOption
>;

export const PlayerProfileBehavioralSeasonFilter = z.object({
	options: z.array(PlayerProfileBehavioralSeasonFilterOption),
});
export type PlayerProfileBehavioralSeasonFilter = z.infer<
	typeof PlayerProfileBehavioralSeasonFilter
>;

export const PlayerProfileBehavioralCohortFilterOption = z.object({
	label: z.string(),
	value: z.string(),
	appliesTo: z
		.array(z.string())
		.describe("Period selections this cohort can be combined with."),
	requiresPosition: z
		.boolean()
		.describe(
			"Whether the position dropdown has to be shown next to this cohort.",
		),
	ageCategory: z.string().describe("Age category").nullable(),
	isDefault: z
		.boolean()
		.describe(
			"Whether this is the default cohort for the period selections it applies to.",
		),
});
export type PlayerProfileBehavioralCohortFilterOption = z.infer<
	typeof PlayerProfileBehavioralCohortFilterOption
>;

export const PlayerProfileBehavioralCohortFilter = z.object({
	options: z.array(PlayerProfileBehavioralCohortFilterOption),
});
export type PlayerProfileBehavioralCohortFilter = z.infer<
	typeof PlayerProfileBehavioralCohortFilter
>;

export const PlayerProfileBehavioralSignalsFiltersResponse = z.object({
	filterPeriod: PlayerProfileBehavioralPeriodFilter,
	filterSeason: PlayerProfileBehavioralSeasonFilter,
	filterCohort: PlayerProfileBehavioralCohortFilter,
	filterPosition: PlayerProfilePositionFilter,
	maximumSnapshotSeasons: z
		.number()
		.int()
		.describe("How many seasons the snapshot radar can overlay at once."),
	minimumAnalyzedMinutes: z
		.number()
		.int()
		.describe(
			"Analyzed minutes required before behavioral signals are calculated.",
		),
});
export type PlayerProfileBehavioralSignalsFiltersResponse = z.infer<
	typeof PlayerProfileBehavioralSignalsFiltersResponse
>;

export const PlayerProfileBehavioralSummary = z.object({
	header: z.string(),
	text: z.string(),
});
export type PlayerProfileBehavioralSummary = z.infer<
	typeof PlayerProfileBehavioralSummary
>;

export const PlayerProfileBehavioralChartPagination = z.object({
	page: z.number().int(),
	pageCount: z.number().int(),
	gamesPerPage: z.number().int(),
});
export type PlayerProfileBehavioralChartPagination = z.infer<
	typeof PlayerProfileBehavioralChartPagination
>;

export const PlayerProfileBehavioralChartPointScore = z.object({
	signal: z.string(),
	score: z.number().int().describe("Signal score on a 0-100 scale."),
});
export type PlayerProfileBehavioralChartPointScore = z.infer<
	typeof PlayerProfileBehavioralChartPointScore
>;

export const PlayerProfileBehavioralChartPoint = z.object({
	label: z
		.string()
		.describe("X-axis label, a match sequence label or a season label."),
	gameId: z
		.number()
		.int()
		.describe(
			"Null on the all-seasons chart, where a point aggregates a whole season.",
		)
		.nullable(),
	opponentName: z.string().nullable(),
	homeTeamName: z
		.string()
		.describe(
			"Home side of the fixture. Equals opponentName when the player played away.",
		)
		.nullable(),
	opponentClubId: z
		.number()
		.int()
		.describe("Opponent club, for the axis label logo.")
		.nullable(),
	opponentClubLogo: z.string().nullable(),
	playedAt: z.string().nullable(),
	season: z.number().int().nullable(),
	teamGoals: z
		.number()
		.int()
		.describe(
			"Goals scored by the player's team. Null on the all-seasons chart.",
		)
		.nullable(),
	opponentGoals: z
		.number()
		.int()
		.describe("Goals scored by the opponent. Null on the all-seasons chart.")
		.nullable(),
	scores: z.array(PlayerProfileBehavioralChartPointScore),
	cohortLowerQuartile: z
		.number()
		.int()
		.describe("Cohort 25th percentile, only set on single-signal charts.")
		.nullable(),
	cohortUpperQuartile: z
		.number()
		.int()
		.describe("Cohort 75th percentile, only set on single-signal charts.")
		.nullable(),
});
export type PlayerProfileBehavioralChartPoint = z.infer<
	typeof PlayerProfileBehavioralChartPoint
>;

export const PlayerProfileBehavioralChart = z.object({
	xAxisType: z.string().describe("What a single point represents."),
	yAxisMinimum: z
		.number()
		.int()
		.describe("Lowest plotted score padded by 10, never below 0."),
	yAxisMaximum: z
		.number()
		.int()
		.describe("Highest plotted score padded by 10, never above 100."),
	averageScore: z
		.number()
		.describe("Average score across the whole selected period")
		.nullable(),
	pagination: PlayerProfileBehavioralChartPagination.nullable(),
	points: z.array(PlayerProfileBehavioralChartPoint),
});
export type PlayerProfileBehavioralChart = z.infer<
	typeof PlayerProfileBehavioralChart
>;

export const PlayerProfileBehavioralSignalPanel = z.object({
	signal: z.string(),
	name: z.string(),
	description: z.string().describe("Tooltip copy explaining the signal."),
	score: z
		.number()
		.int()
		.describe("Current score on a 0-100 scale.")
		.nullable(),
	band: z.string().nullable(),
	bandLabel: z
		.string()
		.describe("Short label of the band, e.g. Clear response.")
		.nullable(),
	typicalRange: z.string().nullable(),
	trendDirection: z
		.string()
		.describe(
			"Null when there is no comparable preceding period, for example on the all-seasons chart.",
		)
		.nullable(),
	trendPercentage: z
		.number()
		.describe(
			"Signed change against the preceding period of the same length, in percent.",
		)
		.nullable(),
	cohortDifferencePercentage: z
		.number()
		.describe("Signed difference against the cohort median, in percent.")
		.nullable(),
});
export type PlayerProfileBehavioralSignalPanel = z.infer<
	typeof PlayerProfileBehavioralSignalPanel
>;

export const PlayerProfileBehavioralSignalsTrendResponse = z.object({
	hasEnoughData: z.boolean(),
	period: z.string(),
	season: z.number().int().nullable(),
	seasonLabel: z.string().nullable(),
	signal: z.string().nullable(),
	cohort: z
		.string()
		.describe(
			"Cohort the IQR band is calculated from. Null on the overview tab.",
		)
		.nullable(),
	position: z.string().describe("Position the trend is restricted.").nullable(),
	ageCategory: z
		.string()
		.describe("Age category the trend is restricted.")
		.nullable(),
	games: z
		.number()
		.int()
		.describe(
			"Games in the selected period. Null for the last-games period, which is not season bound.",
		)
		.nullable(),
	minutes: z
		.number()
		.int()
		.describe("Analyzed minutes in the selected period."),
	confidence: z.string().nullable(),
	summary: PlayerProfileBehavioralSummary.describe(
		"Combined summary on the overview tab, single-signal summary on the other tabs.",
	).nullable(),
	chart: PlayerProfileBehavioralChart.nullable(),
	signals: z.array(PlayerProfileBehavioralSignalPanel),
});
export type PlayerProfileBehavioralSignalsTrendResponse = z.infer<
	typeof PlayerProfileBehavioralSignalsTrendResponse
>;

export const PlayerProfileBehavioralSeasonReference = z.object({
	season: z.number().int(),
	seasonLabel: z.string(),
});
export type PlayerProfileBehavioralSeasonReference = z.infer<
	typeof PlayerProfileBehavioralSeasonReference
>;

export const PlayerProfileBehavioralSnapshotAxis = z.object({
	signal: z.string(),
	name: z.string(),
	score: z.number().int().describe("Signal score on a 0-100 scale."),
});
export type PlayerProfileBehavioralSnapshotAxis = z.infer<
	typeof PlayerProfileBehavioralSnapshotAxis
>;

export const PlayerProfileBehavioralSnapshotSeries = z.object({
	season: z.number().int().nullable(),
	seasonLabel: z.string(),
	games: z.number().int(),
	minutes: z.number().int(),
	axes: z.array(PlayerProfileBehavioralSnapshotAxis),
});
export type PlayerProfileBehavioralSnapshotSeries = z.infer<
	typeof PlayerProfileBehavioralSnapshotSeries
>;

export const PlayerProfileBehavioralSignalsSnapshotResponse = z.object({
	hasEnoughData: z.boolean(),
	period: z.string(),
	selectedSeasons: z
		.array(PlayerProfileBehavioralSeasonReference)
		.describe("Seasons"),
	games: z
		.number()
		.int()
		.describe("Games across all rendered series.")
		.nullable(),
	minutes: z
		.number()
		.int()
		.describe("Analyzed minutes across all rendered series."),
	confidence: z.string().nullable(),
	summary: PlayerProfileBehavioralSummary.describe(
		"Combined summary of the most recent rendered series.",
	).nullable(),
	series: z.array(PlayerProfileBehavioralSnapshotSeries),
	signals: z
		.array(PlayerProfileBehavioralSignalPanel)
		.describe("Right-hand panel rows for the most recent rendered series."),
});
export type PlayerProfileBehavioralSignalsSnapshotResponse = z.infer<
	typeof PlayerProfileBehavioralSignalsSnapshotResponse
>;

export const PlayerProfileArchetype = z.object({
	archetype: z.string(),
	name: z.string(),
	tagline: z.string(),
	alignmentScore: z
		.number()
		.int()
		.describe("Alignment with this archetype on a 0-100 scale."),
	tags: z.array(z.string()),
	shortDescription: z.string(),
	fullDescription: z.string(),
	observedPatterns: z.array(z.string()),
	developmentFocus: z.string(),
});
export type PlayerProfileArchetype = z.infer<typeof PlayerProfileArchetype>;

export const PlayerProfileArchetypesResponse = z.object({
	status: z
		.string()
		.describe("Archetype content is only returned for completedAndShared."),
	completedAt: z
		.string()
		.describe("Date the player last submitted the questionnaire.")
		.nullable(),
	summary: z.string().nullable(),
	dominantArchetype: PlayerProfileArchetype.nullable(),
	secondaryArchetypes: z.array(PlayerProfileArchetype),
});
export type PlayerProfileArchetypesResponse = z.infer<
	typeof PlayerProfileArchetypesResponse
>;

export const JsonLoginRequest = z
	.object({
		email: z.string().nullable(),
		password: z.string().nullable(),
		accessToken: z.string().nullable(),
	})
	.partial();
export type JsonLoginRequest = z.infer<typeof JsonLoginRequest>;

export const Resource = z.enum([
	"product_management",
	"poles",
	"pole",
	"non_partner_clubs",
	"non_partner_club",
	"non_partner_players",
	"non_partner_player",
	"roles_permissions_management",
	"role_permission_management",
	"admins_creation",
	"admin_creation",
	"subscriptions",
	"subscription",
	"team_groups",
	"team_group",
	"notifications",
	"notification",
	"specific_notiifcations",
	"specific_notiifcation",
	"schedule_notifications",
	"schedule_notification",
	"countries_expansion",
	"country_expansion",
	"player_accounts_management",
	"player_account_management",
	"feed_area",
	"single_feed_area",
	"clubs_amateur",
	"club_amateur_single",
	"club_amateur_users",
	"club_amateur_user_directors",
	"club_amateur_user_director",
	"club_amateur_user_coachs",
	"club_amateur_user_coach",
	"club_amateur_teams",
	"club_amateur_team",
	"club_amateur_team_players",
	"club_amateur_team_player",
	"club_amateur_games",
	"club_amateur_game",
	"game_uploads",
	"game_upload_single",
	"clubs_pro",
	"clubs_pro_single",
	"clubs_pro_users",
	"club_pro_user_director",
	"discover_sections_management",
	"discover_section_management",
	"discover_token_management",
	"discover_subscription_management",
	"api_management",
	"die_ligen",
	"competitions",
	"competition",
	"operations_management",
	"all",
	"amateur_clubs",
	"discover_user_enterprise_management",
	"club_pro_pipedrive_org_id",
	"subscription_deal_id",
	"operations_management_summary_tab",
	"log_in_as_user",
	"provider_operations",
]);
export type Resource = z.infer<typeof Resource>;

export const AdminManagementPermission = z.literal("set_permissions");
export type AdminManagementPermission = z.infer<
	typeof AdminManagementPermission
>;

export const BundlePermission = z.literal("assign_bundle");
export type BundlePermission = z.infer<typeof BundlePermission>;

export const LogAsUserPermission = z.literal("log_in_as_user");
export type LogAsUserPermission = z.infer<typeof LogAsUserPermission>;

export const NonPartnerPermission = z.enum([
	"migrate_to_nonpartner",
	"migrate_to_partner",
	"convert_to_partner",
]);
export type NonPartnerPermission = z.infer<typeof NonPartnerPermission>;

export const NotificationPermission = z.enum([
	"test_email",
	"SMS",
	"Email",
	"Firebase",
	"Notification",
]);
export type NotificationPermission = z.infer<typeof NotificationPermission>;

export const Permission = z.enum([
	"read",
	"list",
	"create",
	"edit",
	"email_edit",
	"delete",
]);
export type Permission = z.infer<typeof Permission>;

export const PolePermission = z.literal("assign_pole");
export type PolePermission = z.infer<typeof PolePermission>;

export const SubscriptionPermission = z.literal("assign_subscription");
export type SubscriptionPermission = z.infer<typeof SubscriptionPermission>;

export const SuperAdminPermission = z.literal("all");
export type SuperAdminPermission = z.infer<typeof SuperAdminPermission>;

export const GamePermission = z.enum([
	"game_teamsheet",
	"game_vip",
	"game_hidden",
	"game_view",
	"game_view_mobile",
	"game_score",
	"game_analyze",
	"game_team_stat",
	"game_player_stat",
	"game_clip",
	"game_ai",
	"game_favorites",
	"game_admin_options",
	"game_view_events",
	"game_report_event_error",
	"game_view_event_comments",
	"game_download_events",
	"game_view_ratings",
	"game_download_video",
	"game_downloads",
	"game_download_player_stat",
	"game_download_team_stat",
	"game_download_team_sheet",
	"game_edit",
	"game_upload_video",
	"game_delete",
	"game_analytics",
	"game_analytics_mobile",
	"game_events_view_mobile",
	"game_view_data_insight",
	"game_view_physical_analysis",
	"game_player_grading",
	"game_create_game",
	"game_assignee_selection",
	"game_view_sla",
	"game_status_edit",
	"game_ai_summary_feature",
	"game_ai_summary_content",
	"game_access_ai_coach_review",
]);
export type GamePermission = z.infer<typeof GamePermission>;

export const UserPermission = z.object({
	id: z.number().int(),
	resource: Resource,
	permission: z.union([
		AdminManagementPermission,
		BundlePermission,
		LogAsUserPermission,
		NonPartnerPermission,
		NotificationPermission,
		Permission,
		PolePermission,
		SubscriptionPermission,
		SuperAdminPermission,
		GamePermission,
	]),
});
export type UserPermission = z.infer<typeof UserPermission>;

export const IntercomDataCustomAttributesResponse = z.object({
	d_UserPosition: z.string().nullable(),
	d_Logged: z.boolean(),
	d_UserID: z.number().int().nullable(),
	d_PlayerID: z.number().int().nullable(),
	d_ClubID: z.number().int().nullable(),
	d_PhoneNumber: z.string().nullable(),
	d_UserName: z.string().nullable(),
	d_Email: z.string().nullable(),
	d_Name: z.string().nullable(),
	d_ClubTeamName: z.string().nullable(),
	d_ClubType: z.string().nullable(),
	d_Product: z.string(),
	d_PaidSupport: z.boolean(),
	d_LastTokenID: z.string().nullable(),
	d_InviteLink: z.string().nullable(),
	d_TeamID: z.number().int().nullable(),
	d_TeamName: z.string().nullable(),
	d_SupportExpiration: z.string().nullable(),
	d_SupportValidFrom: z.string().nullable(),
	d_AccessType: z.string().nullable(),
	d_Migrated: z.string().nullable(),
});
export type IntercomDataCustomAttributesResponse = z.infer<
	typeof IntercomDataCustomAttributesResponse
>;

export const IntercomDataResponse = z.object({
	customAttributes: IntercomDataCustomAttributesResponse,
	name: z.string().nullable(),
	email: z.string().nullable(),
	phone: z.string().nullable(),
	userHash: z.string().nullable(),
	userId: z.string().nullish().default(null),
	intercomUserJwt: z.string().nullish().default(null),
});
export type IntercomDataResponse = z.infer<typeof IntercomDataResponse>;

export const UserSubscriptionOptions = z.enum(["full", "partial", "none"]);
export type UserSubscriptionOptions = z.infer<typeof UserSubscriptionOptions>;

export const AdminSubMenuItem = z.object({ id: z.string(), name: z.string() });
export type AdminSubMenuItem = z.infer<typeof AdminSubMenuItem>;

export const AdminMenuItem = z.object({
	id: z.string(),
	name: z.string(),
	icon: z.string(),
	hasSubMenu: z.boolean().default(false),
	subMenuItems: z.array(AdminSubMenuItem).default([]),
});
export type AdminMenuItem = z.infer<typeof AdminMenuItem>;

export const MenuData = z
	.object({
		admin_management: AdminMenuItem.nullable(),
		leagues_and_tournaments: AdminMenuItem.nullable(),
		non_partner_dataset: AdminMenuItem.nullable(),
		poles: AdminMenuItem.nullable(),
		subscriptions: AdminMenuItem.nullable(),
		notifications: AdminMenuItem.nullable(),
		country_expansion: AdminMenuItem.nullable(),
		feed_area: AdminMenuItem.nullable(),
		player_account_management: AdminMenuItem.nullable(),
		game_uploads_management: AdminMenuItem.nullable(),
		discover_sections_management: AdminMenuItem.nullable(),
		api_management: AdminMenuItem.nullable(),
		product_quality_management: AdminMenuItem.nullable(),
	})
	.partial();
export type MenuData = z.infer<typeof MenuData>;

export const UserResponse = z.object({
	id: z.number().int(),
	email: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	roles: z.array(z.string()),
	adminRoles: z.array(z.string()),
	role: z.string(),
	isVip: z.boolean(),
	position: z.string(),
	clubId: z.string(),
	clubName: z.string(),
	clubLogo: z.string().nullable(),
	photo: z.string().nullable(),
	locale: z.string(),
	hasAcceptedTermsOfUse: z.boolean(),
	permissions: z.array(UserPermission),
	intercomData: IntercomDataResponse,
	eyeballAddons: z.array(z.string()).default([]),
	isSuperAdmin: z.boolean(),
	isClubAdministrator: z.boolean(),
	playerStatsSearchSubscription:
		UserSubscriptionOptions.nullable().default(null),
	productManagementOptions: MenuData.nullish().default(null),
	termsOfUseLatestVersion: z.number().int().nullable().default(1),
	isMigratedToDiscover: z.boolean(),
	selectedGender: GenderEnum.nullable().default(null),
	selectedDataSource: z.string().nullable().default(null),
	selectedTeamGroups: z
		.union([z.array(z.string()), z.record(z.string())])
		.default([]),
});
export type UserResponse = z.infer<typeof UserResponse>;

export const JWTResponse = z.object({ token: z.string() });
export type JWTResponse = z.infer<typeof JWTResponse>;

export const LocaleChangeRequest = z.object({
	locale: z.string().min(2).max(2),
});
export type LocaleChangeRequest = z.infer<typeof LocaleChangeRequest>;

export const JsonResetPasswordRequest = z.object({ newPassword: z.string() });
export type JsonResetPasswordRequest = z.infer<typeof JsonResetPasswordRequest>;

export const SubscriptionCountryData = z.object({
	removedCountries: z.record(z.string()),
	activeSubscriptionsCount: z.number().int(),
});
export type SubscriptionCountryData = z.infer<typeof SubscriptionCountryData>;

export const TimeRange = z.object({ start: z.string(), end: z.string() });
export type TimeRange = z.infer<typeof TimeRange>;

export const PaginationRequest = z
	.object({
		page: z.number().int().default(1),
		limit: z.number().int().default(50),
	})
	.partial();
export type PaginationRequest = z.infer<typeof PaginationRequest>;

export const Sort = z.object({
	name: z.string().max(255),
	direction: z.string(),
});
export type Sort = z.infer<typeof Sort>;

export const YearRange = z
	.object({
		gte: z.number().int().nullable(),
		gt: z.number().int().nullable(),
		lte: z.number().int().nullable(),
		lt: z.number().int().nullable(),
	})
	.partial();
export type YearRange = z.infer<typeof YearRange>;

export const IntRange = z
	.object({
		gte: z.number().int().nullable(),
		gt: z.number().int().nullable(),
		lte: z.number().int().nullable(),
		lt: z.number().int().nullable(),
	})
	.partial();
export type IntRange = z.infer<typeof IntRange>;

export const PlayerSearchRequestPlayer = z
	.object({
		countryCodes: z.array(z.string()).nullable(),
		playerName: z.string().max(255).nullable(),
		clubId: z.array(z.number().int()).nullable(),
		clubName: z.array(z.string()).nullable(),
		teamName: z.array(z.string()).nullable(),
		teamId: z.array(z.number().int()).nullable(),
		teamAgeGroup: z.array(z.string()).nullable(),
		teamLevel: z.array(z.string()).nullable(),
		nationality: z.array(z.string()).nullable(),
		strongFoot: z.string().max(20).nullable(),
		playedInNationalTeam: z.number().int().nullable(),
		position: z.string().max(20).nullable(),
		birthday: YearRange.nullable(),
		height: IntRange.nullable(),
		weight: IntRange.nullable(),
		gender: z.string().max(20).nullable(),
	})
	.partial();
export type PlayerSearchRequestPlayer = z.infer<
	typeof PlayerSearchRequestPlayer
>;

export const FloatRange = z
	.object({
		gte: z.number().nullable(),
		gt: z.number().nullable(),
		lte: z.number().nullable(),
		lt: z.number().nullable(),
	})
	.partial();
export type FloatRange = z.infer<typeof FloatRange>;

export const PlayerSearchRequestStats = z
	.object({
		rating: FloatRange.nullable(),
		goalsScored: FloatRange.nullable(),
		goalsOwn: FloatRange.nullable(),
		assists: FloatRange.nullable(),
		totalGoals: FloatRange.nullable(),
		totalAssists: FloatRange.nullable(),
		passes: FloatRange.nullable(),
		passesAccuracy: FloatRange.nullable(),
		keyPasses: FloatRange.nullable(),
		passesForward: FloatRange.nullable(),
		crosses: FloatRange.nullable(),
		controlUnderPressure: FloatRange.nullable(),
		tackles: FloatRange.nullable(),
		aerialDuels: FloatRange.nullable(),
		groundDuels: FloatRange.nullable(),
		recoveries: FloatRange.nullable(),
		goalsConceded: FloatRange.nullable(),
		catches: FloatRange.nullable(),
		punches: FloatRange.nullable(),
		goalsKicks: FloatRange.nullable(),
		aerialClearances: FloatRange.nullable(),
		shotsOnTarget: FloatRange.nullable(),
		shotsInsidePa: FloatRange.nullable(),
		shotsOutsidePa: FloatRange.nullable(),
		minutesPlayed: FloatRange.nullable(),
		shots: FloatRange.nullable(),
		takeons: FloatRange.nullable(),
		gamesCount: FloatRange.nullable(),
		totalXg: FloatRange.nullable(),
		nonPenaltyXg: FloatRange.nullable(),
		totalXa: FloatRange.nullable(),
		nonPenaltyXgPerShot: FloatRange.nullable(),
		goalsXgRatio: FloatRange.nullable(),
		nonPenaltyXgPlusXaPer90: FloatRange.nullable(),
	})
	.partial();
export type PlayerSearchRequestStats = z.infer<typeof PlayerSearchRequestStats>;

export const PlayerSearchRequest = z.object({
	timeRange: TimeRange.nullish(),
	season: z.number().int().nullish(),
	pagination: PaginationRequest.nullish(),
	sort: z.array(Sort),
	player: PlayerSearchRequestPlayer.nullish(),
	stats: PlayerSearchRequestStats.nullish(),
});
export type PlayerSearchRequest = z.infer<typeof PlayerSearchRequest>;

export const PlayerSearchResponseStat = z
	.object({ value: z.number().nullable() })
	.partial();
export type PlayerSearchResponseStat = z.infer<typeof PlayerSearchResponseStat>;

export const PlayerSearchResponseSuccessTotalStat = z.object({
	success: PlayerSearchResponseStat,
	total: PlayerSearchResponseStat,
});
export type PlayerSearchResponseSuccessTotalStat = z.infer<
	typeof PlayerSearchResponseSuccessTotalStat
>;

export const PlayerSearchResponseCountry = z.object({
	countryCode: z.string(),
	countryLabel: z.string(),
});
export type PlayerSearchResponseCountry = z.infer<
	typeof PlayerSearchResponseCountry
>;

export const PlayerSearchResponsePlayer = z.object({
	rating: PlayerSearchResponseStat,
	goalsScored: z.number(),
	goalsOwn: z.number(),
	assists: PlayerSearchResponseStat,
	totalGoals: z.number().int(),
	totalAssists: z.number().int(),
	passes: PlayerSearchResponseSuccessTotalStat,
	passesAccuracy: PlayerSearchResponseStat,
	keyPasses: PlayerSearchResponseStat,
	passesForward: PlayerSearchResponseSuccessTotalStat,
	crosses: PlayerSearchResponseSuccessTotalStat,
	controlUnderPressure: PlayerSearchResponseStat,
	tackles: PlayerSearchResponseSuccessTotalStat,
	aerialDuels: PlayerSearchResponseSuccessTotalStat,
	groundDuels: PlayerSearchResponseSuccessTotalStat,
	recoveries: PlayerSearchResponseStat,
	interceptions: PlayerSearchResponseStat,
	clearances: PlayerSearchResponseStat,
	blocks: PlayerSearchResponseStat,
	takeons: PlayerSearchResponseSuccessTotalStat,
	goalsConceded: PlayerSearchResponseStat,
	catches: PlayerSearchResponseStat,
	punches: PlayerSearchResponseStat,
	goalsKicks: PlayerSearchResponseSuccessTotalStat,
	aerialClearances: PlayerSearchResponseSuccessTotalStat,
	shotsOnTarget: PlayerSearchResponseStat,
	shotsInsidePa: PlayerSearchResponseStat,
	shotsOutsidePa: PlayerSearchResponseStat,
	shots: PlayerSearchResponseStat,
	totalXg: PlayerSearchResponseStat,
	nonPenaltyXg: PlayerSearchResponseStat,
	totalXa: PlayerSearchResponseStat,
	nonPenaltyXgPerShot: PlayerSearchResponseStat,
	goalsXgRatio: PlayerSearchResponseStat,
	nonPenaltyXgPlusXaPer90: PlayerSearchResponseStat,
	totalXgTotal: z.number().nullish(),
	nonPenaltyXgTotal: z.number().nullish(),
	totalXaTotal: z.number().nullish(),
	playedInNationalTeam: z.number().int().nullish(),
	id: z.number().int(),
	partnerFlag: z.boolean().nullish(),
	firstName: z.string().nullish(),
	lastName: z.string().nullish(),
	birthday: z.string().nullish(),
	clubName: z.string().nullish(),
	teamName: z.string().nullish(),
	countryCode: z.string().nullish(),
	nationality: PlayerSearchResponseCountry.nullish(),
	nationality2: PlayerSearchResponseCountry.nullish(),
	weight: z.string().nullish(),
	height: z.string().nullish(),
	position: z.string().nullish(),
	positionCount: z.number().int().nullish(),
	teamAgeGroup: z.string().nullish(),
	teamLevel: z.string().nullish(),
	strongFoot: z.string().nullish(),
	gamesCount: z.string().nullish(),
	minutesPlayed: z.number().int(),
	isFavorite: z.boolean(),
	gender: z.string().nullish().default(null),
	maleMinutesPlayed: z.number().int().nullish().default(null),
	femaleMinutesPlayed: z.number().int().nullish().default(null),
});
export type PlayerSearchResponsePlayer = z.infer<
	typeof PlayerSearchResponsePlayer
>;

export const PlayerSearchResponseItems = z.object({
	items: z.array(PlayerSearchResponsePlayer),
});
export type PlayerSearchResponseItems = z.infer<
	typeof PlayerSearchResponseItems
>;

export const PlayerSearchResponse = z.object({
	_embedded: PlayerSearchResponseItems,
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type PlayerSearchResponse = z.infer<typeof PlayerSearchResponse>;

export const PlayerSearchPresetColumnDTO = z.object({
	id: z.string(),
	name: z.string(),
	default: z.boolean(),
	rating: z.boolean(),
	minutesPlayed: z.boolean(),
	goalsScored: z.boolean(),
	goalsOwn: z.boolean(),
	assists: z.boolean(),
	shots: z.boolean(),
	shotsOnTarget: z.boolean(),
	shotsInsidePa: z.boolean(),
	shotsOutsidePa: z.boolean(),
	takeons: z.boolean(),
	tackles: z.boolean(),
	aerialDuels: z.boolean(),
	groundDuels: z.boolean(),
	recoveries: z.boolean(),
	passes: z.boolean(),
	passesAccuracy: z.boolean(),
	keyPasses: z.boolean(),
	passesForward: z.boolean(),
	crosses: z.boolean(),
	controlUnderPressure: z.boolean(),
	goalsConceded: z.boolean(),
	catches: z.boolean(),
	punches: z.boolean(),
	goalsKicks: z.boolean(),
	aerialClearances: z.boolean(),
	birthday: z.boolean(),
	clubName: z.boolean(),
	teamName: z.boolean(),
	height: z.boolean(),
	nationality: z.boolean(),
	position: z.boolean(),
	strongFoot: z.boolean(),
	teamAgeGroup: z.boolean(),
	weight: z.boolean(),
	gamesCount: z.boolean(),
	totalGoals: z.boolean(),
	totalAssists: z.boolean(),
});
export type PlayerSearchPresetColumnDTO = z.infer<
	typeof PlayerSearchPresetColumnDTO
>;

export const PlayerSearchPresetColumnResponse = z.object({
	items: z.array(PlayerSearchPresetColumnDTO),
});
export type PlayerSearchPresetColumnResponse = z.infer<
	typeof PlayerSearchPresetColumnResponse
>;

export const PlayerSearchPresetColumnUpsertRequest = z.object({
	name: z.string().max(255),
	rating: z.boolean(),
	minutesPlayed: z.boolean(),
	goalsScored: z.boolean(),
	goalsOwn: z.boolean(),
	assists: z.boolean(),
	shots: z.boolean(),
	shotsOnTarget: z.boolean(),
	shotsInsidePa: z.boolean(),
	shotsOutsidePa: z.boolean(),
	takeons: z.boolean(),
	tackles: z.boolean(),
	groundDuels: z.boolean(),
	recoveries: z.boolean(),
	passes: z.boolean(),
	passesAccuracy: z.boolean(),
	keyPasses: z.boolean(),
	passesForward: z.boolean(),
	crosses: z.boolean(),
	controlUnderPressure: z.boolean(),
	goalsConceded: z.boolean(),
	catches: z.boolean(),
	punches: z.boolean(),
	goalsKicks: z.boolean(),
	aerialClearances: z.boolean(),
	aerialDuels: z.boolean(),
	birthday: z.boolean(),
	clubName: z.boolean(),
	teamName: z.boolean(),
	height: z.boolean(),
	nationality: z.boolean(),
	position: z.boolean(),
	strongFoot: z.boolean(),
	teamAgeGroup: z.boolean(),
	weight: z.boolean(),
	gamesCount: z.boolean(),
	totalGoals: z.boolean(),
	totalAssists: z.boolean(),
});
export type PlayerSearchPresetColumnUpsertRequest = z.infer<
	typeof PlayerSearchPresetColumnUpsertRequest
>;

export const FilterValues = z
	.object({ gte: z.number().int().nullable(), lt: z.number().int().nullable() })
	.partial();
export type FilterValues = z.infer<typeof FilterValues>;

export const PlayerSearchPresetFilterDTO = z.object({
	id: z.string(),
	name: z.string(),
	default: z.boolean(),
	rating: FilterValues,
	minutesPlayed: FilterValues,
	gamesCount: FilterValues,
	goalsScored: FilterValues,
	goalsOwn: FilterValues,
	assists: FilterValues,
	totalGoals: FilterValues,
	totalAssists: FilterValues,
	shots: FilterValues,
	shotsOnTarget: FilterValues,
	shotsInsidePa: FilterValues,
	shotsOutsidePa: FilterValues,
	takeons: FilterValues,
	tackles: FilterValues,
	aerialDuels: FilterValues,
	groundDuels: FilterValues,
	recoveries: FilterValues,
	passes: FilterValues,
	passesAccuracy: FilterValues,
	keyPasses: FilterValues,
	passesForward: FilterValues,
	crosses: FilterValues,
	controlUnderPressure: FilterValues,
	goalsConceded: FilterValues,
	catches: FilterValues,
	punches: FilterValues,
	goalsKicks: FilterValues,
	aerialClearances: FilterValues,
});
export type PlayerSearchPresetFilterDTO = z.infer<
	typeof PlayerSearchPresetFilterDTO
>;

export const PlayerSearchPresetFilterResponse = z.object({
	items: z.array(PlayerSearchPresetFilterDTO),
});
export type PlayerSearchPresetFilterResponse = z.infer<
	typeof PlayerSearchPresetFilterResponse
>;

export const PlayerSearchPresetFilterUpsertRequest = z.object({
	name: z.string().max(255),
	rating: FilterValues,
	minutesPlayed: FilterValues,
	gamesCount: FilterValues,
	goalsScored: FilterValues,
	goalsOwn: FilterValues,
	assists: FilterValues,
	totalGoals: FilterValues,
	totalAssists: FilterValues,
	shots: FilterValues,
	shotsOnTarget: FilterValues,
	shotsInsidePa: FilterValues,
	shotsOutsidePa: FilterValues,
	takeons: FilterValues,
	tackles: FilterValues,
	aerialDuels: FilterValues,
	groundDuels: FilterValues,
	recoveries: FilterValues,
	passes: FilterValues,
	passesAccuracy: FilterValues,
	keyPasses: FilterValues,
	passesForward: FilterValues,
	crosses: FilterValues,
	controlUnderPressure: FilterValues,
	goalsConceded: FilterValues,
	catches: FilterValues,
	punches: FilterValues,
	goalsKicks: FilterValues,
	aerialClearances: FilterValues,
});
export type PlayerSearchPresetFilterUpsertRequest = z.infer<
	typeof PlayerSearchPresetFilterUpsertRequest
>;

export const NationalityData = z.object({
	EU: z.record(z.string()),
	AF: z.record(z.string()),
	SA: z.record(z.string()),
	NA: z.record(z.string()),
	AI: z.record(z.string()),
	OC: z.record(z.string()),
});
export type NationalityData = z.infer<typeof NationalityData>;

export const PlayerGradeResponseItem = z.object({
	grade: z.number().int(),
	gameId: z.number().int(),
});
export type PlayerGradeResponseItem = z.infer<typeof PlayerGradeResponseItem>;

export const PlayerGradeResponse = z.object({
	average: z.number(),
	count: z.number().int(),
	grades: z.array(PlayerGradeResponseItem),
});
export type PlayerGradeResponse = z.infer<typeof PlayerGradeResponse>;

export const PlayerData = z.object({
	id: z.number().int(),
	type: z.string(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	birthday: z.string().nullable(),
	strongestFoot: z.string().nullable(),
	height: z.number().int().nullable(),
	weight: z.number().int().nullable(),
	position: z.string().nullable(),
	position2: z.string().nullable(),
	photo: z.string().nullable(),
	nationality: z.string().nullable(),
	nationalityCode: z.string().nullable(),
	nationality2: z.string().nullable(),
	nationality2Code: z.string().nullable(),
	playedInNationalTeam: z.boolean(),
	club: z
		.object({
			name: z.string(),
			country: z.string(),
			countryCode: z.string().optional(),
			logo: z.string().nullable(),
			region: z.string().nullable(),
		})

		.nullable(),
	team: z.object({ category: z.string().nullable() })

	.nullable(),
	isFavorite: z.boolean(),
	performanceGrade: PlayerGradeResponse.nullish().default(null),
	potentialGrade: PlayerGradeResponse.nullish().default(null),
	notesCount: z.number().int().nullish().default(null),
	lastGraded: z.string().nullish().default(null),
	rating: z.number().nullish().default(null),
	isPinned: z.boolean().nullish(),
	playerName: z.string(),
});
export type PlayerData = z.infer<typeof PlayerData>;

export const TermsOfUseRequest = z.object({
	subscribeNewsletter: z.boolean(),
	version: z.number().int(),
});
export type TermsOfUseRequest = z.infer<typeof TermsOfUseRequest>;

export const ClubType = z.enum(["partner", "nonpartner", "discover"]);
export type ClubType = z.infer<typeof ClubType>;

export const ClubSuggestion = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullable(),
	countryCode: z.string(),
	country: z.string().default(""),
	type: ClubType,
});
export type ClubSuggestion = z.infer<typeof ClubSuggestion>;

export const PlayerType = z.enum(["partner", "nonpartner", "discover"]);
export type PlayerType = z.infer<typeof PlayerType>;

export const PlayerSuggestion = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
	photo: z.string().nullable(),
	clubName: z.string().nullable(),
	type: PlayerType,
	clubCountry: z.string().nullable(),
});
export type PlayerSuggestion = z.infer<typeof PlayerSuggestion>;

export const GameSuggestion = z.object({
	id: z.number().int(),
	category: z.string().nullable(),
	date: z.string(),
	type: z.string().nullable(),
	homeTeam: z.object({
		teamName: z.string().nullable(),
		clubName: z.string().nullable(),
		score: z.number().int().nullable(),
		clubCountry: z.string().nullable(),
	}),
	awayTeam: z.object({
		teamName: z.string().nullable(),
		clubName: z.string().nullable(),
		score: z.number().int().nullable(),
		clubCountry: z.string().nullable(),
	}),
});
export type GameSuggestion = z.infer<typeof GameSuggestion>;

export const ClubData = z.object({
	id: z.number().int(),
	name: z.string(),
	gamesCount: z.number().int(),
	playersCount: z.number().int(),
	teamsCount: z.number().int(),
	type: z.string(),
	slug: z.string(),
	isFavorite: z.boolean(),
	showCase: z.boolean(),
	logo: z.string().nullable(),
	region: z.string().nullable(),
	country: z.string().nullable(),
	countryCode: z.string().nullable(),
});
export type ClubData = z.infer<typeof ClubData>;

export const GlobalSearchResponse = z.object({
	data: z.array(z.union([ClubData, PlayerData, GameData])),
	clubsCount: z.number().int(),
	playersCount: z.number().int(),
	gamesCount: z.number().int(),
	unselectedSubscribedClubsCount: z.number().int().nullish(),
	unselectedSubscribedPlayersCount: z.number().int().nullish(),
	unselectedSubscribedGamesCount: z.number().int().nullish(),
	nonOwnerShipGamesCount: z.number().int(),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type GlobalSearchResponse = z.infer<typeof GlobalSearchResponse>;

export const ClubFavoriteResponse = z.object({
	id: z.number().int(),
	type: z.string(),
	isFavorite: z.boolean(),
});
export type ClubFavoriteResponse = z.infer<typeof ClubFavoriteResponse>;

export const Feature = z.enum([
	"NewScoutHomepage",
	"PlayerSignUpLinkGenerationEnabled",
	"SyncUserToAzure",
	"DiscoverHelpLinkEnabled",
	"DiscoverTeamMembershipAutomaticApproval",
	"PerformanceLabPaywall",
]);
export type Feature = z.infer<typeof Feature>;

export const FeatureResponse = z.object({
	feature: Feature,
	enabled: z.boolean(),
});
export type FeatureResponse = z.infer<typeof FeatureResponse>;

export const GamesSeasonsResponse = z.array(z.number());
export type GamesSeasonsResponse = z.infer<typeof GamesSeasonsResponse>;

export const AggregatePlayerStatRequest = z.object({
	types: z.array(z.string()),
	compareTo: z.string().nullish(),
});
export type AggregatePlayerStatRequest = z.infer<
	typeof AggregatePlayerStatRequest
>;

export const PlayerSummaryStatData = z.object({
	teamAgeGroup: z.string().nullable(),
	rating: z.number().nullable(),
	minutesPlayed: z.number().int().nullable(),
	gamesPlayed: z.number().int().nullable(),
	goalsScored: z.number().nullable(),
	assists: z.number().nullable(),
});
export type PlayerSummaryStatData = z.infer<typeof PlayerSummaryStatData>;

export const AggregatePlayerStatResponse = z.object({
	stats: z
		.object({
			summary: PlayerSummaryStatData,
			offensiveStat: PlayerOffensiveStatData,
			defensiveStat: PlayerDefensiveStatData,
			distributionStat: PlayerDistributionStatData,
			goalkeeperStat: PlayerGoalKeepingStatData,
		})
		.partial(),
	positions: z.array(z.string()),
});
export type AggregatePlayerStatResponse = z.infer<
	typeof AggregatePlayerStatResponse
>;

export const PlayerGameResponse = z.object({
	games: z.array(GameData),
	nonOwnerShipGamesCount: z.number().int().nullish().default(null),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type PlayerGameResponse = z.infer<typeof PlayerGameResponse>;

export const CreatePlayerPossibleDuplicateRequest = z.object({
	possibleDuplicateId: z
		.number()
		.int()
		.describe("The ID of the newly created player profile"),
	clubId: z
		.number()
		.int()
		.describe(
			"Club context for duplicate search; resolved from the logged-in user when omitted",
		)
		.nullish(),
	firstName: z.string(),
	lastName: z.string(),
	birthday: z.string().describe("Birthday used for duplicate search").nullish(),
	notes: z.string().describe("Optional notes about the duplicate").nullish(),
});
export type CreatePlayerPossibleDuplicateRequest = z.infer<
	typeof CreatePlayerPossibleDuplicateRequest
>;

export const PlayerPossibleDuplicateResponse = z.object({
	id: z.number().int(),
	playerId: z.number().int().nullish(),
	possibleDuplicateId: z.number().int().nullish(),
	similarityScore: z.string().nullish().default(null),
	notes: z.string().nullish().default(null),
});
export type PlayerPossibleDuplicateResponse = z.infer<
	typeof PlayerPossibleDuplicateResponse
>;

export const ImageTypeResponse = z.object({
	url: z.string(),
	width: z.number().int(),
	height: z.number().int(),
});
export type ImageTypeResponse = z.infer<typeof ImageTypeResponse>;

export const FormatResponse = z
	.object({
		large: ImageTypeResponse.nullable(),
		small: ImageTypeResponse.nullable(),
		medium: ImageTypeResponse.nullable(),
		thumbnail: ImageTypeResponse.nullable(),
	})
	.partial();
export type FormatResponse = z.infer<typeof FormatResponse>;

export const ImageMedia = z.object({
	formats: FormatResponse.nullable(),
	blurhash: z.string().nullable(),
	caption: z.string().nullable(),
	alternativeText: z.string().nullable(),
});
export type ImageMedia = z.infer<typeof ImageMedia>;

export const NewsfeedPost = z.object({
	slug: z.string(),
	heading: z.string(),
	teaser: z.string(),
	publishedAt: z.number().int().nullable(),
	cover: ImageMedia,
});
export type NewsfeedPost = z.infer<typeof NewsfeedPost>;

export const GetNewsfeedResponse = z.object({
	posts: z.array(NewsfeedPost),
	hasAccess: z.boolean(),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type GetNewsfeedResponse = z.infer<typeof GetNewsfeedResponse>;

export const Text = z.object({
	__component: z.literal("content.text"),
	text: z.string(),
});
export type Text = z.infer<typeof Text>;

export const Html = z.object({
	__component: z.literal("content.html"),
	html: z.string(),
});
export type Html = z.infer<typeof Html>;

export const Markdown = z.object({
	__component: z.literal("content.markdown"),
	md: z.string(),
});
export type Markdown = z.infer<typeof Markdown>;

export const LinkableImage = z.object({
	__component: z.literal("content.linkableimage"),
	link: z.string(),
	image: ImageMedia,
});
export type LinkableImage = z.infer<typeof LinkableImage>;

export const VideoMedia = z.object({
	url: z.string(),
	mime: z.string(),
	caption: z.string().nullable(),
	alternativeText: z.string().nullable(),
});
export type VideoMedia = z.infer<typeof VideoMedia>;

export const Video = z.object({
	__component: z.literal("content.video"),
	video: VideoMedia,
});
export type Video = z.infer<typeof Video>;

export const VideoWithText = z.object({
	__component: z.literal("content.video-with-text"),
	description: z.string(),
	media: z.array(VideoMedia),
});
export type VideoWithText = z.infer<typeof VideoWithText>;

export const Gallery = z.object({
	__component: z.enum(["content.gallery", "content.text"]),
	media: z.array(z.union([ImageMedia, VideoMedia])),
});
export type Gallery = z.infer<typeof Gallery>;

export const LinkableImageGallery = z.object({
	__component: z.literal("content.linkable-image-gallery"),
	images: z.array(z.object({ link: z.string(), image: ImageMedia })),
});
export type LinkableImageGallery = z.infer<typeof LinkableImageGallery>;

export const Post = z.object({
	content: z.array(
		z.union([
			Text,
			Html,
			Markdown,
			LinkableImage,
			Video,
			VideoWithText,
			Gallery,
			LinkableImageGallery,
		]),
	),
	tags: z.array(z.string()),
	slug: z.string(),
	heading: z.string(),
	teaser: z.string(),
	publishedAt: z.number().int().nullable(),
	cover: ImageMedia,
});
export type Post = z.infer<typeof Post>;

export const GetPostDetail = z.object({
	article: Post,
	next: Post.nullable(),
	previous: Post.nullable(),
	related: z.array(Post),
});
export type GetPostDetail = z.infer<typeof GetPostDetail>;

export const UserActivityLogRequest = z.record(
	z.union([z.string(), z.number()]),
);
export type UserActivityLogRequest = z.infer<typeof UserActivityLogRequest>;

export const GetClubsResponse = z.object({
	clubs: z.array(ClubData),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type GetClubsResponse = z.infer<typeof GetClubsResponse>;

export const order__4 = z
	.object({
		field: z
			.enum([
				"lastName",
				"club",
				"birthday",
				"nationality",
				"position",
				"strongestFoot",
				"updatedAt",
				"performanceGradeAverage",
				"potentialGradeAverage",
				"gradeNotesCount",
				"eyeballRating",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__4 = z.infer<typeof order__4>;

export const PlayerResponse = z.object({
	showRating: z.boolean().default(true),
	players: z.array(PlayerData),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type PlayerResponse = z.infer<typeof PlayerResponse>;

export const ClipResponse = z.object({
	event: z.array(
		z
			.object({
				end: z.number().int(),
				start: z.number().int(),
				source: z.number().int(),
				position: z.number().int(),
				beproEventId: z.number().int().nullable(),
			})
			.partial(),
	),
	id: z.number().int(),
	name: z.string(),
	isDownloadable: z.boolean(),
	isFinished: z.boolean().nullable(),
	shareSlug: z.string().nullable(),
	description: z.string().nullable(),
	expireLinkAt: z.string().nullable(),
	password: z.string().nullable(),
	url: z.string().nullable(),
	beproEventCount: z.number().int(),
	isCrossGame: z.boolean().nullable(),
});
export type ClipResponse = z.infer<typeof ClipResponse>;

export const renameClip_Body = z
	.object({ clipId: z.number().int(), name: z.string() })
	.partial();
export type renameClip_Body = z.infer<typeof renameClip_Body>;

export const BeproEventResponse = z.object({
	id: z.number().int(),
	game_id: z.number().int(),
	game_team_id: z.number().int(),
	game_player_id: z.number().int(),
	provider_event_id: z.number().int(),
	event_time: z.number().int(),
	event_video_time: z.number().int(),
	event_period: z.string(),
	x: z.number(),
	y: z.number(),
	event_type: z.string(),
	ball_position_x: z.number().nullable(),
	ball_position_y: z.number().nullable(),
	relative_event_id: z.number().int().nullable(),
	relative_event_time: z.number().int().nullable(),
	relative_event_player_id: z.number().int().nullable(),
	relative_event_x: z.number().nullable(),
	relative_event_y: z.number().nullable(),
	subEventType: z.string().nullable(),
});
export type BeproEventResponse = z.infer<typeof BeproEventResponse>;

export const LineupResponse = z.object({
	position: z.object({ x: z.number(), y: z.number() })

	.nullish(),
	id: z.number().int(),
	team_id: z.number().int(),
	player_id: z.number().int(),
	back_number: z.number().int(),
	player_name: z.string().nullable(),
	position_name: z.string().nullable(),
	game_player_id: z.number().int(),
	player_last_name: z.string().nullable(),
	is_starting_lineup: z.union([z.boolean(), z.number()]),
});
export type LineupResponse = z.infer<typeof LineupResponse>;

export const GameResponse = z.object({
	homeTeam: z.object({ id: z.number().int(), colorShirt: z.string() }),
	awayTeam: z.object({ id: z.number().int(), colorShirt: z.string() }),
	videoInfo: z.array(
		z
			.object({
				event_period: z.string(),
				padding: z.number().int(),
				start_match_time: z.number().int(),
				end_match_time: z.number().int(),
			})
			.partial(),
	),
});
export type GameResponse = z.infer<typeof GameResponse>;

export const shareClipLink_Body = z.object({
	expireDate: z.string(),
	password: z.string(),
	description: z.string(),
	isPasswordUpdated: z.boolean().optional(),
});
export type shareClipLink_Body = z.infer<typeof shareClipLink_Body>;

export const EyeballAddons = z.object({
	ai_scout: z.boolean(),
	ai_support_pack: z.boolean(),
	ai_player_data: z.boolean(),
	efg: z.boolean(),
});
export type EyeballAddons = z.infer<typeof EyeballAddons>;

export const UserSubscriptionData = z.object({
	id: z.number().int(),
	comment: z.string().nullish(),
	product: z.string(),
	countries: z.array(
		z
			.object({
				label: z.string().describe("label"),
				value: z.string().describe("value"),
				continent: z.string().describe("continent"),
			})
			.partial(),
	),
	countriesCount: z.number().int(),
	nationalCountries: z.array(
		z.object({ label: z.string(), value: z.string() }).partial(),
	),
	gameOptions: z
		.object({
			ai: z.boolean().describe("ai"),
			statistics_rating: z.boolean().describe("statistics_rating"),
			statistics_players: z.boolean().describe("statistics_players"),
			statistics_teams: z.boolean().describe("statistics_teams"),
			events: z.boolean().describe("events"),
			clips: z.boolean().describe("clips"),
			downloadable: z.boolean().describe("downloadable"),
			downloadable_gametags: z.boolean().describe("downloadable_gametags"),
			player_stats_search: z.boolean().describe("player_stats_search"),
			physical_metrics: z.boolean().describe("physical_metrics"),
		})
		.partial()

		.nullish(),
	eyeballAddons: EyeballAddons,
	maxAccounts: z.number().int().nullish(),
	startDate: z.string().nullish(),
	expirationDate: z.string().nullish(),
	active: z.boolean(),
	expiresIn: z.string().nullish(),
	ownerships: z.array(z.unknown()),
	genderOptions: z
		.object({
			Female: z.boolean().describe("Female"),
			Male: z.boolean().describe("Male"),
		})
		.partial()

		.nullish(),
	teamGroupName: z.string().nullish(),
});
export type UserSubscriptionData = z.infer<typeof UserSubscriptionData>;

export const TeamData = z.object({
	id: z.number().int(),
	name: z.string(),
	category: z.string().nullable(),
	slug: z.string(),
	link: z.string(),
	clubId: z.number().int().nullable(),
	clubName: z.string().nullable(),
	clubLogo: z.string().nullable(),
	clubCountry: z.string().nullable(),
	country: z.string().nullish(),
	clubRegion: z.string().nullable(),
	isFavorite: z.boolean(),
	gamesCount: z.number().int(),
	playersCount: z.number().int(),
});
export type TeamData = z.infer<typeof TeamData>;

export const GetTeamResponse = z.object({
	teams: z.array(TeamData),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type GetTeamResponse = z.infer<typeof GetTeamResponse>;

export const UpdateUserProfile = z
	.object({
		firstName: z.string().nullable(),
		lastName: z.string().nullable(),
		password: z.string().nullable(),
		photo: z.string().nullable(),
		phoneNumber: z.string().nullable(),
		timezone: z.string().nullable(),
		locale: z.string().nullable(),
		hasNewsletterSubscription: z.boolean().nullable(),
	})
	.partial();
export type UpdateUserProfile = z.infer<typeof UpdateUserProfile>;

export const updateUserInformation_Body = z
	.object({ user: UpdateUserProfile })
	.partial();
export type updateUserInformation_Body = z.infer<
	typeof updateUserInformation_Body
>;

export const UserInfo = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
	phoneNumber: z.string().nullable(),
	timezone: z.string().nullable(),
	locale: z.string().nullish(),
	photo: z.string().nullish(),
	hasNewsletterSubscription: z.boolean(),
});
export type UserInfo = z.infer<typeof UserInfo>;

export const UserClubInfo = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullish(),
	active: z.boolean(),
});
export type UserClubInfo = z.infer<typeof UserClubInfo>;

export const UserProfileResponse = z.object({
	user: UserInfo,
	club: UserClubInfo,
});
export type UserProfileResponse = z.infer<typeof UserProfileResponse>;

export const DefaultSelectResponse = z.object({
	label: z.string(),
	value: z.union([z.string(), z.number(), z.number(), z.string()]),
	gender: z.string().nullish(),
});
export type DefaultSelectResponse = z.infer<typeof DefaultSelectResponse>;

export const updateEmailNotifications_Body = z
	.object({
		emailNotificationTypes: z
			.object({ player: z.boolean(), club: z.boolean(), team: z.boolean() })
			.partial(),
		isEmailNotificationEnabled: z.boolean(),
	})
	.partial();
export type updateEmailNotifications_Body = z.infer<
	typeof updateEmailNotifications_Body
>;

export const PlayerData2 = z.object({
	id: z.number().int(),
	playerType: z.string(),
	firstName: z.string(),
	name: z.string(),
	lastName: z.string(),
	notificationId: z.number().int().nullable(),
	isActive: z.boolean().nullable(),
	frequency: z.string().nullable(),
	types: z.record(z.boolean()).nullable(),
});
export type PlayerData2 = z.infer<typeof PlayerData2>;

export const PlayerResponse2 = z.object({
	players: z.array(PlayerData2),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type PlayerResponse2 = z.infer<typeof PlayerResponse2>;

export const UpdatePlayerEmailNotification = z.object({
	id: z.number().int(),
	playerType: PlayerType,
	frequency: z.enum(["daily", "weekly"]),
	types: z.record(z.boolean()),
	isActive: z.boolean().default(true),
});
export type UpdatePlayerEmailNotification = z.infer<
	typeof UpdatePlayerEmailNotification
>;

export const BatchUpdatePlayerEmailNotification = z.object({
	notifications: z.array(UpdatePlayerEmailNotification),
});
export type BatchUpdatePlayerEmailNotification = z.infer<
	typeof BatchUpdatePlayerEmailNotification
>;

export const ClubData2 = z.object({
	id: z.number().int(),
	name: z.string(),
	clubType: z.string(),
	notificationId: z.number().int().nullable(),
	isActive: z.boolean().nullable(),
	frequency: z.string().nullable(),
	types: z.record(z.boolean()).nullable(),
});
export type ClubData2 = z.infer<typeof ClubData2>;

export const ClubResponse = z.object({
	clubs: z.array(ClubData2),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type ClubResponse = z.infer<typeof ClubResponse>;

export const UpdateClubEmailNotification = z.object({
	id: z.number().int(),
	clubType: ClubType,
	frequency: z.enum(["daily", "weekly"]),
	types: z.record(z.boolean()),
	isActive: z.boolean().default(true),
});
export type UpdateClubEmailNotification = z.infer<
	typeof UpdateClubEmailNotification
>;

export const BatchUpdateClubEmailNotification = z.object({
	notifications: z.array(UpdateClubEmailNotification),
});
export type BatchUpdateClubEmailNotification = z.infer<
	typeof BatchUpdateClubEmailNotification
>;

export const TeamData2 = z.object({
	id: z.number().int(),
	name: z.string(),
	category: z.string(),
	clubName: z.string(),
	notificationId: z.number().int().nullable(),
	isActive: z.boolean().nullable(),
	frequency: z.string().nullable(),
	types: z.record(z.boolean()).nullable(),
});
export type TeamData2 = z.infer<typeof TeamData2>;

export const TeamResponse = z.object({
	teams: z.array(TeamData2),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type TeamResponse = z.infer<typeof TeamResponse>;

export const UpdateTeamEmailNotification = z.object({
	id: z.number().int(),
	frequency: z.enum(["daily", "weekly"]),
	types: z.record(z.boolean()),
	isActive: z.boolean().default(true),
});
export type UpdateTeamEmailNotification = z.infer<
	typeof UpdateTeamEmailNotification
>;

export const BatchUpdateTeamEmailNotification = z.object({
	notifications: z.array(UpdateTeamEmailNotification),
});
export type BatchUpdateTeamEmailNotification = z.infer<
	typeof BatchUpdateTeamEmailNotification
>;

export const TeamInfo = z.object({
	id: z.number().int(),
	name: z.string(),
	category: z.string().nullish(),
	slug: z.string(),
	teamUrl: z.string().nullish(),
	isFavorite: z.boolean().nullish(),
});
export type TeamInfo = z.infer<typeof TeamInfo>;

export const ClubStatsSearchData = z.object({
	id: z.number().int(),
	name: z.string(),
});
export type ClubStatsSearchData = z.infer<typeof ClubStatsSearchData>;

export const TeamData3 = z.object({
	id: z.number().int(),
	name: z.string(),
	clubName: z.string().nullable(),
	clubId: z.number().int().nullable(),
	logo: z.string().nullable(),
	type: z.string().nullable(),
	teamId: z.number().int().nullable(),
	teamName: z.string().nullable(),
	teamCategory: z.string().nullable(),
});
export type TeamData3 = z.infer<typeof TeamData3>;

export const TeamScore = z.object({
	homeTeamScore: z.number().int().nullable(),
	awayTeamScore: z.number().int().nullable(),
});
export type TeamScore = z.infer<typeof TeamScore>;

export const GameData2 = z.object({
	homeTeam: TeamData3,
	awayTeam: TeamData3,
	scores: TeamScore,
	id: z.number().int(),
	type: z.string().nullable(),
	date: z.string().nullable(),
	isAnalysisFinished: z.boolean().nullable(),
	gameUrl: z.string().nullable(),
	category: z.string().nullable(),
	hasVideo: z.boolean().default(false),
	teamSheetFilled: z.boolean().default(false),
	editScore: z.boolean().default(false),
	editGame: z.boolean().default(false),
	isNational: z.boolean().default(false),
	canShowGameDetailWithoutVideo: z.boolean().optional().default(false),
	gender: z.string().nullable(),
});
export type GameData2 = z.infer<typeof GameData2>;

export const ProgramGameResponse = z.object({
	games: z.array(GameData2),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type ProgramGameResponse = z.infer<typeof ProgramGameResponse>;

export const GameManagementRequest = z.object({
	game: z.object({
		gameId: z.number().int().nullish(),
		awayClub: z
			.object({
				label: z.string(),
				value: z.union([z.string(), z.number()]),
				type: z.string().nullable(),
			})
			.partial(),
		awayTeam: z.object({ label: z.string(), value: z.number().int() }).partial()

		.optional(),
		date: z.string(),
		homeTeam: z
			.object({
				label: z.string(),
				value: z.number().int(),
				type: z.string().nullable(),
			})
			.partial(),
		hour: z.string(),
		isNational: z.boolean().default(false),
		minute: z.string(),
		type: z.string(),
		veoLink: z.string().max(1024).nullish(),
	}),
});
export type GameManagementRequest = z.infer<typeof GameManagementRequest>;

export const updateTeamScores_Body = z
	.object({
		scores: z.object({
			homeTeamScore: z.number().int(),
			awayTeamScore: z.number().int(),
		}),
	})
	.partial();
export type updateTeamScores_Body = z.infer<typeof updateTeamScores_Body>;

export const CoachInfo = z.object({
	id: z.number().int(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
});
export type CoachInfo = z.infer<typeof CoachInfo>;

export const ClubTeamInfo = z.object({
	coaches: z.array(CoachInfo),
	id: z.number().int(),
	name: z.string(),
	level: z.number().int(),
	playerCount: z.number().int(),
	clubId: z.number().int().nullable(),
	clubName: z.string().nullable(),
	category: z.string().nullable(),
	gender: GenderEnum.nullable().default(null),
});
export type ClubTeamInfo = z.infer<typeof ClubTeamInfo>;

export const TeamResponse2 = z.object({
	data: z.array(ClubTeamInfo),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type TeamResponse2 = z.infer<typeof TeamResponse2>;

export const addUpdateTeam_Body = z.object({
	id: z.number().int().nullish(),
	category: z.string(),
	name: z.string(),
	gender: z.string().nullish(),
});
export type addUpdateTeam_Body = z.infer<typeof addUpdateTeam_Body>;

export const TeamPageClubInfo = z.object({
	id: z.number().int(),
	name: z.string(),
	logo: z.string().nullish(),
	countryCode: z.string().nullish(),
	country: z.string().nullish(),
	region: z.string().nullish(),
	type: z.string().nullish(),
});
export type TeamPageClubInfo = z.infer<typeof TeamPageClubInfo>;

export const TeamPageInfo = z.object({
	id: z.number().int(),
	name: z.string(),
	category: z.string().nullish(),
	slug: z.string(),
	logo: z.string().nullish(),
	countryCode: z.string().nullish(),
	country: z.string().nullish(),
	region: z.string().nullish(),
	city: z.string().nullish(),
	isDiscover: z.boolean(),
	isFavorite: z.boolean(),
	gamesCount: z.number().int().nullish(),
	playersCount: z.number().int().nullish(),
	club: TeamPageClubInfo.nullish(),
});
export type TeamPageInfo = z.infer<typeof TeamPageInfo>;

export const order__5 = z
	.object({
		name: z.string().describe("Sorting column name"),
		sort: z.string().describe("Sorting column type"),
	})
	.partial()

	.optional();
export type order__5 = z.infer<typeof order__5>;

export const PlayerLastGameData = z
	.object({
		id: z.number().int().nullable().default(null),
		homeTeamTeamName: z.string().nullable().default(null),
		homeTeamClubName: z.string().nullable().default(null),
		homeTeamScore: z.number().int().nullable().default(null),
		awayTeamTeamName: z.string().nullable().default(null),
		awayTeamClubName: z.string().nullable().default(null),
		awayTeamScore: z.number().int().nullable().default(null),
		awayClubLogo: z.string().nullable().default(null),
		homeClubLogo: z.string().nullable().default(null),
		category: z.string().nullable().default(null),
		date: z.string().datetime({ offset: true }).nullable().default(null),
		type: z.string().nullable().default(null),
		rating: z.number().nullable().default(null),
		analysisApproved: z.boolean().nullable().default(null),
		providerTag: z.string().nullable().default(null),
		isAnalysisFinished: z.number().int().nullable(),
	})
	.partial();
export type PlayerLastGameData = z.infer<typeof PlayerLastGameData>;

export const ClubPlayersInfo = z.object({
	id: z.number().int(),
	playerDataId: z.number().int(),
	firstName: z.string(),
	lastName: z.string().nullable(),
	birthday: z.string().nullable(),
	birthdayPure: z.string().nullish(),
	strongestFoot: z.string().nullable(),
	height: z.union([z.string(), z.number(), z.number(), z.string()]).nullable(),
	weight: z.union([z.string(), z.number(), z.number(), z.string()]).nullable(),
	position: z.string().nullable(),
	position2: z.string().nullable(),
	photo: z.string().nullable(),
	nationality: z.string().nullable(),
	nationality2: z.string().nullable(),
	isRegistered: z.boolean().nullish(),
	editable: z.boolean().nullish(),
	notes: z.string().nullable(),
	vip: z.boolean().nullish(),
	playedInNationalTeam: z.boolean(),
	teamId: z.number().int().nullable(),
	teamName: z.string().nullable(),
	tranLink: z.string().nullish(),
	federationLink: z.string().nullish(),
	isPlayerDataEditable: z.boolean(),
	playerLastGame: PlayerLastGameData.nullish(),
	clubName: z.string().nullish(),
	gender: z.string().nullish().default(null),
});
export type ClubPlayersInfo = z.infer<typeof ClubPlayersInfo>;

export const ClubPlayersResponse = z.object({
	data: z.array(ClubPlayersInfo),
	countWithoutFilter: z.number().int(),
	page: z.number().int(),
	limit: z.number().int(),
	pages: z.number().int(),
	total: z.number().int(),
});
export type ClubPlayersResponse = z.infer<typeof ClubPlayersResponse>;

export const PlayerRequest2 = z.object({
	birthday: z.string(),
	checked: z.boolean(),
	firstName: z.string(),
	lastName: z.string(),
	height: z.number().int().nullable(),
	weight: z.number().int().nullable(),
	photo: z.string().nullable(),
	strongestFoot: z.string(),
	id: z.number().int().nullable(),
	teamId: z.number().int().nullable(),
	nationality: z.string(),
	nationality2: z.string().nullable(),
	position: z.string(),
	position2: z.string().nullable(),
	notes: z.string().nullable(),
	playedInNationalTeam: z.boolean().nullish(),
	tranLink: z.string().nullable(),
	federationLink: z.string().nullable(),
	gender: z.string().nullable(),
});
export type PlayerRequest2 = z.infer<typeof PlayerRequest2>;

export const addUpdatePlayer_Body = z
	.object({ player: PlayerRequest2 })
	.partial();
export type addUpdatePlayer_Body = z.infer<typeof addUpdatePlayer_Body>;

export const assignCoachesToTeam_Body = z
	.object({ coachIds: z.array(z.number().int()) })
	.partial();
export type assignCoachesToTeam_Body = z.infer<typeof assignCoachesToTeam_Body>;

export const ClubDetailResponseDTO = z.object({
	id: z.number().int(),
	name: z.string(),
	country: z.string(),
	countryCode: z.string(),
	logo: z.string().nullable(),
	region: z.string().nullable(),
});
export type ClubDetailResponseDTO = z.infer<typeof ClubDetailResponseDTO>;

export const TeamDetailResponseDTO = z.object({
	category: z.string().nullable(),
});
export type TeamDetailResponseDTO = z.infer<typeof TeamDetailResponseDTO>;

export const PlayerStatDetail = z.object({
	value: z.string(),
	name: z.string(),
});
export type PlayerStatDetail = z.infer<typeof PlayerStatDetail>;

export const WeeklyPlayerDetailDTO = z.object({
	type: z.string(),
	id: z.number().int(),
	playerDataId: z.number().int(),
	club: ClubDetailResponseDTO.nullable(),
	playedInNationalTeam: z.boolean(),
	isFavorite: z.boolean(),
	firstName: z.string(),
	playerName: z.string(),
	lastName: z.string(),
	birthday: z.string().nullable(),
	nationalityCode: z.string().nullable(),
	nationality2Code: z.string().nullable(),
	nationality: z.string().nullable(),
	nationality2: z.string().nullable(),
	photo: z.string().nullable(),
	strongestFoot: z.string().nullable(),
	height: z.number().int().nullable(),
	weight: z.number().int().nullable(),
	position: z.string().nullable(),
	position2: z.string().nullable(),
	team: TeamDetailResponseDTO.nullable(),
	statDetail: PlayerStatDetail,
	xg: PlayerStatDetail,
	xa: PlayerStatDetail,
	nonPenaltyXg: PlayerStatDetail,
	gameLink: z.string().nullish(),
});
export type WeeklyPlayerDetailDTO = z.infer<typeof WeeklyPlayerDetailDTO>;

export const WeeklyPlayersDTO = z.object({
	players: z.array(WeeklyPlayerDetailDTO),
});
export type WeeklyPlayersDTO = z.infer<typeof WeeklyPlayersDTO>;

export const WeeklyPlayerSearchFilterDTO = z.object({
	field: z
		.enum(["type", "weeks", "age-category", "nationalities", "position", "dob"])
		.describe("Field to filter on"),
	type: z.string().describe("Type of filter"),
	value: z
		.union([
			z.string(),
			z.number(),
			z
				.object({
					start: z.string().datetime({ offset: true }),
					end: z.string().datetime({ offset: true }),
				})
				.partial(),
			z.union([z.array(z.string()), z.record(z.string())]),
			z.number(),
			z.string(),
		])
		.describe("Filter value, can be a string, integer, date or a date range")
		.nullish(),
});
export type WeeklyPlayerSearchFilterDTO = z.infer<
	typeof WeeklyPlayerSearchFilterDTO
>;

export const WeekNumberResponseDTO = z.object({
	weekNumber: z.number().int(),
	weekDate: z.string(),
});
export type WeekNumberResponseDTO = z.infer<typeof WeekNumberResponseDTO>;

export const WeekNumbersResponseDTO = z.object({
	numbers: z.array(WeekNumberResponseDTO),
	weeklyPlayersActiveWeek: z.number().int(),
});
export type WeekNumbersResponseDTO = z.infer<typeof WeekNumbersResponseDTO>;

export const WidgetSettingItem = z.object({
	key: z.string(),
	mapType: z.string(),
	isCustom: z.boolean(),
	parameters: z
		.array(
			z
				.object({ key: z.string(), value: z.string(), eventType: z.string() })
				.partial(),
		)
		.optional()
		.default([]),
	category: z.string(),
	eventType: z.string(),
	group: z.string().optional().default(""),
	label: z.string().optional().default(""),
	isPlaceholder: z.boolean(),
});
export type WidgetSettingItem = z.infer<typeof WidgetSettingItem>;

export const DisplaySettingsResponse = z
	.object({
		offensive: z.array(WidgetSettingItem).default([]),
		defensive: z.array(WidgetSettingItem).default([]),
		distribution: z.array(WidgetSettingItem).default([]),
		goalkeeper: z.array(WidgetSettingItem).default([]),
		customGroup1: z.array(WidgetSettingItem).nullable().default([]),
		customGroup2: z.array(WidgetSettingItem).nullable().default([]),
		customGroup3: z.array(WidgetSettingItem).nullable().default([]),
		customGroup4: z.array(WidgetSettingItem).nullable().default([]),
		customGroup5: z.array(WidgetSettingItem).nullable().default([]),
		customGroup6: z.array(WidgetSettingItem).nullable().default([]),
		customGroup7: z.array(WidgetSettingItem).nullable().default([]),
		customGroup8: z.array(WidgetSettingItem).nullable().default([]),
		customGroup9: z.array(WidgetSettingItem).nullable().default([]),
		customGroup10: z.array(WidgetSettingItem).nullable().default([]),
		customGroup11: z.array(WidgetSettingItem).nullable().default([]),
		customGroup12: z.array(WidgetSettingItem).nullable().default([]),
		customGroup13: z.array(WidgetSettingItem).nullable().default([]),
		customGroup14: z.array(WidgetSettingItem).nullable().default([]),
		customGroup15: z.array(WidgetSettingItem).nullable().default([]),
		customGroup16: z.array(WidgetSettingItem).nullable().default([]),
	})
	.partial();
export type DisplaySettingsResponse = z.infer<typeof DisplaySettingsResponse>;

export const UserWidgetSettingsResponse = z.object({
	insightDisplaySettings: DisplaySettingsResponse.nullable(),
});
export type UserWidgetSettingsResponse = z.infer<
	typeof UserWidgetSettingsResponse
>;

export const TokenResponse = z.object({ token: z.string().nullable() });
export type TokenResponse = z.infer<typeof TokenResponse>;

export const GameAnalysisPhysicalResponse = z.object({
	gameId: z.number().int().default(0),
	physicalMetricsVideoUrls: z
		.union([z.array(z.string()), z.record(z.string())])
		.default([]),
	physicalMetricsReportUrl: z.string().nullable().default(""),
	physicalMetricsPlayerDataUrl: z.string().nullable().default(""),
	physicalMetricsXyEventUrl: z.string().nullable().default(""),
	downloadableReportUrl: z.string().nullable().default(""),
	analysisStatus: z.string().nullable().default(""),
	rejectedReason: z.string().nullish().default(null),
});
export type GameAnalysisPhysicalResponse = z.infer<
	typeof GameAnalysisPhysicalResponse
>;

export const filters__4 = z
	.array(
		z
			.object({
				type: z
					.enum([
						"exact",
						"search",
						"boolean",
						"exists",
						"inarray",
						"datetimerange",
						"numberrange",
					])
					.describe("Type of filter"),
				field: z
					.enum(["user", "name", "formation"])
					.describe("Property to filter"),
				value: z
					.union([
						z.string(),
						z.number(),
						z.boolean(),
						z
							.object({
								start: z.string().datetime({ offset: true }),
								end: z.string().datetime({ offset: true }),
							})
							.partial(),
					])
					.describe(
						"Filter value, can be a string, integer, date or a date range",
					),
			})
			.partial(),
	)
	.optional();
export type filters__4 = z.infer<typeof filters__4>;

export const order__6 = z
	.object({
		field: z
			.enum([
				"name",
				"formation",
				"country",
				"playersCount",
				"createdAt",
				"updatedAt",
			])
			.describe("Sorting field"),
		direction: z.enum(["ASC", "DESC"]).describe("Sorting direction"),
	})
	.partial()

	.optional();
export type order__6 = z.infer<typeof order__6>;

export const ShadowTeamsRowResponse = z.object({
	id: z.number().int().default(0),
	name: z.string().default(""),
	formation: z.string(),
	playersCount: z.number().int().default(0),
	createdAt: z.string().datetime({ offset: true }),
	updatedAt: z.string().datetime({ offset: true }),
});
export type ShadowTeamsRowResponse = z.infer<typeof ShadowTeamsRowResponse>;

export const ShadowTeamsResponse = z
	.object({
		page: z.number().int().default(0),
		limit: z.number().int().default(0),
		pages: z.number().int().default(0),
		total: z.number().int().default(0),
		items: z.array(ShadowTeamsRowResponse).default([]),
	})
	.partial();
export type ShadowTeamsResponse = z.infer<typeof ShadowTeamsResponse>;

export const ShadowTeamPositionPostPutRequest = z.object({
	x: z.union([z.number(), z.number()]),
	y: z.union([z.number(), z.number()]),
	uuid: z.string(),
	name: z.string().nullable(),
	playerUuids: z.array(z.string()),
});
export type ShadowTeamPositionPostPutRequest = z.infer<
	typeof ShadowTeamPositionPostPutRequest
>;

export const ShadowTeamPlayerPostPutRequest = z.object({
	uuid: z.string(),
	playerId: z.number().int().nullable().default(null),
	name: z.string().nullable().default(null),
	clubName: z.string().nullable().default(null),
	nationality: z.string().nullable().default(null),
	age: z.number().int().nullable().default(null),
});
export type ShadowTeamPlayerPostPutRequest = z.infer<
	typeof ShadowTeamPlayerPostPutRequest
>;

export const ShadowTeamPostPutRequest = z.object({
	name: z.string(),
	formation: z.string(),
	positions: z.array(ShadowTeamPositionPostPutRequest),
	players: z.array(ShadowTeamPlayerPostPutRequest),
});
export type ShadowTeamPostPutRequest = z.infer<typeof ShadowTeamPostPutRequest>;

export const ShadowTeamPositionResponse = z.object({
	uuid: z.string(),
	name: z.string().nullable(),
	x: z.number(),
	y: z.number(),
	playerUuids: z.array(z.string()),
});
export type ShadowTeamPositionResponse = z.infer<
	typeof ShadowTeamPositionResponse
>;

export const PositionEnum = z.enum([
	"G",
	"DD",
	"DC",
	"DG",
	"MDC",
	"MC",
	"MOC",
	"MG",
	"MD",
	"MR",
	"ATT",
	"AG",
	"AD",
]);
export type PositionEnum = z.infer<typeof PositionEnum>;

export const ShadowTeamPlayerResponse = z.object({
	uuid: z.string(),
	playerId: z.number().int().nullable(),
	name: z.string().nullish(),
	clubName: z.string().nullable(),
	clubLogo: z.string().nullable(),
	nationality: z.string().nullable(),
	nationalityCode: z.string().nullable(),
	birthday: z.string().nullable(),
	age: z.number().int().nullable(),
	photo: z.string().nullable(),
	height: z.number().int().nullable(),
	weight: z.number().int().nullable(),
	position: PositionEnum.nullable(),
	strongestFoot: z.string().nullable(),
});
export type ShadowTeamPlayerResponse = z.infer<typeof ShadowTeamPlayerResponse>;

export const ShadowTeamResponse = z.object({
	id: z.number().int(),
	name: z.string(),
	formation: z.string(),
	playersCount: z.number().int(),
	positions: z.array(ShadowTeamPositionResponse),
	players: z.array(ShadowTeamPlayerResponse),
	createdAt: z.string().datetime({ offset: true }),
	updatedAt: z.string().datetime({ offset: true }),
});
export type ShadowTeamResponse = z.infer<typeof ShadowTeamResponse>;

export const ShadowTeamPatchRequest = z.object({
	name: z.string().default(""),
});
export type ShadowTeamPatchRequest = z.infer<typeof ShadowTeamPatchRequest>;

const putGameDescriptionRequestBody = z
	.object({ description: z.string().nullable().default(null) })
	.partial();
export type putGameDescriptionRequestBody = z.infer<
	typeof putGameDescriptionRequestBody
>;

const postGameClipSharedPasswordValidationRequestBody = z.object({
	password: z.string(),
});
export type postGameClipSharedPasswordValidationRequestBody = z.infer<
	typeof postGameClipSharedPasswordValidationRequestBody
>;

const portalAIConversationsPatchRequestBody = z.object({ pinned: z.boolean() });
export type portalAIConversationsPatchRequestBody = z.infer<
	typeof portalAIConversationsPatchRequestBody
>;

const changeUserLocaleRequestBody = z.object({
	locale: z.string().min(2).max(2),
});
export type changeUserLocaleRequestBody = z.infer<
	typeof changeUserLocaleRequestBody
>;

const forgotPasswordRequestBody = z.object({ email: z.string() }).partial();
export type forgotPasswordRequestBody = z.infer<
	typeof forgotPasswordRequestBody
>;

const resetPasswordRequestBody = z.object({ newPassword: z.string() });
export type resetPasswordRequestBody = z.infer<typeof resetPasswordRequestBody>;

const uploadUserProfilePictureRequestBody = z
	.object({ file: z.string() })
	.partial();
export type uploadUserProfilePictureRequestBody = z.infer<
	typeof uploadUserProfilePictureRequestBody
>;

const removeProfileRequestBody = z
	.object({ reason: z.string().nullable() })
	.partial();
export type removeProfileRequestBody = z.infer<typeof removeProfileRequestBody>;

const uploadPlayerPhotoRequestBody = z.object({ file: z.string() }).partial();
export type uploadPlayerPhotoRequestBody = z.infer<
	typeof uploadPlayerPhotoRequestBody
>;

const generateSignUpLinkRequestBody = z
	.object({ playerId: z.number().int() })
	.partial();
export type generateSignUpLinkRequestBody = z.infer<
	typeof generateSignUpLinkRequestBody
>;

const saveUserWidgetSettingsRequestBody = z
	.object({ insightDisplaySettings: z.object({}).partial() })
	.partial();
export type saveUserWidgetSettingsRequestBody = z.infer<
	typeof saveUserWidgetSettingsRequestBody
>;

const updateShadowTeamPartiallyRequestBody = z.object({
	name: z.string().default(""),
});
export type updateShadowTeamPartiallyRequestBody = z.infer<
	typeof updateShadowTeamPartiallyRequestBody
>;

const getTokenMovementsResponse400 = z.string();
export type getTokenMovementsResponse400 = z.infer<
	typeof getTokenMovementsResponse400
>;

const getTokenMovementsResponse403 = z.string();
export type getTokenMovementsResponse403 = z.infer<
	typeof getTokenMovementsResponse403
>;

const postTokenMovementResponse200 = z.string();
export type postTokenMovementResponse200 = z.infer<
	typeof postTokenMovementResponse200
>;

const postTokenMovementResponse400 = z.string();
export type postTokenMovementResponse400 = z.infer<
	typeof postTokenMovementResponse400
>;

const postTokenMovementResponse403 = z.string();
export type postTokenMovementResponse403 = z.infer<
	typeof postTokenMovementResponse403
>;

const getGamePageResponse400 = z.string();
export type getGamePageResponse400 = z.infer<typeof getGamePageResponse400>;

const getGamePageResponse403 = z.string();
export type getGamePageResponse403 = z.infer<typeof getGamePageResponse403>;

const getGamePageResponse404 = z.string();
export type getGamePageResponse404 = z.infer<typeof getGamePageResponse404>;

const getGamePageLazyResponse400 = z.string();
export type getGamePageLazyResponse400 = z.infer<
	typeof getGamePageLazyResponse400
>;

const getGamePageLazyResponse403 = z.string();
export type getGamePageLazyResponse403 = z.infer<
	typeof getGamePageLazyResponse403
>;

const getGamePageLazyResponse404 = z.string();
export type getGamePageLazyResponse404 = z.infer<
	typeof getGamePageLazyResponse404
>;

const putGameScoreResponse400 = z.string();
export type putGameScoreResponse400 = z.infer<typeof putGameScoreResponse400>;

const putGameScoreResponse403 = z.string();
export type putGameScoreResponse403 = z.infer<typeof putGameScoreResponse403>;

const putGameScoreResponse404 = z.string();
export type putGameScoreResponse404 = z.infer<typeof putGameScoreResponse404>;

const getGameTeamStatResponse400 = z.string();
export type getGameTeamStatResponse400 = z.infer<
	typeof getGameTeamStatResponse400
>;

const getGameTeamStatResponse403 = z.string();
export type getGameTeamStatResponse403 = z.infer<
	typeof getGameTeamStatResponse403
>;

const getGameTeamStatResponse404 = z.string();
export type getGameTeamStatResponse404 = z.infer<
	typeof getGameTeamStatResponse404
>;

const getGamePlayerStatResponse400 = z.string();
export type getGamePlayerStatResponse400 = z.infer<
	typeof getGamePlayerStatResponse400
>;

const getGamePlayerStatResponse403 = z.string();
export type getGamePlayerStatResponse403 = z.infer<
	typeof getGamePlayerStatResponse403
>;

const getGamePlayerStatResponse404 = z.string();
export type getGamePlayerStatResponse404 = z.infer<
	typeof getGamePlayerStatResponse404
>;

const putGameTeamColorsResponse400 = z.string();
export type putGameTeamColorsResponse400 = z.infer<
	typeof putGameTeamColorsResponse400
>;

const putGameTeamColorsResponse403 = z.string();
export type putGameTeamColorsResponse403 = z.infer<
	typeof putGameTeamColorsResponse403
>;

const putGameTeamColorsResponse404 = z.string();
export type putGameTeamColorsResponse404 = z.infer<
	typeof putGameTeamColorsResponse404
>;

const setRatingToGamePlayerResponse400 = z.string();
export type setRatingToGamePlayerResponse400 = z.infer<
	typeof setRatingToGamePlayerResponse400
>;

const setRatingToGamePlayerResponse403 = z.string();
export type setRatingToGamePlayerResponse403 = z.infer<
	typeof setRatingToGamePlayerResponse403
>;

const setRatingToGamePlayerResponse404 = z.string();
export type setRatingToGamePlayerResponse404 = z.infer<
	typeof setRatingToGamePlayerResponse404
>;

const setBadgesToGamePlayerResponse400 = z.string();
export type setBadgesToGamePlayerResponse400 = z.infer<
	typeof setBadgesToGamePlayerResponse400
>;

const setBadgesToGamePlayerResponse403 = z.string();
export type setBadgesToGamePlayerResponse403 = z.infer<
	typeof setBadgesToGamePlayerResponse403
>;

const setBadgesToGamePlayerResponse404 = z.string();
export type setBadgesToGamePlayerResponse404 = z.infer<
	typeof setBadgesToGamePlayerResponse404
>;

const postGameHighlightResponse400 = z.string();
export type postGameHighlightResponse400 = z.infer<
	typeof postGameHighlightResponse400
>;

const postGameHighlightResponse403 = z.string();
export type postGameHighlightResponse403 = z.infer<
	typeof postGameHighlightResponse403
>;

const postGameHighlightResponse404 = z.string();
export type postGameHighlightResponse404 = z.infer<
	typeof postGameHighlightResponse404
>;

const deleteGameHighlightResponse400 = z.string();
export type deleteGameHighlightResponse400 = z.infer<
	typeof deleteGameHighlightResponse400
>;

const deleteGameHighlightResponse403 = z.string();
export type deleteGameHighlightResponse403 = z.infer<
	typeof deleteGameHighlightResponse403
>;

const deleteGameHighlightResponse404 = z.string();
export type deleteGameHighlightResponse404 = z.infer<
	typeof deleteGameHighlightResponse404
>;

const patchGameHighlightResponse400 = z.string();
export type patchGameHighlightResponse400 = z.infer<
	typeof patchGameHighlightResponse400
>;

const patchGameHighlightResponse403 = z.string();
export type patchGameHighlightResponse403 = z.infer<
	typeof patchGameHighlightResponse403
>;

const patchGameHighlightResponse404 = z.string();
export type patchGameHighlightResponse404 = z.infer<
	typeof patchGameHighlightResponse404
>;

const getGameClipsResponse400 = z.string();
export type getGameClipsResponse400 = z.infer<typeof getGameClipsResponse400>;

const getGameClipsResponse403 = z.string();
export type getGameClipsResponse403 = z.infer<typeof getGameClipsResponse403>;

const getGameClipResponse400 = z.string();
export type getGameClipResponse400 = z.infer<typeof getGameClipResponse400>;

const getGameClipResponse403 = z.string();
export type getGameClipResponse403 = z.infer<typeof getGameClipResponse403>;

const putGameClipResponse400 = z.string();
export type putGameClipResponse400 = z.infer<typeof putGameClipResponse400>;

const putGameClipResponse403 = z.string();
export type putGameClipResponse403 = z.infer<typeof putGameClipResponse403>;

const putGameClipResponse404 = z.string();
export type putGameClipResponse404 = z.infer<typeof putGameClipResponse404>;

const deleteGameClipResponse400 = z.string();
export type deleteGameClipResponse400 = z.infer<
	typeof deleteGameClipResponse400
>;

const deleteGameClipResponse403 = z.string();
export type deleteGameClipResponse403 = z.infer<
	typeof deleteGameClipResponse403
>;

const deleteGameClipResponse404 = z.string();
export type deleteGameClipResponse404 = z.infer<
	typeof deleteGameClipResponse404
>;

const postGameClipResponse400 = z.string();
export type postGameClipResponse400 = z.infer<typeof postGameClipResponse400>;

const postGameClipResponse403 = z.string();
export type postGameClipResponse403 = z.infer<typeof postGameClipResponse403>;

const postGameClipResponse404 = z.string();
export type postGameClipResponse404 = z.infer<typeof postGameClipResponse404>;

const putGameTeamSheetResponse400 = z.string();
export type putGameTeamSheetResponse400 = z.infer<
	typeof putGameTeamSheetResponse400
>;

const putGameTeamSheetResponse403 = z.string();
export type putGameTeamSheetResponse403 = z.infer<
	typeof putGameTeamSheetResponse403
>;

const putGameTeamSheetResponse404 = z.string();
export type putGameTeamSheetResponse404 = z.infer<
	typeof putGameTeamSheetResponse404
>;

const getGameTeamSheetCompositionPlayersResponse400 = z.string();
export type getGameTeamSheetCompositionPlayersResponse400 = z.infer<
	typeof getGameTeamSheetCompositionPlayersResponse400
>;

const getGameTeamSheetCompositionPlayersResponse403 = z.string();
export type getGameTeamSheetCompositionPlayersResponse403 = z.infer<
	typeof getGameTeamSheetCompositionPlayersResponse403
>;

const getGameTeamSheetCompositionPlayersResponse404 = z.string();
export type getGameTeamSheetCompositionPlayersResponse404 = z.infer<
	typeof getGameTeamSheetCompositionPlayersResponse404
>;

const getGameTeamPlayersSearchResponse400 = z.string();
export type getGameTeamPlayersSearchResponse400 = z.infer<
	typeof getGameTeamPlayersSearchResponse400
>;

const getGameTeamPlayersSearchResponse403 = z.string();
export type getGameTeamPlayersSearchResponse403 = z.infer<
	typeof getGameTeamPlayersSearchResponse403
>;

const getGameTeamPlayersSearchResponse404 = z.string();
export type getGameTeamPlayersSearchResponse404 = z.infer<
	typeof getGameTeamPlayersSearchResponse404
>;

const getGameTeamSheetClubPlayersResponse400 = z.string();
export type getGameTeamSheetClubPlayersResponse400 = z.infer<
	typeof getGameTeamSheetClubPlayersResponse400
>;

const getGameTeamSheetClubPlayersResponse403 = z.string();
export type getGameTeamSheetClubPlayersResponse403 = z.infer<
	typeof getGameTeamSheetClubPlayersResponse403
>;

const getGameTeamSheetClubPlayersResponse404 = z.string();
export type getGameTeamSheetClubPlayersResponse404 = z.infer<
	typeof getGameTeamSheetClubPlayersResponse404
>;

const putGameDescriptionResponse400 = z.string();
export type putGameDescriptionResponse400 = z.infer<
	typeof putGameDescriptionResponse400
>;

const putGameDescriptionResponse403 = z.string();
export type putGameDescriptionResponse403 = z.infer<
	typeof putGameDescriptionResponse403
>;

const putGameDescriptionResponse404 = z.string();
export type putGameDescriptionResponse404 = z.infer<
	typeof putGameDescriptionResponse404
>;

const postGameStatsExportResponse400 = z.string();
export type postGameStatsExportResponse400 = z.infer<
	typeof postGameStatsExportResponse400
>;

const postGameStatsExportResponse403 = z.string();
export type postGameStatsExportResponse403 = z.infer<
	typeof postGameStatsExportResponse403
>;

const postGameStatsExportResponse404 = z.string();
export type postGameStatsExportResponse404 = z.infer<
	typeof postGameStatsExportResponse404
>;

const postGameEventDataExportResponse400 = z.string();
export type postGameEventDataExportResponse400 = z.infer<
	typeof postGameEventDataExportResponse400
>;

const postGameEventDataExportResponse403 = z.string();
export type postGameEventDataExportResponse403 = z.infer<
	typeof postGameEventDataExportResponse403
>;

const postGameEventDataExportResponse404 = z.string();
export type postGameEventDataExportResponse404 = z.infer<
	typeof postGameEventDataExportResponse404
>;

const getSharedGameClipIsProtectedResponse400 = z.string();
export type getSharedGameClipIsProtectedResponse400 = z.infer<
	typeof getSharedGameClipIsProtectedResponse400
>;

const getSharedGameClipIsProtectedResponse401 = z.string();
export type getSharedGameClipIsProtectedResponse401 = z.infer<
	typeof getSharedGameClipIsProtectedResponse401
>;

const getSharedGameClipIsProtectedResponse403 = z.string();
export type getSharedGameClipIsProtectedResponse403 = z.infer<
	typeof getSharedGameClipIsProtectedResponse403
>;

const getSharedGameClipResponse400 = z.string();
export type getSharedGameClipResponse400 = z.infer<
	typeof getSharedGameClipResponse400
>;

const getSharedGameClipResponse403 = z.string();
export type getSharedGameClipResponse403 = z.infer<
	typeof getSharedGameClipResponse403
>;

const postGameClipSharedPasswordValidationResponse400 = z.string();
export type postGameClipSharedPasswordValidationResponse400 = z.infer<
	typeof postGameClipSharedPasswordValidationResponse400
>;

const postGameClipSharedPasswordValidationResponse403 = z.string();
export type postGameClipSharedPasswordValidationResponse403 = z.infer<
	typeof postGameClipSharedPasswordValidationResponse403
>;

const postGameClipSharedPasswordValidationResponse404 = z.string();
export type postGameClipSharedPasswordValidationResponse404 = z.infer<
	typeof postGameClipSharedPasswordValidationResponse404
>;

const getGameEventFiltersResponse200 = z.array(GameEventFilterResponse);
export type getGameEventFiltersResponse200 = z.infer<
	typeof getGameEventFiltersResponse200
>;

const getGameEventFiltersResponse400 = z.string();
export type getGameEventFiltersResponse400 = z.infer<
	typeof getGameEventFiltersResponse400
>;

const getGameEventFiltersResponse403 = z.string();
export type getGameEventFiltersResponse403 = z.infer<
	typeof getGameEventFiltersResponse403
>;

const postGameEventFilterResponse400 = z.string();
export type postGameEventFilterResponse400 = z.infer<
	typeof postGameEventFilterResponse400
>;

const postGameEventFilterResponse403 = z.string();
export type postGameEventFilterResponse403 = z.infer<
	typeof postGameEventFilterResponse403
>;

const postGameEventFilterResponse404 = z.string();
export type postGameEventFilterResponse404 = z.infer<
	typeof postGameEventFilterResponse404
>;

const deleteGameEventFilterResponse400 = z.string();
export type deleteGameEventFilterResponse400 = z.infer<
	typeof deleteGameEventFilterResponse400
>;

const deleteGameEventFilterResponse403 = z.string();
export type deleteGameEventFilterResponse403 = z.infer<
	typeof deleteGameEventFilterResponse403
>;

const deleteGameEventFilterResponse404 = z.string();
export type deleteGameEventFilterResponse404 = z.infer<
	typeof deleteGameEventFilterResponse404
>;

const addCommentToGameEventResponse201 = z.unknown();
export type addCommentToGameEventResponse201 = z.infer<
	typeof addCommentToGameEventResponse201
>;

const updateSelectedViewResponse400 = z.string();
export type updateSelectedViewResponse400 = z.infer<
	typeof updateSelectedViewResponse400
>;

const updateSelectedViewResponse403 = z.string();
export type updateSelectedViewResponse403 = z.infer<
	typeof updateSelectedViewResponse403
>;

const getMfaDomainListResponse403 = z.string();
export type getMfaDomainListResponse403 = z.infer<
	typeof getMfaDomainListResponse403
>;

const getMfaDomainRequiredCheckResponse200 = z.object({
	isMfaRequired: z.boolean(),
});
export type getMfaDomainRequiredCheckResponse200 = z.infer<
	typeof getMfaDomainRequiredCheckResponse200
>;

const getMfaDomainRequiredCheckResponse403 = z.string();
export type getMfaDomainRequiredCheckResponse403 = z.infer<
	typeof getMfaDomainRequiredCheckResponse403
>;

const postReportedTaggingIssueResponse400 = z.string();
export type postReportedTaggingIssueResponse400 = z.infer<
	typeof postReportedTaggingIssueResponse400
>;

const postReportedTaggingIssueResponse403 = z.string();
export type postReportedTaggingIssueResponse403 = z.infer<
	typeof postReportedTaggingIssueResponse403
>;

const postReportedTaggingIssueResponse404 = z.string();
export type postReportedTaggingIssueResponse404 = z.infer<
	typeof postReportedTaggingIssueResponse404
>;

const updateUserSelectedCountriesResponse200 = z
	.object({
		status: z.union([z.string(), z.boolean()]).describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type updateUserSelectedCountriesResponse200 = z.infer<
	typeof updateUserSelectedCountriesResponse200
>;

const updateUserSelectedCountriesResponse400 = z.unknown();
export type updateUserSelectedCountriesResponse400 = z.infer<
	typeof updateUserSelectedCountriesResponse400
>;

const updateUserSelectedCountriesResponse403 = z.unknown();
export type updateUserSelectedCountriesResponse403 = z.infer<
	typeof updateUserSelectedCountriesResponse403
>;

const portalAIPromptCardsListResponse200 = z.array(PromptCardResponse);
export type portalAIPromptCardsListResponse200 = z.infer<
	typeof portalAIPromptCardsListResponse200
>;

const getLeaguesResponse200 = z.array(NationalLeaguesResponse);
export type getLeaguesResponse200 = z.infer<typeof getLeaguesResponse200>;

const getClubsResponse200 = z.array(NationalLeaguesClubsResponse);
export type getClubsResponse200 = z.infer<typeof getClubsResponse200>;

const getLeagueCountriesResponse200 = z.array(NationalLeaguesCountriesResponse);
export type getLeagueCountriesResponse200 = z.infer<
	typeof getLeagueCountriesResponse200
>;

const getLeagueCountriesResponse400 = z.object({ code: z.string() });
export type getLeagueCountriesResponse400 = z.infer<
	typeof getLeagueCountriesResponse400
>;

const postPlayerGradingResponse400 = z.string();
export type postPlayerGradingResponse400 = z.infer<
	typeof postPlayerGradingResponse400
>;

const postPlayerGradingResponse403 = z.string();
export type postPlayerGradingResponse403 = z.infer<
	typeof postPlayerGradingResponse403
>;

const getPlayerGradingResponse403 = z.string();
export type getPlayerGradingResponse403 = z.infer<
	typeof getPlayerGradingResponse403
>;

const putPlayerGradingResponse400 = z.string();
export type putPlayerGradingResponse400 = z.infer<
	typeof putPlayerGradingResponse400
>;

const putPlayerGradingResponse403 = z.string();
export type putPlayerGradingResponse403 = z.infer<
	typeof putPlayerGradingResponse403
>;

const deletePlayerGradingResponse403 = z.string();
export type deletePlayerGradingResponse403 = z.infer<
	typeof deletePlayerGradingResponse403
>;

const getPlayerGradingNotesResponse400 = z.string();
export type getPlayerGradingNotesResponse400 = z.infer<
	typeof getPlayerGradingNotesResponse400
>;

const getPlayerGradingNotesResponse403 = z.string();
export type getPlayerGradingNotesResponse403 = z.infer<
	typeof getPlayerGradingNotesResponse403
>;

const getPlayerGradingContributorsResponse400 = z.string();
export type getPlayerGradingContributorsResponse400 = z.infer<
	typeof getPlayerGradingContributorsResponse400
>;

const getPlayerGradingContributorsResponse403 = z.string();
export type getPlayerGradingContributorsResponse403 = z.infer<
	typeof getPlayerGradingContributorsResponse403
>;

const postPinMyPlayerResponse400 = z.string();
export type postPinMyPlayerResponse400 = z.infer<
	typeof postPinMyPlayerResponse400
>;

const postPinMyPlayerResponse403 = z.string();
export type postPinMyPlayerResponse403 = z.infer<
	typeof postPinMyPlayerResponse403
>;

const deletePinMyPlayerResponse400 = z.string();
export type deletePinMyPlayerResponse400 = z.infer<
	typeof deletePinMyPlayerResponse400
>;

const deletePinMyPlayerResponse403 = z.string();
export type deletePinMyPlayerResponse403 = z.infer<
	typeof deletePinMyPlayerResponse403
>;

const getPlayerProfileHeaderResponse403 = z.unknown();
export type getPlayerProfileHeaderResponse403 = z.infer<
	typeof getPlayerProfileHeaderResponse403
>;

const getPlayerProfileHeaderResponse404 = z.unknown();
export type getPlayerProfileHeaderResponse404 = z.infer<
	typeof getPlayerProfileHeaderResponse404
>;

const getPlayerProfileAverageRatingResponse403 = z.unknown();
export type getPlayerProfileAverageRatingResponse403 = z.infer<
	typeof getPlayerProfileAverageRatingResponse403
>;

const getPlayerProfileAverageRatingResponse404 = z.unknown();
export type getPlayerProfileAverageRatingResponse404 = z.infer<
	typeof getPlayerProfileAverageRatingResponse404
>;

const getPlayerProfileStandoutMetricsResponse403 = z.unknown();
export type getPlayerProfileStandoutMetricsResponse403 = z.infer<
	typeof getPlayerProfileStandoutMetricsResponse403
>;

const getPlayerProfileStandoutMetricsResponse404 = z.unknown();
export type getPlayerProfileStandoutMetricsResponse404 = z.infer<
	typeof getPlayerProfileStandoutMetricsResponse404
>;

const getPlayerProfileSeasonalStatsResponse403 = z.unknown();
export type getPlayerProfileSeasonalStatsResponse403 = z.infer<
	typeof getPlayerProfileSeasonalStatsResponse403
>;

const getPlayerProfileSeasonalStatsResponse404 = z.unknown();
export type getPlayerProfileSeasonalStatsResponse404 = z.infer<
	typeof getPlayerProfileSeasonalStatsResponse404
>;

const getPlayerProfileLatestGamesResponse403 = z.unknown();
export type getPlayerProfileLatestGamesResponse403 = z.infer<
	typeof getPlayerProfileLatestGamesResponse403
>;

const getPlayerProfileLatestGamesResponse404 = z.unknown();
export type getPlayerProfileLatestGamesResponse404 = z.infer<
	typeof getPlayerProfileLatestGamesResponse404
>;

const getPlayerProfileGamesResponse403 = z.unknown();
export type getPlayerProfileGamesResponse403 = z.infer<
	typeof getPlayerProfileGamesResponse403
>;

const getPlayerProfileGamesResponse404 = z.unknown();
export type getPlayerProfileGamesResponse404 = z.infer<
	typeof getPlayerProfileGamesResponse404
>;

const getPlayerProfilePerformanceRadarResponse403 = z.unknown();
export type getPlayerProfilePerformanceRadarResponse403 = z.infer<
	typeof getPlayerProfilePerformanceRadarResponse403
>;

const getPlayerProfilePerformanceRadarResponse404 = z.unknown();
export type getPlayerProfilePerformanceRadarResponse404 = z.infer<
	typeof getPlayerProfilePerformanceRadarResponse404
>;

const getPlayerProfileClubHistoryResponse403 = z.unknown();
export type getPlayerProfileClubHistoryResponse403 = z.infer<
	typeof getPlayerProfileClubHistoryResponse403
>;

const getPlayerProfileClubHistoryResponse404 = z.unknown();
export type getPlayerProfileClubHistoryResponse404 = z.infer<
	typeof getPlayerProfileClubHistoryResponse404
>;

const getPlayerProfilePerformanceResponse404 = z.unknown();
export type getPlayerProfilePerformanceResponse404 = z.infer<
	typeof getPlayerProfilePerformanceResponse404
>;

const getPlayerProfileEventTypesResponse403 = z.unknown();
export type getPlayerProfileEventTypesResponse403 = z.infer<
	typeof getPlayerProfileEventTypesResponse403
>;

const getPlayerProfileEventTypesResponse404 = z.unknown();
export type getPlayerProfileEventTypesResponse404 = z.infer<
	typeof getPlayerProfileEventTypesResponse404
>;

const getPlayerProfileEventFiltersResponse403 = z.unknown();
export type getPlayerProfileEventFiltersResponse403 = z.infer<
	typeof getPlayerProfileEventFiltersResponse403
>;

const getPlayerProfileEventFiltersResponse404 = z.unknown();
export type getPlayerProfileEventFiltersResponse404 = z.infer<
	typeof getPlayerProfileEventFiltersResponse404
>;

const getPlayerProfileEventsResponse403 = z.unknown();
export type getPlayerProfileEventsResponse403 = z.infer<
	typeof getPlayerProfileEventsResponse403
>;

const getPlayerProfileEventsResponse404 = z.unknown();
export type getPlayerProfileEventsResponse404 = z.infer<
	typeof getPlayerProfileEventsResponse404
>;

const getPlayerProfileKeyEventsResponse403 = z.unknown();
export type getPlayerProfileKeyEventsResponse403 = z.infer<
	typeof getPlayerProfileKeyEventsResponse403
>;

const getPlayerProfileKeyEventsResponse404 = z.unknown();
export type getPlayerProfileKeyEventsResponse404 = z.infer<
	typeof getPlayerProfileKeyEventsResponse404
>;

const postPlayerProfileCrossGameClipResponse400 = z.string();
export type postPlayerProfileCrossGameClipResponse400 = z.infer<
	typeof postPlayerProfileCrossGameClipResponse400
>;

const postPlayerProfileCrossGameClipResponse403 = z.string();
export type postPlayerProfileCrossGameClipResponse403 = z.infer<
	typeof postPlayerProfileCrossGameClipResponse403
>;

const postPlayerProfileCrossGameClipResponse404 = z.string();
export type postPlayerProfileCrossGameClipResponse404 = z.infer<
	typeof postPlayerProfileCrossGameClipResponse404
>;

const getPlayerProfileShadowTeamListResponse403 = z.unknown();
export type getPlayerProfileShadowTeamListResponse403 = z.infer<
	typeof getPlayerProfileShadowTeamListResponse403
>;

const getPlayerProfileShadowTeamListResponse404 = z.unknown();
export type getPlayerProfileShadowTeamListResponse404 = z.infer<
	typeof getPlayerProfileShadowTeamListResponse404
>;

const postPlayerProfileShadowTeamPlayerResponse403 = z.unknown();
export type postPlayerProfileShadowTeamPlayerResponse403 = z.infer<
	typeof postPlayerProfileShadowTeamPlayerResponse403
>;

const postPlayerProfileShadowTeamPlayerResponse404 = z.unknown();
export type postPlayerProfileShadowTeamPlayerResponse404 = z.infer<
	typeof postPlayerProfileShadowTeamPlayerResponse404
>;

const postPlayerProfileShadowTeamResponse201 = z.unknown();
export type postPlayerProfileShadowTeamResponse201 = z.infer<
	typeof postPlayerProfileShadowTeamResponse201
>;

const postPlayerProfileShadowTeamResponse400 = z.unknown();
export type postPlayerProfileShadowTeamResponse400 = z.infer<
	typeof postPlayerProfileShadowTeamResponse400
>;

const postPlayerProfileShadowTeamResponse403 = z.unknown();
export type postPlayerProfileShadowTeamResponse403 = z.infer<
	typeof postPlayerProfileShadowTeamResponse403
>;

const getPlayerProfileRecruitmentInformationResponse403 = z.unknown();
export type getPlayerProfileRecruitmentInformationResponse403 = z.infer<
	typeof getPlayerProfileRecruitmentInformationResponse403
>;

const getPlayerProfileRecruitmentInformationResponse404 = z.unknown();
export type getPlayerProfileRecruitmentInformationResponse404 = z.infer<
	typeof getPlayerProfileRecruitmentInformationResponse404
>;

const getPlayerProfileBiometricsAvailabilityResponse403 = z.unknown();
export type getPlayerProfileBiometricsAvailabilityResponse403 = z.infer<
	typeof getPlayerProfileBiometricsAvailabilityResponse403
>;

const getPlayerProfileBiometricsAvailabilityResponse404 = z.unknown();
export type getPlayerProfileBiometricsAvailabilityResponse404 = z.infer<
	typeof getPlayerProfileBiometricsAvailabilityResponse404
>;

const getPlayerProfileBodyMeasurementsResponse403 = z.unknown();
export type getPlayerProfileBodyMeasurementsResponse403 = z.infer<
	typeof getPlayerProfileBodyMeasurementsResponse403
>;

const getPlayerProfileBodyMeasurementsResponse404 = z.unknown();
export type getPlayerProfileBodyMeasurementsResponse404 = z.infer<
	typeof getPlayerProfileBodyMeasurementsResponse404
>;

const getPlayerProfileBodyMetricTrendResponse403 = z.unknown();
export type getPlayerProfileBodyMetricTrendResponse403 = z.infer<
	typeof getPlayerProfileBodyMetricTrendResponse403
>;

const getPlayerProfileBodyMetricTrendResponse404 = z.unknown();
export type getPlayerProfileBodyMetricTrendResponse404 = z.infer<
	typeof getPlayerProfileBodyMetricTrendResponse404
>;

const getPlayerProfileBodyCompositionChangeResponse403 = z.unknown();
export type getPlayerProfileBodyCompositionChangeResponse403 = z.infer<
	typeof getPlayerProfileBodyCompositionChangeResponse403
>;

const getPlayerProfileBodyCompositionChangeResponse404 = z.unknown();
export type getPlayerProfileBodyCompositionChangeResponse404 = z.infer<
	typeof getPlayerProfileBodyCompositionChangeResponse404
>;

const getPlayerProfilePhysicalMetricsFiltersResponse403 = z.unknown();
export type getPlayerProfilePhysicalMetricsFiltersResponse403 = z.infer<
	typeof getPlayerProfilePhysicalMetricsFiltersResponse403
>;

const getPlayerProfilePhysicalMetricsFiltersResponse404 = z.unknown();
export type getPlayerProfilePhysicalMetricsFiltersResponse404 = z.infer<
	typeof getPlayerProfilePhysicalMetricsFiltersResponse404
>;

const getPlayerProfilePhysicalMetricsResponse403 = z.unknown();
export type getPlayerProfilePhysicalMetricsResponse403 = z.infer<
	typeof getPlayerProfilePhysicalMetricsResponse403
>;

const getPlayerProfilePhysicalMetricsResponse404 = z.unknown();
export type getPlayerProfilePhysicalMetricsResponse404 = z.infer<
	typeof getPlayerProfilePhysicalMetricsResponse404
>;

const getPlayerProfileBehavioralSignalsFiltersResponse403 = z.unknown();
export type getPlayerProfileBehavioralSignalsFiltersResponse403 = z.infer<
	typeof getPlayerProfileBehavioralSignalsFiltersResponse403
>;

const getPlayerProfileBehavioralSignalsFiltersResponse404 = z.unknown();
export type getPlayerProfileBehavioralSignalsFiltersResponse404 = z.infer<
	typeof getPlayerProfileBehavioralSignalsFiltersResponse404
>;

const getPlayerProfileBehavioralSignalsTrendResponse403 = z.unknown();
export type getPlayerProfileBehavioralSignalsTrendResponse403 = z.infer<
	typeof getPlayerProfileBehavioralSignalsTrendResponse403
>;

const getPlayerProfileBehavioralSignalsTrendResponse404 = z.unknown();
export type getPlayerProfileBehavioralSignalsTrendResponse404 = z.infer<
	typeof getPlayerProfileBehavioralSignalsTrendResponse404
>;

const getPlayerProfileBehavioralSignalsSnapshotResponse403 = z.unknown();
export type getPlayerProfileBehavioralSignalsSnapshotResponse403 = z.infer<
	typeof getPlayerProfileBehavioralSignalsSnapshotResponse403
>;

const getPlayerProfileBehavioralSignalsSnapshotResponse404 = z.unknown();
export type getPlayerProfileBehavioralSignalsSnapshotResponse404 = z.infer<
	typeof getPlayerProfileBehavioralSignalsSnapshotResponse404
>;

const getPlayerProfileBehavioralArchetypesResponse403 = z.unknown();
export type getPlayerProfileBehavioralArchetypesResponse403 = z.infer<
	typeof getPlayerProfileBehavioralArchetypesResponse403
>;

const getPlayerProfileBehavioralArchetypesResponse404 = z.unknown();
export type getPlayerProfileBehavioralArchetypesResponse404 = z.infer<
	typeof getPlayerProfileBehavioralArchetypesResponse404
>;

const portalAccessTokenPostResponse200 = z.object({ token: z.string() });
export type portalAccessTokenPostResponse200 = z.infer<
	typeof portalAccessTokenPostResponse200
>;

const getCurrentUserResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getCurrentUserResponse401 = z.infer<
	typeof getCurrentUserResponse401
>;

const jsonLogoutResponse200 = z
	.object({ message: z.string().describe("message") })
	.partial();
export type jsonLogoutResponse200 = z.infer<typeof jsonLogoutResponse200>;

const jsonLogoutResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type jsonLogoutResponse401 = z.infer<typeof jsonLogoutResponse401>;

const changeUserLocaleResponse400 = z.object({ code: z.string() });
export type changeUserLocaleResponse400 = z.infer<
	typeof changeUserLocaleResponse400
>;

const forgotPasswordResponse200 = z.unknown();
export type forgotPasswordResponse200 = z.infer<
	typeof forgotPasswordResponse200
>;

const forgotPasswordResponse404 = z.unknown();
export type forgotPasswordResponse404 = z.infer<
	typeof forgotPasswordResponse404
>;

const resetPasswordResponse200 = z.unknown();
export type resetPasswordResponse200 = z.infer<typeof resetPasswordResponse200>;

const resetPasswordResponse404 = z.unknown();
export type resetPasswordResponse404 = z.infer<typeof resetPasswordResponse404>;

const validateUserPasswordResetResponse200 = z.unknown();
export type validateUserPasswordResetResponse200 = z.infer<
	typeof validateUserPasswordResetResponse200
>;

const validateUserPasswordResetResponse403 = z.unknown();
export type validateUserPasswordResetResponse403 = z.infer<
	typeof validateUserPasswordResetResponse403
>;

const getSubscriptionCountryMessageResponse403 = z.unknown();
export type getSubscriptionCountryMessageResponse403 = z.infer<
	typeof getSubscriptionCountryMessageResponse403
>;

const removeCountriesSessionDetailsResponse200 = z
	.object({
		status: z.string().describe("EventStatus"),
		message: z.string().describe("Change Status Message"),
	})
	.partial();
export type removeCountriesSessionDetailsResponse200 = z.infer<
	typeof removeCountriesSessionDetailsResponse200
>;

const removeCountriesSessionDetailsResponse403 = z.unknown();
export type removeCountriesSessionDetailsResponse403 = z.infer<
	typeof removeCountriesSessionDetailsResponse403
>;

const playerSearchResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchResponse401 = z.infer<typeof playerSearchResponse401>;

const playerSearchResponse403 = z.string();
export type playerSearchResponse403 = z.infer<typeof playerSearchResponse403>;

const playerSearchPresetColumnGetResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchPresetColumnGetResponse401 = z.infer<
	typeof playerSearchPresetColumnGetResponse401
>;

const playerSearchPresetColumnPutResponse200 = z.unknown();
export type playerSearchPresetColumnPutResponse200 = z.infer<
	typeof playerSearchPresetColumnPutResponse200
>;

const playerSearchPresetColumnPutResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchPresetColumnPutResponse401 = z.infer<
	typeof playerSearchPresetColumnPutResponse401
>;

const playerSearchPresetColumnDeleteResponse200 = z.unknown();
export type playerSearchPresetColumnDeleteResponse200 = z.infer<
	typeof playerSearchPresetColumnDeleteResponse200
>;

const playerSearchPresetColumnDeleteResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchPresetColumnDeleteResponse401 = z.infer<
	typeof playerSearchPresetColumnDeleteResponse401
>;

const playerSearchPresetFilterGetResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchPresetFilterGetResponse401 = z.infer<
	typeof playerSearchPresetFilterGetResponse401
>;

const playerSearchPresetFilterPutResponse200 = z.unknown();
export type playerSearchPresetFilterPutResponse200 = z.infer<
	typeof playerSearchPresetFilterPutResponse200
>;

const playerSearchPresetFilterPutResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchPresetFilterPutResponse401 = z.infer<
	typeof playerSearchPresetFilterPutResponse401
>;

const playerSearchPresetFilterDeleteResponse200 = z.unknown();
export type playerSearchPresetFilterDeleteResponse200 = z.infer<
	typeof playerSearchPresetFilterDeleteResponse200
>;

const playerSearchPresetFilterDeleteResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type playerSearchPresetFilterDeleteResponse401 = z.infer<
	typeof playerSearchPresetFilterDeleteResponse401
>;

const getAvailableNationalitiesResponse200 = z.union([
	NationalityData,
	z.object({}).partial(),
]);
export type getAvailableNationalitiesResponse200 = z.infer<
	typeof getAvailableNationalitiesResponse200
>;

const getAvailableNationalitiesResponse401 = z.unknown();
export type getAvailableNationalitiesResponse401 = z.infer<
	typeof getAvailableNationalitiesResponse401
>;

const getAvailableNationalitiesResponse403 = z.unknown();
export type getAvailableNationalitiesResponse403 = z.infer<
	typeof getAvailableNationalitiesResponse403
>;

const getHightLightedPlayersResponse200 = z.array(PlayerData);
export type getHightLightedPlayersResponse200 = z.infer<
	typeof getHightLightedPlayersResponse200
>;

const getLinkCountsResponse200 = z
	.object({ playersCount: z.number().int(), clubsCount: z.number().int() })
	.partial();
export type getLinkCountsResponse200 = z.infer<typeof getLinkCountsResponse200>;

const getMyEyeballResponse200 = z
	.object({
		clubsCount: z.number().int(),
		teamsCount: z.number().int(),
		playersCount: z.number().int(),
		gamesCount: z.number().int(),
	})
	.partial();
export type getMyEyeballResponse200 = z.infer<typeof getMyEyeballResponse200>;

const globalSearchSuggestionsResponse200 = z.object({
	clubs: z
		.object({
			subscribed: z.array(ClubSuggestion),
			unsubscribed: z.array(ClubSuggestion),
		})
		.partial(),
	players: z
		.object({
			subscribed: z.array(PlayerSuggestion),
			unsubscribed: z.array(PlayerSuggestion),
		})
		.partial(),
	games: z
		.object({
			subscribed: z.array(GameSuggestion),
			unsubscribed: z.array(GameSuggestion),
		})
		.partial(),
});
export type globalSearchSuggestionsResponse200 = z.infer<
	typeof globalSearchSuggestionsResponse200
>;

const getSuggestedGamesResponse200 = z.array(GameData);
export type getSuggestedGamesResponse200 = z.infer<
	typeof getSuggestedGamesResponse200
>;

const favoriteClubResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type favoriteClubResponse400 = z.infer<typeof favoriteClubResponse400>;

const favoriteClubResponse404 = z.unknown();
export type favoriteClubResponse404 = z.infer<typeof favoriteClubResponse404>;

const unFavoriteClubResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type unFavoriteClubResponse400 = z.infer<
	typeof unFavoriteClubResponse400
>;

const unFavoriteClubResponse404 = z.unknown();
export type unFavoriteClubResponse404 = z.infer<
	typeof unFavoriteClubResponse404
>;

const getRandomClubsResponse200 = z.array(ClubData);
export type getRandomClubsResponse200 = z.infer<
	typeof getRandomClubsResponse200
>;

const favoritePlayerResponse200 = z.unknown();
export type favoritePlayerResponse200 = z.infer<
	typeof favoritePlayerResponse200
>;

const favoritePlayerResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type favoritePlayerResponse400 = z.infer<
	typeof favoritePlayerResponse400
>;

const favoritePlayerResponse404 = z.unknown();
export type favoritePlayerResponse404 = z.infer<
	typeof favoritePlayerResponse404
>;

const unFavoritePlayerResponse200 = z.unknown();
export type unFavoritePlayerResponse200 = z.infer<
	typeof unFavoritePlayerResponse200
>;

const unFavoritePlayerResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type unFavoritePlayerResponse400 = z.infer<
	typeof unFavoritePlayerResponse400
>;

const unFavoritePlayerResponse404 = z.unknown();
export type unFavoritePlayerResponse404 = z.infer<
	typeof unFavoritePlayerResponse404
>;

const getLatestGamesResponse200 = z.array(GameData);
export type getLatestGamesResponse200 = z.infer<
	typeof getLatestGamesResponse200
>;

const favoriteTeamResponse200 = z.unknown();
export type favoriteTeamResponse200 = z.infer<typeof favoriteTeamResponse200>;

const favoriteTeamResponse404 = z.unknown();
export type favoriteTeamResponse404 = z.infer<typeof favoriteTeamResponse404>;

const unFavoriteTeamResponse200 = z.unknown();
export type unFavoriteTeamResponse200 = z.infer<
	typeof unFavoriteTeamResponse200
>;

const unFavoriteTeamResponse404 = z.unknown();
export type unFavoriteTeamResponse404 = z.infer<
	typeof unFavoriteTeamResponse404
>;

const favoriteGameResponse200 = z.unknown();
export type favoriteGameResponse200 = z.infer<typeof favoriteGameResponse200>;

const favoriteGameResponse404 = z.unknown();
export type favoriteGameResponse404 = z.infer<typeof favoriteGameResponse404>;

const unfavoriteGameResponse200 = z.unknown();
export type unfavoriteGameResponse200 = z.infer<
	typeof unfavoriteGameResponse200
>;

const unfavoriteGameResponse404 = z.unknown();
export type unfavoriteGameResponse404 = z.infer<
	typeof unfavoriteGameResponse404
>;

const getFeaturesResponse200 = z.array(FeatureResponse);
export type getFeaturesResponse200 = z.infer<typeof getFeaturesResponse200>;

const getSeasonsResponse200 = z.array(z.number());
export type getSeasonsResponse200 = z.infer<typeof getSeasonsResponse200>;

const aggregatePlayerStatResponse400 = z
	.object({
		property_path: z.string().describe("Invalid column"),
		message: z.string().describe("Error message"),
	})
	.partial();
export type aggregatePlayerStatResponse400 = z.infer<
	typeof aggregatePlayerStatResponse400
>;

const aggregatePlayerStatResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type aggregatePlayerStatResponse401 = z.infer<
	typeof aggregatePlayerStatResponse401
>;

const aggregatePlayerStatResponse403 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type aggregatePlayerStatResponse403 = z.infer<
	typeof aggregatePlayerStatResponse403
>;

const aggregatePlayerStatResponse404 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type aggregatePlayerStatResponse404 = z.infer<
	typeof aggregatePlayerStatResponse404
>;

const aggregatePlayerStatResponse500 = z.unknown();
export type aggregatePlayerStatResponse500 = z.infer<
	typeof aggregatePlayerStatResponse500
>;

const getPlayerPlayedGamesResponse400 = z.string();
export type getPlayerPlayedGamesResponse400 = z.infer<
	typeof getPlayerPlayedGamesResponse400
>;

const getPlayerPlayedGamesResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getPlayerPlayedGamesResponse401 = z.infer<
	typeof getPlayerPlayedGamesResponse401
>;

const getPlayerPlayedGamesResponse404 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getPlayerPlayedGamesResponse404 = z.infer<
	typeof getPlayerPlayedGamesResponse404
>;

const portalCreatePlayerPossibleDuplicateResponse201 = z.unknown();
export type portalCreatePlayerPossibleDuplicateResponse201 = z.infer<
	typeof portalCreatePlayerPossibleDuplicateResponse201
>;

const portalCreatePlayerPossibleDuplicateResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type portalCreatePlayerPossibleDuplicateResponse400 = z.infer<
	typeof portalCreatePlayerPossibleDuplicateResponse400
>;

const portalCreatePlayerPossibleDuplicateResponse403 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type portalCreatePlayerPossibleDuplicateResponse403 = z.infer<
	typeof portalCreatePlayerPossibleDuplicateResponse403
>;

const portalCreatePlayerPossibleDuplicateResponse404 = z
	.object({ status: z.number().int(), message: z.string() })
	.partial();
export type portalCreatePlayerPossibleDuplicateResponse404 = z.infer<
	typeof portalCreatePlayerPossibleDuplicateResponse404
>;

const getNewsfeedResponse403 = z.unknown();
export type getNewsfeedResponse403 = z.infer<typeof getNewsfeedResponse403>;

const getNewsfeedTagsResponse200 = z.array(z.string());
export type getNewsfeedTagsResponse200 = z.infer<
	typeof getNewsfeedTagsResponse200
>;

const getNewsPostDetailResponse403 = z.unknown();
export type getNewsPostDetailResponse403 = z.infer<
	typeof getNewsPostDetailResponse403
>;

const getNewsPostDetailResponse404 = z.unknown();
export type getNewsPostDetailResponse404 = z.infer<
	typeof getNewsPostDetailResponse404
>;

const getNewsPreviewResponse404 = z.unknown();
export type getNewsPreviewResponse404 = z.infer<
	typeof getNewsPreviewResponse404
>;

const logUserActivityResponse400 = z.object({ code: z.string() });
export type logUserActivityResponse400 = z.infer<
	typeof logUserActivityResponse400
>;

const getAllClubsResponse401 = z.unknown();
export type getAllClubsResponse401 = z.infer<typeof getAllClubsResponse401>;

const getAllClubsResponse403 = z.unknown();
export type getAllClubsResponse403 = z.infer<typeof getAllClubsResponse403>;

const getAllPlayersResponse401 = z.unknown();
export type getAllPlayersResponse401 = z.infer<typeof getAllPlayersResponse401>;

const getAllPlayersResponse403 = z.unknown();
export type getAllPlayersResponse403 = z.infer<typeof getAllPlayersResponse403>;

const getAllGamesResponse401 = z.unknown();
export type getAllGamesResponse401 = z.infer<typeof getAllGamesResponse401>;

const getAllGamesResponse403 = z.unknown();
export type getAllGamesResponse403 = z.infer<typeof getAllGamesResponse403>;

const getUserClipsResponse200 = z
	.object({
		clips: z.array(ClipResponse),
		page: z.number().int(),
		limit: z.number().int(),
		pages: z.number().int(),
		total: z.number().int(),
	})
	.partial();
export type getUserClipsResponse200 = z.infer<typeof getUserClipsResponse200>;

const renameClipResponse200 = z.unknown();
export type renameClipResponse200 = z.infer<typeof renameClipResponse200>;

const renameClipResponse404 = z.unknown();
export type renameClipResponse404 = z.infer<typeof renameClipResponse404>;

const deleteClipResponse200 = z.unknown();
export type deleteClipResponse200 = z.infer<typeof deleteClipResponse200>;

const deleteClipResponse404 = z.unknown();
export type deleteClipResponse404 = z.infer<typeof deleteClipResponse404>;

const getUserClipEventDetailResponse200 = z
	.object({
		beproEvent: z.array(BeproEventResponse),
		lineup: z.array(LineupResponse),
		game: GameResponse,
	})
	.partial();
export type getUserClipEventDetailResponse200 = z.infer<
	typeof getUserClipEventDetailResponse200
>;

const generateClipLinkResponse200 = z.object({ link: z.string() }).partial();
export type generateClipLinkResponse200 = z.infer<
	typeof generateClipLinkResponse200
>;

const downloadClipResponse200 = z.object({ link: z.string() }).partial();
export type downloadClipResponse200 = z.infer<typeof downloadClipResponse200>;

const downloadClipResponse403 = z.string();
export type downloadClipResponse403 = z.infer<typeof downloadClipResponse403>;

const downloadClipResponse404 = z.unknown();
export type downloadClipResponse404 = z.infer<typeof downloadClipResponse404>;

const shareClipLinkResponse200 = z
	.object({
		status: z.string(),
		message: z.string(),
		update: z.number().int(),
		data: z.object({ clip_link: z.string() }).partial(),
	})
	.partial();
export type shareClipLinkResponse200 = z.infer<typeof shareClipLinkResponse200>;

const shareClipLinkResponse404 = z.unknown();
export type shareClipLinkResponse404 = z.infer<typeof shareClipLinkResponse404>;

const getUserClipClubResponse200 = z.array(
	z.object({ id: z.number().int(), name: z.string() }).partial(),
);
export type getUserClipClubResponse200 = z.infer<
	typeof getUserClipClubResponse200
>;

const getUserClipPlayerResponse200 = z.array(
	z
		.object({ id: z.number().int(), name: z.string(), type: z.string() })
		.partial(),
);
export type getUserClipPlayerResponse200 = z.infer<
	typeof getUserClipPlayerResponse200
>;

const getUserSubscriptionsResponse200 = z.array(UserSubscriptionData);
export type getUserSubscriptionsResponse200 = z.infer<
	typeof getUserSubscriptionsResponse200
>;

const getAllTeamsResponse401 = z.unknown();
export type getAllTeamsResponse401 = z.infer<typeof getAllTeamsResponse401>;

const getAllTeamsResponse403 = z.unknown();
export type getAllTeamsResponse403 = z.infer<typeof getAllTeamsResponse403>;

const updateUserInformationResponse200 = z.unknown();
export type updateUserInformationResponse200 = z.infer<
	typeof updateUserInformationResponse200
>;

const updateUserInformationResponse400 = z
	.object({
		property_path: z.string().describe("Invalid column"),
		message: z.string().describe("Error message"),
	})
	.partial();
export type updateUserInformationResponse400 = z.infer<
	typeof updateUserInformationResponse400
>;

const updateUserInformationResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type updateUserInformationResponse401 = z.infer<
	typeof updateUserInformationResponse401
>;

const updateUserInformationResponse403 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type updateUserInformationResponse403 = z.infer<
	typeof updateUserInformationResponse403
>;

const getUserInformationResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getUserInformationResponse401 = z.infer<
	typeof getUserInformationResponse401
>;

const getUserInformationResponse403 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getUserInformationResponse403 = z.infer<
	typeof getUserInformationResponse403
>;

const uploadUserProfilePictureResponse200 = z
	.object({
		status: z.string().describe("status"),
		message: z.string().describe("message"),
		url: z.string().describe("message"),
	})
	.partial();
export type uploadUserProfilePictureResponse200 = z.infer<
	typeof uploadUserProfilePictureResponse200
>;

const uploadUserProfilePictureResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type uploadUserProfilePictureResponse401 = z.infer<
	typeof uploadUserProfilePictureResponse401
>;

const uploadUserProfilePictureResponse403 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type uploadUserProfilePictureResponse403 = z.infer<
	typeof uploadUserProfilePictureResponse403
>;

const uploadUserProfilePictureResponse500 = z.unknown();
export type uploadUserProfilePictureResponse500 = z.infer<
	typeof uploadUserProfilePictureResponse500
>;

const removeProfileResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type removeProfileResponse401 = z.infer<typeof removeProfileResponse401>;

const removeProfileResponse403 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type removeProfileResponse403 = z.infer<typeof removeProfileResponse403>;

const getTimezonesResponse200 = z.record(z.string());
export type getTimezonesResponse200 = z.infer<typeof getTimezonesResponse200>;

const getEmailNotificationsResponse200 = z
	.object({
		isEmailNotificationEnabled: z.boolean(),
		isPlayerEnabled: z.boolean(),
		isClubEnabled: z.boolean(),
		isTeamEnabled: z.boolean(),
		emailNotificationTypes: z.array(DefaultSelectResponse.nullable()),
	})
	.partial();
export type getEmailNotificationsResponse200 = z.infer<
	typeof getEmailNotificationsResponse200
>;

const updateEmailNotificationsResponse200 = z.unknown();
export type updateEmailNotificationsResponse200 = z.infer<
	typeof updateEmailNotificationsResponse200
>;

const getPlayerEmailNotificationsResponse401 = z.unknown();
export type getPlayerEmailNotificationsResponse401 = z.infer<
	typeof getPlayerEmailNotificationsResponse401
>;

const getPlayerEmailNotificationsResponse403 = z.unknown();
export type getPlayerEmailNotificationsResponse403 = z.infer<
	typeof getPlayerEmailNotificationsResponse403
>;

const updatePlayerEmailNotificationsResponse200 = z.unknown();
export type updatePlayerEmailNotificationsResponse200 = z.infer<
	typeof updatePlayerEmailNotificationsResponse200
>;

const updatePlayerEmailNotificationsResponse404 = z.unknown();
export type updatePlayerEmailNotificationsResponse404 = z.infer<
	typeof updatePlayerEmailNotificationsResponse404
>;

const getClubEmailNotificationsResponse401 = z.unknown();
export type getClubEmailNotificationsResponse401 = z.infer<
	typeof getClubEmailNotificationsResponse401
>;

const getClubEmailNotificationsResponse403 = z.unknown();
export type getClubEmailNotificationsResponse403 = z.infer<
	typeof getClubEmailNotificationsResponse403
>;

const updateClubEmailNotificationsResponse200 = z.unknown();
export type updateClubEmailNotificationsResponse200 = z.infer<
	typeof updateClubEmailNotificationsResponse200
>;

const updateClubEmailNotificationsResponse404 = z.unknown();
export type updateClubEmailNotificationsResponse404 = z.infer<
	typeof updateClubEmailNotificationsResponse404
>;

const getTeamEmailNotificationsResponse401 = z.unknown();
export type getTeamEmailNotificationsResponse401 = z.infer<
	typeof getTeamEmailNotificationsResponse401
>;

const getTeamEmailNotificationsResponse403 = z.unknown();
export type getTeamEmailNotificationsResponse403 = z.infer<
	typeof getTeamEmailNotificationsResponse403
>;

const updateTeamEmailNotificationsResponse200 = z.unknown();
export type updateTeamEmailNotificationsResponse200 = z.infer<
	typeof updateTeamEmailNotificationsResponse200
>;

const getClubResponse404 = z.string();
export type getClubResponse404 = z.infer<typeof getClubResponse404>;

const getClubTeamsResponse200 = z.array(TeamInfo);
export type getClubTeamsResponse200 = z.infer<typeof getClubTeamsResponse200>;

const getClubTeamsResponse404 = z.unknown();
export type getClubTeamsResponse404 = z.infer<typeof getClubTeamsResponse404>;

const getClubGamesResponse400 = z.string();
export type getClubGamesResponse400 = z.infer<typeof getClubGamesResponse400>;

const getClubGamesResponse404 = z.string();
export type getClubGamesResponse404 = z.infer<typeof getClubGamesResponse404>;

const getClubGamesAgeCategoriesResponse200 = z.array(DefaultSelectResponse);
export type getClubGamesAgeCategoriesResponse200 = z.infer<
	typeof getClubGamesAgeCategoriesResponse200
>;

const getClubGamesAgeCategoriesResponse400 = z.string();
export type getClubGamesAgeCategoriesResponse400 = z.infer<
	typeof getClubGamesAgeCategoriesResponse400
>;

const getClubPlayersAgeCategoriesResponse200 = z.array(DefaultSelectResponse);
export type getClubPlayersAgeCategoriesResponse200 = z.infer<
	typeof getClubPlayersAgeCategoriesResponse200
>;

const getClubPlayersAgeCategoriesResponse400 = z.string();
export type getClubPlayersAgeCategoriesResponse400 = z.infer<
	typeof getClubPlayersAgeCategoriesResponse400
>;

const getGamePlayedTeamsResponse200 = z.array(TeamInfo);
export type getGamePlayedTeamsResponse200 = z.infer<
	typeof getGamePlayedTeamsResponse200
>;

const getGamePlayedTeamsResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getGamePlayedTeamsResponse401 = z.infer<
	typeof getGamePlayedTeamsResponse401
>;

const getGamePlayedTeamsResponse404 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type getGamePlayedTeamsResponse404 = z.infer<
	typeof getGamePlayedTeamsResponse404
>;

const playerClubListResponse200 = z.array(ClubStatsSearchData);
export type playerClubListResponse200 = z.infer<
	typeof playerClubListResponse200
>;

const playerNameListResponse200 = z.array(z.string());
export type playerNameListResponse200 = z.infer<
	typeof playerNameListResponse200
>;

const addOrUpdateGameResponse200 = z.object({
	status: z.number().int(),
	message: z.string(),
});
export type addOrUpdateGameResponse200 = z.infer<
	typeof addOrUpdateGameResponse200
>;

const addOrUpdateGameResponse401 = z.unknown();
export type addOrUpdateGameResponse401 = z.infer<
	typeof addOrUpdateGameResponse401
>;

const addOrUpdateGameResponse404 = z.string();
export type addOrUpdateGameResponse404 = z.infer<
	typeof addOrUpdateGameResponse404
>;

const deleteGameResponse200 = z.object({
	status: z.number().int(),
	message: z.string(),
});
export type deleteGameResponse200 = z.infer<typeof deleteGameResponse200>;

const deleteGameResponse403 = z.object({
	status: z.number().int(),
	message: z.string(),
});
export type deleteGameResponse403 = z.infer<typeof deleteGameResponse403>;

const updateTeamScoresResponse200 = z.object({
	status: z.number().int(),
	message: z.string(),
});
export type updateTeamScoresResponse200 = z.infer<
	typeof updateTeamScoresResponse200
>;

const updateTeamScoresResponse400 = z.object({
	property_path: z.string(),
	message: z.string(),
});
export type updateTeamScoresResponse400 = z.infer<
	typeof updateTeamScoresResponse400
>;

const gameDropdownDataResponse200 = z.object({
	opponentClubs: z.array(z.string()),
	homeClubs: z.array(z.string()),
});
export type gameDropdownDataResponse200 = z.infer<
	typeof gameDropdownDataResponse200
>;

const loadHomeAndAwayTeamsResponse200 = z
	.object({
		homeTeams: z.array(DefaultSelectResponse),
		awayTeams: z.array(DefaultSelectResponse),
	})
	.partial();
export type loadHomeAndAwayTeamsResponse200 = z.infer<
	typeof loadHomeAndAwayTeamsResponse200
>;

const searchClubsResponse200 = z.array(
	z
		.object({ label: z.string(), value: z.number().int(), type: z.string() })
		.partial(),
);
export type searchClubsResponse200 = z.infer<typeof searchClubsResponse200>;

const searchClubsResponse400 = z.array(
	z.object({ property_path: z.string(), message: z.string() }).partial(),
);
export type searchClubsResponse400 = z.infer<typeof searchClubsResponse400>;

const similarClubsResponse200 = z.object({
	status: z.number().int(),
	data: z.array(
		z
			.object({
				type: z.string(),
				value: z.number().int(),
				label: z.string(),
				country: z.string(),
			})
			.partial(),
	),
});
export type similarClubsResponse200 = z.infer<typeof similarClubsResponse200>;

const similarClubsResponse400 = z.array(
	z.object({ property_path: z.string(), message: z.string() }).partial(),
);
export type similarClubsResponse400 = z.infer<typeof similarClubsResponse400>;

const getClubAllTeamsResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type getClubAllTeamsResponse400 = z.infer<
	typeof getClubAllTeamsResponse400
>;

const addUpdateTeamResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type addUpdateTeamResponse200 = z.infer<typeof addUpdateTeamResponse200>;

const addUpdateTeamResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type addUpdateTeamResponse400 = z.infer<typeof addUpdateTeamResponse400>;

const addUpdateTeamResponse409 = z.unknown();
export type addUpdateTeamResponse409 = z.infer<typeof addUpdateTeamResponse409>;

const getTeamDetailResponse404 = z.string();
export type getTeamDetailResponse404 = z.infer<typeof getTeamDetailResponse404>;

const deleteTeamResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type deleteTeamResponse200 = z.infer<typeof deleteTeamResponse200>;

const deleteTeamResponse404 = z.unknown();
export type deleteTeamResponse404 = z.infer<typeof deleteTeamResponse404>;

const clubCoachesResponse200 = z.array(DefaultSelectResponse);
export type clubCoachesResponse200 = z.infer<typeof clubCoachesResponse200>;

const getClubTeamsListResponse200 = z.array(DefaultSelectResponse);
export type getClubTeamsListResponse200 = z.infer<
	typeof getClubTeamsListResponse200
>;

const teamPlayersResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type teamPlayersResponse400 = z.infer<typeof teamPlayersResponse400>;

const addUpdatePlayerResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
		data: ClubPlayersInfo,
	})
	.partial();
export type addUpdatePlayerResponse200 = z.infer<
	typeof addUpdatePlayerResponse200
>;

const addUpdatePlayerResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type addUpdatePlayerResponse400 = z.infer<
	typeof addUpdatePlayerResponse400
>;

const addUpdatePlayerResponse409 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
		data: z.array(ClubPlayersInfo).describe("Player Data"),
	})
	.partial();
export type addUpdatePlayerResponse409 = z.infer<
	typeof addUpdatePlayerResponse409
>;

const formerPlayersResponse400 = z
	.object({ property_path: z.string(), message: z.string() })
	.partial();
export type formerPlayersResponse400 = z.infer<typeof formerPlayersResponse400>;

const getTeamByIdAndCoachIdResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		data: z.array(ClubTeamInfo).describe("team details"),
	})
	.partial();
export type getTeamByIdAndCoachIdResponse200 = z.infer<
	typeof getTeamByIdAndCoachIdResponse200
>;

const uploadPlayerPhotoResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
		url: z.string().describe("message"),
	})
	.partial();
export type uploadPlayerPhotoResponse200 = z.infer<
	typeof uploadPlayerPhotoResponse200
>;

const uploadPlayerPhotoResponse400 = z.unknown();
export type uploadPlayerPhotoResponse400 = z.infer<
	typeof uploadPlayerPhotoResponse400
>;

const assignCoachesToTeamResponse200 = z
	.object({
		status: z.number().int().describe("status"),
		message: z.string().describe("message"),
	})
	.partial();
export type assignCoachesToTeamResponse200 = z.infer<
	typeof assignCoachesToTeamResponse200
>;

const getPositionsResponse200 = z.array(DefaultSelectResponse);
export type getPositionsResponse200 = z.infer<typeof getPositionsResponse200>;

const getNationalitiesResponse200 = z.array(DefaultSelectResponse);
export type getNationalitiesResponse200 = z.infer<
	typeof getNationalitiesResponse200
>;

const getCategoriesResponse200 = z.array(DefaultSelectResponse);
export type getCategoriesResponse200 = z.infer<typeof getCategoriesResponse200>;

const generateSignUpLinkResponse200 = z.object({
	shortLink: z.string().describe("Mobile app sign-up short URL"),
	previewLink: z.string().describe("Mobile app sign-up preview URL"),
});
export type generateSignUpLinkResponse200 = z.infer<
	typeof generateSignUpLinkResponse200
>;

const generateSignUpLinkResponse400 = z.unknown();
export type generateSignUpLinkResponse400 = z.infer<
	typeof generateSignUpLinkResponse400
>;

const generateSignUpLinkResponse403 = z.unknown();
export type generateSignUpLinkResponse403 = z.infer<
	typeof generateSignUpLinkResponse403
>;

const generateSignUpLinkResponse404 = z.unknown();
export type generateSignUpLinkResponse404 = z.infer<
	typeof generateSignUpLinkResponse404
>;

const generateSignUpLinkResponse409 = z.unknown();
export type generateSignUpLinkResponse409 = z.infer<
	typeof generateSignUpLinkResponse409
>;

const getPlayerLatestLogEntriesResponse200 = z.object({
	height: z.string().nullable(),
	weight: z.string().nullable(),
	photo: z.string().nullable(),
});
export type getPlayerLatestLogEntriesResponse200 = z.infer<
	typeof getPlayerLatestLogEntriesResponse200
>;

const postGameTeamSheetExportResponse400 = z.string();
export type postGameTeamSheetExportResponse400 = z.infer<
	typeof postGameTeamSheetExportResponse400
>;

const postGameTeamSheetExportResponse403 = z.string();
export type postGameTeamSheetExportResponse403 = z.infer<
	typeof postGameTeamSheetExportResponse403
>;

const postGameTeamSheetExportResponse404 = z.string();
export type postGameTeamSheetExportResponse404 = z.infer<
	typeof postGameTeamSheetExportResponse404
>;

const getUserWidgetSettingsResponse200 = z.union([
	UserWidgetSettingsResponse,
	z.null(),
]);
export type getUserWidgetSettingsResponse200 = z.infer<
	typeof getUserWidgetSettingsResponse200
>;

const saveUserWidgetSettingsResponse200 = z
	.object({ insightDisplaySettings: DisplaySettingsResponse })
	.partial();
export type saveUserWidgetSettingsResponse200 = z.infer<
	typeof saveUserWidgetSettingsResponse200
>;

const getAnnounceKitTokenResponse200 = z.object({
	token: z.string().nullable(),
});
export type getAnnounceKitTokenResponse200 = z.infer<
	typeof getAnnounceKitTokenResponse200
>;

const getAnnounceKitTokenResponse403 = z.string();
export type getAnnounceKitTokenResponse403 = z.infer<
	typeof getAnnounceKitTokenResponse403
>;

const portalGetGameAnalysisPhysicalResponse401 = z
	.object({ error: z.string() })
	.partial();
export type portalGetGameAnalysisPhysicalResponse401 = z.infer<
	typeof portalGetGameAnalysisPhysicalResponse401
>;

const portalGetGameAnalysisPhysicalResponse403 = z
	.object({ error: z.string() })
	.partial();
export type portalGetGameAnalysisPhysicalResponse403 = z.infer<
	typeof portalGetGameAnalysisPhysicalResponse403
>;

const portalGetGameAnalysisPhysicalResponse404 = z
	.object({ error: z.string() })
	.partial();
export type portalGetGameAnalysisPhysicalResponse404 = z.infer<
	typeof portalGetGameAnalysisPhysicalResponse404
>;

const generateDiscoverRedirectResponse200 = z
	.object({ token: z.string() })
	.partial();
export type generateDiscoverRedirectResponse200 = z.infer<
	typeof generateDiscoverRedirectResponse200
>;

const generateDiscoverRedirectResponse401 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type generateDiscoverRedirectResponse401 = z.infer<
	typeof generateDiscoverRedirectResponse401
>;

const generateDiscoverRedirectResponse403 = z
	.object({ message: z.string().describe("Error message") })
	.partial();
export type generateDiscoverRedirectResponse403 = z.infer<
	typeof generateDiscoverRedirectResponse403
>;

const getShadowTeamsResponse400 = z.string();
export type getShadowTeamsResponse400 = z.infer<
	typeof getShadowTeamsResponse400
>;

const getShadowTeamsResponse403 = z.string();
export type getShadowTeamsResponse403 = z.infer<
	typeof getShadowTeamsResponse403
>;

const createShadowTeamResponse400 = z.string();
export type createShadowTeamResponse400 = z.infer<
	typeof createShadowTeamResponse400
>;

const createShadowTeamResponse403 = z.string();
export type createShadowTeamResponse403 = z.infer<
	typeof createShadowTeamResponse403
>;

const createShadowTeamResponse404 = z.string();
export type createShadowTeamResponse404 = z.infer<
	typeof createShadowTeamResponse404
>;

const getShadowTeamResponse400 = z.string();
export type getShadowTeamResponse400 = z.infer<typeof getShadowTeamResponse400>;

const getShadowTeamResponse403 = z.string();
export type getShadowTeamResponse403 = z.infer<typeof getShadowTeamResponse403>;

const getShadowTeamResponse404 = z.string();
export type getShadowTeamResponse404 = z.infer<typeof getShadowTeamResponse404>;

const updateShadowTeamResponse400 = z.string();
export type updateShadowTeamResponse400 = z.infer<
	typeof updateShadowTeamResponse400
>;

const updateShadowTeamResponse403 = z.string();
export type updateShadowTeamResponse403 = z.infer<
	typeof updateShadowTeamResponse403
>;

const updateShadowTeamResponse404 = z.string();
export type updateShadowTeamResponse404 = z.infer<
	typeof updateShadowTeamResponse404
>;

const deleteShadowTeamResponse400 = z.string();
export type deleteShadowTeamResponse400 = z.infer<
	typeof deleteShadowTeamResponse400
>;

const deleteShadowTeamResponse403 = z.string();
export type deleteShadowTeamResponse403 = z.infer<
	typeof deleteShadowTeamResponse403
>;

const updateShadowTeamPartiallyResponse400 = z.string();
export type updateShadowTeamPartiallyResponse400 = z.infer<
	typeof updateShadowTeamPartiallyResponse400
>;

const updateShadowTeamPartiallyResponse403 = z.string();
export type updateShadowTeamPartiallyResponse403 = z.infer<
	typeof updateShadowTeamPartiallyResponse403
>;

const updateShadowTeamPartiallyResponse404 = z.string();
export type updateShadowTeamPartiallyResponse404 = z.infer<
	typeof updateShadowTeamPartiallyResponse404
>;

const getTokenMovements = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<DiscoverTokenMovementsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum([
									"id",
									"amount",
									"clubId",
									"clubName",
									"teamId",
									"teamName",
									"relatedClubId",
									"relatedClubName",
									"relatedTeamId",
									"relatedTeamName",
									"userId",
									"userName",
									"movementType",
									"authorType",
									"source",
									"purchasedAt",
									"createdAt",
									"note",
									"paymentLink",
								])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"id",
								"amount",
								"clubName",
								"teamName",
								"relatedClubName",
								"relatedTeamName",
								"userName",
								"movementType",
								"purchasedAt",
								"createdAt",
								"note",
								"authorType",
								"paymentLink",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/admin/token-movements")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = DiscoverTokenMovementsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getTokenMovementsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getTokenMovementsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<DiscoverTokenMovementsResponse>,
					GenericWretchError<
						getTokenMovementsResponse400 | getTokenMovementsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<DiscoverTokenMovementsResponse>,
			GenericWretchError<
				getTokenMovementsResponse400 | getTokenMovementsResponse403
			>
		> => {
			const key = ["getTokenMovements", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTokenMovementsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<DiscoverTokenMovementsResponse>,
					GenericWretchError<
						getTokenMovementsResponse400 | getTokenMovementsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<DiscoverTokenMovementsResponse>,
			GenericWretchError<
				getTokenMovementsResponse400 | getTokenMovementsResponse403
			>
		> => {
			const key = ["getTokenMovements", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<DiscoverTokenMovementsResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<DiscoverTokenMovementsResponse>,
				GenericWretchError<
					getTokenMovementsResponse400 | getTokenMovementsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postTokenMovement = (wretch: Http) => {
	const mutation = async (params: {
		body: DiscoverTokenMovementPostRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/admin/token-movement")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = postTokenMovementResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postTokenMovementResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postTokenMovementResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postTokenMovementResponse200> | undefined,
					GenericWretchError<
						postTokenMovementResponse400 | postTokenMovementResponse403
					>,
					{ body: DiscoverTokenMovementPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postTokenMovementResponse200> | undefined,
				GenericWretchError<
					postTokenMovementResponse400 | postTokenMovementResponse403
				>,
				{ body: DiscoverTokenMovementPostRequest }
			>(mutation, options || {});
		},
	};
};

const getGamePage = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamePageResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/page`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamePageResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGamePageResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGamePageResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePageResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamePageResponse>,
					GenericWretchError<
						| getGamePageResponse400
						| getGamePageResponse403
						| getGamePageResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamePageResponse>,
			GenericWretchError<
				getGamePageResponse400 | getGamePageResponse403 | getGamePageResponse404
			>
		> => {
			const key = ["getGamePage", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamePageResponse>,
					GenericWretchError<
						| getGamePageResponse400
						| getGamePageResponse403
						| getGamePageResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamePageResponse>,
			GenericWretchError<
				getGamePageResponse400 | getGamePageResponse403 | getGamePageResponse404
			>
		> => {
			const key = ["getGamePage", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamePageResponse>,
				GenericWretchError<
					| getGamePageResponse400
					| getGamePageResponse403
					| getGamePageResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGamePageLazy = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamePageLazyResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/page/lazy`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamePageLazyResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGamePageLazyResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGamePageLazyResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePageLazyResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamePageLazyResponse>,
					GenericWretchError<
						| getGamePageLazyResponse400
						| getGamePageLazyResponse403
						| getGamePageLazyResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamePageLazyResponse>,
			GenericWretchError<
				| getGamePageLazyResponse400
				| getGamePageLazyResponse403
				| getGamePageLazyResponse404
			>
		> => {
			const key = ["getGamePageLazy", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageLazyResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamePageLazyResponse>,
					GenericWretchError<
						| getGamePageLazyResponse400
						| getGamePageLazyResponse403
						| getGamePageLazyResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamePageLazyResponse>,
			GenericWretchError<
				| getGamePageLazyResponse400
				| getGamePageLazyResponse403
				| getGamePageLazyResponse404
			>
		> => {
			const key = ["getGamePageLazy", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePageLazyResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamePageLazyResponse>,
				GenericWretchError<
					| getGamePageLazyResponse400
					| getGamePageLazyResponse403
					| getGamePageLazyResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameScore = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GamePutScoreRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch.url(`/game/${id}/score`).json(body).put().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameScoreResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameScoreResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameScoreResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameScoreResponse400
						| putGameScoreResponse403
						| putGameScoreResponse404
					>,
					{ id: number; body: GamePutScoreRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameScoreResponse400
					| putGameScoreResponse403
					| putGameScoreResponse404
				>,
				{ id: number; body: GamePutScoreRequest }
			>(mutation, options || {});
		},
	};
};

const getGameTeamStat = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamStatResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/team-stat`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamStatResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamStatResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamStatResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamStatResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamStatResponse>,
					GenericWretchError<
						| getGameTeamStatResponse400
						| getGameTeamStatResponse403
						| getGameTeamStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamStatResponse>,
			GenericWretchError<
				| getGameTeamStatResponse400
				| getGameTeamStatResponse403
				| getGameTeamStatResponse404
			>
		> => {
			const key = ["getGameTeamStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameTeamStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamStatResponse>,
					GenericWretchError<
						| getGameTeamStatResponse400
						| getGameTeamStatResponse403
						| getGameTeamStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamStatResponse>,
			GenericWretchError<
				| getGameTeamStatResponse400
				| getGameTeamStatResponse403
				| getGameTeamStatResponse404
			>
		> => {
			const key = ["getGameTeamStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameTeamStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamStatResponse>,
				GenericWretchError<
					| getGameTeamStatResponse400
					| getGameTeamStatResponse403
					| getGameTeamStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGamePlayerStat = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamePlayerStatResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game/${id}/player-stat`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamePlayerStatResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGamePlayerStatResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGamePlayerStatResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePlayerStatResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamePlayerStatResponse>,
					GenericWretchError<
						| getGamePlayerStatResponse400
						| getGamePlayerStatResponse403
						| getGamePlayerStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamePlayerStatResponse>,
			GenericWretchError<
				| getGamePlayerStatResponse400
				| getGamePlayerStatResponse403
				| getGamePlayerStatResponse404
			>
		> => {
			const key = ["getGamePlayerStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePlayerStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamePlayerStatResponse>,
					GenericWretchError<
						| getGamePlayerStatResponse400
						| getGamePlayerStatResponse403
						| getGamePlayerStatResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamePlayerStatResponse>,
			GenericWretchError<
				| getGamePlayerStatResponse400
				| getGamePlayerStatResponse403
				| getGamePlayerStatResponse404
			>
		> => {
			const key = ["getGamePlayerStat", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamePlayerStatResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamePlayerStatResponse>,
				GenericWretchError<
					| getGamePlayerStatResponse400
					| getGamePlayerStatResponse403
					| getGamePlayerStatResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameTeamColors = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameTeamPutColorsRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-team/${id}/colors`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameTeamColorsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameTeamColorsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameTeamColorsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameTeamColorsResponse400
						| putGameTeamColorsResponse403
						| putGameTeamColorsResponse404
					>,
					{ id: number; body: GameTeamPutColorsRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameTeamColorsResponse400
					| putGameTeamColorsResponse403
					| putGameTeamColorsResponse404
				>,
				{ id: number; body: GameTeamPutColorsRequest }
			>(mutation, options || {});
		},
	};
};

const setRatingToGamePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number; body: RatingPutRequest }) => {
		const args = z
			.object({
				id: z.number().int().describe("ID of the game player"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-player/${id}/rating`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = setRatingToGamePlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = setRatingToGamePlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = setRatingToGamePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| setRatingToGamePlayerResponse400
						| setRatingToGamePlayerResponse403
						| setRatingToGamePlayerResponse404
					>,
					{ id: number; body: RatingPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| setRatingToGamePlayerResponse400
					| setRatingToGamePlayerResponse403
					| setRatingToGamePlayerResponse404
				>,
				{ id: number; body: RatingPutRequest }
			>(mutation, options || {});
		},
	};
};

const setBadgesToGamePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number; body: BadgesPutRequest }) => {
		const args = z
			.object({
				id: z.number().int().describe("ID of the game player"),
				body: z.any(),
			})
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-player/${id}/badges`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = setBadgesToGamePlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = setBadgesToGamePlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = setBadgesToGamePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| setBadgesToGamePlayerResponse400
						| setBadgesToGamePlayerResponse403
						| setBadgesToGamePlayerResponse404
					>,
					{ id: number; body: BadgesPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| setBadgesToGamePlayerResponse400
					| setBadgesToGamePlayerResponse403
					| setBadgesToGamePlayerResponse404
				>,
				{ id: number; body: BadgesPutRequest }
			>(mutation, options || {});
		},
	};
};

const postGameHighlight = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: HighlightPostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/highlight/create/${gameId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameHighlightResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameHighlightResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameHighlightResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameHighlightResponse400
						| postGameHighlightResponse403
						| postGameHighlightResponse404
					>,
					{ gameId: number; body: HighlightPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameHighlightResponse400
					| postGameHighlightResponse403
					| postGameHighlightResponse404
				>,
				{ gameId: number; body: HighlightPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameHighlight = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/highlight/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteGameHighlightResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteGameHighlightResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteGameHighlightResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteGameHighlightResponse400
						| deleteGameHighlightResponse403
						| deleteGameHighlightResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteGameHighlightResponse400
					| deleteGameHighlightResponse403
					| deleteGameHighlightResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const patchGameHighlight = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: HighlightPatchRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch.url(`/highlight/${id}`).json(body).patch().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = patchGameHighlightResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = patchGameHighlightResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = patchGameHighlightResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| patchGameHighlightResponse400
						| patchGameHighlightResponse403
						| patchGameHighlightResponse404
					>,
					{ id: number; body: HighlightPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| patchGameHighlightResponse400
					| patchGameHighlightResponse403
					| patchGameHighlightResponse404
				>,
				{ id: number; body: HighlightPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getGameClips = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum(["gameId", "author", "isFavorite", "tag"])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z.literal("createdAt").describe("Sorting field").optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/game-clip")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameClipsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameClipsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipsResponse>,
					GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipsResponse>,
			GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
		> => {
			const key = ["getGameClips", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipsResponse>,
					GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipsResponse>,
			GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
		> => {
			const key = ["getGameClips", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipsResponse>,
				GenericWretchError<getGameClipsResponse400 | getGameClipsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameClip = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-clip/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameClipResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
		> => {
			const key = ["getGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
		> => {
			const key = ["getGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<getGameClipResponse400 | getGameClipResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameClip = (wretch: Http) => {
	const mutation = async (params: { id: number; body: GameClipPutRequest }) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch.url(`/game-clip/${id}`).json(body).put().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameClipResponse400
						| putGameClipResponse403
						| putGameClipResponse404
					>,
					{ id: number; body: GameClipPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameClipResponse400
					| putGameClipResponse403
					| putGameClipResponse404
				>,
				{ id: number; body: GameClipPutRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameClip = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/game-clip/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteGameClipResponse400
						| deleteGameClipResponse403
						| deleteGameClipResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteGameClipResponse400
					| deleteGameClipResponse403
					| deleteGameClipResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const postGameClip = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: GameClipPostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/game-clip/create/${gameId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameClipResponse400
						| postGameClipResponse403
						| postGameClipResponse404
					>,
					{ gameId: number; body: GameClipPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameClipResponse400
					| postGameClipResponse403
					| postGameClipResponse404
				>,
				{ gameId: number; body: GameClipPostRequest }
			>(mutation, options || {});
		},
	};
};

const putGameTeamSheet = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameTeamSheetRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-team/${id}/team-sheet`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameTeamSheetResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameTeamSheetResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameTeamSheetResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameTeamSheetResponse400
						| putGameTeamSheetResponse403
						| putGameTeamSheetResponse404
					>,
					{ id: number; body: GameTeamSheetRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameTeamSheetResponse400
					| putGameTeamSheetResponse403
					| putGameTeamSheetResponse404
				>,
				{ id: number; body: GameTeamSheetRequest }
			>(mutation, options || {});
		},
	};
};

const getGameTeamSheetCompositionPlayers = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; composition: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamSheetCompositionPlayersResponse>> => {
		const args = z
			.object({
				id: z.number().int().describe("Game team ID"),
				composition: z
					.enum([
						"TEAM_SHEET_EMPTY",
						"TEAM_SHEET_4_3_3_OFFENSIF",
						"TEAM_SHEET_4_3_3",
						"TEAM_SHEET_4_3_2_1",
						"TEAM_SHEET_4_3_1_2",
						"TEAM_SHEET_4_4_2_PLAT",
						"TEAM_SHEET_4_4_2_LOSANGE",
						"TEAM_SHEET_4_2_4",
						"TEAM_SHEET_4_4_1_1",
						"TEAM_SHEET_4_5_1",
						"TEAM_SHEET_3_5_2",
						"TEAM_SHEET_3_4_3",
						"TEAM_SHEET_3_4_1_2",
						"TEAM_SHEET_5_3_2",
						"TEAM_SHEET_5_4_1",
						"TEAM_SHEET_5_2_1_2",
					])
					.describe("Composition type"),
			})
			.parse(params);
		const { id, composition } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-team/${id}/team-sheet/${composition}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamSheetCompositionPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamSheetCompositionPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamSheetCompositionPlayersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamSheetCompositionPlayersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; composition: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamSheetCompositionPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetCompositionPlayersResponse400
						| getGameTeamSheetCompositionPlayersResponse403
						| getGameTeamSheetCompositionPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamSheetCompositionPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetCompositionPlayersResponse400
				| getGameTeamSheetCompositionPlayersResponse403
				| getGameTeamSheetCompositionPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetCompositionPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; composition: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamSheetCompositionPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetCompositionPlayersResponse400
						| getGameTeamSheetCompositionPlayersResponse403
						| getGameTeamSheetCompositionPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamSheetCompositionPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetCompositionPlayersResponse400
				| getGameTeamSheetCompositionPlayersResponse403
				| getGameTeamSheetCompositionPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetCompositionPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamSheetCompositionPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetCompositionPlayersResponse400
					| getGameTeamSheetCompositionPlayersResponse403
					| getGameTeamSheetCompositionPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameTeamPlayersSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number; limit?: number; filter?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>> => {
		const args = z
			.object({
				id: z.number().int().describe("Game team ID"),
				limit: z.number().int().describe("limit").optional(),
				filter: z.string().describe("filter - player name").optional(),
			})
			.parse(params);
		const { id, limit, filter } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-team/${id}/team-sheet/players`)
				.query(qs.stringify({ limit, filter }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamSheetGameTeamPlayersSearchResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamPlayersSearchResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamPlayersSearchResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamPlayersSearchResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; limit?: number; filter?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
					GenericWretchError<
						| getGameTeamPlayersSearchResponse400
						| getGameTeamPlayersSearchResponse403
						| getGameTeamPlayersSearchResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
			GenericWretchError<
				| getGameTeamPlayersSearchResponse400
				| getGameTeamPlayersSearchResponse403
				| getGameTeamPlayersSearchResponse404
			>
		> => {
			const key = [
				"getGameTeamPlayersSearch",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; limit?: number; filter?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
					GenericWretchError<
						| getGameTeamPlayersSearchResponse400
						| getGameTeamPlayersSearchResponse403
						| getGameTeamPlayersSearchResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
			GenericWretchError<
				| getGameTeamPlayersSearchResponse400
				| getGameTeamPlayersSearchResponse403
				| getGameTeamPlayersSearchResponse404
			>
		> => {
			const key = [
				"getGameTeamPlayersSearch",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamSheetGameTeamPlayersSearchResponse>,
				GenericWretchError<
					| getGameTeamPlayersSearchResponse400
					| getGameTeamPlayersSearchResponse403
					| getGameTeamPlayersSearchResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGameTeamSheetClubPlayers = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameTeamSheetPlayersResponse>> => {
		const args = z
			.object({ id: z.number().int().describe("Game team ID") })
			.parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-team/${id}/team-sheet/club-players`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameTeamSheetPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameTeamSheetClubPlayersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameTeamSheetClubPlayersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getGameTeamSheetClubPlayersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameTeamSheetPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetClubPlayersResponse400
						| getGameTeamSheetClubPlayersResponse403
						| getGameTeamSheetClubPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameTeamSheetPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetClubPlayersResponse400
				| getGameTeamSheetClubPlayersResponse403
				| getGameTeamSheetClubPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetClubPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameTeamSheetPlayersResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameTeamSheetPlayersResponse>,
					GenericWretchError<
						| getGameTeamSheetClubPlayersResponse400
						| getGameTeamSheetClubPlayersResponse403
						| getGameTeamSheetClubPlayersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameTeamSheetPlayersResponse>,
			GenericWretchError<
				| getGameTeamSheetClubPlayersResponse400
				| getGameTeamSheetClubPlayersResponse403
				| getGameTeamSheetClubPlayersResponse404
			>
		> => {
			const key = [
				"getGameTeamSheetClubPlayers",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameTeamSheetPlayersResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameTeamSheetPlayersResponse>,
				GenericWretchError<
					| getGameTeamSheetClubPlayersResponse400
					| getGameTeamSheetClubPlayersResponse403
					| getGameTeamSheetClubPlayersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putGameDescription = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: putGameDescriptionRequestBody;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/description`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putGameDescriptionResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putGameDescriptionResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = putGameDescriptionResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| putGameDescriptionResponse400
						| putGameDescriptionResponse403
						| putGameDescriptionResponse404
					>,
					{ id: number; body: putGameDescriptionRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| putGameDescriptionResponse400
					| putGameDescriptionResponse403
					| putGameDescriptionResponse404
				>,
				{ id: number; body: putGameDescriptionRequestBody }
			>(mutation, options || {});
		},
	};
};

const postGameStatsExport = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameStatsExportRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/export/stats`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameStatsExportResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameStatsExportResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameStatsExportResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FileResponse> | undefined,
					GenericWretchError<
						| postGameStatsExportResponse400
						| postGameStatsExportResponse403
						| postGameStatsExportResponse404
					>,
					{ id: number; body: GameStatsExportRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FileResponse> | undefined,
				GenericWretchError<
					| postGameStatsExportResponse400
					| postGameStatsExportResponse403
					| postGameStatsExportResponse404
				>,
				{ id: number; body: GameStatsExportRequest }
			>(mutation, options || {});
		},
	};
};

const postGameEventDataExport = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameEventDataExportRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/export/events`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameEventDataExportResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameEventDataExportResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameEventDataExportResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FileResponse> | undefined,
					GenericWretchError<
						| postGameEventDataExportResponse400
						| postGameEventDataExportResponse403
						| postGameEventDataExportResponse404
					>,
					{ id: number; body: GameEventDataExportRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FileResponse> | undefined,
				GenericWretchError<
					| postGameEventDataExportResponse400
					| postGameEventDataExportResponse403
					| postGameEventDataExportResponse404
				>,
				{ id: number; body: GameEventDataExportRequest }
			>(mutation, options || {});
		},
	};
};

const getSharedGameClipIsProtected = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { shareSlug: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipProtectedResponse>> => {
		const args = z.object({ shareSlug: z.string() }).parse(params);
		const { shareSlug } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-clip/shared/protected/${shareSlug}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipProtectedResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSharedGameClipIsProtectedResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = getSharedGameClipIsProtectedResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getSharedGameClipIsProtectedResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipProtectedResponse>,
					GenericWretchError<
						| getSharedGameClipIsProtectedResponse400
						| getSharedGameClipIsProtectedResponse401
						| getSharedGameClipIsProtectedResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipProtectedResponse>,
			GenericWretchError<
				| getSharedGameClipIsProtectedResponse400
				| getSharedGameClipIsProtectedResponse401
				| getSharedGameClipIsProtectedResponse403
			>
		> => {
			const key = [
				"getSharedGameClipIsProtected",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameClipProtectedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipProtectedResponse>,
					GenericWretchError<
						| getSharedGameClipIsProtectedResponse400
						| getSharedGameClipIsProtectedResponse401
						| getSharedGameClipIsProtectedResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipProtectedResponse>,
			GenericWretchError<
				| getSharedGameClipIsProtectedResponse400
				| getSharedGameClipIsProtectedResponse401
				| getSharedGameClipIsProtectedResponse403
			>
		> => {
			const key = [
				"getSharedGameClipIsProtected",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameClipProtectedResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipProtectedResponse>,
				GenericWretchError<
					| getSharedGameClipIsProtectedResponse400
					| getSharedGameClipIsProtectedResponse401
					| getSharedGameClipIsProtectedResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSharedGameClip = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { shareSlug: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameClipResponse>> => {
		const args = z.object({ shareSlug: z.string() }).parse(params);
		const { shareSlug } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/game-clip/shared/${shareSlug}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameClipResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getSharedGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getSharedGameClipResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<
						getSharedGameClipResponse400 | getSharedGameClipResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<
				getSharedGameClipResponse400 | getSharedGameClipResponse403
			>
		> => {
			const key = ["getSharedGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { shareSlug: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameClipResponse>,
					GenericWretchError<
						getSharedGameClipResponse400 | getSharedGameClipResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameClipResponse>,
			GenericWretchError<
				getSharedGameClipResponse400 | getSharedGameClipResponse403
			>
		> => {
			const key = ["getSharedGameClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GameClipResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameClipResponse>,
				GenericWretchError<
					getSharedGameClipResponse400 | getSharedGameClipResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameClipSharedPasswordValidation = (wretch: Http) => {
	const mutation = async (params: {
		shareSlug: string;
		body: postGameClipSharedPasswordValidationRequestBody;
	}) => {
		const args = z
			.object({ shareSlug: z.string(), body: z.any() })
			.parse(params);
		const { shareSlug, body } = args;

		try {
			const res = await wretch
				.url(`/game-clip/shared/password-validation/${shareSlug}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json =
						postGameClipSharedPasswordValidationResponse400.parse(json);
					break;
				}
				case 403: {
					err.json =
						postGameClipSharedPasswordValidationResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						postGameClipSharedPasswordValidationResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameClipSharedPasswordValidationResponse400
						| postGameClipSharedPasswordValidationResponse403
						| postGameClipSharedPasswordValidationResponse404
					>,
					{
						shareSlug: string;
						body: postGameClipSharedPasswordValidationRequestBody;
					}
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameClipSharedPasswordValidationResponse400
					| postGameClipSharedPasswordValidationResponse403
					| postGameClipSharedPasswordValidationResponse404
				>,
				{
					shareSlug: string;
					body: postGameClipSharedPasswordValidationRequestBody;
				}
			>(mutation, options || {});
		},
	};
};

const getGameEventFilters = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getGameEventFiltersResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/game-event-filter")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getGameEventFiltersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getGameEventFiltersResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getGameEventFiltersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getGameEventFiltersResponse200>,
					GenericWretchError<
						getGameEventFiltersResponse400 | getGameEventFiltersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getGameEventFiltersResponse200>,
			GenericWretchError<
				getGameEventFiltersResponse400 | getGameEventFiltersResponse403
			>
		> => {
			const key = ["getGameEventFilters", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGameEventFiltersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getGameEventFiltersResponse200>,
					GenericWretchError<
						getGameEventFiltersResponse400 | getGameEventFiltersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getGameEventFiltersResponse200>,
			GenericWretchError<
				getGameEventFiltersResponse400 | getGameEventFiltersResponse403
			>
		> => {
			const key = ["getGameEventFilters", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGameEventFiltersResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getGameEventFiltersResponse200>,
				GenericWretchError<
					getGameEventFiltersResponse400 | getGameEventFiltersResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameEventFilter = (wretch: Http) => {
	const mutation = async (params: { body: GameEventFilterPostRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/game-event-filter")
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameEventFilterResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameEventFilterResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameEventFilterResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postGameEventFilterResponse400
						| postGameEventFilterResponse403
						| postGameEventFilterResponse404
					>,
					{ body: GameEventFilterPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postGameEventFilterResponse400
					| postGameEventFilterResponse403
					| postGameEventFilterResponse404
				>,
				{ body: GameEventFilterPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameEventFilter = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/game-event-filter/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteGameEventFilterResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteGameEventFilterResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = deleteGameEventFilterResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| deleteGameEventFilterResponse400
						| deleteGameEventFilterResponse403
						| deleteGameEventFilterResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| deleteGameEventFilterResponse400
					| deleteGameEventFilterResponse403
					| deleteGameEventFilterResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const addCommentToGameEvent = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: GameEventCommentPostRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/game-event-comment/create/${id}`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = addCommentToGameEventResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 404: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addCommentToGameEventResponse201> | undefined,
					GenericWretchError<
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
					>,
					{ id: number; body: GameEventCommentPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addCommentToGameEventResponse201> | undefined,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
				>,
				{ id: number; body: GameEventCommentPostRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGameEventComment = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/game-event-comment/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 404: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
						| StatusMessageResponse
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
					| StatusMessageResponse
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getUserView = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserViewResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/view")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserViewResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<UserViewResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<UserViewResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserView", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserViewResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserViewResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserViewResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserView", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserViewResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserViewResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateSelectedView = (wretch: Http) => {
	const mutation = async (params: { body: UserViewRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/user/view").json(body).patch().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateSelectedViewResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = updateSelectedViewResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						updateSelectedViewResponse400 | updateSelectedViewResponse403
					>,
					{ body: UserViewRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					updateSelectedViewResponse400 | updateSelectedViewResponse403
				>,
				{ body: UserViewRequest }
			>(mutation, options || {});
		},
	};
};

const getMfaDomainList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { product?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<MfaDomainListResponse>> => {
		const args = z
			.object({ product: z.literal("Scout").optional() })
			.parse(params);
		const { product } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/mfa/domains")
				.query(qs.stringify({ product }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = MfaDomainListResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getMfaDomainListResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { product?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<MfaDomainListResponse>,
					GenericWretchError<getMfaDomainListResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<MfaDomainListResponse>,
			GenericWretchError<getMfaDomainListResponse403>
		> => {
			const key = ["getMfaDomainList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<MfaDomainListResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { product?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<MfaDomainListResponse>,
					GenericWretchError<getMfaDomainListResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<MfaDomainListResponse>,
			GenericWretchError<getMfaDomainListResponse403>
		> => {
			const key = ["getMfaDomainList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<MfaDomainListResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<MfaDomainListResponse>,
				GenericWretchError<getMfaDomainListResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getMfaDomainRequiredCheck = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { email: string; product?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getMfaDomainRequiredCheckResponse200>> => {
		const args = z
			.object({
				email: z
					.string()
					.describe("user email — domain is extracted on the server side"),
				product: z.string().describe("optional product filter").optional(),
			})
			.parse(params);
		const { email, product } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/mfa/domain/required")
				.query(qs.stringify({ email, product }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getMfaDomainRequiredCheckResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getMfaDomainRequiredCheckResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { email: string; product?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getMfaDomainRequiredCheckResponse200>,
					GenericWretchError<getMfaDomainRequiredCheckResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getMfaDomainRequiredCheckResponse200>,
			GenericWretchError<getMfaDomainRequiredCheckResponse403>
		> => {
			const key = [
				"getMfaDomainRequiredCheck",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getMfaDomainRequiredCheckResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { email: string; product?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getMfaDomainRequiredCheckResponse200>,
					GenericWretchError<getMfaDomainRequiredCheckResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getMfaDomainRequiredCheckResponse200>,
			GenericWretchError<getMfaDomainRequiredCheckResponse403>
		> => {
			const key = [
				"getMfaDomainRequiredCheck",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getMfaDomainRequiredCheckResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getMfaDomainRequiredCheckResponse200>,
				GenericWretchError<getMfaDomainRequiredCheckResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postReportedTaggingIssue = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		body: ReportedTaggingIssuePostRequest;
	}) => {
		const args = z
			.object({ gameId: z.number().int(), body: z.any() })
			.parse(params);
		const { gameId, body } = args;

		try {
			const res = await wretch
				.url(`/reported-tagging-issue/create/${gameId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postReportedTaggingIssueResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postReportedTaggingIssueResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postReportedTaggingIssueResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postReportedTaggingIssueResponse400
						| postReportedTaggingIssueResponse403
						| postReportedTaggingIssueResponse404
					>,
					{ gameId: number; body: ReportedTaggingIssuePostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postReportedTaggingIssueResponse400
					| postReportedTaggingIssueResponse403
					| postReportedTaggingIssueResponse404
				>,
				{ gameId: number; body: ReportedTaggingIssuePostRequest }
			>(mutation, options || {});
		},
	};
};

const getUserAllCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { gender?: string; scope?: string; teamGroups?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<CountryData>> => {
		const args = z
			.object({
				gender: z.enum(["Male", "Female"]).nullable().default(null),
				scope: z.string().nullable().default(null),
				teamGroups: z.string().nullable().default(null),
			})
			.parse(params);
		const { gender, scope, teamGroups } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/countries/all")
				.query(qs.stringify({ gender, scope, teamGroups }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = CountryData.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gender?: string; scope?: string; teamGroups?: string },
			options?: Omit<
				UseQueryOptions<ApiResponse<CountryData>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<CountryData>, GenericWretchError<null>> => {
			const key = ["getUserAllCountries", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<CountryData>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gender?: string; scope?: string; teamGroups?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<CountryData>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<CountryData>,
			GenericWretchError<null>
		> => {
			const key = ["getUserAllCountries", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<CountryData>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<CountryData>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserSelectedCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SelectedCountriesResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/countries/selected")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SelectedCountriesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SelectedCountriesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SelectedCountriesResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSelectedCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SelectedCountriesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SelectedCountriesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SelectedCountriesResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserSelectedCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<SelectedCountriesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SelectedCountriesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateUserSelectedCountries = (wretch: Http) => {
	const mutation = async (params: { body: UserCountriesPatchRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/user/countries/selected")
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateUserSelectedCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateUserSelectedCountriesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = updateUserSelectedCountriesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateUserSelectedCountriesResponse200> | undefined,
					GenericWretchError<
						| updateUserSelectedCountriesResponse400
						| updateUserSelectedCountriesResponse403
					>,
					{ body: UserCountriesPatchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateUserSelectedCountriesResponse200> | undefined,
				GenericWretchError<
					| updateUserSelectedCountriesResponse400
					| updateUserSelectedCountriesResponse403
				>,
				{ body: UserCountriesPatchRequest }
			>(mutation, options || {});
		},
	};
};

const getTeamGroupVisibleEntities = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { gender: string; teamGroups: string; countries: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamGroupVisibleEntitiesResponse>> => {
		const args = z
			.object({
				gender: z.enum(["Male", "Female"]),
				teamGroups: z.string(),
				countries: z.string(),
			})
			.parse(params);
		const { gender, teamGroups, countries } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/team-groups/visible-entities")
				.query(qs.stringify({ gender, teamGroups, countries }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamGroupVisibleEntitiesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gender: string; teamGroups: string; countries: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamGroupVisibleEntitiesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamGroupVisibleEntitiesResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getTeamGroupVisibleEntities",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<TeamGroupVisibleEntitiesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gender: string; teamGroups: string; countries: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamGroupVisibleEntitiesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamGroupVisibleEntitiesResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getTeamGroupVisibleEntities",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<TeamGroupVisibleEntitiesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamGroupVisibleEntitiesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalAIGameSummaryGet = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { gameId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameSummaryResponse>> => {
		const args = z.object({ gameId: z.number().int() }).parse(params);
		const { gameId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/games/${gameId}/ai-game-summary`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameSummaryResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gameId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameSummaryResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameSummaryResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"portalAIGameSummaryGet",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameSummaryResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameSummaryResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameSummaryResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gameId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameSummaryResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameSummaryResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"portalAIGameSummaryGet",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameSummaryResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameSummaryResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameSummaryResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalAIGameSummaryFeedbackPut = (wretch: Http) => {
	const mutation = async (params: {
		gameId: number;
		gameSummaryId: string;
		body: PutGameSummaryFeedbackRequest;
	}) => {
		const args = z
			.object({
				gameId: z.number().int(),
				gameSummaryId: z
					.string()
					.uuid()
					.regex(
						new RegExp(
							"[0-9a-f]{8}-[0-9a-f]{4}-[13-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}",
						),
					),
				body: z.any(),
			})
			.parse(params);
		const { gameId, gameSummaryId, body } = args;

		try {
			const res = await wretch
				.url(
					`/portal/games/${gameId}/ai-game-summary/${gameSummaryId}/feedback`,
				)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameSummaryFeedbackResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 400: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<GameSummaryFeedbackResponse> | undefined,
					GenericWretchError<null>,
					{
						gameId: number;
						gameSummaryId: string;
						body: PutGameSummaryFeedbackRequest;
					}
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<GameSummaryFeedbackResponse> | undefined,
				GenericWretchError<null>,
				{
					gameId: number;
					gameSummaryId: string;
					body: PutGameSummaryFeedbackRequest;
				}
			>(mutation, options || {});
		},
	};
};

const portalAIScoutSearchUserInfo = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserInfoResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/ai/apps/scout-search/user-info")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserInfoResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<UserInfoResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<UserInfoResponse>,
			GenericWretchError<null>
		> => {
			const key = ["portalAIScoutSearchUserInfo", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserInfoResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserInfoResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserInfoResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserInfoResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserInfoResponse>,
			GenericWretchError<null>
		> => {
			const key = ["portalAIScoutSearchUserInfo", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserInfoResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserInfoResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserInfoResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalAIConversationsList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			pinned?: boolean;
			messagesCount?: number;
			page?: number;
			limit?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ConversationsResponse>> => {
		const args = z
			.object({
				pinned: z.boolean().optional(),
				messagesCount: z.number().int().optional(),
				page: z.number().int().gte(1).default(1),
				limit: z.number().int().lte(50).default(10),
			})
			.parse(params);
		const { pinned, messagesCount, page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/ai/apps/scout-search/conversations")
				.query(qs.stringify({ pinned, messagesCount, page, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ConversationsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 403: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				pinned?: boolean;
				messagesCount?: number;
				page?: number;
				limit?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ConversationsResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ConversationsResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"portalAIConversationsList",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ConversationsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ConversationsResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ConversationsResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				pinned?: boolean;
				messagesCount?: number;
				page?: number;
				limit?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ConversationsResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ConversationsResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"portalAIConversationsList",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ConversationsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ConversationsResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ConversationsResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalAIConversationsDelete = (wretch: Http) => {
	const mutation = async (params: { conversationId: string }) => {
		const args = z
			.object({
				conversationId: z
					.string()
					.regex(
						new RegExp(
							"[0-9a-f]{8}-[0-9a-f]{4}-[13-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}",
						),
					),
			})
			.parse(params);
		const { conversationId } = args;

		try {
			const res = await wretch
				.url(`/portal/me/ai/apps/scout-search/conversations/${conversationId}`)
				.delete()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 403: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ conversationId: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ conversationId: string }
			>(mutation, options || {});
		},
	};
};

const portalAIConversationsPatch = (wretch: Http) => {
	const mutation = async (params: {
		conversationId: string;
		body: portalAIConversationsPatchRequestBody;
	}) => {
		const args = z
			.object({
				conversationId: z
					.string()
					.regex(
						new RegExp(
							"[0-9a-f]{8}-[0-9a-f]{4}-[13-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}",
						),
					),
				body: z.any(),
			})
			.parse(params);
		const { conversationId, body } = args;

		try {
			const res = await wretch
				.url(`/portal/me/ai/apps/scout-search/conversations/${conversationId}`)
				.json(body)
				.patch()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 400: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
				case 409: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{
						conversationId: string;
						body: portalAIConversationsPatchRequestBody;
					}
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ conversationId: string; body: portalAIConversationsPatchRequestBody }
			>(mutation, options || {});
		},
	};
};

const portalAIPromptCardsList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<portalAIPromptCardsListResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/ai/apps/scout-search/prompt-cards")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = portalAIPromptCardsListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 403: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<portalAIPromptCardsListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<portalAIPromptCardsListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["portalAIPromptCardsList", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<portalAIPromptCardsListResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<portalAIPromptCardsListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<portalAIPromptCardsListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<portalAIPromptCardsListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<portalAIPromptCardsListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["portalAIPromptCardsList", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<portalAIPromptCardsListResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<portalAIPromptCardsListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<portalAIPromptCardsListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalAIPutExecutionFeedback = (wretch: Http) => {
	const mutation = async (params: {
		executionId: string;
		body: portalAIPutExecutionFeedback_Body;
	}) => {
		const args = z
			.object({
				executionId: z
					.string()
					.regex(
						new RegExp(
							"[0-9a-f]{8}-[0-9a-f]{4}-[13-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}",
						),
					),
				body: z.any(),
			})
			.parse(params);
		const { executionId, body } = args;

		try {
			const res = await wretch
				.url(
					`/portal/me/ai/apps/scout-search/executions/${executionId}/feedback`,
				)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FeedbackResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 400: {
					break;
				}
				case 403: {
					break;
				}
				case 404: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FeedbackResponse> | undefined,
					GenericWretchError<null>,
					{ executionId: string; body: portalAIPutExecutionFeedback_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FeedbackResponse> | undefined,
				GenericWretchError<null>,
				{ executionId: string; body: portalAIPutExecutionFeedback_Body }
			>(mutation, options || {});
		},
	};
};

const getLeagues = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { countries?: string[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getLeaguesResponse200>> => {
		const args = z
			.object({ countries: z.array(z.string()).optional() })
			.parse(params);
		const { countries } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/national-leagues")
				.query(qs.stringify({ countries }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getLeaguesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { countries?: string[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getLeaguesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getLeaguesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getLeagues", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLeaguesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getLeaguesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getLeaguesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { countries?: string[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getLeaguesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getLeaguesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getLeagues", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLeaguesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getLeaguesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getLeaguesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { countries?: string[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubsResponse200>> => {
		const args = z
			.object({ countries: z.array(z.string()).optional() })
			.parse(params);
		const { countries } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/national-leagues/clubs")
				.query(qs.stringify({ countries }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { countries?: string[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { countries?: string[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNationalLeagueGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			countries?: CountriesLeagueRequest[];
			startDate: string;
			endDate: string;
			club: string;
			season: string;
			page: number;
			limit: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamesResponse>> => {
		const args = z
			.object({
				countries: z.array(z.any()).optional(),
				startDate: z.string(),
				endDate: z.string(),
				club: z.string(),
				season: z.string(),
				page: z.number().int(),
				limit: z.number().int(),
			})
			.parse(params);
		const { countries, startDate, endDate, club, season, page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/national-leagues/games")
				.query(
					qs.stringify({
						countries,
						startDate,
						endDate,
						club,
						season,
						page,
						limit,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				countries?: CountriesLeagueRequest[];
				startDate: string;
				endDate: string;
				club: string;
				season: string;
				page: number;
				limit: number;
			},
			options?: Omit<
				UseQueryOptions<ApiResponse<GamesResponse>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<GamesResponse>, GenericWretchError<null>> => {
			const key = [
				"getNationalLeagueGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				countries?: CountriesLeagueRequest[];
				startDate: string;
				endDate: string;
				club: string;
				season: string;
				page: number;
				limit: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamesResponse>,
			GenericWretchError<null>
		> => {
			const key = [
				"getNationalLeagueGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getLeagueCountries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getLeagueCountriesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/national-leagues/countries")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getLeagueCountriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getLeagueCountriesResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getLeagueCountriesResponse200>,
					GenericWretchError<getLeagueCountriesResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getLeagueCountriesResponse200>,
			GenericWretchError<getLeagueCountriesResponse400>
		> => {
			const key = ["getLeagueCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLeagueCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getLeagueCountriesResponse200>,
				GenericWretchError<getLeagueCountriesResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getLeagueCountriesResponse200>,
				GenericWretchError<getLeagueCountriesResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getLeagueCountriesResponse200>,
					GenericWretchError<getLeagueCountriesResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getLeagueCountriesResponse200>,
			GenericWretchError<getLeagueCountriesResponse400>
		> => {
			const key = ["getLeagueCountries", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLeagueCountriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getLeagueCountriesResponse200>,
				GenericWretchError<getLeagueCountriesResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getLeagueCountriesResponse200>,
				GenericWretchError<getLeagueCountriesResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPlayerGrading = (wretch: Http) => {
	const mutation = async (params: {
		gameId: string;
		gamePlayerId: string;
		body: PlayerGradingPostRequest;
	}) => {
		const args = z
			.object({
				gameId: z.string(),
				gamePlayerId: z.string().regex(new RegExp("\\d+")),
				body: z.any(),
			})
			.parse(params);
		const { gameId, gamePlayerId, body } = args;

		try {
			const res = await wretch
				.url(`/${gameId}/player-grading/${gamePlayerId}`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 201: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPlayerGradingResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerGradingResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						postPlayerGradingResponse400 | postPlayerGradingResponse403
					>,
					{
						gameId: string;
						gamePlayerId: string;
						body: PlayerGradingPostRequest;
					}
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					postPlayerGradingResponse400 | postPlayerGradingResponse403
				>,
				{ gameId: string; gamePlayerId: string; body: PlayerGradingPostRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerGrading = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { playerGradingId: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerGradingDetailResponseDTO>> => {
		const args = z
			.object({ playerGradingId: z.string().regex(new RegExp("\\d+")) })
			.parse(params);
		const { playerGradingId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-grading/${playerGradingId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerGradingDetailResponseDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getPlayerGradingResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { playerGradingId: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerGradingDetailResponseDTO>,
					GenericWretchError<getPlayerGradingResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerGradingDetailResponseDTO>,
			GenericWretchError<getPlayerGradingResponse403>
		> => {
			const key = ["getPlayerGrading", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerGradingDetailResponseDTO>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerGradingDetailResponseDTO>,
				GenericWretchError<getPlayerGradingResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerGradingDetailResponseDTO>,
				GenericWretchError<getPlayerGradingResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { playerGradingId: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerGradingDetailResponseDTO>,
					GenericWretchError<getPlayerGradingResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerGradingDetailResponseDTO>,
			GenericWretchError<getPlayerGradingResponse403>
		> => {
			const key = ["getPlayerGrading", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerGradingDetailResponseDTO>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerGradingDetailResponseDTO>,
				GenericWretchError<getPlayerGradingResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerGradingDetailResponseDTO>,
				GenericWretchError<getPlayerGradingResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const putPlayerGrading = (wretch: Http) => {
	const mutation = async (params: {
		playerGradingId: string;
		body: PlayerGradingPutRequest;
	}) => {
		const args = z
			.object({
				playerGradingId: z.string().regex(new RegExp("\\d+")),
				body: z.any(),
			})
			.parse(params);
		const { playerGradingId, body } = args;

		try {
			const res = await wretch
				.url(`/player-grading/${playerGradingId}`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = putPlayerGradingResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = putPlayerGradingResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						putPlayerGradingResponse400 | putPlayerGradingResponse403
					>,
					{ playerGradingId: string; body: PlayerGradingPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					putPlayerGradingResponse400 | putPlayerGradingResponse403
				>,
				{ playerGradingId: string; body: PlayerGradingPutRequest }
			>(mutation, options || {});
		},
	};
};

const deletePlayerGrading = (wretch: Http) => {
	const mutation = async (params: { playerGradingId: string }) => {
		const args = z
			.object({ playerGradingId: z.string().regex(new RegExp("\\d+")) })
			.parse(params);
		const { playerGradingId } = args;

		try {
			const res = await wretch
				.url(`/player-grading/${playerGradingId}`)
				.delete()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deletePlayerGradingResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<deletePlayerGradingResponse403>,
					{ playerGradingId: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<deletePlayerGradingResponse403>,
				{ playerGradingId: string }
			>(mutation, options || {});
		},
	};
};

const getPlayerGradingNotes = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			playerId: string;
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerGradingNotesResponseDTO>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum(["performanceGrade", "potentialGrade", "contributor"])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.array(z.string()),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z.literal("gradedAt").describe("Sorting field").optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
				playerId: z.string().regex(new RegExp("\\d+")),
			})
			.parse(params);
		const { playerId, page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-grading/${playerId}/notes`)
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerGradingNotesResponseDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getPlayerGradingNotesResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerGradingNotesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				playerId: string;
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerGradingNotesResponseDTO>,
					GenericWretchError<
						getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerGradingNotesResponseDTO>,
			GenericWretchError<
				getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
			>
		> => {
			const key = [
				"getPlayerGradingNotes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerGradingNotesResponseDTO>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerGradingNotesResponseDTO>,
				GenericWretchError<
					getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerGradingNotesResponseDTO>,
				GenericWretchError<
					getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				playerId: string;
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerGradingNotesResponseDTO>,
					GenericWretchError<
						getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerGradingNotesResponseDTO>,
			GenericWretchError<
				getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
			>
		> => {
			const key = [
				"getPlayerGradingNotes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerGradingNotesResponseDTO>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerGradingNotesResponseDTO>,
				GenericWretchError<
					getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerGradingNotesResponseDTO>,
				GenericWretchError<
					getPlayerGradingNotesResponse400 | getPlayerGradingNotesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerGradingContributors = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerGradingContributorsResponseDTO>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/player-grading/contributors/filter-list")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerGradingContributorsResponseDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getPlayerGradingContributorsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerGradingContributorsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerGradingContributorsResponseDTO>,
					GenericWretchError<
						| getPlayerGradingContributorsResponse400
						| getPlayerGradingContributorsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerGradingContributorsResponseDTO>,
			GenericWretchError<
				| getPlayerGradingContributorsResponse400
				| getPlayerGradingContributorsResponse403
			>
		> => {
			const key = [
				"getPlayerGradingContributors",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerGradingContributorsResponseDTO>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerGradingContributorsResponseDTO>,
				GenericWretchError<
					| getPlayerGradingContributorsResponse400
					| getPlayerGradingContributorsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerGradingContributorsResponseDTO>,
				GenericWretchError<
					| getPlayerGradingContributorsResponse400
					| getPlayerGradingContributorsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerGradingContributorsResponseDTO>,
					GenericWretchError<
						| getPlayerGradingContributorsResponse400
						| getPlayerGradingContributorsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerGradingContributorsResponseDTO>,
			GenericWretchError<
				| getPlayerGradingContributorsResponse400
				| getPlayerGradingContributorsResponse403
			>
		> => {
			const key = [
				"getPlayerGradingContributors",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerGradingContributorsResponseDTO>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerGradingContributorsResponseDTO>,
				GenericWretchError<
					| getPlayerGradingContributorsResponse400
					| getPlayerGradingContributorsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerGradingContributorsResponseDTO>,
				GenericWretchError<
					| getPlayerGradingContributorsResponse400
					| getPlayerGradingContributorsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPinMyPlayer = (wretch: Http) => {
	const mutation = async (params: { playerId: string }) => {
		const args = z
			.object({ playerId: z.string().regex(new RegExp("\\d+")) })
			.parse(params);
		const { playerId } = args;

		try {
			const res = await wretch.url(`/my-players/${playerId}/pin`).post().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPinMyPlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postPinMyPlayerResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						postPinMyPlayerResponse400 | postPinMyPlayerResponse403
					>,
					{ playerId: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					postPinMyPlayerResponse400 | postPinMyPlayerResponse403
				>,
				{ playerId: string }
			>(mutation, options || {});
		},
	};
};

const deletePinMyPlayer = (wretch: Http) => {
	const mutation = async (params: { playerId: string }) => {
		const args = z
			.object({ playerId: z.string().regex(new RegExp("\\d+")) })
			.parse(params);
		const { playerId } = args;

		try {
			const res = await wretch
				.url(`/my-players/${playerId}/pin`)
				.delete()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deletePinMyPlayerResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deletePinMyPlayerResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						deletePinMyPlayerResponse400 | deletePinMyPlayerResponse403
					>,
					{ playerId: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					deletePinMyPlayerResponse400 | deletePinMyPlayerResponse403
				>,
				{ playerId: string }
			>(mutation, options || {});
		},
	};
};

const getPlayerProfileHeader = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileHeaderResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/header`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileHeaderResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileHeaderResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileHeaderResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileHeaderResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileHeaderResponse403
						| getPlayerProfileHeaderResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileHeaderResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileHeaderResponse403
				| getPlayerProfileHeaderResponse404
			>
		> => {
			const key = [
				"getPlayerProfileHeader",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileHeaderResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileHeaderResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileHeaderResponse403
					| getPlayerProfileHeaderResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileHeaderResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileHeaderResponse403
					| getPlayerProfileHeaderResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileHeaderResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileHeaderResponse403
						| getPlayerProfileHeaderResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileHeaderResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileHeaderResponse403
				| getPlayerProfileHeaderResponse404
			>
		> => {
			const key = [
				"getPlayerProfileHeader",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileHeaderResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileHeaderResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileHeaderResponse403
					| getPlayerProfileHeaderResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileHeaderResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileHeaderResponse403
					| getPlayerProfileHeaderResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileAverageRating = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; numberOfGames?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileAverageRatingResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				numberOfGames: z
					.union([z.literal(1), z.literal(5), z.literal(10)])
					.nullable()
					.optional(),
			})
			.parse(params);
		const { id, numberOfGames } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/average-rating`)
				.query(qs.stringify({ numberOfGames }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileAverageRatingResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileAverageRatingResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileAverageRatingResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; numberOfGames?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileAverageRatingResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileAverageRatingResponse403
						| getPlayerProfileAverageRatingResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileAverageRatingResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileAverageRatingResponse403
				| getPlayerProfileAverageRatingResponse404
			>
		> => {
			const key = [
				"getPlayerProfileAverageRating",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileAverageRatingResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileAverageRatingResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileAverageRatingResponse403
					| getPlayerProfileAverageRatingResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileAverageRatingResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileAverageRatingResponse403
					| getPlayerProfileAverageRatingResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; numberOfGames?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileAverageRatingResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileAverageRatingResponse403
						| getPlayerProfileAverageRatingResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileAverageRatingResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileAverageRatingResponse403
				| getPlayerProfileAverageRatingResponse404
			>
		> => {
			const key = [
				"getPlayerProfileAverageRating",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileAverageRatingResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileAverageRatingResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileAverageRatingResponse403
					| getPlayerProfileAverageRatingResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileAverageRatingResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileAverageRatingResponse403
					| getPlayerProfileAverageRatingResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileStandoutMetrics = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; numberOfGames?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileStandoutMetricsResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				numberOfGames: z
					.union([z.literal(1), z.literal(5), z.literal(10)])
					.nullable()
					.optional(),
			})
			.parse(params);
		const { id, numberOfGames } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/standout-metrics`)
				.query(qs.stringify({ numberOfGames }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileStandoutMetricsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileStandoutMetricsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileStandoutMetricsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; numberOfGames?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileStandoutMetricsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileStandoutMetricsResponse403
						| getPlayerProfileStandoutMetricsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileStandoutMetricsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileStandoutMetricsResponse403
				| getPlayerProfileStandoutMetricsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileStandoutMetrics",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileStandoutMetricsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileStandoutMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileStandoutMetricsResponse403
					| getPlayerProfileStandoutMetricsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileStandoutMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileStandoutMetricsResponse403
					| getPlayerProfileStandoutMetricsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; numberOfGames?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileStandoutMetricsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileStandoutMetricsResponse403
						| getPlayerProfileStandoutMetricsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileStandoutMetricsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileStandoutMetricsResponse403
				| getPlayerProfileStandoutMetricsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileStandoutMetrics",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileStandoutMetricsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileStandoutMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileStandoutMetricsResponse403
					| getPlayerProfileStandoutMetricsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileStandoutMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileStandoutMetricsResponse403
					| getPlayerProfileStandoutMetricsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileSeasonalStats = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; season?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileSeasonalStatsResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z.number().int().nullable().optional(),
			})
			.parse(params);
		const { id, season } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/seasonal-stats`)
				.query(qs.stringify({ season }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileSeasonalStatsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileSeasonalStatsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileSeasonalStatsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; season?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileSeasonalStatsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileSeasonalStatsResponse403
						| getPlayerProfileSeasonalStatsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileSeasonalStatsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileSeasonalStatsResponse403
				| getPlayerProfileSeasonalStatsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileSeasonalStats",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileSeasonalStatsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileSeasonalStatsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileSeasonalStatsResponse403
					| getPlayerProfileSeasonalStatsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileSeasonalStatsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileSeasonalStatsResponse403
					| getPlayerProfileSeasonalStatsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; season?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileSeasonalStatsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileSeasonalStatsResponse403
						| getPlayerProfileSeasonalStatsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileSeasonalStatsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileSeasonalStatsResponse403
				| getPlayerProfileSeasonalStatsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileSeasonalStats",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileSeasonalStatsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileSeasonalStatsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileSeasonalStatsResponse403
					| getPlayerProfileSeasonalStatsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileSeasonalStatsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileSeasonalStatsResponse403
					| getPlayerProfileSeasonalStatsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileLatestGames = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; gamesType: string; season?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileLatestGamesResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				gamesType: z.enum(["latest", "best"]).nullable(),
				season: z.number().int().nullable().optional(),
			})
			.parse(params);
		const { id, gamesType, season } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/latest-games`)
				.query(qs.stringify({ gamesType, season }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileLatestGamesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileLatestGamesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileLatestGamesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; gamesType: string; season?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileLatestGamesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileLatestGamesResponse403
						| getPlayerProfileLatestGamesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileLatestGamesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileLatestGamesResponse403
				| getPlayerProfileLatestGamesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileLatestGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileLatestGamesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileLatestGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileLatestGamesResponse403
					| getPlayerProfileLatestGamesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileLatestGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileLatestGamesResponse403
					| getPlayerProfileLatestGamesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; gamesType: string; season?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileLatestGamesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileLatestGamesResponse403
						| getPlayerProfileLatestGamesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileLatestGamesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileLatestGamesResponse403
				| getPlayerProfileLatestGamesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileLatestGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileLatestGamesResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileLatestGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileLatestGamesResponse403
					| getPlayerProfileLatestGamesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileLatestGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileLatestGamesResponse403
					| getPlayerProfileLatestGamesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			gamesType?: string;
			gamesNational?: string;
			season?: number;
			startDate?: string;
			endDate?: string;
			team?: number;
			onlyAnalyzed?: boolean;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileGamesResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				gamesType: z.enum(["latest", "best"]).nullable().optional(),
				gamesNational: z.enum(["club", "national"]).nullable().optional(),
				season: z.number().int().nullable().optional(),
				startDate: z.string().nullable().optional(),
				endDate: z.string().nullable().optional(),
				team: z.number().int().nullable().optional(),
				onlyAnalyzed: z.boolean().nullable().optional(),
			})
			.parse(params);
		const {
			id,
			gamesType,
			gamesNational,
			season,
			startDate,
			endDate,
			team,
			onlyAnalyzed,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/games`)
				.query(
					qs.stringify({
						gamesType,
						gamesNational,
						season,
						startDate,
						endDate,
						team,
						onlyAnalyzed,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileGamesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileGamesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileGamesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				gamesType?: string;
				gamesNational?: string;
				season?: number;
				startDate?: string;
				endDate?: string;
				team?: number;
				onlyAnalyzed?: boolean;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileGamesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileGamesResponse403
						| getPlayerProfileGamesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileGamesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileGamesResponse403
				| getPlayerProfileGamesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileGamesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileGamesResponse403
					| getPlayerProfileGamesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileGamesResponse403
					| getPlayerProfileGamesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				gamesType?: string;
				gamesNational?: string;
				season?: number;
				startDate?: string;
				endDate?: string;
				team?: number;
				onlyAnalyzed?: boolean;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileGamesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileGamesResponse403
						| getPlayerProfileGamesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileGamesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileGamesResponse403
				| getPlayerProfileGamesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileGamesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileGamesResponse403
					| getPlayerProfileGamesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileGamesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileGamesResponse403
					| getPlayerProfileGamesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfilePerformanceRadar = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; position?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfilePerformanceRadarResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				position: z
					.enum([
						"G",
						"DD",
						"DC",
						"DG",
						"MDC",
						"MC",
						"MOC",
						"MG",
						"MD",
						"MR",
						"ATT",
						"AG",
						"AD",
					])
					.nullable()
					.optional(),
			})
			.parse(params);
		const { id, position } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/performance-radar`)
				.query(qs.stringify({ position }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfilePerformanceRadarResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfilePerformanceRadarResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfilePerformanceRadarResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; position?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfilePerformanceRadarResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePerformanceRadarResponse403
						| getPlayerProfilePerformanceRadarResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfilePerformanceRadarResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePerformanceRadarResponse403
				| getPlayerProfilePerformanceRadarResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePerformanceRadar",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePerformanceRadarResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfilePerformanceRadarResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePerformanceRadarResponse403
					| getPlayerProfilePerformanceRadarResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfilePerformanceRadarResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePerformanceRadarResponse403
					| getPlayerProfilePerformanceRadarResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; position?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfilePerformanceRadarResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePerformanceRadarResponse403
						| getPlayerProfilePerformanceRadarResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfilePerformanceRadarResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePerformanceRadarResponse403
				| getPlayerProfilePerformanceRadarResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePerformanceRadar",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePerformanceRadarResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfilePerformanceRadarResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePerformanceRadarResponse403
					| getPlayerProfilePerformanceRadarResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfilePerformanceRadarResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePerformanceRadarResponse403
					| getPlayerProfilePerformanceRadarResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileClubHistory = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileClubHistoryResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/club-history`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileClubHistoryResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileClubHistoryResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileClubHistoryResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileClubHistoryResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileClubHistoryResponse403
						| getPlayerProfileClubHistoryResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileClubHistoryResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileClubHistoryResponse403
				| getPlayerProfileClubHistoryResponse404
			>
		> => {
			const key = [
				"getPlayerProfileClubHistory",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileClubHistoryResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileClubHistoryResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileClubHistoryResponse403
					| getPlayerProfileClubHistoryResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileClubHistoryResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileClubHistoryResponse403
					| getPlayerProfileClubHistoryResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileClubHistoryResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileClubHistoryResponse403
						| getPlayerProfileClubHistoryResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileClubHistoryResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileClubHistoryResponse403
				| getPlayerProfileClubHistoryResponse404
			>
		> => {
			const key = [
				"getPlayerProfileClubHistory",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileClubHistoryResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileClubHistoryResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileClubHistoryResponse403
					| getPlayerProfileClubHistoryResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileClubHistoryResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileClubHistoryResponse403
					| getPlayerProfileClubHistoryResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfilePerformance = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			id: number;
			position?: string;
			numberOfGames?: number;
			season?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfilePerformance>> => {
		const args = z
			.object({
				id: z.number().int(),
				position: z
					.enum([
						"G",
						"DD",
						"DC",
						"DG",
						"MDC",
						"MC",
						"MOC",
						"MG",
						"MD",
						"MR",
						"ATT",
						"AG",
						"AD",
					])
					.nullable()
					.optional(),
				numberOfGames: z
					.union([z.literal(1), z.literal(5), z.literal(10)])
					.nullable()
					.optional(),
				season: z.number().int().nullable().optional(),
			})
			.parse(params);
		const { id, position, numberOfGames, season } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/performance`)
				.query(qs.stringify({ position, numberOfGames, season }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfilePerformance.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfilePerformanceResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				position?: string;
				numberOfGames?: number;
				season?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfilePerformance>,
					GenericWretchError<
						| StatusMessageResponse
						| StatusMessageResponse
						| getPlayerProfilePerformanceResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfilePerformance>,
			GenericWretchError<
				| StatusMessageResponse
				| StatusMessageResponse
				| getPlayerProfilePerformanceResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePerformance",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfilePerformance>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfilePerformance>,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| getPlayerProfilePerformanceResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfilePerformance>,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| getPlayerProfilePerformanceResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				position?: string;
				numberOfGames?: number;
				season?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfilePerformance>,
					GenericWretchError<
						| StatusMessageResponse
						| StatusMessageResponse
						| getPlayerProfilePerformanceResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfilePerformance>,
			GenericWretchError<
				| StatusMessageResponse
				| StatusMessageResponse
				| getPlayerProfilePerformanceResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePerformance",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfilePerformance>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfilePerformance>,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| getPlayerProfilePerformanceResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfilePerformance>,
				GenericWretchError<
					| StatusMessageResponse
					| StatusMessageResponse
					| getPlayerProfilePerformanceResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileEventTypes = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			season?: number;
			startDate?: string;
			endDate?: string;
			ageGroup?: string[];
			club?: number[];
			position?: string[];
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<EventTypes>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z.number().int().nullable().optional(),
				startDate: z.string().nullable().optional(),
				endDate: z.string().nullable().optional(),
				ageGroup: z.array(z.string()).optional(),
				club: z.array(z.number()).optional(),
				position: z.array(z.string()).optional(),
			})
			.parse(params);
		const { id, season, startDate, endDate, ageGroup, club, position } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/event-types`)
				.query(
					qs.stringify({
						season,
						startDate,
						endDate,
						ageGroup,
						club,
						position,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = EventTypes.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileEventTypesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileEventTypesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<EventTypes>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventTypesResponse403
						| getPlayerProfileEventTypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<EventTypes>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventTypesResponse403
				| getPlayerProfileEventTypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventTypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventTypes>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<EventTypes>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventTypesResponse403
						| getPlayerProfileEventTypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<EventTypes>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventTypesResponse403
				| getPlayerProfileEventTypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventTypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventTypes>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<EventTypes>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventTypesResponse403
					| getPlayerProfileEventTypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileEventFilters = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<EventFilters>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/event-filters`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = EventFilters.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileEventFiltersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileEventFiltersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<EventFilters>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventFiltersResponse403
						| getPlayerProfileEventFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<EventFilters>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventFiltersResponse403
				| getPlayerProfileEventFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventFilters>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<EventFilters>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventFiltersResponse403
						| getPlayerProfileEventFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<EventFilters>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventFiltersResponse403
				| getPlayerProfileEventFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEventFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<EventFilters>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<EventFilters>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventFiltersResponse403
					| getPlayerProfileEventFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileEvents = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			season?: number;
			startDate?: string;
			endDate?: string;
			ageGroup?: string[];
			club?: number[];
			position?: string[];
			outcome?: boolean;
			eventTypes?: string[];
			excludeEventIds?: number[];
			page?: number;
			limit?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<Events>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z.number().int().nullable().optional(),
				startDate: z.string().nullable().optional(),
				endDate: z.string().nullable().optional(),
				ageGroup: z.array(z.string()).optional(),
				club: z.array(z.number()).optional(),
				position: z.array(z.string()).optional(),
				outcome: z.boolean().nullable().optional(),
				eventTypes: z.array(z.string()).nullable().optional(),
				excludeEventIds: z
					.array(z.number())
					.nullable()
					.describe("Event ids to leave out of the list and the total")
					.optional(),
				page: z.number().int().gte(1).default(1),
				limit: z.number().int().gte(1).lte(100).default(15),
			})
			.parse(params);
		const {
			id,
			season,
			startDate,
			endDate,
			ageGroup,
			club,
			position,
			outcome,
			eventTypes,
			excludeEventIds,
			page,
			limit,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/events`)
				.query(
					qs.stringify({
						season,
						startDate,
						endDate,
						ageGroup,
						club,
						position,
						outcome,
						eventTypes,
						excludeEventIds,
						page,
						limit,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = Events.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileEventsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileEventsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
				outcome?: boolean;
				eventTypes?: string[];
				excludeEventIds?: number[];
				page?: number;
				limit?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<Events>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventsResponse403
						| getPlayerProfileEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<Events>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventsResponse403
				| getPlayerProfileEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<Events>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				season?: number;
				startDate?: string;
				endDate?: string;
				ageGroup?: string[];
				club?: number[];
				position?: string[];
				outcome?: boolean;
				eventTypes?: string[];
				excludeEventIds?: number[];
				page?: number;
				limit?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<Events>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileEventsResponse403
						| getPlayerProfileEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<Events>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileEventsResponse403
				| getPlayerProfileEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<Events>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<Events>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileEventsResponse403
					| getPlayerProfileEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileKeyEvents = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<KeyEvents>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/key-events`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = KeyEvents.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileKeyEventsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileKeyEventsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<KeyEvents>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileKeyEventsResponse403
						| getPlayerProfileKeyEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<KeyEvents>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileKeyEventsResponse403
				| getPlayerProfileKeyEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileKeyEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<KeyEvents>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<KeyEvents>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileKeyEventsResponse403
						| getPlayerProfileKeyEventsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<KeyEvents>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileKeyEventsResponse403
				| getPlayerProfileKeyEventsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileKeyEvents",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<KeyEvents>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<KeyEvents>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileKeyEventsResponse403
					| getPlayerProfileKeyEventsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPlayerProfileCrossGameClip = (wretch: Http) => {
	const mutation = async (params: {
		body: PlayerProfileCrossGameClipPostRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/player-profile/cross-game-clip")
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPlayerProfileCrossGameClipResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerProfileCrossGameClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postPlayerProfileCrossGameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| postPlayerProfileCrossGameClipResponse400
						| postPlayerProfileCrossGameClipResponse403
						| postPlayerProfileCrossGameClipResponse404
					>,
					{ body: PlayerProfileCrossGameClipPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| postPlayerProfileCrossGameClipResponse400
					| postPlayerProfileCrossGameClipResponse403
					| postPlayerProfileCrossGameClipResponse404
				>,
				{ body: PlayerProfileCrossGameClipPostRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerProfileShadowTeamList = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; search?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ShadowTeamList>> => {
		const args = z
			.object({
				id: z.number().int(),
				search: z.string().nullable().default(null),
			})
			.parse(params);
		const { id, search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/shadow-team-list`)
				.query(qs.stringify({ search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ShadowTeamList.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileShadowTeamListResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileShadowTeamListResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; search?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ShadowTeamList>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileShadowTeamListResponse403
						| getPlayerProfileShadowTeamListResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ShadowTeamList>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileShadowTeamListResponse403
				| getPlayerProfileShadowTeamListResponse404
			>
		> => {
			const key = [
				"getPlayerProfileShadowTeamList",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ShadowTeamList>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; search?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ShadowTeamList>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileShadowTeamListResponse403
						| getPlayerProfileShadowTeamListResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ShadowTeamList>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileShadowTeamListResponse403
				| getPlayerProfileShadowTeamListResponse404
			>
		> => {
			const key = [
				"getPlayerProfileShadowTeamList",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ShadowTeamList>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ShadowTeamList>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileShadowTeamListResponse403
					| getPlayerProfileShadowTeamListResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postPlayerProfileShadowTeamPlayer = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: ShadowTeamPlayerPostRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/player-profile/${id}/shadow-team-player`)
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerProfileShadowTeamPlayerResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postPlayerProfileShadowTeamPlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| StatusMessageResponse
						| postPlayerProfileShadowTeamPlayerResponse403
						| postPlayerProfileShadowTeamPlayerResponse404
					>,
					{ id: number; body: ShadowTeamPlayerPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| StatusMessageResponse
					| postPlayerProfileShadowTeamPlayerResponse403
					| postPlayerProfileShadowTeamPlayerResponse404
				>,
				{ id: number; body: ShadowTeamPlayerPostRequest }
			>(mutation, options || {});
		},
	};
};

const postPlayerProfileShadowTeam = (wretch: Http) => {
	const mutation = async (params: { body: ShadowTeamPostRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/player-profile/shadow-team")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data = postPlayerProfileShadowTeamResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postPlayerProfileShadowTeamResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = postPlayerProfileShadowTeamResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<postPlayerProfileShadowTeamResponse201> | undefined,
					GenericWretchError<
						| postPlayerProfileShadowTeamResponse400
						| StatusMessageResponse
						| postPlayerProfileShadowTeamResponse403
					>,
					{ body: ShadowTeamPostRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<postPlayerProfileShadowTeamResponse201> | undefined,
				GenericWretchError<
					| postPlayerProfileShadowTeamResponse400
					| StatusMessageResponse
					| postPlayerProfileShadowTeamResponse403
				>,
				{ body: ShadowTeamPostRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerProfileRecruitmentInformation = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileRecruitmentInformationResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/recruitment-information`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileRecruitmentInformationResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileRecruitmentInformationResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileRecruitmentInformationResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileRecruitmentInformationResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileRecruitmentInformationResponse403
						| getPlayerProfileRecruitmentInformationResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileRecruitmentInformationResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileRecruitmentInformationResponse403
				| getPlayerProfileRecruitmentInformationResponse404
			>
		> => {
			const key = [
				"getPlayerProfileRecruitmentInformation",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileRecruitmentInformationResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileRecruitmentInformationResponse403
						| getPlayerProfileRecruitmentInformationResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileRecruitmentInformationResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileRecruitmentInformationResponse403
				| getPlayerProfileRecruitmentInformationResponse404
			>
		> => {
			const key = [
				"getPlayerProfileRecruitmentInformation",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileRecruitmentInformationResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileRecruitmentInformationResponse403
					| getPlayerProfileRecruitmentInformationResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBiometricsAvailability = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBiometricsAvailabilityResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/biometrics-availability`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBiometricsAvailabilityResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBiometricsAvailabilityResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBiometricsAvailabilityResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBiometricsAvailabilityResponse403
						| getPlayerProfileBiometricsAvailabilityResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBiometricsAvailabilityResponse403
				| getPlayerProfileBiometricsAvailabilityResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBiometricsAvailability",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBiometricsAvailabilityResponse403
						| getPlayerProfileBiometricsAvailabilityResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBiometricsAvailabilityResponse403
				| getPlayerProfileBiometricsAvailabilityResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBiometricsAvailability",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBiometricsAvailabilityResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBiometricsAvailabilityResponse403
					| getPlayerProfileBiometricsAvailabilityResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBodyMeasurements = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBodyMeasurementsResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/body-measurements`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBodyMeasurementsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileBodyMeasurementsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileBodyMeasurementsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBodyMeasurementsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMeasurementsResponse403
						| getPlayerProfileBodyMeasurementsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBodyMeasurementsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMeasurementsResponse403
				| getPlayerProfileBodyMeasurementsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMeasurements",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBodyMeasurementsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMeasurementsResponse403
						| getPlayerProfileBodyMeasurementsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBodyMeasurementsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMeasurementsResponse403
				| getPlayerProfileBodyMeasurementsResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMeasurements",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBodyMeasurementsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMeasurementsResponse403
					| getPlayerProfileBodyMeasurementsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBodyMetricTrend = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; metric?: string; page?: number; limit?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBodyMetricTrendResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				metric: z
					.enum(["weight", "height", "wingspan", "bmi", "bri"])
					.nullable()
					.optional(),
				page: z
					.number()
					.int()
					.gte(1)
					.describe(
						"Page 1 holds the newest scans, the last page the oldest ones.",
					)
					.default(1),
				limit: z.number().int().gte(1).describe("Scans per page.").default(10),
			})
			.parse(params);
		const { id, metric, page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/body-metric-trend`)
				.query(qs.stringify({ metric, page, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBodyMetricTrendResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfileBodyMetricTrendResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfileBodyMetricTrendResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; metric?: string; page?: number; limit?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBodyMetricTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMetricTrendResponse403
						| getPlayerProfileBodyMetricTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBodyMetricTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMetricTrendResponse403
				| getPlayerProfileBodyMetricTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMetricTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; metric?: string; page?: number; limit?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBodyMetricTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyMetricTrendResponse403
						| getPlayerProfileBodyMetricTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBodyMetricTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyMetricTrendResponse403
				| getPlayerProfileBodyMetricTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyMetricTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBodyMetricTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyMetricTrendResponse403
					| getPlayerProfileBodyMetricTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBodyCompositionChange = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; firstScanId?: string; secondScanId?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBodyCompositionChangeResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				firstScanId: z.string().nullable().optional(),
				secondScanId: z.string().nullable().optional(),
			})
			.parse(params);
		const { id, firstScanId, secondScanId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/body-composition-change`)
				.query(qs.stringify({ firstScanId, secondScanId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBodyCompositionChangeResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBodyCompositionChangeResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBodyCompositionChangeResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; firstScanId?: string; secondScanId?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyCompositionChangeResponse403
						| getPlayerProfileBodyCompositionChangeResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyCompositionChangeResponse403
				| getPlayerProfileBodyCompositionChangeResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyCompositionChange",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; firstScanId?: string; secondScanId?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBodyCompositionChangeResponse403
						| getPlayerProfileBodyCompositionChangeResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBodyCompositionChangeResponse403
				| getPlayerProfileBodyCompositionChangeResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBodyCompositionChange",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBodyCompositionChangeResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBodyCompositionChangeResponse403
					| getPlayerProfileBodyCompositionChangeResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfilePhysicalMetricsFilters = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/physical-metrics-filters`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfilePhysicalMetricsFiltersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfilePhysicalMetricsFiltersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfilePhysicalMetricsFiltersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsFiltersResponse403
						| getPlayerProfilePhysicalMetricsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsFiltersResponse403
				| getPlayerProfilePhysicalMetricsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetricsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsFiltersResponse403
						| getPlayerProfilePhysicalMetricsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsFiltersResponse403
				| getPlayerProfilePhysicalMetricsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetricsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsFiltersResponse403
					| getPlayerProfilePhysicalMetricsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfilePhysicalMetrics = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; season?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfilePhysicalMetricsResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				season: z
					.number()
					.int()
					.nullable()
					.describe(
						"Season to aggregate. Omit to aggregate across all seasons.",
					)
					.optional(),
			})
			.parse(params);
		const { id, season } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/physical-metrics`)
				.query(qs.stringify({ season }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfilePhysicalMetricsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerProfilePhysicalMetricsResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerProfilePhysicalMetricsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; season?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsResponse403
						| getPlayerProfilePhysicalMetricsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsResponse403
				| getPlayerProfilePhysicalMetricsResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetrics",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; season?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfilePhysicalMetricsResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfilePhysicalMetricsResponse403
						| getPlayerProfilePhysicalMetricsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfilePhysicalMetricsResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfilePhysicalMetricsResponse403
				| getPlayerProfilePhysicalMetricsResponse404
			>
		> => {
			const key = [
				"getPlayerProfilePhysicalMetrics",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfilePhysicalMetricsResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfilePhysicalMetricsResponse403
					| getPlayerProfilePhysicalMetricsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralSignalsFilters = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-signals-filters`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						PlayerProfileBehavioralSignalsFiltersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralSignalsFiltersResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralSignalsFiltersResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsFiltersResponse403
						| getPlayerProfileBehavioralSignalsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsFiltersResponse403
				| getPlayerProfileBehavioralSignalsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsFiltersResponse403
						| getPlayerProfileBehavioralSignalsFiltersResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsFiltersResponse403
				| getPlayerProfileBehavioralSignalsFiltersResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsFilters",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsFiltersResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsFiltersResponse403
					| getPlayerProfileBehavioralSignalsFiltersResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralSignalsTrend = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			id: number;
			period?: string;
			season?: number;
			signalType?: string;
			cohort?: string;
			position?: string;
			ageCategory?: string;
			page?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				period: z
					.enum(["last10Games", "season"])
					.describe("Selected period. Defaults to the last games period.")
					.optional(),
				season: z
					.number()
					.int()
					.nullable()
					.describe(
						"Season to display. Only used with the season period; omit to aggregate all seasons.",
					)
					.optional(),
				signalType: z
					.enum(["sensitivity", "tolerance", "recovery"])
					.nullable()
					.describe(
						"Single signal tab. Omit for the overview tab showing all three signals.",
					)
					.optional(),
				cohort: z
					.enum(["ageGroup", "ageGroupPosition", "position"])
					.nullable()
					.describe("Cohort")
					.optional(),
				position: z
					.string()
					.nullable()
					.describe("Restricts the trend to the games played in this position.")
					.optional(),
				ageCategory: z
					.string()
					.nullable()
					.describe(
						"Restricts the trend to the games played in this age category.",
					)
					.optional(),
				page: z.number().int().describe("page").default(1),
			})
			.parse(params);
		const {
			id,
			period,
			season,
			signalType,
			cohort,
			position,
			ageCategory,
			page,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-signals-trend`)
				.query(
					qs.stringify({
						period,
						season,
						signalType,
						cohort,
						position,
						ageCategory,
						page,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileBehavioralSignalsTrendResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralSignalsTrendResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralSignalsTrendResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				period?: string;
				season?: number;
				signalType?: string;
				cohort?: string;
				position?: string;
				ageCategory?: string;
				page?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsTrendResponse403
						| getPlayerProfileBehavioralSignalsTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsTrendResponse403
				| getPlayerProfileBehavioralSignalsTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				period?: string;
				season?: number;
				signalType?: string;
				cohort?: string;
				position?: string;
				ageCategory?: string;
				page?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsTrendResponse403
						| getPlayerProfileBehavioralSignalsTrendResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsTrendResponse403
				| getPlayerProfileBehavioralSignalsTrendResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsTrend",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsTrendResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsTrendResponse403
					| getPlayerProfileBehavioralSignalsTrendResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralSignalsSnapshot = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number; period?: string; seasons?: number[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				period: z
					.enum(["last10Games", "season"])
					.describe("Selected period. Defaults to the season period.")
					.optional(),
				seasons: z.array(z.number()).max(3).describe("Seasons").optional(),
			})
			.parse(params);
		const { id, period, seasons } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-signals-snapshot`)
				.query(qs.stringify({ period, seasons }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data =
						PlayerProfileBehavioralSignalsSnapshotResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralSignalsSnapshotResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralSignalsSnapshotResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; period?: string; seasons?: number[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsSnapshotResponse403
						| getPlayerProfileBehavioralSignalsSnapshotResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsSnapshotResponse403
				| getPlayerProfileBehavioralSignalsSnapshotResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsSnapshot",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; period?: string; seasons?: number[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralSignalsSnapshotResponse403
						| getPlayerProfileBehavioralSignalsSnapshotResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralSignalsSnapshotResponse403
				| getPlayerProfileBehavioralSignalsSnapshotResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralSignalsSnapshot",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileBehavioralSignalsSnapshotResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralSignalsSnapshotResponse403
					| getPlayerProfileBehavioralSignalsSnapshotResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayerProfileBehavioralArchetypes = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerProfileArchetypesResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/player-profile/${id}/behavioral-archetypes`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerProfileArchetypesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = StatusMessageResponse.parse(json);
					break;
				}
				case 403: {
					err.json =
						getPlayerProfileBehavioralArchetypesResponse403.parse(json);
					break;
				}
				case 404: {
					err.json =
						getPlayerProfileBehavioralArchetypesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerProfileArchetypesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralArchetypesResponse403
						| getPlayerProfileBehavioralArchetypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerProfileArchetypesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralArchetypesResponse403
				| getPlayerProfileBehavioralArchetypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralArchetypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileArchetypesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerProfileArchetypesResponse>,
					GenericWretchError<
						| StatusMessageResponse
						| getPlayerProfileBehavioralArchetypesResponse403
						| getPlayerProfileBehavioralArchetypesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerProfileArchetypesResponse>,
			GenericWretchError<
				| StatusMessageResponse
				| getPlayerProfileBehavioralArchetypesResponse403
				| getPlayerProfileBehavioralArchetypesResponse404
			>
		> => {
			const key = [
				"getPlayerProfileBehavioralArchetypes",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerProfileArchetypesResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerProfileArchetypesResponse>,
				GenericWretchError<
					| StatusMessageResponse
					| getPlayerProfileBehavioralArchetypesResponse403
					| getPlayerProfileBehavioralArchetypesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const jsonLogin = (wretch: Http) => {
	const mutation = async (params: { body: JsonLoginRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/json_login")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<UserResponse> | undefined,
					GenericWretchError<null>,
					{ body: JsonLoginRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<UserResponse> | undefined,
				GenericWretchError<null>,
				{ body: JsonLoginRequest }
			>(mutation, options || {});
		},
	};
};

const portalAccessTokenPost = (wretch: Http) => {
	const mutation = async () => {
		try {
			const res = await wretch.url("/portal/access-token").post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = portalAccessTokenPostResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			switch (err.status) {
				case 401: {
					break;
				}
				case 403: {
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<portalAccessTokenPostResponse200> | undefined,
					GenericWretchError<null>
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<portalAccessTokenPostResponse200> | undefined,
				GenericWretchError<null>
			>(mutation, options || {});
		},
	};
};

const getCurrentUser = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getCurrentUserResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<UserResponse>,
					GenericWretchError<getCurrentUserResponse401>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<UserResponse>,
			GenericWretchError<getCurrentUserResponse401>
		> => {
			const key = ["getCurrentUser", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserResponse>,
				GenericWretchError<getCurrentUserResponse401>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserResponse>,
				GenericWretchError<getCurrentUserResponse401>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserResponse>,
					GenericWretchError<getCurrentUserResponse401>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserResponse>,
			GenericWretchError<getCurrentUserResponse401>
		> => {
			const key = ["getCurrentUser", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserResponse>,
				GenericWretchError<getCurrentUserResponse401>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserResponse>,
				GenericWretchError<getCurrentUserResponse401>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const jsonLogout = (wretch: Http) => {
	const mutation = async () => {
		try {
			const res = await wretch.url("/portal/json_logout").post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = jsonLogoutResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = jsonLogoutResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<jsonLogoutResponse200> | undefined,
					GenericWretchError<jsonLogoutResponse401>,
					void
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<jsonLogoutResponse200> | undefined,
				GenericWretchError<jsonLogoutResponse401>,
				void
			>(mutation, options || {});
		},
	};
};

const changeUserLocale = (wretch: Http) => {
	const mutation = async (params: { body: changeUserLocaleRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/update_locale")
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = changeUserLocaleResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<changeUserLocaleResponse400>,
					{ body: changeUserLocaleRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<changeUserLocaleResponse400>,
				{ body: changeUserLocaleRequestBody }
			>(mutation, options || {});
		},
	};
};

const forgotPassword = (wretch: Http) => {
	const mutation = async (params: { body: forgotPasswordRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/requestPassword")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = forgotPasswordResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = forgotPasswordResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<forgotPasswordResponse200> | undefined,
					GenericWretchError<forgotPasswordResponse404>,
					{ body: forgotPasswordRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<forgotPasswordResponse200> | undefined,
				GenericWretchError<forgotPasswordResponse404>,
				{ body: forgotPasswordRequestBody }
			>(mutation, options || {});
		},
	};
};

const resetPassword = (wretch: Http) => {
	const mutation = async (params: {
		token: string;
		body: resetPasswordRequestBody;
	}) => {
		const args = z.object({ token: z.string(), body: z.any() }).parse(params);
		const { token, body } = args;

		try {
			const res = await wretch
				.url(`/portal/resetPassword/${token}`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = resetPasswordResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = resetPasswordResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<resetPasswordResponse200> | undefined,
					GenericWretchError<resetPasswordResponse404>,
					{ token: string; body: resetPasswordRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<resetPasswordResponse200> | undefined,
				GenericWretchError<resetPasswordResponse404>,
				{ token: string; body: resetPasswordRequestBody }
			>(mutation, options || {});
		},
	};
};

const validateUserPasswordReset = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { token: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<validateUserPasswordResetResponse200>> => {
		const args = z.object({ token: z.string() }).parse(params);
		const { token } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/validateUserPasswordReset/${token}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = validateUserPasswordResetResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = validateUserPasswordResetResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { token: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<validateUserPasswordResetResponse200>,
					GenericWretchError<validateUserPasswordResetResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<validateUserPasswordResetResponse200>,
			GenericWretchError<validateUserPasswordResetResponse403>
		> => {
			const key = [
				"validateUserPasswordReset",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<validateUserPasswordResetResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { token: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<validateUserPasswordResetResponse200>,
					GenericWretchError<validateUserPasswordResetResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<validateUserPasswordResetResponse200>,
			GenericWretchError<validateUserPasswordResetResponse403>
		> => {
			const key = [
				"validateUserPasswordReset",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<validateUserPasswordResetResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<validateUserPasswordResetResponse200>,
				GenericWretchError<validateUserPasswordResetResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSubscriptionCountryMessage = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<SubscriptionCountryData>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/subscriptionCountryMessage")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = SubscriptionCountryData.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getSubscriptionCountryMessageResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<SubscriptionCountryData>,
					GenericWretchError<getSubscriptionCountryMessageResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<SubscriptionCountryData>,
			GenericWretchError<getSubscriptionCountryMessageResponse403>
		> => {
			const key = [
				"getSubscriptionCountryMessage",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<SubscriptionCountryData>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<SubscriptionCountryData>,
				GenericWretchError<getSubscriptionCountryMessageResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<SubscriptionCountryData>,
				GenericWretchError<getSubscriptionCountryMessageResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<SubscriptionCountryData>,
					GenericWretchError<getSubscriptionCountryMessageResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<SubscriptionCountryData>,
			GenericWretchError<getSubscriptionCountryMessageResponse403>
		> => {
			const key = [
				"getSubscriptionCountryMessage",
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<SubscriptionCountryData>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<SubscriptionCountryData>,
				GenericWretchError<getSubscriptionCountryMessageResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<SubscriptionCountryData>,
				GenericWretchError<getSubscriptionCountryMessageResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const removeCountriesSessionDetails = (wretch: Http) => {
	const mutation = async () => {
		try {
			const res = await wretch
				.url("/portal/user/removeCountrySession")
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = removeCountriesSessionDetailsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = removeCountriesSessionDetailsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<removeCountriesSessionDetailsResponse200> | undefined,
					GenericWretchError<removeCountriesSessionDetailsResponse403>,
					void
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<removeCountriesSessionDetailsResponse200> | undefined,
				GenericWretchError<removeCountriesSessionDetailsResponse403>,
				void
			>(mutation, options || {});
		},
	};
};

const playerSearch = (wretch: Http) => {
	const mutation = async (params: { body: PlayerSearchRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/player/search")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerSearchResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = playerSearchResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<PlayerSearchResponse> | undefined,
					GenericWretchError<playerSearchResponse401 | playerSearchResponse403>,
					{ body: PlayerSearchRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<PlayerSearchResponse> | undefined,
				GenericWretchError<playerSearchResponse401 | playerSearchResponse403>,
				{ body: PlayerSearchRequest }
			>(mutation, options || {});
		},
	};
};

const playerSearchPresetColumnGet = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerSearchPresetColumnResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/player/search/presets/columns")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerSearchPresetColumnResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchPresetColumnGetResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerSearchPresetColumnResponse>,
					GenericWretchError<playerSearchPresetColumnGetResponse401>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerSearchPresetColumnResponse>,
			GenericWretchError<playerSearchPresetColumnGetResponse401>
		> => {
			const key = ["playerSearchPresetColumnGet", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<PlayerSearchPresetColumnResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerSearchPresetColumnResponse>,
				GenericWretchError<playerSearchPresetColumnGetResponse401>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerSearchPresetColumnResponse>,
				GenericWretchError<playerSearchPresetColumnGetResponse401>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerSearchPresetColumnResponse>,
					GenericWretchError<playerSearchPresetColumnGetResponse401>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerSearchPresetColumnResponse>,
			GenericWretchError<playerSearchPresetColumnGetResponse401>
		> => {
			const key = ["playerSearchPresetColumnGet", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<PlayerSearchPresetColumnResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerSearchPresetColumnResponse>,
				GenericWretchError<playerSearchPresetColumnGetResponse401>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerSearchPresetColumnResponse>,
				GenericWretchError<playerSearchPresetColumnGetResponse401>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const playerSearchPresetColumnPut = (wretch: Http) => {
	const mutation = async (params: {
		id: string;
		body: PlayerSearchPresetColumnUpsertRequest;
	}) => {
		const args = z.object({ id: z.string(), body: z.any() }).parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/player/search/presets/columns/${id}`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerSearchPresetColumnPutResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchPresetColumnPutResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerSearchPresetColumnPutResponse200> | undefined,
					GenericWretchError<playerSearchPresetColumnPutResponse401>,
					{ id: string; body: PlayerSearchPresetColumnUpsertRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerSearchPresetColumnPutResponse200> | undefined,
				GenericWretchError<playerSearchPresetColumnPutResponse401>,
				{ id: string; body: PlayerSearchPresetColumnUpsertRequest }
			>(mutation, options || {});
		},
	};
};

const playerSearchPresetColumnDelete = (wretch: Http) => {
	const mutation = async (params: { id: string }) => {
		const args = z.object({ id: z.string() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/player/search/presets/columns/${id}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerSearchPresetColumnDeleteResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchPresetColumnDeleteResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerSearchPresetColumnDeleteResponse200> | undefined,
					GenericWretchError<playerSearchPresetColumnDeleteResponse401>,
					{ id: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerSearchPresetColumnDeleteResponse200> | undefined,
				GenericWretchError<playerSearchPresetColumnDeleteResponse401>,
				{ id: string }
			>(mutation, options || {});
		},
	};
};

const playerSearchPresetFilterGet = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerSearchPresetFilterResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/player/search/presets/filters")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerSearchPresetFilterResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchPresetFilterGetResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerSearchPresetFilterResponse>,
					GenericWretchError<playerSearchPresetFilterGetResponse401>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerSearchPresetFilterResponse>,
			GenericWretchError<playerSearchPresetFilterGetResponse401>
		> => {
			const key = ["playerSearchPresetFilterGet", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<PlayerSearchPresetFilterResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerSearchPresetFilterResponse>,
				GenericWretchError<playerSearchPresetFilterGetResponse401>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerSearchPresetFilterResponse>,
				GenericWretchError<playerSearchPresetFilterGetResponse401>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerSearchPresetFilterResponse>,
					GenericWretchError<playerSearchPresetFilterGetResponse401>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerSearchPresetFilterResponse>,
			GenericWretchError<playerSearchPresetFilterGetResponse401>
		> => {
			const key = ["playerSearchPresetFilterGet", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<PlayerSearchPresetFilterResponse>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerSearchPresetFilterResponse>,
				GenericWretchError<playerSearchPresetFilterGetResponse401>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerSearchPresetFilterResponse>,
				GenericWretchError<playerSearchPresetFilterGetResponse401>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const playerSearchPresetFilterPut = (wretch: Http) => {
	const mutation = async (params: {
		id: string;
		body: PlayerSearchPresetFilterUpsertRequest;
	}) => {
		const args = z.object({ id: z.string(), body: z.any() }).parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/player/search/presets/filters/${id}`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerSearchPresetFilterPutResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchPresetFilterPutResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerSearchPresetFilterPutResponse200> | undefined,
					GenericWretchError<playerSearchPresetFilterPutResponse401>,
					{ id: string; body: PlayerSearchPresetFilterUpsertRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerSearchPresetFilterPutResponse200> | undefined,
				GenericWretchError<playerSearchPresetFilterPutResponse401>,
				{ id: string; body: PlayerSearchPresetFilterUpsertRequest }
			>(mutation, options || {});
		},
	};
};

const playerSearchPresetFilterDelete = (wretch: Http) => {
	const mutation = async (params: { id: string }) => {
		const args = z.object({ id: z.string() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/player/search/presets/filters/${id}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerSearchPresetFilterDeleteResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = playerSearchPresetFilterDeleteResponse401.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<playerSearchPresetFilterDeleteResponse200> | undefined,
					GenericWretchError<playerSearchPresetFilterDeleteResponse401>,
					{ id: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<playerSearchPresetFilterDeleteResponse200> | undefined,
				GenericWretchError<playerSearchPresetFilterDeleteResponse401>,
				{ id: string }
			>(mutation, options || {});
		},
	};
};

const getAvailableNationalities = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAvailableNationalitiesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/available-nationalities")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAvailableNationalitiesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getAvailableNationalitiesResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getAvailableNationalitiesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAvailableNationalitiesResponse200>,
					GenericWretchError<
						| getAvailableNationalitiesResponse401
						| getAvailableNationalitiesResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAvailableNationalitiesResponse200>,
			GenericWretchError<
				| getAvailableNationalitiesResponse401
				| getAvailableNationalitiesResponse403
			>
		> => {
			const key = ["getAvailableNationalities", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getAvailableNationalitiesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAvailableNationalitiesResponse200>,
				GenericWretchError<
					| getAvailableNationalitiesResponse401
					| getAvailableNationalitiesResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAvailableNationalitiesResponse200>,
				GenericWretchError<
					| getAvailableNationalitiesResponse401
					| getAvailableNationalitiesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAvailableNationalitiesResponse200>,
					GenericWretchError<
						| getAvailableNationalitiesResponse401
						| getAvailableNationalitiesResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAvailableNationalitiesResponse200>,
			GenericWretchError<
				| getAvailableNationalitiesResponse401
				| getAvailableNationalitiesResponse403
			>
		> => {
			const key = ["getAvailableNationalities", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getAvailableNationalitiesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAvailableNationalitiesResponse200>,
				GenericWretchError<
					| getAvailableNationalitiesResponse401
					| getAvailableNationalitiesResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAvailableNationalitiesResponse200>,
				GenericWretchError<
					| getAvailableNationalitiesResponse401
					| getAvailableNationalitiesResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getHightLightedPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getHightLightedPlayersResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/highlighted-players")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getHightLightedPlayersResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getHightLightedPlayersResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getHightLightedPlayersResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getHightLightedPlayers", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getHightLightedPlayersResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getHightLightedPlayersResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getHightLightedPlayersResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getHightLightedPlayersResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getHightLightedPlayersResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getHightLightedPlayers", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getHightLightedPlayersResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getHightLightedPlayersResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getHightLightedPlayersResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getLinkCounts = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getLinkCountsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/link-counts")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getLinkCountsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getLinkCountsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getLinkCountsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getLinkCounts", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLinkCountsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getLinkCountsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getLinkCountsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getLinkCountsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getLinkCountsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getLinkCounts", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLinkCountsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getLinkCountsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getLinkCountsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getMyEyeball = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getMyEyeballResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/my-eyeball")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getMyEyeballResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getMyEyeballResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getMyEyeballResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getMyEyeball", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getMyEyeballResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getMyEyeballResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getMyEyeballResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getMyEyeballResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getMyEyeballResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getMyEyeball", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getMyEyeballResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getMyEyeballResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getMyEyeballResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const acceptTermsOfUse = (wretch: Http) => {
	const mutation = async (params: { body: TermsOfUseRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/terms-of-use")
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>,
					{ body: TermsOfUseRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>,
				{ body: TermsOfUseRequest }
			>(mutation, options || {});
		},
	};
};

const globalSearchSuggestions = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { query: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<globalSearchSuggestionsResponse200>> => {
		const args = z.object({ query: z.string() }).parse(params);
		const { query } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/search/suggestion")
				.query(qs.stringify({ query }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = globalSearchSuggestionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { query: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<globalSearchSuggestionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<globalSearchSuggestionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"globalSearchSuggestions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<globalSearchSuggestionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<globalSearchSuggestionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<globalSearchSuggestionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { query: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<globalSearchSuggestionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<globalSearchSuggestionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"globalSearchSuggestions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<globalSearchSuggestionsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<globalSearchSuggestionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<globalSearchSuggestionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const globalSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			type: string;
			category: string;
			search: string;
			limit: number;
			page: number;
			order?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GlobalSearchResponse>> => {
		const args = z
			.object({
				type: z.string(),
				category: z.string(),
				search: z.string(),
				limit: z.number().int(),
				page: z.number().int(),
				order: z.string().optional(),
			})
			.parse(params);
		const { type, category, search, limit, page, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/search")
				.query(qs.stringify({ type, category, search, limit, page, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GlobalSearchResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				type: string;
				category: string;
				search: string;
				limit: number;
				page: number;
				order?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GlobalSearchResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GlobalSearchResponse>,
			GenericWretchError<null>
		> => {
			const key = ["globalSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GlobalSearchResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GlobalSearchResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GlobalSearchResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				type: string;
				category: string;
				search: string;
				limit: number;
				page: number;
				order?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GlobalSearchResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GlobalSearchResponse>,
			GenericWretchError<null>
		> => {
			const key = ["globalSearch", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GlobalSearchResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GlobalSearchResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GlobalSearchResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSuggestedGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getSuggestedGamesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/suggested-games")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getSuggestedGamesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getSuggestedGamesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getSuggestedGamesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getSuggestedGames", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getSuggestedGamesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getSuggestedGamesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getSuggestedGamesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getSuggestedGamesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getSuggestedGamesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getSuggestedGames", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getSuggestedGamesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getSuggestedGamesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getSuggestedGamesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const favoriteClub = (wretch: Http) => {
	const mutation = async (params: { id: number; type?: string }) => {
		const args = z
			.object({ id: z.number().int(), type: z.string().optional() })
			.parse(params);
		const { id, type } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/clubs/${id}`)
				.query(qs.stringify({ type }))
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubFavoriteResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = favoriteClubResponse400.parse(json);
					break;
				}
				case 404: {
					err.json = favoriteClubResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<ClubFavoriteResponse> | undefined,
					GenericWretchError<favoriteClubResponse400 | favoriteClubResponse404>,
					{ id: number; type?: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<ClubFavoriteResponse> | undefined,
				GenericWretchError<favoriteClubResponse400 | favoriteClubResponse404>,
				{ id: number; type?: string }
			>(mutation, options || {});
		},
	};
};

const unFavoriteClub = (wretch: Http) => {
	const mutation = async (params: { id: number; type: string }) => {
		const args = z
			.object({ id: z.number().int(), type: z.string() })
			.parse(params);
		const { id, type } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/clubs/${id}`)
				.query(qs.stringify({ type }))
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubFavoriteResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = unFavoriteClubResponse400.parse(json);
					break;
				}
				case 404: {
					err.json = unFavoriteClubResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<ClubFavoriteResponse> | undefined,
					GenericWretchError<
						unFavoriteClubResponse400 | unFavoriteClubResponse404
					>,
					{ id: number; type: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<ClubFavoriteResponse> | undefined,
				GenericWretchError<
					unFavoriteClubResponse400 | unFavoriteClubResponse404
				>,
				{ id: number; type: string }
			>(mutation, options || {});
		},
	};
};

const getRandomClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getRandomClubsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/random-club")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getRandomClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getRandomClubsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getRandomClubsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getRandomClubs", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getRandomClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getRandomClubsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getRandomClubsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getRandomClubsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getRandomClubsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getRandomClubs", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getRandomClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getRandomClubsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getRandomClubsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const favoritePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/players/${id}`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = favoritePlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = favoritePlayerResponse400.parse(json);
					break;
				}
				case 404: {
					err.json = favoritePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<favoritePlayerResponse200> | undefined,
					GenericWretchError<
						favoritePlayerResponse400 | favoritePlayerResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<favoritePlayerResponse200> | undefined,
				GenericWretchError<
					favoritePlayerResponse400 | favoritePlayerResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const unFavoritePlayer = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/players/${id}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = unFavoritePlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = unFavoritePlayerResponse400.parse(json);
					break;
				}
				case 404: {
					err.json = unFavoritePlayerResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<unFavoritePlayerResponse200> | undefined,
					GenericWretchError<
						unFavoritePlayerResponse400 | unFavoritePlayerResponse404
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<unFavoritePlayerResponse200> | undefined,
				GenericWretchError<
					unFavoritePlayerResponse400 | unFavoritePlayerResponse404
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getLatestGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getLatestGamesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/latest-games")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getLatestGamesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getLatestGamesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getLatestGamesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getLatestGames", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLatestGamesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getLatestGamesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getLatestGamesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getLatestGamesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getLatestGamesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getLatestGames", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getLatestGamesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getLatestGamesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getLatestGamesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const favoriteTeam = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/teams/${id}`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = favoriteTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = favoriteTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<favoriteTeamResponse200> | undefined,
					GenericWretchError<favoriteTeamResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<favoriteTeamResponse200> | undefined,
				GenericWretchError<favoriteTeamResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const unFavoriteTeam = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/teams/${id}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = unFavoriteTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = unFavoriteTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<unFavoriteTeamResponse200> | undefined,
					GenericWretchError<unFavoriteTeamResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<unFavoriteTeamResponse200> | undefined,
				GenericWretchError<unFavoriteTeamResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const favoriteGame = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/games/${id}`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = favoriteGameResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = favoriteGameResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<favoriteGameResponse200> | undefined,
					GenericWretchError<favoriteGameResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<favoriteGameResponse200> | undefined,
				GenericWretchError<favoriteGameResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const unfavoriteGame = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch
				.url(`/portal/user/favorites/games/${id}`)
				.delete()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = unfavoriteGameResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = unfavoriteGameResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<unfavoriteGameResponse200> | undefined,
					GenericWretchError<unfavoriteGameResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<unfavoriteGameResponse200> | undefined,
				GenericWretchError<unfavoriteGameResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getFeatures = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { features: Feature[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getFeaturesResponse200>> => {
		const args = z.object({ features: z.array(z.any()) }).parse(params);
		const { features } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/features")
				.query(qs.stringify({ features }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getFeaturesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { features: Feature[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getFeaturesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getFeaturesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getFeatures", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getFeaturesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getFeaturesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getFeaturesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { features: Feature[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getFeaturesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getFeaturesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getFeatures", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getFeaturesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getFeaturesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getFeaturesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getSeasons = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getSeasonsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/api/seasons")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getSeasonsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getSeasonsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getSeasonsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getSeasons", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getSeasonsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getSeasonsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getSeasonsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getSeasonsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getSeasonsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getSeasons", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getSeasonsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getSeasonsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getSeasonsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const aggregatePlayerStat = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: AggregatePlayerStatRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/player/${id}/stats`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = AggregatePlayerStatResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = aggregatePlayerStatResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = aggregatePlayerStatResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = aggregatePlayerStatResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = aggregatePlayerStatResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<AggregatePlayerStatResponse> | undefined,
					GenericWretchError<
						| aggregatePlayerStatResponse400
						| aggregatePlayerStatResponse401
						| aggregatePlayerStatResponse403
						| aggregatePlayerStatResponse404
						| aggregatePlayerStatResponse500
					>,
					{ id: number; body: AggregatePlayerStatRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<AggregatePlayerStatResponse> | undefined,
				GenericWretchError<
					| aggregatePlayerStatResponse400
					| aggregatePlayerStatResponse401
					| aggregatePlayerStatResponse403
					| aggregatePlayerStatResponse404
					| aggregatePlayerStatResponse500
				>,
				{ id: number; body: AggregatePlayerStatRequest }
			>(mutation, options || {});
		},
	};
};

const getPlayerPlayedGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			startDate?: string;
			endDate?: string;
			direction?: string;
			sortingCriteria?: string;
			page: number;
			limit: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerGameResponse>> => {
		const args = z
			.object({
				id: z.number().int(),
				startDate: z.string().optional(),
				endDate: z.string().optional(),
				direction: z.string().optional(),
				sortingCriteria: z.string().optional(),
				page: z.number().int(),
				limit: z.number().int(),
			})
			.parse(params);
		const { id, startDate, endDate, direction, sortingCriteria, page, limit } =
			args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/player/${id}/games`)
				.query(
					qs.stringify({
						startDate,
						endDate,
						direction,
						sortingCriteria,
						page,
						limit,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerGameResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getPlayerPlayedGamesResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = getPlayerPlayedGamesResponse401.parse(json);
					break;
				}
				case 404: {
					err.json = getPlayerPlayedGamesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				startDate?: string;
				endDate?: string;
				direction?: string;
				sortingCriteria?: string;
				page: number;
				limit: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerGameResponse>,
					GenericWretchError<
						| getPlayerPlayedGamesResponse400
						| getPlayerPlayedGamesResponse401
						| getPlayerPlayedGamesResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerGameResponse>,
			GenericWretchError<
				| getPlayerPlayedGamesResponse400
				| getPlayerPlayedGamesResponse401
				| getPlayerPlayedGamesResponse404
			>
		> => {
			const key = [
				"getPlayerPlayedGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<
					| getPlayerPlayedGamesResponse400
					| getPlayerPlayedGamesResponse401
					| getPlayerPlayedGamesResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<
					| getPlayerPlayedGamesResponse400
					| getPlayerPlayedGamesResponse401
					| getPlayerPlayedGamesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				startDate?: string;
				endDate?: string;
				direction?: string;
				sortingCriteria?: string;
				page: number;
				limit: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerGameResponse>,
					GenericWretchError<
						| getPlayerPlayedGamesResponse400
						| getPlayerPlayedGamesResponse401
						| getPlayerPlayedGamesResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerGameResponse>,
			GenericWretchError<
				| getPlayerPlayedGamesResponse400
				| getPlayerPlayedGamesResponse401
				| getPlayerPlayedGamesResponse404
			>
		> => {
			const key = [
				"getPlayerPlayedGames",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<
					| getPlayerPlayedGamesResponse400
					| getPlayerPlayedGamesResponse401
					| getPlayerPlayedGamesResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<
					| getPlayerPlayedGamesResponse400
					| getPlayerPlayedGamesResponse401
					| getPlayerPlayedGamesResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalCreatePlayerPossibleDuplicate = (wretch: Http) => {
	const mutation = async (params: {
		body: CreatePlayerPossibleDuplicateRequest;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/api/player/possible-duplicate")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 201: {
					const data =
						portalCreatePlayerPossibleDuplicateResponse201.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = portalCreatePlayerPossibleDuplicateResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = portalCreatePlayerPossibleDuplicateResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = portalCreatePlayerPossibleDuplicateResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					| ApiResponse<portalCreatePlayerPossibleDuplicateResponse201>
					| undefined,
					GenericWretchError<
						| portalCreatePlayerPossibleDuplicateResponse400
						| portalCreatePlayerPossibleDuplicateResponse403
						| portalCreatePlayerPossibleDuplicateResponse404
					>,
					{ body: CreatePlayerPossibleDuplicateRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<portalCreatePlayerPossibleDuplicateResponse201> | undefined,
				GenericWretchError<
					| portalCreatePlayerPossibleDuplicateResponse400
					| portalCreatePlayerPossibleDuplicateResponse403
					| portalCreatePlayerPossibleDuplicateResponse404
				>,
				{ body: CreatePlayerPossibleDuplicateRequest }
			>(mutation, options || {});
		},
	};
};

const getNewsfeed = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			tags?: string[];
			from?: string;
			to?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GetNewsfeedResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				tags: z.array(z.string()).optional(),
				from: z.string().optional(),
				to: z.string().optional(),
			})
			.parse(params);
		const { page, limit, tags, from, to } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/news")
				.query(qs.stringify({ page, limit, tags, from, to }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GetNewsfeedResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getNewsfeedResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				tags?: string[];
				from?: string;
				to?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GetNewsfeedResponse>,
					GenericWretchError<getNewsfeedResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GetNewsfeedResponse>,
			GenericWretchError<getNewsfeedResponse403>
		> => {
			const key = ["getNewsfeed", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetNewsfeedResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GetNewsfeedResponse>,
				GenericWretchError<getNewsfeedResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GetNewsfeedResponse>,
				GenericWretchError<getNewsfeedResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				tags?: string[];
				from?: string;
				to?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GetNewsfeedResponse>,
					GenericWretchError<getNewsfeedResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GetNewsfeedResponse>,
			GenericWretchError<getNewsfeedResponse403>
		> => {
			const key = ["getNewsfeed", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetNewsfeedResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GetNewsfeedResponse>,
				GenericWretchError<getNewsfeedResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GetNewsfeedResponse>,
				GenericWretchError<getNewsfeedResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNewsfeedTags = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getNewsfeedTagsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/news/tags")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getNewsfeedTagsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getNewsfeedTagsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getNewsfeedTagsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getNewsfeedTags", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNewsfeedTagsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getNewsfeedTagsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getNewsfeedTagsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getNewsfeedTagsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getNewsfeedTagsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getNewsfeedTags", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNewsfeedTagsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getNewsfeedTagsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getNewsfeedTagsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNewsPostDetail = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { slug: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GetPostDetail>> => {
		const args = z.object({ slug: z.string() }).parse(params);
		const { slug } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/news/${slug}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GetPostDetail.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getNewsPostDetailResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getNewsPostDetailResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { slug: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GetPostDetail>,
					GenericWretchError<
						getNewsPostDetailResponse403 | getNewsPostDetailResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GetPostDetail>,
			GenericWretchError<
				getNewsPostDetailResponse403 | getNewsPostDetailResponse404
			>
		> => {
			const key = ["getNewsPostDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetPostDetail>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GetPostDetail>,
				GenericWretchError<
					getNewsPostDetailResponse403 | getNewsPostDetailResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GetPostDetail>,
				GenericWretchError<
					getNewsPostDetailResponse403 | getNewsPostDetailResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { slug: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GetPostDetail>,
					GenericWretchError<
						getNewsPostDetailResponse403 | getNewsPostDetailResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GetPostDetail>,
			GenericWretchError<
				getNewsPostDetailResponse403 | getNewsPostDetailResponse404
			>
		> => {
			const key = ["getNewsPostDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetPostDetail>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GetPostDetail>,
				GenericWretchError<
					getNewsPostDetailResponse403 | getNewsPostDetailResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GetPostDetail>,
				GenericWretchError<
					getNewsPostDetailResponse403 | getNewsPostDetailResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNewsPreview = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { uid: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<Post>> => {
		const args = z.object({ uid: z.string() }).parse(params);
		const { uid } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/news/preview/${uid}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = Post.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = getNewsPreviewResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { uid: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<Post>,
					GenericWretchError<getNewsPreviewResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<Post>,
			GenericWretchError<getNewsPreviewResponse404>
		> => {
			const key = ["getNewsPreview", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<Post>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<Post>,
				GenericWretchError<getNewsPreviewResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<Post>,
				GenericWretchError<getNewsPreviewResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { uid: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<Post>,
					GenericWretchError<getNewsPreviewResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<Post>,
			GenericWretchError<getNewsPreviewResponse404>
		> => {
			const key = ["getNewsPreview", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<Post>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<Post>,
				GenericWretchError<getNewsPreviewResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<Post>,
				GenericWretchError<getNewsPreviewResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const get_portal_news_webhook = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<null>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/news/webhook")
				.get()
				.res();
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<ApiResponse<null>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<null>, GenericWretchError<null>> => {
			const key = ["get_portal_news_webhook", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<null>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<ApiResponse<null>, GenericWretchError<null>>(
				key,
				query,
				options || {},
			) as UseQueryResult<ApiResponse<null>, GenericWretchError<null>>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<ApiResponse<null>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<ApiResponse<null>, GenericWretchError<null>> => {
			const key = ["get_portal_news_webhook", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<null>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<null>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<null>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const post_portal_news_webhook = (wretch: Http) => {
	const mutation = async () => {
		try {
			const res = await wretch.url("/portal/news/webhook").post().res();
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<null>
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<null>
			>(mutation, options || {});
		},
	};
};

const logUserActivity = (wretch: Http) => {
	const mutation = async (params: { body: UserActivityLogRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/portal/telemetry").json(body).post().res();
			switch (res.status) {
				case 201: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = logUserActivityResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<logUserActivityResponse400>,
					{ body: UserActivityLogRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<logUserActivityResponse400>,
				{ body: UserActivityLogRequest }
			>(mutation, options || {});
		},
	};
};

const getAllClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			countries?: string[];
			regions?: string[];
			search?: string;
			order?: string;
			page: number;
			limit: number;
			isFavorite?: boolean;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GetClubsResponse>> => {
		const args = z
			.object({
				countries: z.array(z.string()).optional(),
				regions: z.array(z.string()).optional(),
				search: z.string().optional(),
				order: z.string().optional(),
				page: z.number().int(),
				limit: z.number().int(),
				isFavorite: z.boolean().optional(),
			})
			.parse(params);
		const { countries, regions, search, order, page, limit, isFavorite } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/clubs")
				.query(
					qs.stringify({
						countries,
						regions,
						search,
						order,
						page,
						limit,
						isFavorite,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GetClubsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getAllClubsResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getAllClubsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				countries?: string[];
				regions?: string[];
				search?: string;
				order?: string;
				page: number;
				limit: number;
				isFavorite?: boolean;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GetClubsResponse>,
					GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GetClubsResponse>,
			GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
		> => {
			const key = ["getAllClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetClubsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GetClubsResponse>,
				GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GetClubsResponse>,
				GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				countries?: string[];
				regions?: string[];
				search?: string;
				order?: string;
				page: number;
				limit: number;
				isFavorite?: boolean;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GetClubsResponse>,
					GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GetClubsResponse>,
			GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
		> => {
			const key = ["getAllClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetClubsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GetClubsResponse>,
				GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GetClubsResponse>,
				GenericWretchError<getAllClubsResponse401 | getAllClubsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getAllPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			order?: object;
			page: number;
			limit: number;
			isFavorite?: boolean;
			region?: string[];
			countryCodes?: string[];
			leagueCountries?: string[];
			nationalCountries?: string[];
			playerName?: string;
			clubName?: string;
			teamAgeGroup?: string[];
			teamLevel?: string;
			playerAgeCategory?: string;
			nationality?: string;
			strongFoot?: string;
			playedInNationalTeam?: number;
			position?: string[];
			birthdayGte?: number;
			birthdayLte?: number;
			weightGte?: number;
			weightLte?: number;
			heightGte?: number;
			heightLte?: number;
			teamId?: number;
			clubId?: number;
			type?: string;
			gradingDisplaying?: string;
			contributors?: number[];
			eyeballRatingGte?: number;
			eyeballRatingLte?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerResponse>> => {
		const args = z
			.object({
				order: z
					.object({
						field: z
							.enum([
								"lastName",
								"club",
								"birthday",
								"nationality",
								"position",
								"strongestFoot",
								"updatedAt",
								"performanceGradeAverage",
								"potentialGradeAverage",
								"gradeNotesCount",
								"eyeballRating",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
				page: z.number().int(),
				limit: z.number().int(),
				isFavorite: z.boolean().nullable().default(false),
				region: z.array(z.string()).nullable().default(null),
				countryCodes: z.array(z.string()).nullable().default(null),
				leagueCountries: z.array(z.string()).nullable().default(null),
				nationalCountries: z.array(z.string()).nullable().default(null),
				playerName: z.string().max(255).nullable().optional(),
				clubName: z.string().nullable().optional(),
				teamAgeGroup: z.array(z.string()).nullable().optional(),
				teamLevel: z.string().nullable().optional(),
				playerAgeCategory: z.string().nullable().optional(),
				nationality: z.string().nullable().optional(),
				strongFoot: z.string().max(20).nullable().optional(),
				playedInNationalTeam: z.number().int().nullable().optional(),
				position: z.array(z.string()).nullable().optional(),
				birthdayGte: z.number().int().nullable().optional(),
				birthdayLte: z.number().int().nullable().optional(),
				weightGte: z.number().int().nullable().optional(),
				weightLte: z.number().int().nullable().optional(),
				heightGte: z.number().int().nullable().optional(),
				heightLte: z.number().int().nullable().optional(),
				teamId: z.number().int().nullable().optional(),
				clubId: z.number().int().nullable().optional(),
				type: z.string().nullable().optional(),
				gradingDisplaying: z
					.enum(["all", "graded", "ungraded"])
					.nullable()
					.optional(),
				contributors: z.array(z.number()).nullable().default(null),
				eyeballRatingGte: z.number().int().nullable().optional(),
				eyeballRatingLte: z.number().int().nullable().optional(),
			})
			.parse(params);
		const {
			order,
			page,
			limit,
			isFavorite,
			region,
			countryCodes,
			leagueCountries,
			nationalCountries,
			playerName,
			clubName,
			teamAgeGroup,
			teamLevel,
			playerAgeCategory,
			nationality,
			strongFoot,
			playedInNationalTeam,
			position,
			birthdayGte,
			birthdayLte,
			weightGte,
			weightLte,
			heightGte,
			heightLte,
			teamId,
			clubId,
			type,
			gradingDisplaying,
			contributors,
			eyeballRatingGte,
			eyeballRatingLte,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/players")
				.query(
					qs.stringify({
						order,
						page,
						limit,
						isFavorite,
						region,
						countryCodes,
						leagueCountries,
						nationalCountries,
						playerName,
						clubName,
						teamAgeGroup,
						teamLevel,
						playerAgeCategory,
						nationality,
						strongFoot,
						playedInNationalTeam,
						position,
						birthdayGte,
						birthdayLte,
						weightGte,
						weightLte,
						heightGte,
						heightLte,
						teamId,
						clubId,
						type,
						gradingDisplaying,
						contributors,
						eyeballRatingGte,
						eyeballRatingLte,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getAllPlayersResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getAllPlayersResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				order?: object;
				page: number;
				limit: number;
				isFavorite?: boolean;
				region?: string[];
				countryCodes?: string[];
				leagueCountries?: string[];
				nationalCountries?: string[];
				playerName?: string;
				clubName?: string;
				teamAgeGroup?: string[];
				teamLevel?: string;
				playerAgeCategory?: string;
				nationality?: string;
				strongFoot?: string;
				playedInNationalTeam?: number;
				position?: string[];
				birthdayGte?: number;
				birthdayLte?: number;
				weightGte?: number;
				weightLte?: number;
				heightGte?: number;
				heightLte?: number;
				teamId?: number;
				clubId?: number;
				type?: string;
				gradingDisplaying?: string;
				contributors?: number[];
				eyeballRatingGte?: number;
				eyeballRatingLte?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerResponse>,
					GenericWretchError<
						getAllPlayersResponse401 | getAllPlayersResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerResponse>,
			GenericWretchError<getAllPlayersResponse401 | getAllPlayersResponse403>
		> => {
			const key = ["getAllPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerResponse>,
				GenericWretchError<getAllPlayersResponse401 | getAllPlayersResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerResponse>,
				GenericWretchError<getAllPlayersResponse401 | getAllPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				order?: object;
				page: number;
				limit: number;
				isFavorite?: boolean;
				region?: string[];
				countryCodes?: string[];
				leagueCountries?: string[];
				nationalCountries?: string[];
				playerName?: string;
				clubName?: string;
				teamAgeGroup?: string[];
				teamLevel?: string;
				playerAgeCategory?: string;
				nationality?: string;
				strongFoot?: string;
				playedInNationalTeam?: number;
				position?: string[];
				birthdayGte?: number;
				birthdayLte?: number;
				weightGte?: number;
				weightLte?: number;
				heightGte?: number;
				heightLte?: number;
				teamId?: number;
				clubId?: number;
				type?: string;
				gradingDisplaying?: string;
				contributors?: number[];
				eyeballRatingGte?: number;
				eyeballRatingLte?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerResponse>,
					GenericWretchError<
						getAllPlayersResponse401 | getAllPlayersResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerResponse>,
			GenericWretchError<getAllPlayersResponse401 | getAllPlayersResponse403>
		> => {
			const key = ["getAllPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerResponse>,
				GenericWretchError<getAllPlayersResponse401 | getAllPlayersResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerResponse>,
				GenericWretchError<getAllPlayersResponse401 | getAllPlayersResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getAllGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			order: string;
			isFavorite?: boolean;
			search?: string;
			region?: string[];
			countryCodes?: string[];
			category?: string[];
			teamLevel?: string;
			startDate?: string;
			endDate?: string;
			season?: number;
			teamId?: number;
			clubId?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamesResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				order: z.string(),
				isFavorite: z.boolean().optional(),
				search: z.string().optional(),
				region: z.array(z.string()).optional(),
				countryCodes: z.array(z.string()).optional(),
				category: z.array(z.string()).optional(),
				teamLevel: z.string().optional(),
				startDate: z.string().optional(),
				endDate: z.string().optional(),
				season: z.number().int().optional(),
				teamId: z.number().int().optional(),
				clubId: z.number().int().optional(),
			})
			.parse(params);
		const {
			page,
			limit,
			order,
			isFavorite,
			search,
			region,
			countryCodes,
			category,
			teamLevel,
			startDate,
			endDate,
			season,
			teamId,
			clubId,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/games")
				.query(
					qs.stringify({
						page,
						limit,
						order,
						isFavorite,
						search,
						region,
						countryCodes,
						category,
						teamLevel,
						startDate,
						endDate,
						season,
						teamId,
						clubId,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getAllGamesResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getAllGamesResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				order: string;
				isFavorite?: boolean;
				search?: string;
				region?: string[];
				countryCodes?: string[];
				category?: string[];
				teamLevel?: string;
				startDate?: string;
				endDate?: string;
				season?: number;
				teamId?: number;
				clubId?: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GamesResponse>,
					GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GamesResponse>,
			GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
		> => {
			const key = ["getAllGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamesResponse>,
				GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamesResponse>,
				GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				order: string;
				isFavorite?: boolean;
				search?: string;
				region?: string[];
				countryCodes?: string[];
				category?: string[];
				teamLevel?: string;
				startDate?: string;
				endDate?: string;
				season?: number;
				teamId?: number;
				clubId?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamesResponse>,
					GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamesResponse>,
			GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
		> => {
			const key = ["getAllGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamesResponse>,
				GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamesResponse>,
				GenericWretchError<getAllGamesResponse401 | getAllGamesResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			order: string;
			isFavorite?: boolean;
			search?: string;
			region?: string[];
			countryCodes?: string[];
			category?: string[];
			teamLevel?: string;
			startDate?: string;
			endDate?: string;
			season?: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GamesResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				order: z.string(),
				isFavorite: z.boolean().optional(),
				search: z.string().optional(),
				region: z.array(z.string()).optional(),
				countryCodes: z.array(z.string()).optional(),
				category: z.array(z.string()).optional(),
				teamLevel: z.string().optional(),
				startDate: z.string().optional(),
				endDate: z.string().optional(),
				season: z.number().int().optional(),
			})
			.parse(params);
		const {
			page,
			limit,
			order,
			isFavorite,
			search,
			region,
			countryCodes,
			category,
			teamLevel,
			startDate,
			endDate,
			season,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/games")
				.query(
					qs.stringify({
						page,
						limit,
						order,
						isFavorite,
						search,
						region,
						countryCodes,
						category,
						teamLevel,
						startDate,
						endDate,
						season,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GamesResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				order: string;
				isFavorite?: boolean;
				search?: string;
				region?: string[];
				countryCodes?: string[];
				category?: string[];
				teamLevel?: string;
				startDate?: string;
				endDate?: string;
				season?: number;
			},
			options?: Omit<
				UseQueryOptions<ApiResponse<GamesResponse>, GenericWretchError<null>>,
				"queryFn"
			>,
		): UseQueryResult<ApiResponse<GamesResponse>, GenericWretchError<null>> => {
			const key = ["getUserGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				order: string;
				isFavorite?: boolean;
				search?: string;
				region?: string[];
				countryCodes?: string[];
				category?: string[];
				teamLevel?: string;
				startDate?: string;
				endDate?: string;
				season?: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GamesResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GamesResponse>,
			GenericWretchError<null>
		> => {
			const key = ["getUserGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GamesResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GamesResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserClips = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			clubId?: number;
			playerId?: number;
			playerType?: string;
			gender?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserClipsResponse200>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				clubId: z.number().int().optional(),
				playerId: z.number().int().optional(),
				playerType: z.string().optional(),
				gender: z.string().optional(),
			})
			.parse(params);
		const { page, limit, clubId, playerId, playerType, gender } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/clips")
				.query(
					qs.stringify({ page, limit, clubId, playerId, playerType, gender }),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserClipsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				clubId?: number;
				playerId?: number;
				playerType?: string;
				gender?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserClipsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserClipsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserClips", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserClipsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserClipsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserClipsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				clubId?: number;
				playerId?: number;
				playerType?: string;
				gender?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserClipsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserClipsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserClips", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserClipsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserClipsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserClipsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const renameClip = (wretch: Http) => {
	const mutation = async (params: { body: renameClip_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/portal/me/clips").json(body).post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = renameClipResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = renameClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<renameClipResponse200> | undefined,
					GenericWretchError<renameClipResponse404>,
					{ body: renameClip_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<renameClipResponse200> | undefined,
				GenericWretchError<renameClipResponse404>,
				{ body: renameClip_Body }
			>(mutation, options || {});
		},
	};
};

const deleteClip = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/portal/me/clips/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteClipResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = deleteClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteClipResponse200> | undefined,
					GenericWretchError<deleteClipResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteClipResponse200> | undefined,
				GenericWretchError<deleteClipResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const getUserClipEventDetail = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserClipEventDetailResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/me/clip/${id}/eventDetail`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserClipEventDetailResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserClipEventDetailResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserClipEventDetailResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getUserClipEventDetail",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getUserClipEventDetailResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserClipEventDetailResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserClipEventDetailResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserClipEventDetailResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserClipEventDetailResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getUserClipEventDetail",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getUserClipEventDetailResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserClipEventDetailResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserClipEventDetailResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const generateClipLink = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<generateClipLinkResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/me/clips/${id}/generateLink`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = generateClipLinkResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<generateClipLinkResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<generateClipLinkResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["generateClipLink", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<generateClipLinkResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<generateClipLinkResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<generateClipLinkResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<generateClipLinkResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<generateClipLinkResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["generateClipLink", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<generateClipLinkResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<generateClipLinkResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<generateClipLinkResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const downloadClip = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<downloadClipResponse200>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/me/clips/${id}/download`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = downloadClipResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = downloadClipResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = downloadClipResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<downloadClipResponse200>,
					GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<downloadClipResponse200>,
			GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
		> => {
			const key = ["downloadClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<downloadClipResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<downloadClipResponse200>,
				GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<downloadClipResponse200>,
				GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<downloadClipResponse200>,
					GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<downloadClipResponse200>,
			GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
		> => {
			const key = ["downloadClip", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<downloadClipResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<downloadClipResponse200>,
				GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<downloadClipResponse200>,
				GenericWretchError<downloadClipResponse403 | downloadClipResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const shareClipLink = (wretch: Http) => {
	const mutation = async (params: { id: number; body: shareClipLink_Body }) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/me/clips/${id}/share`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = shareClipLinkResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = shareClipLinkResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<shareClipLinkResponse200> | undefined,
					GenericWretchError<shareClipLinkResponse404>,
					{ id: number; body: shareClipLink_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<shareClipLinkResponse200> | undefined,
				GenericWretchError<shareClipLinkResponse404>,
				{ id: number; body: shareClipLink_Body }
			>(mutation, options || {});
		},
	};
};

const getUserClipClub = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserClipClubResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/clip/clubs")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserClipClubResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserClipClubResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserClipClubResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserClipClub", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserClipClubResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserClipClubResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserClipClubResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserClipClubResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserClipClubResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserClipClub", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserClipClubResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserClipClubResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserClipClubResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserClipPlayer = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserClipPlayerResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/clip/players")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserClipPlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserClipPlayerResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserClipPlayerResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserClipPlayer", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserClipPlayerResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserClipPlayerResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserClipPlayerResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserClipPlayerResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserClipPlayerResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserClipPlayer", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getUserClipPlayerResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserClipPlayerResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserClipPlayerResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getUserSubscriptions = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { isActive: boolean },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserSubscriptionsResponse200>> => {
		const args = z.object({ isActive: z.boolean() }).parse(params);
		const { isActive } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/subscriptions")
				.query(qs.stringify({ isActive }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserSubscriptionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { isActive: boolean },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserSubscriptionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserSubscriptionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getUserSubscriptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<getUserSubscriptionsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserSubscriptionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserSubscriptionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { isActive: boolean },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserSubscriptionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserSubscriptionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getUserSubscriptions",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<getUserSubscriptionsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserSubscriptionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserSubscriptionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getAllTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			countries?: string[];
			regions?: string[];
			category?: string[];
			search?: string;
			clubId?: number;
			order?: string;
			page: number;
			limit: number;
			isFavorite?: boolean;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GetTeamResponse>> => {
		const args = z
			.object({
				countries: z.array(z.string()).optional(),
				regions: z.array(z.string()).optional(),
				category: z.array(z.string()).optional(),
				search: z.string().optional(),
				clubId: z.number().int().optional(),
				order: z.string().optional(),
				page: z.number().int(),
				limit: z.number().int(),
				isFavorite: z.boolean().optional(),
			})
			.parse(params);
		const {
			countries,
			regions,
			category,
			search,
			clubId,
			order,
			page,
			limit,
			isFavorite,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/me/teams")
				.query(
					qs.stringify({
						countries,
						regions,
						category,
						search,
						clubId,
						order,
						page,
						limit,
						isFavorite,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GetTeamResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getAllTeamsResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getAllTeamsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				countries?: string[];
				regions?: string[];
				category?: string[];
				search?: string;
				clubId?: number;
				order?: string;
				page: number;
				limit: number;
				isFavorite?: boolean;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GetTeamResponse>,
					GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GetTeamResponse>,
			GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
		> => {
			const key = ["getAllTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetTeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GetTeamResponse>,
				GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GetTeamResponse>,
				GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				countries?: string[];
				regions?: string[];
				category?: string[];
				search?: string;
				clubId?: number;
				order?: string;
				page: number;
				limit: number;
				isFavorite?: boolean;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GetTeamResponse>,
					GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GetTeamResponse>,
			GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
		> => {
			const key = ["getAllTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<GetTeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GetTeamResponse>,
				GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GetTeamResponse>,
				GenericWretchError<getAllTeamsResponse401 | getAllTeamsResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateUserInformation = (wretch: Http) => {
	const mutation = async (params: { body: updateUserInformation_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/user/update/my-account")
				.json(body)
				.patch()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateUserInformationResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateUserInformationResponse400.parse(json);
					break;
				}
				case 401: {
					err.json = updateUserInformationResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = updateUserInformationResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateUserInformationResponse200> | undefined,
					GenericWretchError<
						| updateUserInformationResponse400
						| updateUserInformationResponse401
						| updateUserInformationResponse403
					>,
					{ body: updateUserInformation_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateUserInformationResponse200> | undefined,
				GenericWretchError<
					| updateUserInformationResponse400
					| updateUserInformationResponse401
					| updateUserInformationResponse403
				>,
				{ body: updateUserInformation_Body }
			>(mutation, options || {});
		},
	};
};

const getUserInformation = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<UserProfileResponse>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/user/my-account")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = UserProfileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getUserInformationResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getUserInformationResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<UserProfileResponse>,
					GenericWretchError<
						getUserInformationResponse401 | getUserInformationResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<UserProfileResponse>,
			GenericWretchError<
				getUserInformationResponse401 | getUserInformationResponse403
			>
		> => {
			const key = ["getUserInformation", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserProfileResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<UserProfileResponse>,
				GenericWretchError<
					getUserInformationResponse401 | getUserInformationResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<UserProfileResponse>,
				GenericWretchError<
					getUserInformationResponse401 | getUserInformationResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<UserProfileResponse>,
					GenericWretchError<
						getUserInformationResponse401 | getUserInformationResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<UserProfileResponse>,
			GenericWretchError<
				getUserInformationResponse401 | getUserInformationResponse403
			>
		> => {
			const key = ["getUserInformation", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<UserProfileResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<UserProfileResponse>,
				GenericWretchError<
					getUserInformationResponse401 | getUserInformationResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<UserProfileResponse>,
				GenericWretchError<
					getUserInformationResponse401 | getUserInformationResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const uploadUserProfilePicture = (wretch: Http) => {
	const mutation = async (params: {
		body: uploadUserProfilePictureRequestBody;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/user/upload-profile-picture")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = uploadUserProfilePictureResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = uploadUserProfilePictureResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = uploadUserProfilePictureResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<uploadUserProfilePictureResponse200> | undefined,
					GenericWretchError<
						| uploadUserProfilePictureResponse401
						| uploadUserProfilePictureResponse403
						| uploadUserProfilePictureResponse500
					>,
					{ body: uploadUserProfilePictureRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<uploadUserProfilePictureResponse200> | undefined,
				GenericWretchError<
					| uploadUserProfilePictureResponse401
					| uploadUserProfilePictureResponse403
					| uploadUserProfilePictureResponse500
				>,
				{ body: uploadUserProfilePictureRequestBody }
			>(mutation, options || {});
		},
	};
};

const removeProfile = (wretch: Http) => {
	const mutation = async (params: { body: removeProfileRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/user/profile-removal")
				.json(body)
				.post()
				.res();
			switch (res.status) {
				case 200: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = removeProfileResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = removeProfileResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						removeProfileResponse401 | removeProfileResponse403
					>,
					{ body: removeProfileRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<removeProfileResponse401 | removeProfileResponse403>,
				{ body: removeProfileRequestBody }
			>(mutation, options || {});
		},
	};
};

const getTimezones = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTimezonesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/timezones")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTimezonesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTimezonesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTimezonesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getTimezones", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTimezonesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTimezonesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTimezonesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getTimezones", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getTimezonesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTimezonesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getEmailNotifications = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getEmailNotificationsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/email-notifications")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getEmailNotificationsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getEmailNotificationsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getEmailNotificationsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getEmailNotifications", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getEmailNotificationsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getEmailNotificationsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getEmailNotificationsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getEmailNotificationsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getEmailNotificationsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getEmailNotifications", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getEmailNotificationsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getEmailNotificationsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getEmailNotificationsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateEmailNotifications = (wretch: Http) => {
	const mutation = async (params: { body: updateEmailNotifications_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/user/email-notifications")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateEmailNotificationsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateEmailNotificationsResponse200> | undefined,
					GenericWretchError<null>,
					{ body: updateEmailNotifications_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateEmailNotificationsResponse200> | undefined,
				GenericWretchError<null>,
				{ body: updateEmailNotifications_Body }
			>(mutation, options || {});
		},
	};
};

const getPlayerEmailNotifications = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			order?: string;
			sort?: string;
			playerName?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerResponse2>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				order: z.string().optional(),
				sort: z.string().optional(),
				playerName: z.string().optional(),
			})
			.parse(params);
		const { page, limit, order, sort, playerName } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/player-email-notifications")
				.query(qs.stringify({ page, limit, order, sort, playerName }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerResponse2.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getPlayerEmailNotificationsResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getPlayerEmailNotificationsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				order?: string;
				sort?: string;
				playerName?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerResponse2>,
					GenericWretchError<
						| getPlayerEmailNotificationsResponse401
						| getPlayerEmailNotificationsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerResponse2>,
			GenericWretchError<
				| getPlayerEmailNotificationsResponse401
				| getPlayerEmailNotificationsResponse403
			>
		> => {
			const key = [
				"getPlayerEmailNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerResponse2>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerResponse2>,
				GenericWretchError<
					| getPlayerEmailNotificationsResponse401
					| getPlayerEmailNotificationsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerResponse2>,
				GenericWretchError<
					| getPlayerEmailNotificationsResponse401
					| getPlayerEmailNotificationsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				order?: string;
				sort?: string;
				playerName?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerResponse2>,
					GenericWretchError<
						| getPlayerEmailNotificationsResponse401
						| getPlayerEmailNotificationsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerResponse2>,
			GenericWretchError<
				| getPlayerEmailNotificationsResponse401
				| getPlayerEmailNotificationsResponse403
			>
		> => {
			const key = [
				"getPlayerEmailNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<PlayerResponse2>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerResponse2>,
				GenericWretchError<
					| getPlayerEmailNotificationsResponse401
					| getPlayerEmailNotificationsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerResponse2>,
				GenericWretchError<
					| getPlayerEmailNotificationsResponse401
					| getPlayerEmailNotificationsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updatePlayerEmailNotifications = (wretch: Http) => {
	const mutation = async (params: {
		body: BatchUpdatePlayerEmailNotification;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/user/player-email-notifications")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updatePlayerEmailNotificationsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = updatePlayerEmailNotificationsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updatePlayerEmailNotificationsResponse200> | undefined,
					GenericWretchError<updatePlayerEmailNotificationsResponse404>,
					{ body: BatchUpdatePlayerEmailNotification }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updatePlayerEmailNotificationsResponse200> | undefined,
				GenericWretchError<updatePlayerEmailNotificationsResponse404>,
				{ body: BatchUpdatePlayerEmailNotification }
			>(mutation, options || {});
		},
	};
};

const getClubEmailNotifications = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			order?: string;
			sort?: string;
			search?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				order: z.string().optional(),
				sort: z.string().optional(),
				search: z.string().optional(),
			})
			.parse(params);
		const { page, limit, order, sort, search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/club-email-notifications")
				.query(qs.stringify({ page, limit, order, sort, search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getClubEmailNotificationsResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getClubEmailNotificationsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				order?: string;
				sort?: string;
				search?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubResponse>,
					GenericWretchError<
						| getClubEmailNotificationsResponse401
						| getClubEmailNotificationsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubResponse>,
			GenericWretchError<
				| getClubEmailNotificationsResponse401
				| getClubEmailNotificationsResponse403
			>
		> => {
			const key = [
				"getClubEmailNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ClubResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubResponse>,
				GenericWretchError<
					| getClubEmailNotificationsResponse401
					| getClubEmailNotificationsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubResponse>,
				GenericWretchError<
					| getClubEmailNotificationsResponse401
					| getClubEmailNotificationsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				order?: string;
				sort?: string;
				search?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubResponse>,
					GenericWretchError<
						| getClubEmailNotificationsResponse401
						| getClubEmailNotificationsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubResponse>,
			GenericWretchError<
				| getClubEmailNotificationsResponse401
				| getClubEmailNotificationsResponse403
			>
		> => {
			const key = [
				"getClubEmailNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<ClubResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubResponse>,
				GenericWretchError<
					| getClubEmailNotificationsResponse401
					| getClubEmailNotificationsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubResponse>,
				GenericWretchError<
					| getClubEmailNotificationsResponse401
					| getClubEmailNotificationsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateClubEmailNotifications = (wretch: Http) => {
	const mutation = async (params: {
		body: BatchUpdateClubEmailNotification;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/user/club-email-notifications")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateClubEmailNotificationsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = updateClubEmailNotificationsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateClubEmailNotificationsResponse200> | undefined,
					GenericWretchError<updateClubEmailNotificationsResponse404>,
					{ body: BatchUpdateClubEmailNotification }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateClubEmailNotificationsResponse200> | undefined,
				GenericWretchError<updateClubEmailNotificationsResponse404>,
				{ body: BatchUpdateClubEmailNotification }
			>(mutation, options || {});
		},
	};
};

const getTeamEmailNotifications = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			order?: string;
			sort?: string;
			search?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				order: z.string().optional(),
				sort: z.string().optional(),
				search: z.string().optional(),
			})
			.parse(params);
		const { page, limit, order, sort, search } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/team-email-notifications")
				.query(qs.stringify({ page, limit, order, sort, search }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getTeamEmailNotificationsResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = getTeamEmailNotificationsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				order?: string;
				sort?: string;
				search?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamResponse>,
					GenericWretchError<
						| getTeamEmailNotificationsResponse401
						| getTeamEmailNotificationsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamResponse>,
			GenericWretchError<
				| getTeamEmailNotificationsResponse401
				| getTeamEmailNotificationsResponse403
			>
		> => {
			const key = [
				"getTeamEmailNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<TeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					| getTeamEmailNotificationsResponse401
					| getTeamEmailNotificationsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					| getTeamEmailNotificationsResponse401
					| getTeamEmailNotificationsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				order?: string;
				sort?: string;
				search?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamResponse>,
					GenericWretchError<
						| getTeamEmailNotificationsResponse401
						| getTeamEmailNotificationsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamResponse>,
			GenericWretchError<
				| getTeamEmailNotificationsResponse401
				| getTeamEmailNotificationsResponse403
			>
		> => {
			const key = [
				"getTeamEmailNotifications",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<TeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					| getTeamEmailNotificationsResponse401
					| getTeamEmailNotificationsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamResponse>,
				GenericWretchError<
					| getTeamEmailNotificationsResponse401
					| getTeamEmailNotificationsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateTeamEmailNotifications = (wretch: Http) => {
	const mutation = async (params: {
		body: BatchUpdateTeamEmailNotification;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/user/team-email-notifications")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateTeamEmailNotificationsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateTeamEmailNotificationsResponse200> | undefined,
					GenericWretchError<null>,
					{ body: BatchUpdateTeamEmailNotification }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateTeamEmailNotificationsResponse200> | undefined,
				GenericWretchError<null>,
				{ body: BatchUpdateTeamEmailNotification }
			>(mutation, options || {});
		},
	};
};

const getClub = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId?: number; type?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubInfo>> => {
		const args = z
			.object({
				clubId: z.number().int().nullable().optional(),
				type: z.string().optional(),
			})
			.parse(params);
		const { clubId, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/club")
				.query(qs.stringify({ clubId, type }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubInfo.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = getClubResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId?: number; type?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubInfo>,
					GenericWretchError<getClubResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubInfo>,
			GenericWretchError<getClubResponse404>
		> => {
			const key = ["getClub", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubInfo>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubInfo>,
				GenericWretchError<getClubResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubInfo>,
				GenericWretchError<getClubResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId?: number; type?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubInfo>,
					GenericWretchError<getClubResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubInfo>,
			GenericWretchError<getClubResponse404>
		> => {
			const key = ["getClub", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubInfo>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubInfo>,
				GenericWretchError<getClubResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubInfo>,
				GenericWretchError<getClubResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubTeamsResponse200>> => {
		const args = z
			.object({ clubId: z.number().int().nullable().optional() })
			.parse(params);
		const { clubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/teams")
				.query(qs.stringify({ clubId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubTeamsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = getClubTeamsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubTeamsResponse200>,
					GenericWretchError<getClubTeamsResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubTeamsResponse200>,
			GenericWretchError<getClubTeamsResponse404>
		> => {
			const key = ["getClubTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubTeamsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubTeamsResponse200>,
				GenericWretchError<getClubTeamsResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubTeamsResponse200>,
				GenericWretchError<getClubTeamsResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubTeamsResponse200>,
					GenericWretchError<getClubTeamsResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubTeamsResponse200>,
			GenericWretchError<getClubTeamsResponse404>
		> => {
			const key = ["getClubTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubTeamsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubTeamsResponse200>,
				GenericWretchError<getClubTeamsResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubTeamsResponse200>,
				GenericWretchError<getClubTeamsResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			clubId?: number;
			type?: string;
			startDate?: string;
			endDate?: string;
			direction?: string;
			teamId?: number;
			ageCategory?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<PlayerGameResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				clubId: z.number().int().optional(),
				type: z.string().optional(),
				startDate: z.string().optional(),
				endDate: z.string().optional(),
				direction: z.string().optional(),
				teamId: z.number().int().optional(),
				ageCategory: z.string().optional(),
			})
			.parse(params);
		const {
			page,
			limit,
			clubId,
			type,
			startDate,
			endDate,
			direction,
			teamId,
			ageCategory,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/games")
				.query(
					qs.stringify({
						page,
						limit,
						clubId,
						type,
						startDate,
						endDate,
						direction,
						teamId,
						ageCategory,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = PlayerGameResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubGamesResponse400.parse(json);
					break;
				}
				case 404: {
					err.json = getClubGamesResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				clubId?: number;
				type?: string;
				startDate?: string;
				endDate?: string;
				direction?: string;
				teamId?: number;
				ageCategory?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<PlayerGameResponse>,
					GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<PlayerGameResponse>,
			GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
		> => {
			const key = ["getClubGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				clubId?: number;
				type?: string;
				startDate?: string;
				endDate?: string;
				direction?: string;
				teamId?: number;
				ageCategory?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<PlayerGameResponse>,
					GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<PlayerGameResponse>,
			GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
		> => {
			const key = ["getClubGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<PlayerGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<PlayerGameResponse>,
				GenericWretchError<getClubGamesResponse400 | getClubGamesResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubGamesAgeCategories = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { type: string; clubId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubGamesAgeCategoriesResponse200>> => {
		const args = z
			.object({ type: z.string(), clubId: z.number().int() })
			.parse(params);
		const { type, clubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/games/age-categories")
				.query(qs.stringify({ type, clubId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubGamesAgeCategoriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubGamesAgeCategoriesResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { type: string; clubId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubGamesAgeCategoriesResponse200>,
					GenericWretchError<getClubGamesAgeCategoriesResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubGamesAgeCategoriesResponse200>,
			GenericWretchError<getClubGamesAgeCategoriesResponse400>
		> => {
			const key = [
				"getClubGamesAgeCategories",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getClubGamesAgeCategoriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubGamesAgeCategoriesResponse200>,
				GenericWretchError<getClubGamesAgeCategoriesResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubGamesAgeCategoriesResponse200>,
				GenericWretchError<getClubGamesAgeCategoriesResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { type: string; clubId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubGamesAgeCategoriesResponse200>,
					GenericWretchError<getClubGamesAgeCategoriesResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubGamesAgeCategoriesResponse200>,
			GenericWretchError<getClubGamesAgeCategoriesResponse400>
		> => {
			const key = [
				"getClubGamesAgeCategories",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getClubGamesAgeCategoriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubGamesAgeCategoriesResponse200>,
				GenericWretchError<getClubGamesAgeCategoriesResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubGamesAgeCategoriesResponse200>,
				GenericWretchError<getClubGamesAgeCategoriesResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubPlayersAgeCategories = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { type: string; clubId: number; teamId?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubPlayersAgeCategoriesResponse200>> => {
		const args = z
			.object({
				type: z.string(),
				clubId: z.number().int(),
				teamId: z.number().int().optional(),
			})
			.parse(params);
		const { type, clubId, teamId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/players/age-categories")
				.query(qs.stringify({ type, clubId, teamId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubPlayersAgeCategoriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubPlayersAgeCategoriesResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { type: string; clubId: number; teamId?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubPlayersAgeCategoriesResponse200>,
					GenericWretchError<getClubPlayersAgeCategoriesResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubPlayersAgeCategoriesResponse200>,
			GenericWretchError<getClubPlayersAgeCategoriesResponse400>
		> => {
			const key = [
				"getClubPlayersAgeCategories",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getClubPlayersAgeCategoriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubPlayersAgeCategoriesResponse200>,
				GenericWretchError<getClubPlayersAgeCategoriesResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubPlayersAgeCategoriesResponse200>,
				GenericWretchError<getClubPlayersAgeCategoriesResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { type: string; clubId: number; teamId?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubPlayersAgeCategoriesResponse200>,
					GenericWretchError<getClubPlayersAgeCategoriesResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubPlayersAgeCategoriesResponse200>,
			GenericWretchError<getClubPlayersAgeCategoriesResponse400>
		> => {
			const key = [
				"getClubPlayersAgeCategories",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getClubPlayersAgeCategoriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubPlayersAgeCategoriesResponse200>,
				GenericWretchError<getClubPlayersAgeCategoriesResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubPlayersAgeCategoriesResponse200>,
				GenericWretchError<getClubPlayersAgeCategoriesResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getGamePlayedTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId?: number; type?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getGamePlayedTeamsResponse200>> => {
		const args = z
			.object({
				clubId: z.number().int().optional(),
				type: z.string().optional(),
			})
			.parse(params);
		const { clubId, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/games/teams")
				.query(qs.stringify({ clubId, type }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getGamePlayedTeamsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = getGamePlayedTeamsResponse401.parse(json);
					break;
				}
				case 404: {
					err.json = getGamePlayedTeamsResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId?: number; type?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getGamePlayedTeamsResponse200>,
					GenericWretchError<
						getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getGamePlayedTeamsResponse200>,
			GenericWretchError<
				getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
			>
		> => {
			const key = ["getGamePlayedTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGamePlayedTeamsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getGamePlayedTeamsResponse200>,
				GenericWretchError<
					getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getGamePlayedTeamsResponse200>,
				GenericWretchError<
					getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId?: number; type?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getGamePlayedTeamsResponse200>,
					GenericWretchError<
						getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getGamePlayedTeamsResponse200>,
			GenericWretchError<
				getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
			>
		> => {
			const key = ["getGamePlayedTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getGamePlayedTeamsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getGamePlayedTeamsResponse200>,
				GenericWretchError<
					getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getGamePlayedTeamsResponse200>,
				GenericWretchError<
					getGamePlayedTeamsResponse401 | getGamePlayedTeamsResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const playerClubList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubName?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<playerClubListResponse200>> => {
		const args = z.object({ clubName: z.string().optional() }).parse(params);
		const { clubName } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/playerClubList")
				.query(qs.stringify({ clubName }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerClubListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubName?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<playerClubListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<playerClubListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["playerClubList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<playerClubListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<playerClubListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<playerClubListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubName?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<playerClubListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<playerClubListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["playerClubList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<playerClubListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<playerClubListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<playerClubListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const playerNameList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { playerName?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<playerNameListResponse200>> => {
		const args = z.object({ playerName: z.string().optional() }).parse(params);
		const { playerName } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/playerNameList")
				.query(qs.stringify({ playerName }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = playerNameListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { playerName?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<playerNameListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<playerNameListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["playerNameList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<playerNameListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<playerNameListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<playerNameListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { playerName?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<playerNameListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<playerNameListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["playerNameList", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<playerNameListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<playerNameListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<playerNameListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const loadGames = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			homeTeam?: string;
			startDate?: string;
			endDate?: string;
			category?: string;
			awayTeam?: string;
			teamSheet?: number;
			upload?: number;
			score?: number;
			isAnalysisFinished?: number;
			finishedGames?: number;
			upcomingGames?: number;
			page: number;
			limit: number;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ProgramGameResponse>> => {
		const args = z
			.object({
				homeTeam: z.string().optional(),
				startDate: z.string().optional(),
				endDate: z.string().optional(),
				category: z.string().optional(),
				awayTeam: z.string().optional(),
				teamSheet: z.number().int().optional(),
				upload: z.number().int().optional(),
				score: z.number().int().optional(),
				isAnalysisFinished: z.number().int().optional(),
				finishedGames: z.number().int().optional(),
				upcomingGames: z.number().int().optional(),
				page: z.number().int(),
				limit: z.number().int(),
			})
			.parse(params);
		const {
			homeTeam,
			startDate,
			endDate,
			category,
			awayTeam,
			teamSheet,
			upload,
			score,
			isAnalysisFinished,
			finishedGames,
			upcomingGames,
			page,
			limit,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/program-games")
				.query(
					qs.stringify({
						homeTeam,
						startDate,
						endDate,
						category,
						awayTeam,
						teamSheet,
						upload,
						score,
						isAnalysisFinished,
						finishedGames,
						upcomingGames,
						page,
						limit,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ProgramGameResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				homeTeam?: string;
				startDate?: string;
				endDate?: string;
				category?: string;
				awayTeam?: string;
				teamSheet?: number;
				upload?: number;
				score?: number;
				isAnalysisFinished?: number;
				finishedGames?: number;
				upcomingGames?: number;
				page: number;
				limit: number;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ProgramGameResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ProgramGameResponse>,
			GenericWretchError<null>
		> => {
			const key = ["loadGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ProgramGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ProgramGameResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ProgramGameResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				homeTeam?: string;
				startDate?: string;
				endDate?: string;
				category?: string;
				awayTeam?: string;
				teamSheet?: number;
				upload?: number;
				score?: number;
				isAnalysisFinished?: number;
				finishedGames?: number;
				upcomingGames?: number;
				page: number;
				limit: number;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ProgramGameResponse>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ProgramGameResponse>,
			GenericWretchError<null>
		> => {
			const key = ["loadGames", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ProgramGameResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ProgramGameResponse>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ProgramGameResponse>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addOrUpdateGame = (wretch: Http) => {
	const mutation = async (params: { body: GameManagementRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/portal/game").json(body).post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addOrUpdateGameResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = addOrUpdateGameResponse401.parse(json);
					break;
				}
				case 404: {
					err.json = addOrUpdateGameResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addOrUpdateGameResponse200> | undefined,
					GenericWretchError<
						addOrUpdateGameResponse401 | addOrUpdateGameResponse404
					>,
					{ body: GameManagementRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addOrUpdateGameResponse200> | undefined,
				GenericWretchError<
					addOrUpdateGameResponse401 | addOrUpdateGameResponse404
				>,
				{ body: GameManagementRequest }
			>(mutation, options || {});
		},
	};
};

const deleteGame = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/portal/game/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteGameResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = deleteGameResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteGameResponse200> | undefined,
					GenericWretchError<deleteGameResponse403>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteGameResponse200> | undefined,
				GenericWretchError<deleteGameResponse403>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const updateTeamScores = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: updateTeamScores_Body;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/game/${id}/update-score`)
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = updateTeamScoresResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateTeamScoresResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<updateTeamScoresResponse200> | undefined,
					GenericWretchError<updateTeamScoresResponse400>,
					{ id: number; body: updateTeamScores_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<updateTeamScoresResponse200> | undefined,
				GenericWretchError<updateTeamScoresResponse400>,
				{ id: number; body: updateTeamScores_Body }
			>(mutation, options || {});
		},
	};
};

const gameDropdownData = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<gameDropdownDataResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/game/fixtures")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = gameDropdownDataResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<gameDropdownDataResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<gameDropdownDataResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["gameDropdownData", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<gameDropdownDataResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<gameDropdownDataResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<gameDropdownDataResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<gameDropdownDataResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<gameDropdownDataResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["gameDropdownData", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<gameDropdownDataResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<gameDropdownDataResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<gameDropdownDataResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const loadHomeAndAwayTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { clubId?: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<loadHomeAndAwayTeamsResponse200>> => {
		const args = z
			.object({ clubId: z.number().int().nullable().optional() })
			.parse(params);
		const { clubId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/game/home-away-teams")
				.query(qs.stringify({ clubId }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = loadHomeAndAwayTeamsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { clubId?: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<loadHomeAndAwayTeamsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<loadHomeAndAwayTeamsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"loadHomeAndAwayTeams",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<loadHomeAndAwayTeamsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<loadHomeAndAwayTeamsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<loadHomeAndAwayTeamsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { clubId?: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<loadHomeAndAwayTeamsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<loadHomeAndAwayTeamsResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"loadHomeAndAwayTeams",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<loadHomeAndAwayTeamsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<loadHomeAndAwayTeamsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<loadHomeAndAwayTeamsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const searchClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { query: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<searchClubsResponse200>> => {
		const args = z.object({ query: z.string() }).parse(params);
		const { query } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/program-game/search-clubs")
				.query(qs.stringify({ query }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = searchClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = searchClubsResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { query: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<searchClubsResponse200>,
					GenericWretchError<searchClubsResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<searchClubsResponse200>,
			GenericWretchError<searchClubsResponse400>
		> => {
			const key = ["searchClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<searchClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<searchClubsResponse200>,
				GenericWretchError<searchClubsResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<searchClubsResponse200>,
				GenericWretchError<searchClubsResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { query: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<searchClubsResponse200>,
					GenericWretchError<searchClubsResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<searchClubsResponse200>,
			GenericWretchError<searchClubsResponse400>
		> => {
			const key = ["searchClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<searchClubsResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<searchClubsResponse200>,
				GenericWretchError<searchClubsResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<searchClubsResponse200>,
				GenericWretchError<searchClubsResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const similarClubs = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { query: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<similarClubsResponse200>> => {
		const args = z.object({ query: z.string() }).parse(params);
		const { query } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/similar-clubs")
				.query(qs.stringify({ query }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = similarClubsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = similarClubsResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { query: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<similarClubsResponse200>,
					GenericWretchError<similarClubsResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<similarClubsResponse200>,
			GenericWretchError<similarClubsResponse400>
		> => {
			const key = ["similarClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<similarClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<similarClubsResponse200>,
				GenericWretchError<similarClubsResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<similarClubsResponse200>,
				GenericWretchError<similarClubsResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { query: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<similarClubsResponse200>,
					GenericWretchError<similarClubsResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<similarClubsResponse200>,
			GenericWretchError<similarClubsResponse400>
		> => {
			const key = ["similarClubs", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<similarClubsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<similarClubsResponse200>,
				GenericWretchError<similarClubsResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<similarClubsResponse200>,
				GenericWretchError<similarClubsResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubAllTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { page: number; limit: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamResponse2>> => {
		const args = z
			.object({ page: z.number().int(), limit: z.number().int() })
			.parse(params);
		const { page, limit } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/api/teams")
				.query(qs.stringify({ page, limit }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamResponse2.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getClubAllTeamsResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { page: number; limit: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamResponse2>,
					GenericWretchError<getClubAllTeamsResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamResponse2>,
			GenericWretchError<getClubAllTeamsResponse400>
		> => {
			const key = ["getClubAllTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamResponse2>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamResponse2>,
				GenericWretchError<getClubAllTeamsResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamResponse2>,
				GenericWretchError<getClubAllTeamsResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { page: number; limit: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamResponse2>,
					GenericWretchError<getClubAllTeamsResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamResponse2>,
			GenericWretchError<getClubAllTeamsResponse400>
		> => {
			const key = ["getClubAllTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamResponse2>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamResponse2>,
				GenericWretchError<getClubAllTeamsResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamResponse2>,
				GenericWretchError<getClubAllTeamsResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdateTeam = (wretch: Http) => {
	const mutation = async (params: { body: addUpdateTeam_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch.url("/portal/club/team").json(body).post().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdateTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = addUpdateTeamResponse400.parse(json);
					break;
				}
				case 409: {
					err.json = addUpdateTeamResponse409.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdateTeamResponse200> | undefined,
					GenericWretchError<
						addUpdateTeamResponse400 | addUpdateTeamResponse409
					>,
					{ body: addUpdateTeam_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdateTeamResponse200> | undefined,
				GenericWretchError<addUpdateTeamResponse400 | addUpdateTeamResponse409>,
				{ body: addUpdateTeam_Body }
			>(mutation, options || {});
		},
	};
};

const getTeamDetail = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<TeamPageInfo>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/team/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = TeamPageInfo.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = getTeamDetailResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<TeamPageInfo>,
					GenericWretchError<getTeamDetailResponse404>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<TeamPageInfo>,
			GenericWretchError<getTeamDetailResponse404>
		> => {
			const key = ["getTeamDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamPageInfo>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<TeamPageInfo>,
				GenericWretchError<getTeamDetailResponse404>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<TeamPageInfo>,
				GenericWretchError<getTeamDetailResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<TeamPageInfo>,
					GenericWretchError<getTeamDetailResponse404>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<TeamPageInfo>,
			GenericWretchError<getTeamDetailResponse404>
		> => {
			const key = ["getTeamDetail", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<TeamPageInfo>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<TeamPageInfo>,
				GenericWretchError<getTeamDetailResponse404>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<TeamPageInfo>,
				GenericWretchError<getTeamDetailResponse404>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const deleteTeam = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/portal/team/${id}`).delete().res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = deleteTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 404: {
					err.json = deleteTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<deleteTeamResponse200> | undefined,
					GenericWretchError<deleteTeamResponse404>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<deleteTeamResponse200> | undefined,
				GenericWretchError<deleteTeamResponse404>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const clubCoaches = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<clubCoachesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/coaches")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = clubCoachesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<clubCoachesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<clubCoachesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["clubCoaches", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubCoachesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<clubCoachesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<clubCoachesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["clubCoaches", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<clubCoachesResponse200>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<clubCoachesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getClubTeamsList = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getClubTeamsListResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club-teams")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getClubTeamsListResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getClubTeamsListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getClubTeamsListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubTeamsList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubTeamsListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getClubTeamsListResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getClubTeamsListResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getClubTeamsList", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getClubTeamsListResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getClubTeamsListResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const teamPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			id: number;
			page: number;
			limit: number;
			order?: object;
			position?: string;
			search?: string;
			strongestFoot?: string;
			nationality?: string;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubPlayersResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				id: z.number().int(),
				order: z
					.object({
						name: z.string().describe("Sorting column name").optional(),
						sort: z.string().describe("Sorting column type").optional(),
					})
					.optional(),
				position: z.string().optional(),
				search: z.string().optional(),
				strongestFoot: z.string().optional(),
				nationality: z.string().optional(),
			})
			.parse(params);
		const {
			id,
			page,
			limit,
			order,
			position,
			search,
			strongestFoot,
			nationality,
		} = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/team/${id}/players`)
				.query(
					qs.stringify({
						page,
						limit,
						order,
						position,
						search,
						strongestFoot,
						nationality,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = teamPlayersResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				id: number;
				page: number;
				limit: number;
				order?: object;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<teamPlayersResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<teamPlayersResponse400>
		> => {
			const key = ["teamPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				id: number;
				page: number;
				limit: number;
				order?: object;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<teamPlayersResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<teamPlayersResponse400>
		> => {
			const key = ["teamPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<teamPlayersResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const addUpdatePlayer = (wretch: Http) => {
	const mutation = async (params: { body: addUpdatePlayer_Body }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/club/player")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = addUpdatePlayerResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = addUpdatePlayerResponse400.parse(json);
					break;
				}
				case 409: {
					err.json = addUpdatePlayerResponse409.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<addUpdatePlayerResponse200> | undefined,
					GenericWretchError<
						addUpdatePlayerResponse400 | addUpdatePlayerResponse409
					>,
					{ body: addUpdatePlayer_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<addUpdatePlayerResponse200> | undefined,
				GenericWretchError<
					addUpdatePlayerResponse400 | addUpdatePlayerResponse409
				>,
				{ body: addUpdatePlayer_Body }
			>(mutation, options || {});
		},
	};
};

const formerPlayers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page: number;
			limit: number;
			position?: string;
			search?: string;
			strongestFoot?: string;
			nationality?: string;
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ClubPlayersResponse>> => {
		const args = z
			.object({
				page: z.number().int(),
				limit: z.number().int(),
				position: z.string().optional(),
				search: z.string().optional(),
				strongestFoot: z.string().optional(),
				nationality: z.string().optional(),
				order: z
					.object({
						name: z.string().describe("Sorting column name").optional(),
						sort: z.string().describe("Sorting column type").optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, position, search, strongestFoot, nationality, order } =
			args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/club/former-players")
				.query(
					qs.stringify({
						page,
						limit,
						position,
						search,
						strongestFoot,
						nationality,
						order,
					}),
				)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ClubPlayersResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = formerPlayersResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page: number;
				limit: number;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<formerPlayersResponse400>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<formerPlayersResponse400>
		> => {
			const key = ["formerPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page: number;
				limit: number;
				position?: string;
				search?: string;
				strongestFoot?: string;
				nationality?: string;
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ClubPlayersResponse>,
					GenericWretchError<formerPlayersResponse400>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ClubPlayersResponse>,
			GenericWretchError<formerPlayersResponse400>
		> => {
			const key = ["formerPlayers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ClubPlayersResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ClubPlayersResponse>,
				GenericWretchError<formerPlayersResponse400>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getTeamByIdAndCoachId = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id?: number; coachId?: number; gender?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getTeamByIdAndCoachIdResponse200>> => {
		const args = z
			.object({
				id: z.number().int().optional(),
				coachId: z.number().int().optional(),
				gender: z.string().optional(),
			})
			.parse(params);
		const { id, coachId, gender } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/team")
				.query(qs.stringify({ id, coachId, gender }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getTeamByIdAndCoachIdResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id?: number; coachId?: number; gender?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getTeamByIdAndCoachIdResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getTeamByIdAndCoachIdResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getTeamByIdAndCoachId",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getTeamByIdAndCoachIdResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id?: number; coachId?: number; gender?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getTeamByIdAndCoachIdResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getTeamByIdAndCoachIdResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getTeamByIdAndCoachId",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getTeamByIdAndCoachIdResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getTeamByIdAndCoachIdResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const uploadPlayerPhoto = (wretch: Http) => {
	const mutation = async (params: { body: uploadPlayerPhotoRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/player/upload-profile-picture")
				.json(body)
				.put()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = uploadPlayerPhotoResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = uploadPlayerPhotoResponse400.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<uploadPlayerPhotoResponse200> | undefined,
					GenericWretchError<uploadPlayerPhotoResponse400>,
					{ body: uploadPlayerPhotoRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<uploadPlayerPhotoResponse200> | undefined,
				GenericWretchError<uploadPlayerPhotoResponse400>,
				{ body: uploadPlayerPhotoRequestBody }
			>(mutation, options || {});
		},
	};
};

const assignCoachesToTeam = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: assignCoachesToTeam_Body;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/team/${id}/assign-coaches`)
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = assignCoachesToTeamResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<assignCoachesToTeamResponse200> | undefined,
					GenericWretchError<null>,
					{ id: number; body: assignCoachesToTeam_Body }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<assignCoachesToTeamResponse200> | undefined,
				GenericWretchError<null>,
				{ id: number; body: assignCoachesToTeam_Body }
			>(mutation, options || {});
		},
	};
};

const getPositions = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getPositionsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/api/positions")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPositionsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getPositionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getPositionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getPositions", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPositionsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getPositionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getPositionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getPositionsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getPositionsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getPositions", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getPositionsResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getPositionsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getPositionsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getNationalities = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getNationalitiesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/api/nationalities")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getNationalitiesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getNationalitiesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getNationalitiesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getNationalities", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNationalitiesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getNationalitiesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getNationalitiesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getNationalities", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getNationalitiesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getNationalitiesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getCategories = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getCategoriesResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/api/categories")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getCategoriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getCategoriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getCategoriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getCategories", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getCategoriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getCategoriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getCategoriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getCategories", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getCategoriesResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getCategoriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const generateSignUpLink = (wretch: Http) => {
	const mutation = async (params: { body: generateSignUpLinkRequestBody }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/sign-up/player/generate-link")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = generateSignUpLinkResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = generateSignUpLinkResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = generateSignUpLinkResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = generateSignUpLinkResponse404.parse(json);
					break;
				}
				case 409: {
					err.json = generateSignUpLinkResponse409.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<generateSignUpLinkResponse200> | undefined,
					GenericWretchError<
						| generateSignUpLinkResponse400
						| generateSignUpLinkResponse403
						| generateSignUpLinkResponse404
						| generateSignUpLinkResponse409
					>,
					{ body: generateSignUpLinkRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<generateSignUpLinkResponse200> | undefined,
				GenericWretchError<
					| generateSignUpLinkResponse400
					| generateSignUpLinkResponse403
					| generateSignUpLinkResponse404
					| generateSignUpLinkResponse409
				>,
				{ body: generateSignUpLinkRequestBody }
			>(mutation, options || {});
		},
	};
};

const getPlayerLatestLogEntries = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number; type: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getPlayerLatestLogEntriesResponse200>> => {
		const args = z
			.object({ id: z.number().int(), type: z.string() })
			.parse(params);
		const { id, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/player/${id}/${type}/latest-updates`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getPlayerLatestLogEntriesResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number; type: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getPlayerLatestLogEntriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getPlayerLatestLogEntriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getPlayerLatestLogEntries",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getPlayerLatestLogEntriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number; type: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getPlayerLatestLogEntriesResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getPlayerLatestLogEntriesResponse200>,
			GenericWretchError<null>
		> => {
			const key = [
				"getPlayerLatestLogEntries",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<
				ApiResponse<getPlayerLatestLogEntriesResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getPlayerLatestLogEntriesResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayersOfTheWeek = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { limit?: number; type: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<WeeklyPlayersDTO>> => {
		const args = z
			.object({
				limit: z.number().int().optional(),
				type: z.enum([
					"rating",
					"goals",
					"assists",
					"shots",
					"take-ons",
					"passes-forward",
					"aerial-duels",
					"xg",
					"xa",
					"non-penalty-xg",
				]),
			})
			.parse(params);
		const { limit, type } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/weekly-players")
				.query(qs.stringify({ limit, type }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = WeeklyPlayersDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { limit?: number; type: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<WeeklyPlayersDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<WeeklyPlayersDTO>,
			GenericWretchError<null>
		> => {
			const key = ["getPlayersOfTheWeek", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<WeeklyPlayersDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { limit?: number; type: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<WeeklyPlayersDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<WeeklyPlayersDTO>,
			GenericWretchError<null>
		> => {
			const key = ["getPlayersOfTheWeek", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<WeeklyPlayersDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getPlayersOfTheWeekSearch = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { limit?: number; filters?: WeeklyPlayerSearchFilterDTO[] },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<WeeklyPlayersDTO>> => {
		const args = z
			.object({
				limit: z.number().int().optional(),
				filters: z.array(z.any()).optional(),
			})
			.parse(params);
		const { limit, filters } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/search-weekly-players")
				.query(qs.stringify({ limit, filters }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = WeeklyPlayersDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { limit?: number; filters?: WeeklyPlayerSearchFilterDTO[] },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<WeeklyPlayersDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<WeeklyPlayersDTO>,
			GenericWretchError<null>
		> => {
			const key = [
				"getPlayersOfTheWeekSearch",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<WeeklyPlayersDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { limit?: number; filters?: WeeklyPlayerSearchFilterDTO[] },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<WeeklyPlayersDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<WeeklyPlayersDTO>,
			GenericWretchError<null>
		> => {
			const key = [
				"getPlayersOfTheWeekSearch",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<WeeklyPlayersDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<WeeklyPlayersDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const getWeekNumbers = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { weekFormat?: string },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<WeekNumbersResponseDTO>> => {
		const args = z.object({ weekFormat: z.string().optional() }).parse(params);
		const { weekFormat } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/week-numbers")
				.query(qs.stringify({ weekFormat }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = WeekNumbersResponseDTO.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { weekFormat?: string },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<WeekNumbersResponseDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<WeekNumbersResponseDTO>,
			GenericWretchError<null>
		> => {
			const key = ["getWeekNumbers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<WeekNumbersResponseDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<WeekNumbersResponseDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<WeekNumbersResponseDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { weekFormat?: string },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<WeekNumbersResponseDTO>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<WeekNumbersResponseDTO>,
			GenericWretchError<null>
		> => {
			const key = ["getWeekNumbers", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<WeekNumbersResponseDTO>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<WeekNumbersResponseDTO>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<WeekNumbersResponseDTO>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const postGameTeamSheetExport = (wretch: Http) => {
	const mutation = async (params: { id: number; exportType: string }) => {
		const args = z
			.object({
				id: z.number().int().describe("Game ID"),
				exportType: z.enum(["csv", "pdf", "xlsx"]).describe("Export type"),
			})
			.parse(params);
		const { id, exportType } = args;

		try {
			const res = await wretch
				.url(`/game/${id}/team-sheet/export/${exportType}`)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = FileResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = postGameTeamSheetExportResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = postGameTeamSheetExportResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = postGameTeamSheetExportResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<FileResponse> | undefined,
					GenericWretchError<
						| postGameTeamSheetExportResponse400
						| postGameTeamSheetExportResponse403
						| postGameTeamSheetExportResponse404
					>,
					{ id: number; exportType: string }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<FileResponse> | undefined,
				GenericWretchError<
					| postGameTeamSheetExportResponse400
					| postGameTeamSheetExportResponse403
					| postGameTeamSheetExportResponse404
				>,
				{ id: number; exportType: string }
			>(mutation, options || {});
		},
	};
};

const getUserWidgetSettings = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getUserWidgetSettingsResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/user/widget-settings")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getUserWidgetSettingsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getUserWidgetSettingsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getUserWidgetSettingsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserWidgetSettings", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getUserWidgetSettingsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getUserWidgetSettingsResponse200>,
					GenericWretchError<null>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getUserWidgetSettingsResponse200>,
			GenericWretchError<null>
		> => {
			const key = ["getUserWidgetSettings", ...(options?.queryKey || [])];
			const query: QueryFunction<
				ApiResponse<getUserWidgetSettingsResponse200>
			> = async ({ meta, queryKey, pageParam, signal }) => {
				return queryFn(signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getUserWidgetSettingsResponse200>,
				GenericWretchError<null>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const saveUserWidgetSettings = (wretch: Http) => {
	const mutation = async (params: {
		body: saveUserWidgetSettingsRequestBody;
	}) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/user/widget-settings")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = saveUserWidgetSettingsResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<saveUserWidgetSettingsResponse200> | undefined,
					GenericWretchError<null>,
					{ body: saveUserWidgetSettingsRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<saveUserWidgetSettingsResponse200> | undefined,
				GenericWretchError<null>,
				{ body: saveUserWidgetSettingsRequestBody }
			>(mutation, options || {});
		},
	};
};

const getAnnounceKitToken = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<getAnnounceKitTokenResponse200>> => {
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/announce-kit/generate-token")
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = getAnnounceKitTokenResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 403: {
					err.json = getAnnounceKitTokenResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			options?: Omit<
				UseQueryOptions<
					ApiResponse<getAnnounceKitTokenResponse200>,
					GenericWretchError<getAnnounceKitTokenResponse403>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<getAnnounceKitTokenResponse200>,
			GenericWretchError<getAnnounceKitTokenResponse403>
		> => {
			const key = ["getAnnounceKitToken", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAnnounceKitTokenResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<getAnnounceKitTokenResponse200>,
				GenericWretchError<getAnnounceKitTokenResponse403>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<getAnnounceKitTokenResponse200>,
				GenericWretchError<getAnnounceKitTokenResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<getAnnounceKitTokenResponse200>,
					GenericWretchError<getAnnounceKitTokenResponse403>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<getAnnounceKitTokenResponse200>,
			GenericWretchError<getAnnounceKitTokenResponse403>
		> => {
			const key = ["getAnnounceKitToken", ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<getAnnounceKitTokenResponse200>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<getAnnounceKitTokenResponse200>,
				GenericWretchError<getAnnounceKitTokenResponse403>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<getAnnounceKitTokenResponse200>,
				GenericWretchError<getAnnounceKitTokenResponse403>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const portalGetGameAnalysisPhysical = (
	wretch: Http,
	queryClient: QueryClient,
) => {
	const queryFn = async (
		params: { gameId: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<GameAnalysisPhysicalResponse>> => {
		const args = z
			.object({
				gameId: z
					.number()
					.int()
					.describe("The ID of the game to retrieve physical analysis for."),
			})
			.parse(params);
		const { gameId } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/game-analysis-physical/${gameId}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = GameAnalysisPhysicalResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = portalGetGameAnalysisPhysicalResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = portalGetGameAnalysisPhysicalResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = portalGetGameAnalysisPhysicalResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { gameId: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<GameAnalysisPhysicalResponse>,
					GenericWretchError<
						| portalGetGameAnalysisPhysicalResponse401
						| portalGetGameAnalysisPhysicalResponse403
						| portalGetGameAnalysisPhysicalResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<GameAnalysisPhysicalResponse>,
			GenericWretchError<
				| portalGetGameAnalysisPhysicalResponse401
				| portalGetGameAnalysisPhysicalResponse403
				| portalGetGameAnalysisPhysicalResponse404
			>
		> => {
			const key = [
				"portalGetGameAnalysisPhysical",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameAnalysisPhysicalResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| portalGetGameAnalysisPhysicalResponse401
					| portalGetGameAnalysisPhysicalResponse403
					| portalGetGameAnalysisPhysicalResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| portalGetGameAnalysisPhysicalResponse401
					| portalGetGameAnalysisPhysicalResponse403
					| portalGetGameAnalysisPhysicalResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { gameId: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<GameAnalysisPhysicalResponse>,
					GenericWretchError<
						| portalGetGameAnalysisPhysicalResponse401
						| portalGetGameAnalysisPhysicalResponse403
						| portalGetGameAnalysisPhysicalResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<GameAnalysisPhysicalResponse>,
			GenericWretchError<
				| portalGetGameAnalysisPhysicalResponse401
				| portalGetGameAnalysisPhysicalResponse403
				| portalGetGameAnalysisPhysicalResponse404
			>
		> => {
			const key = [
				"portalGetGameAnalysisPhysical",
				params,
				...(options?.queryKey || []),
			];
			const query: QueryFunction<ApiResponse<GameAnalysisPhysicalResponse>> =
				async ({ meta, queryKey, pageParam, signal }) => {
					return queryFn(params, signal);
				};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| portalGetGameAnalysisPhysicalResponse401
					| portalGetGameAnalysisPhysicalResponse403
					| portalGetGameAnalysisPhysicalResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<GameAnalysisPhysicalResponse>,
				GenericWretchError<
					| portalGetGameAnalysisPhysicalResponse401
					| portalGetGameAnalysisPhysicalResponse403
					| portalGetGameAnalysisPhysicalResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const generateDiscoverRedirect = (wretch: Http) => {
	const mutation = async () => {
		try {
			const res = await wretch
				.url("/portal/generate-discover-redirect")
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = generateDiscoverRedirectResponse200.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 401: {
					err.json = generateDiscoverRedirectResponse401.parse(json);
					break;
				}
				case 403: {
					err.json = generateDiscoverRedirectResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<generateDiscoverRedirectResponse200> | undefined,
					GenericWretchError<
						| generateDiscoverRedirectResponse401
						| generateDiscoverRedirectResponse403
					>
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<generateDiscoverRedirectResponse200> | undefined,
				GenericWretchError<
					| generateDiscoverRedirectResponse401
					| generateDiscoverRedirectResponse403
				>
			>(mutation, options || {});
		},
	};
};

const getShadowTeams = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: {
			page?: number;
			limit?: number;
			filters?: object[];
			order?: object;
		},
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ShadowTeamsResponse>> => {
		const args = z
			.object({
				page: z.number().int().optional(),
				limit: z.number().int().optional(),
				filters: z
					.array(
						z.object({
							type: z
								.enum([
									"exact",
									"search",
									"boolean",
									"exists",
									"inarray",
									"datetimerange",
									"numberrange",
								])
								.describe("Type of filter")
								.optional(),
							field: z
								.enum(["user", "name", "formation"])
								.describe("Property to filter")
								.optional(),
							value: z
								.any()
								.superRefine((x, ctx) => {
									const schemas = [
										z.string(),
										z.number().int(),
										z.boolean(),
										z.object({
											start: z.string().datetime({ offset: true }).optional(),
											end: z.string().datetime({ offset: true }).optional(),
										}),
									];
									const errors = schemas.reduce<z.ZodError[]>(
										(errors, schema) =>
											((result) =>
												result.error ? [...errors, result.error] : errors)(
												schema.safeParse(x),
											),
										[],
									);
									if (schemas.length - errors.length !== 1) {
										ctx.addIssue({
											path: ctx.path,
											code: "invalid_union",
											unionErrors: errors,
											message: "Invalid input: Should pass single schema",
										});
									}
								})
								.describe(
									"Filter value, can be a string, integer, date or a date range",
								)
								.optional(),
						}),
					)
					.optional(),
				order: z
					.object({
						field: z
							.enum([
								"name",
								"formation",
								"country",
								"playersCount",
								"createdAt",
								"updatedAt",
							])
							.describe("Sorting field")
							.optional(),
						direction: z
							.enum(["ASC", "DESC"])
							.describe("Sorting direction")
							.optional(),
					})
					.optional(),
			})
			.parse(params);
		const { page, limit, filters, order } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url("/portal/shadow-teams")
				.query(qs.stringify({ page, limit, filters, order }))
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ShadowTeamsResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getShadowTeamsResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getShadowTeamsResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ShadowTeamsResponse>,
					GenericWretchError<
						getShadowTeamsResponse400 | getShadowTeamsResponse403
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ShadowTeamsResponse>,
			GenericWretchError<getShadowTeamsResponse400 | getShadowTeamsResponse403>
		> => {
			const key = ["getShadowTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ShadowTeamsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ShadowTeamsResponse>,
				GenericWretchError<
					getShadowTeamsResponse400 | getShadowTeamsResponse403
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ShadowTeamsResponse>,
				GenericWretchError<
					getShadowTeamsResponse400 | getShadowTeamsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: {
				page?: number;
				limit?: number;
				filters?: object[];
				order?: object;
			},
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ShadowTeamsResponse>,
					GenericWretchError<
						getShadowTeamsResponse400 | getShadowTeamsResponse403
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ShadowTeamsResponse>,
			GenericWretchError<getShadowTeamsResponse400 | getShadowTeamsResponse403>
		> => {
			const key = ["getShadowTeams", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ShadowTeamsResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ShadowTeamsResponse>,
				GenericWretchError<
					getShadowTeamsResponse400 | getShadowTeamsResponse403
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ShadowTeamsResponse>,
				GenericWretchError<
					getShadowTeamsResponse400 | getShadowTeamsResponse403
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const createShadowTeam = (wretch: Http) => {
	const mutation = async (params: { body: ShadowTeamPostPutRequest }) => {
		const args = z.object({ body: z.any() }).parse(params);
		const { body } = args;

		try {
			const res = await wretch
				.url("/portal/shadow-teams")
				.json(body)
				.post()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ShadowTeamResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = createShadowTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = createShadowTeamResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = createShadowTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<ShadowTeamResponse> | undefined,
					GenericWretchError<
						| createShadowTeamResponse400
						| createShadowTeamResponse403
						| createShadowTeamResponse404
					>,
					{ body: ShadowTeamPostPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<ShadowTeamResponse> | undefined,
				GenericWretchError<
					| createShadowTeamResponse400
					| createShadowTeamResponse403
					| createShadowTeamResponse404
				>,
				{ body: ShadowTeamPostPutRequest }
			>(mutation, options || {});
		},
	};
};

const getShadowTeam = (wretch: Http, queryClient: QueryClient) => {
	const queryFn = async (
		params: { id: number },
		signal?: AbortSignal | undefined,
	): Promise<ApiResponse<ShadowTeamResponse>> => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;
		try {
			const res = await wretch
				.options({ signal })
				.url(`/portal/shadow-team/${id}`)
				.get()
				.res();
			const json = await res.json();
			switch (res.status) {
				case 200: {
					const data = ShadowTeamResponse.parse(json);
					return {
						status: res.status,
						data,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = getShadowTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = getShadowTeamResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = getShadowTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		query: queryFn,
		useQuery: (
			params: { id: number },
			options?: Omit<
				UseQueryOptions<
					ApiResponse<ShadowTeamResponse>,
					GenericWretchError<
						| getShadowTeamResponse400
						| getShadowTeamResponse403
						| getShadowTeamResponse404
					>
				>,
				"queryFn"
			>,
		): UseQueryResult<
			ApiResponse<ShadowTeamResponse>,
			GenericWretchError<
				| getShadowTeamResponse400
				| getShadowTeamResponse403
				| getShadowTeamResponse404
			>
		> => {
			const key = ["getShadowTeam", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ShadowTeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useQuery<
				ApiResponse<ShadowTeamResponse>,
				GenericWretchError<
					| getShadowTeamResponse400
					| getShadowTeamResponse403
					| getShadowTeamResponse404
				>
			>(key, query, options || {}) as UseQueryResult<
				ApiResponse<ShadowTeamResponse>,
				GenericWretchError<
					| getShadowTeamResponse400
					| getShadowTeamResponse403
					| getShadowTeamResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
		useInfiniteQuery: (
			params: { id: number },
			options?: Omit<
				UseInfiniteQueryOptions<
					ApiResponse<ShadowTeamResponse>,
					GenericWretchError<
						| getShadowTeamResponse400
						| getShadowTeamResponse403
						| getShadowTeamResponse404
					>
				>,
				"queryFn"
			>,
		): UseInfiniteQueryResult<
			ApiResponse<ShadowTeamResponse>,
			GenericWretchError<
				| getShadowTeamResponse400
				| getShadowTeamResponse403
				| getShadowTeamResponse404
			>
		> => {
			const key = ["getShadowTeam", params, ...(options?.queryKey || [])];
			const query: QueryFunction<ApiResponse<ShadowTeamResponse>> = async ({
				meta,
				queryKey,
				pageParam,
				signal,
			}) => {
				return queryFn(params, signal);
			};
			const invalidate = () => queryClient.invalidateQueries(key);
			const queryResult = useInfiniteQuery<
				ApiResponse<ShadowTeamResponse>,
				GenericWretchError<
					| getShadowTeamResponse400
					| getShadowTeamResponse403
					| getShadowTeamResponse404
				>
			>(key, query, options || {}) as UseInfiniteQueryResult<
				ApiResponse<ShadowTeamResponse>,
				GenericWretchError<
					| getShadowTeamResponse400
					| getShadowTeamResponse403
					| getShadowTeamResponse404
				>
			>;
			queryResult.invalidate = invalidate;
			queryResult.key = key;
			return queryResult;
		},
	};
};

const updateShadowTeam = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: ShadowTeamPostPutRequest;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/shadow-team/${id}`)
				.json(body)
				.put()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateShadowTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = updateShadowTeamResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = updateShadowTeamResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| updateShadowTeamResponse400
						| updateShadowTeamResponse403
						| updateShadowTeamResponse404
					>,
					{ id: number; body: ShadowTeamPostPutRequest }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| updateShadowTeamResponse400
					| updateShadowTeamResponse403
					| updateShadowTeamResponse404
				>,
				{ id: number; body: ShadowTeamPostPutRequest }
			>(mutation, options || {});
		},
	};
};

const deleteShadowTeam = (wretch: Http) => {
	const mutation = async (params: { id: number }) => {
		const args = z.object({ id: z.number().int() }).parse(params);
		const { id } = args;

		try {
			const res = await wretch.url(`/portal/shadow-team/${id}`).delete().res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = deleteShadowTeamResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = deleteShadowTeamResponse403.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						deleteShadowTeamResponse400 | deleteShadowTeamResponse403
					>,
					{ id: number }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					deleteShadowTeamResponse400 | deleteShadowTeamResponse403
				>,
				{ id: number }
			>(mutation, options || {});
		},
	};
};

const updateShadowTeamPartially = (wretch: Http) => {
	const mutation = async (params: {
		id: number;
		body: updateShadowTeamPartiallyRequestBody;
	}) => {
		const args = z
			.object({ id: z.number().int(), body: z.any() })
			.parse(params);
		const { id, body } = args;

		try {
			const res = await wretch
				.url(`/portal/shadow-team/${id}`)
				.json(body)
				.patch()
				.res();
			switch (res.status) {
				case 204: {
					return {
						status: res.status,
						data: null,
						headers: res.headers,
						url: res.url,
					};
				}
			}
		} catch (e) {
			const err = error(e);
			const json = err.json;
			switch (err.status) {
				case 400: {
					err.json = updateShadowTeamPartiallyResponse400.parse(json);
					break;
				}
				case 403: {
					err.json = updateShadowTeamPartiallyResponse403.parse(json);
					break;
				}
				case 404: {
					err.json = updateShadowTeamPartiallyResponse404.parse(json);
					break;
				}
			}
			throw err;
		}

		invariant(false);
	};

	return {
		mutation,
		useMutation: (
			options?: Omit<
				UseMutationOptions<
					ApiResponse<null> | undefined,
					GenericWretchError<
						| updateShadowTeamPartiallyResponse400
						| updateShadowTeamPartiallyResponse403
						| updateShadowTeamPartiallyResponse404
					>,
					{ id: number; body: updateShadowTeamPartiallyRequestBody }
				>,
				"mutationFn"
			>,
		) => {
			return useMutation<
				ApiResponse<null> | undefined,
				GenericWretchError<
					| updateShadowTeamPartiallyResponse400
					| updateShadowTeamPartiallyResponse403
					| updateShadowTeamPartiallyResponse404
				>,
				{ id: number; body: updateShadowTeamPartiallyRequestBody }
			>(mutation, options || {});
		},
	};
};

type Http = ReturnType<PortalApiClient["createHttp"]>;

export class PortalApiClient {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private operations: Map<string, any>;

	public http: Http;
	public queryClient: QueryClient;
	private onHttpConfigure: (wretch: Http) => Http;

	/**
				get token movements
			*/
	declare getTokenMovements: ReturnType<typeof getTokenMovements>;

	/**
				post token movement
			*/
	declare postTokenMovement: ReturnType<typeof postTokenMovement>;

	declare getGamePage: ReturnType<typeof getGamePage>;

	declare getGamePageLazy: ReturnType<typeof getGamePageLazy>;

	/**
				put game score
			*/
	declare putGameScore: ReturnType<typeof putGameScore>;

	/**
				endpoint to gain game teams stat data for game page
			*/
	declare getGameTeamStat: ReturnType<typeof getGameTeamStat>;

	/**
				endpoint to gain game players stat data for game page
			*/
	declare getGamePlayerStat: ReturnType<typeof getGamePlayerStat>;

	/**
				put game team colors
			*/
	declare putGameTeamColors: ReturnType<typeof putGameTeamColors>;

	/**
				sets rating to game player
			*/
	declare setRatingToGamePlayer: ReturnType<typeof setRatingToGamePlayer>;

	/**
				sets badges to game player
			*/
	declare setBadgesToGamePlayer: ReturnType<typeof setBadgesToGamePlayer>;

	/**
				post highlight
			*/
	declare postGameHighlight: ReturnType<typeof postGameHighlight>;

	/**
				delete highlight
			*/
	declare deleteGameHighlight: ReturnType<typeof deleteGameHighlight>;

	/**
				patch highlight
			*/
	declare patchGameHighlight: ReturnType<typeof patchGameHighlight>;

	/**
				get game clips, limited by current user ownership
			*/
	declare getGameClips: ReturnType<typeof getGameClips>;

	/**
				get game clip
			*/
	declare getGameClip: ReturnType<typeof getGameClip>;

	/**
				put game clip
			*/
	declare putGameClip: ReturnType<typeof putGameClip>;

	/**
				delete game clip
			*/
	declare deleteGameClip: ReturnType<typeof deleteGameClip>;

	/**
				post game clip
			*/
	declare postGameClip: ReturnType<typeof postGameClip>;

	/**
				Edit game team sheet
			*/
	declare putGameTeamSheet: ReturnType<typeof putGameTeamSheet>;

	/**
				endpoint to gain game team sheet load by composition
			*/
	declare getGameTeamSheetCompositionPlayers: ReturnType<
		typeof getGameTeamSheetCompositionPlayers
	>;

	/**
				endpoint to gain game team sheet players based on associated club
			*/
	declare getGameTeamPlayersSearch: ReturnType<typeof getGameTeamPlayersSearch>;

	/**
				endpoint to gain game team sheet club players
			*/
	declare getGameTeamSheetClubPlayers: ReturnType<
		typeof getGameTeamSheetClubPlayers
	>;

	/**
				put game description
			*/
	declare putGameDescription: ReturnType<typeof putGameDescription>;

	/**
				post game stats export
			*/
	declare postGameStatsExport: ReturnType<typeof postGameStatsExport>;

	/**
				post game event data export
			*/
	declare postGameEventDataExport: ReturnType<typeof postGameEventDataExport>;

	/**
				get game clip is protected
			*/
	declare getSharedGameClipIsProtected: ReturnType<
		typeof getSharedGameClipIsProtected
	>;

	/**
				get game clip
			*/
	declare getSharedGameClip: ReturnType<typeof getSharedGameClip>;

	/**
				post game clip shared password validation
			*/
	declare postGameClipSharedPasswordValidation: ReturnType<
		typeof postGameClipSharedPasswordValidation
	>;

	/**
				get game event filters, limited by current user ownership
			*/
	declare getGameEventFilters: ReturnType<typeof getGameEventFilters>;

	/**
				post game event filter
			*/
	declare postGameEventFilter: ReturnType<typeof postGameEventFilter>;

	/**
				delete game event filter
			*/
	declare deleteGameEventFilter: ReturnType<typeof deleteGameEventFilter>;

	declare addCommentToGameEvent: ReturnType<typeof addCommentToGameEvent>;

	declare deleteGameEventComment: ReturnType<typeof deleteGameEventComment>;

	/**
				Get all available view options including countries per data source and gender
			*/
	declare getUserView: ReturnType<typeof getUserView>;

	declare updateSelectedView: ReturnType<typeof updateSelectedView>;

	/**
				get mfa domain list
			*/
	declare getMfaDomainList: ReturnType<typeof getMfaDomainList>;

	/**
				Check whether a domain requires MFA.
			*/
	declare getMfaDomainRequiredCheck: ReturnType<
		typeof getMfaDomainRequiredCheck
	>;

	/**
				Report a tagging issue for a game event
			*/
	declare postReportedTaggingIssue: ReturnType<typeof postReportedTaggingIssue>;

	/**
				Get all user countries
			*/
	declare getUserAllCountries: ReturnType<typeof getUserAllCountries>;

	/**
				Get user&#39;s selected countries
			*/
	declare getUserSelectedCountries: ReturnType<typeof getUserSelectedCountries>;

	declare updateUserSelectedCountries: ReturnType<
		typeof updateUserSelectedCountries
	>;

	/**
				Get team-group teams and clubs visible in search for the country-selection modal preview
			*/
	declare getTeamGroupVisibleEntities: ReturnType<
		typeof getTeamGroupVisibleEntities
	>;

	/**
				Returns the AI-generated summary for a game.
			*/
	declare portalAIGameSummaryGet: ReturnType<typeof portalAIGameSummaryGet>;

	/**
				Submits or updates feedback for an AI-generated game summary.
			*/
	declare portalAIGameSummaryFeedbackPut: ReturnType<
		typeof portalAIGameSummaryFeedbackPut
	>;

	/**
				Returns the authenticated recruiter info.
			*/
	declare portalAIScoutSearchUserInfo: ReturnType<
		typeof portalAIScoutSearchUserInfo
	>;

	/**
				Lists AI scout-search conversations
			*/
	declare portalAIConversationsList: ReturnType<
		typeof portalAIConversationsList
	>;

	/**
				Deletes an AI scout-search conversation.
			*/
	declare portalAIConversationsDelete: ReturnType<
		typeof portalAIConversationsDelete
	>;

	/**
				Updates an AI scout-search conversation.
			*/
	declare portalAIConversationsPatch: ReturnType<
		typeof portalAIConversationsPatch
	>;

	/**
				Returns AI scout-search prompt cards (quick, deep, and advanced).
			*/
	declare portalAIPromptCardsList: ReturnType<typeof portalAIPromptCardsList>;

	/**
				Submits feedback for an AI scout search execution.
			*/
	declare portalAIPutExecutionFeedback: ReturnType<
		typeof portalAIPutExecutionFeedback
	>;

	/**
				Get the League list based on the country code
			*/
	declare getLeagues: ReturnType<typeof getLeagues>;

	/**
				Get the national leagues clubs
			*/
	declare getClubs: ReturnType<typeof getClubs>;

	/**
				Get the national league games
			*/
	declare getNationalLeagueGames: ReturnType<typeof getNationalLeagueGames>;

	/**
				Get the countries list in league table
			*/
	declare getLeagueCountries: ReturnType<typeof getLeagueCountries>;

	/**
				Add player grading
			*/
	declare postPlayerGrading: ReturnType<typeof postPlayerGrading>;

	/**
				Get player grading detail
			*/
	declare getPlayerGrading: ReturnType<typeof getPlayerGrading>;

	/**
				Edit player grading
			*/
	declare putPlayerGrading: ReturnType<typeof putPlayerGrading>;

	/**
				Delete player grading
			*/
	declare deletePlayerGrading: ReturnType<typeof deletePlayerGrading>;

	/**
				Get player grading notes
			*/
	declare getPlayerGradingNotes: ReturnType<typeof getPlayerGradingNotes>;

	/**
				Get player grading contributors
			*/
	declare getPlayerGradingContributors: ReturnType<
		typeof getPlayerGradingContributors
	>;

	/**
				Pin player
			*/
	declare postPinMyPlayer: ReturnType<typeof postPinMyPlayer>;

	/**
				Unpin player
			*/
	declare deletePinMyPlayer: ReturnType<typeof deletePinMyPlayer>;

	/**
				Get player profile header
			*/
	declare getPlayerProfileHeader: ReturnType<typeof getPlayerProfileHeader>;

	/**
				Get player profile average rating
			*/
	declare getPlayerProfileAverageRating: ReturnType<
		typeof getPlayerProfileAverageRating
	>;

	/**
				Get player profile standout metrics
			*/
	declare getPlayerProfileStandoutMetrics: ReturnType<
		typeof getPlayerProfileStandoutMetrics
	>;

	/**
				Get player profile seasonal stats
			*/
	declare getPlayerProfileSeasonalStats: ReturnType<
		typeof getPlayerProfileSeasonalStats
	>;

	/**
				Get player profile latest or best games
			*/
	declare getPlayerProfileLatestGames: ReturnType<
		typeof getPlayerProfileLatestGames
	>;

	/**
				Get player profile games
			*/
	declare getPlayerProfileGames: ReturnType<typeof getPlayerProfileGames>;

	/**
				Get player profile performance radar
			*/
	declare getPlayerProfilePerformanceRadar: ReturnType<
		typeof getPlayerProfilePerformanceRadar
	>;

	/**
				Get player profile club history
			*/
	declare getPlayerProfileClubHistory: ReturnType<
		typeof getPlayerProfileClubHistory
	>;

	/**
				Get player profile performance
			*/
	declare getPlayerProfilePerformance: ReturnType<
		typeof getPlayerProfilePerformance
	>;

	/**
				Get player profile event types list
			*/
	declare getPlayerProfileEventTypes: ReturnType<
		typeof getPlayerProfileEventTypes
	>;

	/**
				Get player profile event filters list
			*/
	declare getPlayerProfileEventFilters: ReturnType<
		typeof getPlayerProfileEventFilters
	>;

	/**
				Get player profile events list
			*/
	declare getPlayerProfileEvents: ReturnType<typeof getPlayerProfileEvents>;

	/**
				Get player profile key events list
			*/
	declare getPlayerProfileKeyEvents: ReturnType<
		typeof getPlayerProfileKeyEvents
	>;

	/**
				Create a video clip composed of intervals from multiple games
			*/
	declare postPlayerProfileCrossGameClip: ReturnType<
		typeof postPlayerProfileCrossGameClip
	>;

	/**
				Get player profile shadow team list
			*/
	declare getPlayerProfileShadowTeamList: ReturnType<
		typeof getPlayerProfileShadowTeamList
	>;

	/**
				Add all players belonging to a player data into one or more shadow teams
			*/
	declare postPlayerProfileShadowTeamPlayer: ReturnType<
		typeof postPlayerProfileShadowTeamPlayer
	>;

	/**
				Create a new shadow team
			*/
	declare postPlayerProfileShadowTeam: ReturnType<
		typeof postPlayerProfileShadowTeam
	>;

	/**
				Get the Recruitment Information section of the player profile. Only populated answers are returned, and the academic ones only once the player has made them public.
			*/
	declare getPlayerProfileRecruitmentInformation: ReturnType<
		typeof getPlayerProfileRecruitmentInformation
	>;

	/**
				Tells the client whether the player profile biometrics sub-tab may be rendered
			*/
	declare getPlayerProfileBiometricsAvailability: ReturnType<
		typeof getPlayerProfileBiometricsAvailability
	>;

	/**
				Get player profile body measurements
			*/
	declare getPlayerProfileBodyMeasurements: ReturnType<
		typeof getPlayerProfileBodyMeasurements
	>;

	/**
				Get player profile body metric trend
			*/
	declare getPlayerProfileBodyMetricTrend: ReturnType<
		typeof getPlayerProfileBodyMetricTrend
	>;

	/**
				Get player profile body composition change
			*/
	declare getPlayerProfileBodyCompositionChange: ReturnType<
		typeof getPlayerProfileBodyCompositionChange
	>;

	/**
				Get player profile physical metrics filters list
			*/
	declare getPlayerProfilePhysicalMetricsFilters: ReturnType<
		typeof getPlayerProfilePhysicalMetricsFilters
	>;

	/**
				Get player profile physical metrics
			*/
	declare getPlayerProfilePhysicalMetrics: ReturnType<
		typeof getPlayerProfilePhysicalMetrics
	>;

	/**
				Get player profile behavioral signals filters list
			*/
	declare getPlayerProfileBehavioralSignalsFilters: ReturnType<
		typeof getPlayerProfileBehavioralSignalsFilters
	>;

	/**
				Get player profile behavioral signals trend chart
			*/
	declare getPlayerProfileBehavioralSignalsTrend: ReturnType<
		typeof getPlayerProfileBehavioralSignalsTrend
	>;

	/**
				Get player profile behavioral signals radar snapshot
			*/
	declare getPlayerProfileBehavioralSignalsSnapshot: ReturnType<
		typeof getPlayerProfileBehavioralSignalsSnapshot
	>;

	/**
				Get player profile behavioral archetype profile
			*/
	declare getPlayerProfileBehavioralArchetypes: ReturnType<
		typeof getPlayerProfileBehavioralArchetypes
	>;

	/**
				Login user via json
			*/
	declare jsonLogin: ReturnType<typeof jsonLogin>;

	/**
				Exchanges the authenticated Portal session for a short-lived jwt access token. If a user does not have an email - the jwt token will not authenticate.
			*/
	declare portalAccessTokenPost: ReturnType<typeof portalAccessTokenPost>;

	/**
				Get currently logged in user
			*/
	declare getCurrentUser: ReturnType<typeof getCurrentUser>;

	/**
				Logout user via json
			*/
	declare jsonLogout: ReturnType<typeof jsonLogout>;

	/**
				Update User Locale
			*/
	declare changeUserLocale: ReturnType<typeof changeUserLocale>;

	/**
				request for changing password
			*/
	declare forgotPassword: ReturnType<typeof forgotPassword>;

	/**
				reset a password
			*/
	declare resetPassword: ReturnType<typeof resetPassword>;

	/**
				check if the user token is valid or not
			*/
	declare validateUserPasswordReset: ReturnType<
		typeof validateUserPasswordReset
	>;

	/**
				Get subscription country message
			*/
	declare getSubscriptionCountryMessage: ReturnType<
		typeof getSubscriptionCountryMessage
	>;

	/**
				Remove the countries session details
			*/
	declare removeCountriesSessionDetails: ReturnType<
		typeof removeCountriesSessionDetails
	>;

	/**
				search for player
			*/
	declare playerSearch: ReturnType<typeof playerSearch>;

	/**
				get presets for current user
			*/
	declare playerSearchPresetColumnGet: ReturnType<
		typeof playerSearchPresetColumnGet
	>;

	/**
				Update or create column preset for current user.
			*/
	declare playerSearchPresetColumnPut: ReturnType<
		typeof playerSearchPresetColumnPut
	>;

	/**
				Delete column preset for current user.
			*/
	declare playerSearchPresetColumnDelete: ReturnType<
		typeof playerSearchPresetColumnDelete
	>;

	/**
				get filter presets for current user
			*/
	declare playerSearchPresetFilterGet: ReturnType<
		typeof playerSearchPresetFilterGet
	>;

	/**
				Update or create filter preset for current user.
			*/
	declare playerSearchPresetFilterPut: ReturnType<
		typeof playerSearchPresetFilterPut
	>;

	/**
				Delete column preset for current user.
			*/
	declare playerSearchPresetFilterDelete: ReturnType<
		typeof playerSearchPresetFilterDelete
	>;

	/**
				Get nationality list
			*/
	declare getAvailableNationalities: ReturnType<
		typeof getAvailableNationalities
	>;

	/**
				Get all highlighted players
			*/
	declare getHightLightedPlayers: ReturnType<typeof getHightLightedPlayers>;

	/**
				Get count of club and player
			*/
	declare getLinkCounts: ReturnType<typeof getLinkCounts>;

	/**
				Get fav count of clubs, players, teams, and games
			*/
	declare getMyEyeball: ReturnType<typeof getMyEyeball>;

	/**
				Accept terms of use
			*/
	declare acceptTermsOfUse: ReturnType<typeof acceptTermsOfUse>;

	/**
				Suggest Games, Clubs and Players
			*/
	declare globalSearchSuggestions: ReturnType<typeof globalSearchSuggestions>;

	/**
				Search Games, Clubs and Players
			*/
	declare globalSearch: ReturnType<typeof globalSearch>;

	/**
				Get Suggested Games
			*/
	declare getSuggestedGames: ReturnType<typeof getSuggestedGames>;

	/**
				add the club in favorites
			*/
	declare favoriteClub: ReturnType<typeof favoriteClub>;

	/**
				remove the club in favorites
			*/
	declare unFavoriteClub: ReturnType<typeof unFavoriteClub>;

	/**
				Get Random Clubs (Partner and Non partner)
			*/
	declare getRandomClubs: ReturnType<typeof getRandomClubs>;

	/**
				add the player in favorites
			*/
	declare favoritePlayer: ReturnType<typeof favoritePlayer>;

	/**
				remove the player in favorites
			*/
	declare unFavoritePlayer: ReturnType<typeof unFavoritePlayer>;

	/**
				Get Latest Games
			*/
	declare getLatestGames: ReturnType<typeof getLatestGames>;

	/**
				add the team in favorites
			*/
	declare favoriteTeam: ReturnType<typeof favoriteTeam>;

	/**
				remove the team in favorites
			*/
	declare unFavoriteTeam: ReturnType<typeof unFavoriteTeam>;

	/**
				add the game in favorites
			*/
	declare favoriteGame: ReturnType<typeof favoriteGame>;

	/**
				remove the game in favorites
			*/
	declare unfavoriteGame: ReturnType<typeof unfavoriteGame>;

	/**
				Get feature
			*/
	declare getFeatures: ReturnType<typeof getFeatures>;

	/**
				Get the seasons list
			*/
	declare getSeasons: ReturnType<typeof getSeasons>;

	/**
				Aggregate Player stat Calculation
			*/
	declare aggregatePlayerStat: ReturnType<typeof aggregatePlayerStat>;

	/**
				Get Player Played Games
			*/
	declare getPlayerPlayedGames: ReturnType<typeof getPlayerPlayedGames>;

	/**
				Create a new player possible duplicate record
			*/
	declare portalCreatePlayerPossibleDuplicate: ReturnType<
		typeof portalCreatePlayerPossibleDuplicate
	>;

	/**
				Get the News Feed
			*/
	declare getNewsfeed: ReturnType<typeof getNewsfeed>;

	/**
				Get the News Feed
			*/
	declare getNewsfeedTags: ReturnType<typeof getNewsfeedTags>;

	/**
				Get the individual post detail by slug
			*/
	declare getNewsPostDetail: ReturnType<typeof getNewsPostDetail>;

	/**
				Get the individual news preview by uid
			*/
	declare getNewsPreview: ReturnType<typeof getNewsPreview>;

	declare get_portal_news_webhook: ReturnType<typeof get_portal_news_webhook>;

	declare post_portal_news_webhook: ReturnType<typeof post_portal_news_webhook>;

	/**
				Log User Activity
			*/
	declare logUserActivity: ReturnType<typeof logUserActivity>;

	/**
				Get Clubs list
			*/
	declare getAllClubs: ReturnType<typeof getAllClubs>;

	/**
				Get Players list
			*/
	declare getAllPlayers: ReturnType<typeof getAllPlayers>;

	/**
				Get Game list
			*/
	declare getAllGames: ReturnType<typeof getAllGames>;

	/**
				Get User Games list
			*/
	declare getUserGames: ReturnType<typeof getUserGames>;

	/**
				Get User Clips list
			*/
	declare getUserClips: ReturnType<typeof getUserClips>;

	/**
				Rename the clip
			*/
	declare renameClip: ReturnType<typeof renameClip>;

	/**
				delete the clip
			*/
	declare deleteClip: ReturnType<typeof deleteClip>;

	/**
				Get User Clip event Detail
			*/
	declare getUserClipEventDetail: ReturnType<typeof getUserClipEventDetail>;

	/**
				Generate clip link
			*/
	declare generateClipLink: ReturnType<typeof generateClipLink>;

	/**
				download the clip
			*/
	declare downloadClip: ReturnType<typeof downloadClip>;

	/**
				share the clip link
			*/
	declare shareClipLink: ReturnType<typeof shareClipLink>;

	/**
				Get User Clip club list
			*/
	declare getUserClipClub: ReturnType<typeof getUserClipClub>;

	/**
				Get User Clip player list
			*/
	declare getUserClipPlayer: ReturnType<typeof getUserClipPlayer>;

	/**
				Get User Subscription list
			*/
	declare getUserSubscriptions: ReturnType<typeof getUserSubscriptions>;

	/**
				Get User Teams list
			*/
	declare getAllTeams: ReturnType<typeof getAllTeams>;

	/**
				Update the User Information Scout/Coach/ClubManager
			*/
	declare updateUserInformation: ReturnType<typeof updateUserInformation>;

	/**
				Getting the User Information Scout/Coach/ClubManager
			*/
	declare getUserInformation: ReturnType<typeof getUserInformation>;

	/**
				Uploading the User profile photo - Scout/Coach/ClubManager
			*/
	declare uploadUserProfilePicture: ReturnType<typeof uploadUserProfilePicture>;

	/**
				Removing the User Information Scout/Coach/ClubManager
			*/
	declare removeProfile: ReturnType<typeof removeProfile>;

	/**
				Get timezones
			*/
	declare getTimezones: ReturnType<typeof getTimezones>;

	/**
				Get User&#39;s email notifications
			*/
	declare getEmailNotifications: ReturnType<typeof getEmailNotifications>;

	/**
				update User&#39;s email notifications
			*/
	declare updateEmailNotifications: ReturnType<typeof updateEmailNotifications>;

	/**
				get players email notifications
			*/
	declare getPlayerEmailNotifications: ReturnType<
		typeof getPlayerEmailNotifications
	>;

	/**
				update players email notifications
			*/
	declare updatePlayerEmailNotifications: ReturnType<
		typeof updatePlayerEmailNotifications
	>;

	/**
				get clubs email notifications
			*/
	declare getClubEmailNotifications: ReturnType<
		typeof getClubEmailNotifications
	>;

	/**
				update clubs email notifications
			*/
	declare updateClubEmailNotifications: ReturnType<
		typeof updateClubEmailNotifications
	>;

	/**
				get teams email notifications
			*/
	declare getTeamEmailNotifications: ReturnType<
		typeof getTeamEmailNotifications
	>;

	/**
				update teams email notifications
			*/
	declare updateTeamEmailNotifications: ReturnType<
		typeof updateTeamEmailNotifications
	>;

	/**
				Get the logged in user club detail
			*/
	declare getClub: ReturnType<typeof getClub>;

	/**
				Get the logged in user club teams detail
			*/
	declare getClubTeams: ReturnType<typeof getClubTeams>;

	/**
				Get the logged in user club games detail
			*/
	declare getClubGames: ReturnType<typeof getClubGames>;

	/**
				Get the logged in user club age categories. Only non-partner clubs are supported
			*/
	declare getClubGamesAgeCategories: ReturnType<
		typeof getClubGamesAgeCategories
	>;

	/**
				Get the logged in user club/team players age categories.
			*/
	declare getClubPlayersAgeCategories: ReturnType<
		typeof getClubPlayersAgeCategories
	>;

	/**
				Getting the Game played Team names, category and slug
			*/
	declare getGamePlayedTeams: ReturnType<typeof getGamePlayedTeams>;

	/**
				search for ClubName
			*/
	declare playerClubList: ReturnType<typeof playerClubList>;

	/**
				search for PlayerName
			*/
	declare playerNameList: ReturnType<typeof playerNameList>;

	/**
				Load the user&#39;s club games list
			*/
	declare loadGames: ReturnType<typeof loadGames>;

	/**
				Add or update Game
			*/
	declare addOrUpdateGame: ReturnType<typeof addOrUpdateGame>;

	/**
				Deleting Programmed Game
			*/
	declare deleteGame: ReturnType<typeof deleteGame>;

	/**
				Updating Game Team scores
			*/
	declare updateTeamScores: ReturnType<typeof updateTeamScores>;

	/**
				Home Club and Away Club - Game Table Dropdown For filters
			*/
	declare gameDropdownData: ReturnType<typeof gameDropdownData>;

	/**
				Load Home and Away Teams in Game Form
			*/
	declare loadHomeAndAwayTeams: ReturnType<typeof loadHomeAndAwayTeams>;

	/**
				Search Clubs
			*/
	declare searchClubs: ReturnType<typeof searchClubs>;

	/**
				Get Matching Partner and NonPartner Clubs
			*/
	declare similarClubs: ReturnType<typeof similarClubs>;

	/**
				Get user club all teams
			*/
	declare getClubAllTeams: ReturnType<typeof getClubAllTeams>;

	/**
				Add and Update the Team
			*/
	declare addUpdateTeam: ReturnType<typeof addUpdateTeam>;

	/**
				Get team detail for team page
			*/
	declare getTeamDetail: ReturnType<typeof getTeamDetail>;

	/**
				Delete the Team by id
			*/
	declare deleteTeam: ReturnType<typeof deleteTeam>;

	/**
				Get Coaches of the Club
			*/
	declare clubCoaches: ReturnType<typeof clubCoaches>;

	/**
				Get user club teams for dropdown
			*/
	declare getClubTeamsList: ReturnType<typeof getClubTeamsList>;

	/**
				Get Team Players
			*/
	declare teamPlayers: ReturnType<typeof teamPlayers>;

	/**
				Add and Update the Player
			*/
	declare addUpdatePlayer: ReturnType<typeof addUpdatePlayer>;

	/**
				Get old or non team players in the club
			*/
	declare formerPlayers: ReturnType<typeof formerPlayers>;

	/**
				Get team by id and coach id
			*/
	declare getTeamByIdAndCoachId: ReturnType<typeof getTeamByIdAndCoachId>;

	/**
				Upload photo for Player
			*/
	declare uploadPlayerPhoto: ReturnType<typeof uploadPlayerPhoto>;

	/**
				Assign and unassign multiple coaches to the team
			*/
	declare assignCoachesToTeam: ReturnType<typeof assignCoachesToTeam>;

	/**
				Get position list
			*/
	declare getPositions: ReturnType<typeof getPositions>;

	/**
				Get nationality list
			*/
	declare getNationalities: ReturnType<typeof getNationalities>;

	/**
				Get category list
			*/
	declare getCategories: ReturnType<typeof getCategories>;

	/**
				generate sign up link for specific player
			*/
	declare generateSignUpLink: ReturnType<typeof generateSignUpLink>;

	/**
				Retrieve the latest updated values of a player&#39;s height, weight and photo.
			*/
	declare getPlayerLatestLogEntries: ReturnType<
		typeof getPlayerLatestLogEntries
	>;

	/**
				Get players of the week
			*/
	declare getPlayersOfTheWeek: ReturnType<typeof getPlayersOfTheWeek>;

	/**
				Search players of the week
			*/
	declare getPlayersOfTheWeekSearch: ReturnType<
		typeof getPlayersOfTheWeekSearch
	>;

	/**
				Week numbers
			*/
	declare getWeekNumbers: ReturnType<typeof getWeekNumbers>;

	/**
				endpoint to gain game team sheet export by type
			*/
	declare postGameTeamSheetExport: ReturnType<typeof postGameTeamSheetExport>;

	/**
				Get user widget settings
			*/
	declare getUserWidgetSettings: ReturnType<typeof getUserWidgetSettings>;

	/**
				Create or update user widget settings
			*/
	declare saveUserWidgetSettings: ReturnType<typeof saveUserWidgetSettings>;

	/**
				get announce kit token
			*/
	declare getAnnounceKitToken: ReturnType<typeof getAnnounceKitToken>;

	/**
				Retrieves physical game analysis data, including report, player data, event, and video URLs, for a specific game identified by its ID.
			*/
	declare portalGetGameAnalysisPhysical: ReturnType<
		typeof portalGetGameAnalysisPhysical
	>;

	/**
				Generates redirect url with one time token to sign in discover
			*/
	declare generateDiscoverRedirect: ReturnType<typeof generateDiscoverRedirect>;

	declare getShadowTeams: ReturnType<typeof getShadowTeams>;

	declare createShadowTeam: ReturnType<typeof createShadowTeam>;

	declare getShadowTeam: ReturnType<typeof getShadowTeam>;

	declare updateShadowTeam: ReturnType<typeof updateShadowTeam>;

	declare deleteShadowTeam: ReturnType<typeof deleteShadowTeam>;

	declare updateShadowTeamPartially: ReturnType<
		typeof updateShadowTeamPartially
	>;

	constructor(
		baseUrl: string,
		queryClient: QueryClient,
		options: Pick<RequestInit, "headers" | "credentials" | "mode"> = {},
		onHttpConfigure: PortalApiClient["onHttpConfigure"] = (wretch) => wretch,
	) {
		this.queryClient = queryClient;
		this.onHttpConfigure = onHttpConfigure;
		this.operations = new Map();

		this.operations.set("getTokenMovements", getTokenMovements);

		this.operations.set("postTokenMovement", postTokenMovement);

		this.operations.set("getGamePage", getGamePage);

		this.operations.set("getGamePageLazy", getGamePageLazy);

		this.operations.set("putGameScore", putGameScore);

		this.operations.set("getGameTeamStat", getGameTeamStat);

		this.operations.set("getGamePlayerStat", getGamePlayerStat);

		this.operations.set("putGameTeamColors", putGameTeamColors);

		this.operations.set("setRatingToGamePlayer", setRatingToGamePlayer);

		this.operations.set("setBadgesToGamePlayer", setBadgesToGamePlayer);

		this.operations.set("postGameHighlight", postGameHighlight);

		this.operations.set("deleteGameHighlight", deleteGameHighlight);

		this.operations.set("patchGameHighlight", patchGameHighlight);

		this.operations.set("getGameClips", getGameClips);

		this.operations.set("getGameClip", getGameClip);

		this.operations.set("putGameClip", putGameClip);

		this.operations.set("deleteGameClip", deleteGameClip);

		this.operations.set("postGameClip", postGameClip);

		this.operations.set("putGameTeamSheet", putGameTeamSheet);

		this.operations.set(
			"getGameTeamSheetCompositionPlayers",
			getGameTeamSheetCompositionPlayers,
		);

		this.operations.set("getGameTeamPlayersSearch", getGameTeamPlayersSearch);

		this.operations.set(
			"getGameTeamSheetClubPlayers",
			getGameTeamSheetClubPlayers,
		);

		this.operations.set("putGameDescription", putGameDescription);

		this.operations.set("postGameStatsExport", postGameStatsExport);

		this.operations.set("postGameEventDataExport", postGameEventDataExport);

		this.operations.set(
			"getSharedGameClipIsProtected",
			getSharedGameClipIsProtected,
		);

		this.operations.set("getSharedGameClip", getSharedGameClip);

		this.operations.set(
			"postGameClipSharedPasswordValidation",
			postGameClipSharedPasswordValidation,
		);

		this.operations.set("getGameEventFilters", getGameEventFilters);

		this.operations.set("postGameEventFilter", postGameEventFilter);

		this.operations.set("deleteGameEventFilter", deleteGameEventFilter);

		this.operations.set("addCommentToGameEvent", addCommentToGameEvent);

		this.operations.set("deleteGameEventComment", deleteGameEventComment);

		this.operations.set("getUserView", getUserView);

		this.operations.set("updateSelectedView", updateSelectedView);

		this.operations.set("getMfaDomainList", getMfaDomainList);

		this.operations.set("getMfaDomainRequiredCheck", getMfaDomainRequiredCheck);

		this.operations.set("postReportedTaggingIssue", postReportedTaggingIssue);

		this.operations.set("getUserAllCountries", getUserAllCountries);

		this.operations.set("getUserSelectedCountries", getUserSelectedCountries);

		this.operations.set(
			"updateUserSelectedCountries",
			updateUserSelectedCountries,
		);

		this.operations.set(
			"getTeamGroupVisibleEntities",
			getTeamGroupVisibleEntities,
		);

		this.operations.set("portalAIGameSummaryGet", portalAIGameSummaryGet);

		this.operations.set(
			"portalAIGameSummaryFeedbackPut",
			portalAIGameSummaryFeedbackPut,
		);

		this.operations.set(
			"portalAIScoutSearchUserInfo",
			portalAIScoutSearchUserInfo,
		);

		this.operations.set("portalAIConversationsList", portalAIConversationsList);

		this.operations.set(
			"portalAIConversationsDelete",
			portalAIConversationsDelete,
		);

		this.operations.set(
			"portalAIConversationsPatch",
			portalAIConversationsPatch,
		);

		this.operations.set("portalAIPromptCardsList", portalAIPromptCardsList);

		this.operations.set(
			"portalAIPutExecutionFeedback",
			portalAIPutExecutionFeedback,
		);

		this.operations.set("getLeagues", getLeagues);

		this.operations.set("getClubs", getClubs);

		this.operations.set("getNationalLeagueGames", getNationalLeagueGames);

		this.operations.set("getLeagueCountries", getLeagueCountries);

		this.operations.set("postPlayerGrading", postPlayerGrading);

		this.operations.set("getPlayerGrading", getPlayerGrading);

		this.operations.set("putPlayerGrading", putPlayerGrading);

		this.operations.set("deletePlayerGrading", deletePlayerGrading);

		this.operations.set("getPlayerGradingNotes", getPlayerGradingNotes);

		this.operations.set(
			"getPlayerGradingContributors",
			getPlayerGradingContributors,
		);

		this.operations.set("postPinMyPlayer", postPinMyPlayer);

		this.operations.set("deletePinMyPlayer", deletePinMyPlayer);

		this.operations.set("getPlayerProfileHeader", getPlayerProfileHeader);

		this.operations.set(
			"getPlayerProfileAverageRating",
			getPlayerProfileAverageRating,
		);

		this.operations.set(
			"getPlayerProfileStandoutMetrics",
			getPlayerProfileStandoutMetrics,
		);

		this.operations.set(
			"getPlayerProfileSeasonalStats",
			getPlayerProfileSeasonalStats,
		);

		this.operations.set(
			"getPlayerProfileLatestGames",
			getPlayerProfileLatestGames,
		);

		this.operations.set("getPlayerProfileGames", getPlayerProfileGames);

		this.operations.set(
			"getPlayerProfilePerformanceRadar",
			getPlayerProfilePerformanceRadar,
		);

		this.operations.set(
			"getPlayerProfileClubHistory",
			getPlayerProfileClubHistory,
		);

		this.operations.set(
			"getPlayerProfilePerformance",
			getPlayerProfilePerformance,
		);

		this.operations.set(
			"getPlayerProfileEventTypes",
			getPlayerProfileEventTypes,
		);

		this.operations.set(
			"getPlayerProfileEventFilters",
			getPlayerProfileEventFilters,
		);

		this.operations.set("getPlayerProfileEvents", getPlayerProfileEvents);

		this.operations.set("getPlayerProfileKeyEvents", getPlayerProfileKeyEvents);

		this.operations.set(
			"postPlayerProfileCrossGameClip",
			postPlayerProfileCrossGameClip,
		);

		this.operations.set(
			"getPlayerProfileShadowTeamList",
			getPlayerProfileShadowTeamList,
		);

		this.operations.set(
			"postPlayerProfileShadowTeamPlayer",
			postPlayerProfileShadowTeamPlayer,
		);

		this.operations.set(
			"postPlayerProfileShadowTeam",
			postPlayerProfileShadowTeam,
		);

		this.operations.set(
			"getPlayerProfileRecruitmentInformation",
			getPlayerProfileRecruitmentInformation,
		);

		this.operations.set(
			"getPlayerProfileBiometricsAvailability",
			getPlayerProfileBiometricsAvailability,
		);

		this.operations.set(
			"getPlayerProfileBodyMeasurements",
			getPlayerProfileBodyMeasurements,
		);

		this.operations.set(
			"getPlayerProfileBodyMetricTrend",
			getPlayerProfileBodyMetricTrend,
		);

		this.operations.set(
			"getPlayerProfileBodyCompositionChange",
			getPlayerProfileBodyCompositionChange,
		);

		this.operations.set(
			"getPlayerProfilePhysicalMetricsFilters",
			getPlayerProfilePhysicalMetricsFilters,
		);

		this.operations.set(
			"getPlayerProfilePhysicalMetrics",
			getPlayerProfilePhysicalMetrics,
		);

		this.operations.set(
			"getPlayerProfileBehavioralSignalsFilters",
			getPlayerProfileBehavioralSignalsFilters,
		);

		this.operations.set(
			"getPlayerProfileBehavioralSignalsTrend",
			getPlayerProfileBehavioralSignalsTrend,
		);

		this.operations.set(
			"getPlayerProfileBehavioralSignalsSnapshot",
			getPlayerProfileBehavioralSignalsSnapshot,
		);

		this.operations.set(
			"getPlayerProfileBehavioralArchetypes",
			getPlayerProfileBehavioralArchetypes,
		);

		this.operations.set("jsonLogin", jsonLogin);

		this.operations.set("portalAccessTokenPost", portalAccessTokenPost);

		this.operations.set("getCurrentUser", getCurrentUser);

		this.operations.set("jsonLogout", jsonLogout);

		this.operations.set("changeUserLocale", changeUserLocale);

		this.operations.set("forgotPassword", forgotPassword);

		this.operations.set("resetPassword", resetPassword);

		this.operations.set("validateUserPasswordReset", validateUserPasswordReset);

		this.operations.set(
			"getSubscriptionCountryMessage",
			getSubscriptionCountryMessage,
		);

		this.operations.set(
			"removeCountriesSessionDetails",
			removeCountriesSessionDetails,
		);

		this.operations.set("playerSearch", playerSearch);

		this.operations.set(
			"playerSearchPresetColumnGet",
			playerSearchPresetColumnGet,
		);

		this.operations.set(
			"playerSearchPresetColumnPut",
			playerSearchPresetColumnPut,
		);

		this.operations.set(
			"playerSearchPresetColumnDelete",
			playerSearchPresetColumnDelete,
		);

		this.operations.set(
			"playerSearchPresetFilterGet",
			playerSearchPresetFilterGet,
		);

		this.operations.set(
			"playerSearchPresetFilterPut",
			playerSearchPresetFilterPut,
		);

		this.operations.set(
			"playerSearchPresetFilterDelete",
			playerSearchPresetFilterDelete,
		);

		this.operations.set("getAvailableNationalities", getAvailableNationalities);

		this.operations.set("getHightLightedPlayers", getHightLightedPlayers);

		this.operations.set("getLinkCounts", getLinkCounts);

		this.operations.set("getMyEyeball", getMyEyeball);

		this.operations.set("acceptTermsOfUse", acceptTermsOfUse);

		this.operations.set("globalSearchSuggestions", globalSearchSuggestions);

		this.operations.set("globalSearch", globalSearch);

		this.operations.set("getSuggestedGames", getSuggestedGames);

		this.operations.set("favoriteClub", favoriteClub);

		this.operations.set("unFavoriteClub", unFavoriteClub);

		this.operations.set("getRandomClubs", getRandomClubs);

		this.operations.set("favoritePlayer", favoritePlayer);

		this.operations.set("unFavoritePlayer", unFavoritePlayer);

		this.operations.set("getLatestGames", getLatestGames);

		this.operations.set("favoriteTeam", favoriteTeam);

		this.operations.set("unFavoriteTeam", unFavoriteTeam);

		this.operations.set("favoriteGame", favoriteGame);

		this.operations.set("unfavoriteGame", unfavoriteGame);

		this.operations.set("getFeatures", getFeatures);

		this.operations.set("getSeasons", getSeasons);

		this.operations.set("aggregatePlayerStat", aggregatePlayerStat);

		this.operations.set("getPlayerPlayedGames", getPlayerPlayedGames);

		this.operations.set(
			"portalCreatePlayerPossibleDuplicate",
			portalCreatePlayerPossibleDuplicate,
		);

		this.operations.set("getNewsfeed", getNewsfeed);

		this.operations.set("getNewsfeedTags", getNewsfeedTags);

		this.operations.set("getNewsPostDetail", getNewsPostDetail);

		this.operations.set("getNewsPreview", getNewsPreview);

		this.operations.set("get_portal_news_webhook", get_portal_news_webhook);

		this.operations.set("post_portal_news_webhook", post_portal_news_webhook);

		this.operations.set("logUserActivity", logUserActivity);

		this.operations.set("getAllClubs", getAllClubs);

		this.operations.set("getAllPlayers", getAllPlayers);

		this.operations.set("getAllGames", getAllGames);

		this.operations.set("getUserGames", getUserGames);

		this.operations.set("getUserClips", getUserClips);

		this.operations.set("renameClip", renameClip);

		this.operations.set("deleteClip", deleteClip);

		this.operations.set("getUserClipEventDetail", getUserClipEventDetail);

		this.operations.set("generateClipLink", generateClipLink);

		this.operations.set("downloadClip", downloadClip);

		this.operations.set("shareClipLink", shareClipLink);

		this.operations.set("getUserClipClub", getUserClipClub);

		this.operations.set("getUserClipPlayer", getUserClipPlayer);

		this.operations.set("getUserSubscriptions", getUserSubscriptions);

		this.operations.set("getAllTeams", getAllTeams);

		this.operations.set("updateUserInformation", updateUserInformation);

		this.operations.set("getUserInformation", getUserInformation);

		this.operations.set("uploadUserProfilePicture", uploadUserProfilePicture);

		this.operations.set("removeProfile", removeProfile);

		this.operations.set("getTimezones", getTimezones);

		this.operations.set("getEmailNotifications", getEmailNotifications);

		this.operations.set("updateEmailNotifications", updateEmailNotifications);

		this.operations.set(
			"getPlayerEmailNotifications",
			getPlayerEmailNotifications,
		);

		this.operations.set(
			"updatePlayerEmailNotifications",
			updatePlayerEmailNotifications,
		);

		this.operations.set("getClubEmailNotifications", getClubEmailNotifications);

		this.operations.set(
			"updateClubEmailNotifications",
			updateClubEmailNotifications,
		);

		this.operations.set("getTeamEmailNotifications", getTeamEmailNotifications);

		this.operations.set(
			"updateTeamEmailNotifications",
			updateTeamEmailNotifications,
		);

		this.operations.set("getClub", getClub);

		this.operations.set("getClubTeams", getClubTeams);

		this.operations.set("getClubGames", getClubGames);

		this.operations.set("getClubGamesAgeCategories", getClubGamesAgeCategories);

		this.operations.set(
			"getClubPlayersAgeCategories",
			getClubPlayersAgeCategories,
		);

		this.operations.set("getGamePlayedTeams", getGamePlayedTeams);

		this.operations.set("playerClubList", playerClubList);

		this.operations.set("playerNameList", playerNameList);

		this.operations.set("loadGames", loadGames);

		this.operations.set("addOrUpdateGame", addOrUpdateGame);

		this.operations.set("deleteGame", deleteGame);

		this.operations.set("updateTeamScores", updateTeamScores);

		this.operations.set("gameDropdownData", gameDropdownData);

		this.operations.set("loadHomeAndAwayTeams", loadHomeAndAwayTeams);

		this.operations.set("searchClubs", searchClubs);

		this.operations.set("similarClubs", similarClubs);

		this.operations.set("getClubAllTeams", getClubAllTeams);

		this.operations.set("addUpdateTeam", addUpdateTeam);

		this.operations.set("getTeamDetail", getTeamDetail);

		this.operations.set("deleteTeam", deleteTeam);

		this.operations.set("clubCoaches", clubCoaches);

		this.operations.set("getClubTeamsList", getClubTeamsList);

		this.operations.set("teamPlayers", teamPlayers);

		this.operations.set("addUpdatePlayer", addUpdatePlayer);

		this.operations.set("formerPlayers", formerPlayers);

		this.operations.set("getTeamByIdAndCoachId", getTeamByIdAndCoachId);

		this.operations.set("uploadPlayerPhoto", uploadPlayerPhoto);

		this.operations.set("assignCoachesToTeam", assignCoachesToTeam);

		this.operations.set("getPositions", getPositions);

		this.operations.set("getNationalities", getNationalities);

		this.operations.set("getCategories", getCategories);

		this.operations.set("generateSignUpLink", generateSignUpLink);

		this.operations.set("getPlayerLatestLogEntries", getPlayerLatestLogEntries);

		this.operations.set("getPlayersOfTheWeek", getPlayersOfTheWeek);

		this.operations.set("getPlayersOfTheWeekSearch", getPlayersOfTheWeekSearch);

		this.operations.set("getWeekNumbers", getWeekNumbers);

		this.operations.set("postGameTeamSheetExport", postGameTeamSheetExport);

		this.operations.set("getUserWidgetSettings", getUserWidgetSettings);

		this.operations.set("saveUserWidgetSettings", saveUserWidgetSettings);

		this.operations.set("getAnnounceKitToken", getAnnounceKitToken);

		this.operations.set(
			"portalGetGameAnalysisPhysical",
			portalGetGameAnalysisPhysical,
		);

		this.operations.set("generateDiscoverRedirect", generateDiscoverRedirect);

		this.operations.set("getShadowTeams", getShadowTeams);

		this.operations.set("createShadowTeam", createShadowTeam);

		this.operations.set("getShadowTeam", getShadowTeam);

		this.operations.set("updateShadowTeam", updateShadowTeam);

		this.operations.set("deleteShadowTeam", deleteShadowTeam);

		this.operations.set("updateShadowTeamPartially", updateShadowTeamPartially);

		const http = this.configureHttp(baseUrl, options);
		this.http = http;

		// eslint-disable-next-line
		// biome-ignore lint/correctness/noConstructorReturn: <explanation>
		return new Proxy(this, {
			get(target, name: string) {
				// eslint-disable-next-line
				// biome-ignore lint/suspicious/noPrototypeBuiltins: <explanation>
				if (target.hasOwnProperty(name)) {
					//@ts-ignore
					return target[name];
				}

				return target.operations.get(name)(http, queryClient);
			},
		});
	}

	private createHttp(
		baseUrl: string,
		options: Pick<RequestInit, "headers" | "credentials" | "mode"> = {},
	) {
		return wretch(baseUrl, options).addon(QueryStringAddon);
	}

	private configureHttp(
		baseUrl: string,
		options: Pick<RequestInit, "headers" | "credentials" | "mode"> = {},
	) {
		return this.onHttpConfigure(this.createHttp(baseUrl, options));
	}
}
